/**
 * Dynamic Viewer UI Module
 *
 * Generates and injects UI elements and CSS for the dynamic viewer.
 * Uses MINIMAL theme for embedded viewers.
 */
import * as pc from 'playcanvas';
import { attrUrl, escapeHtml, safeCssColor, safeUrl } from './safeUrls';
import { richTextHtml, RICH_TEXT_CSS } from './richText';
import { PROTECT_MEDIA_CSS, isMediaProtected } from './mediaProtection';
import { PSEUDO_FULLSCREEN_CSS, createPseudoFullscreen, detectFullscreenMode, syncButtonGroupVisibility } from './fullscreen';
import { STEREO_EYE_CLASS } from './stereoUi';
import { extraWaypointMenuEntries, waypointMenuEntries } from './extraWaypoints';
import { MEASUREMENT_SETS_HEADING, MEASUREMENTS_SECTION_TITLE, SHOW_ALL_MEASUREMENTS_LABEL } from './measurementSetsPicker';
import { OTHER_MEASUREMENTS_LABEL } from '../utils/measurementSets';
import type { OverlayData, HotspotContentBlock, ViewerConfig, ButtonLabels, ViewerButtonLabels, WaypointData, HotspotData, PortalData, ViewerTheme, UILayout, ElementPosition, UIElementId, SceneMenuLink } from '../types';
interface StorySplatOfflineAssets {
    disableRemoteFonts?: boolean;
    preloaderFontUrl?: string;
    pdfJsModuleUrl?: string;
    pdfJsWorkerUrl?: string;
}

function getOfflineAssets(): StorySplatOfflineAssets | undefined {
    return (window as typeof window & { __STORYSPLAT_OFFLINE_ASSETS__?: StorySplatOfflineAssets }).__STORYSPLAT_OFFLINE_ASSETS__;
}
interface ViewerUIController {
    nextWaypoint: () => void;
    prevWaypoint: () => void;
    play: () => void;
    pause: () => void;
    isPlaying: () => boolean;
    getCurrentWaypointIndex: () => number;
    getWaypointCount: () => number;
    getWaypoints?: () => WaypointData[];
    setCameraMode?: (mode: string) => void;
    /** The live camera mode ('tour', 'explore', 'walk', 'fpv'); panorama reports 'explore'. */
    getCameraMode?: () => string;
    shareCurrentView?: () => Promise<void>;
    setPerformanceMode?: (enabled: boolean) => void;
    getPerformanceMode?: () => boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    on: (event: string, callback: (...args: any[]) => void) => void;
}
interface WebkitFullscreenDocument extends Document {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
}
interface WebkitFullscreenElement extends HTMLElement {
    webkitRequestFullscreen?: () => Promise<void> | void;
}
/**
 * Default button labels for internationalization (i18n)
 */
// Not part of ButtonLabels: the frozen legacy HTML export mirrors that type as Required<>, so
// new keys there would break it. Fixed English strings for the performance controls.
const PERFORMANCE_MODE_LABEL = 'Performance mode';
const PERFORMANCE_MODE_DESC = 'Lower resolution and detail for slower devices';
export const DEFAULT_BUTTON_LABELS: Required<ViewerButtonLabels> = {
    tour: 'Tour',
    explore: 'Explore',
    hybrid: 'Hybrid',
    walk: 'Walk',
    orbit: 'Orbit',
    fly: 'Fly',
    next: 'Next',
    previous: 'Prev',
    startExperience: 'Start Experience',
    fullscreen: 'Fullscreen',
    mute: 'Mute',
    unmute: 'Unmute',
    share: 'Share',
    capturingPhoto: 'Capturing photo...',
    photoReady: 'Photo ready',
    downloadPhoto: 'Download Photo',
    photoDownloaded: 'Photo downloaded',
    copyLink: 'Copy Experience Link',
    linkCopied: 'Link copied!',
    shareFailed: 'Unable to share photo',
    waypoints: 'Waypoints',
    close: 'Close',
    yes: 'Yes',
    cancel: 'Cancel',
    switchScenes: 'Switch scenes?',
    hotspotDefaultTitle: 'Hotspot',
    openExternalLink: 'Open External Link',
    vr: 'VR',
    ar: 'AR',
    exitVr: 'Exit VR',
    enterVr: 'Enter VR',
    exitAr: 'Exit AR',
    exitPanorama: 'Exit 360°',
    loading: 'Loading...',
    loadingScene: 'Loading {name}...',
    helpTitle: 'Controls & Help',
    helpCameraModes: 'Camera Modes:',
    helpTourDesc: 'Follow predefined path',
    helpExploreDesc: 'Free movement',
    helpWalkDesc: 'First-person walking',
    helpTourControls: 'Tour Mode:',
    helpTourScroll: 'Scroll - Move along path',
    helpTourDrag: 'Drag - Look around',
    helpExploreControls: 'Explore Mode:',
    helpExploreLMB: 'LMB Drag - Orbit camera',
    helpExploreRMB: 'RMB Drag - Fly/look',
    helpExploreWASD: 'WASD/QE - Move camera',
    helpExploreShift: 'Shift - Move fast',
    helpExploreScroll: 'Scroll/Pinch - Zoom',
    helpExploreDblClick: 'Double-click - Focus',
    helpWalkControls: 'Walk Mode:',
    helpWalkClick: 'Click to lock mouse',
    helpWalkWASD: 'WASD/Arrows - Move',
    helpWalkMouse: 'Mouse - Look around',
    helpWalkShift: 'Shift - Sprint',
    helpWalkSpace: 'Space - Jump',
    errorWebGLTitle: 'Unable to Initialize 3D Graphics',
    errorWebGLMessage: 'Your browser or device may not support WebGL. Please try a different browser or device.',
    percentageFormat: '{n}%',
    scenes: 'Scenes',
    // Autoplay waiting for levels of detail (scene option autoPlayWaitForFullDetail)
    addingMoreDetail: 'Adding in more detail',
    startTourAnyway: 'Start the tour anyways',
    // Controls card
    controlsTitleMouse: 'Explore using your mouse',
    controlsTitleMouseKeyboard: 'Explore using your mouse and keyboard',
    controlsLookAround: 'Look around',
    controlsTourScroll: 'Progress through the tour',
    controlsOrbitRotate: 'Drag to rotate',
    controlsOrbitZoom: 'Scroll to zoom',
    controlsMove: 'Move',
    controlsTouchTour: 'Tap and drag to explore the scene',
    controlsTouchOrbit: 'Drag to rotate, pinch to zoom',
    controlsTouchMove: 'Drag to look around, use the joystick to move',
    // Measure panel
    measureButton: 'Measure',
    measureTitle: MEASUREMENTS_SECTION_TITLE,
    measureClearAll: 'Clear all',
    measureType: 'Measurement type',
    measureDistance: 'Distance',
    measureArea: 'Area',
    measureDistanceHint: 'Click two points to measure',
    measureAreaHint: 'Click points around a surface to measure its area',
    measureAreaOnePoint: '1 point — keep clicking around the surface',
    measureAreaPoints: '{n} points — keep clicking around the surface',
    measureAreaClose: 'Click the first point (or press Enter) to close',
    measureAreaFinish: 'Finish area',
    measureLocked: 'Default measurement',
    measureAreaNotFlat: 'These points are not on one flat surface; measure each face separately for an accurate area.',
    measureAreaCrossing: 'The outline crosses itself; redraw it without crossing edges.',
    measureSets: MEASUREMENT_SETS_HEADING,
    measureOtherSet: OTHER_MEASUREMENTS_LABEL,
    measureAllSets: SHOW_ALL_MEASUREMENTS_LABEL,
    // Visitor directory (its title is the creator's own)
    directoryViews: 'Views',
    directorySearch: 'Search',
    directorySearchLabel: 'Search the list',
    directoryNoMatch: 'No hotspots match',
    directoryNoMatchWithViews: 'No hotspots or views match',
    directoryUnavailable: 'Not visible here',
    directoryShowFolder: 'Show {name} in the scene',
    directoryHotspotCount: '{n} hotspot',
    directoryHotspotsCount: '{n} hotspots',
    directoryItemCount: '{n} item',
    directoryItemsCount: '{n} items',
    // VR
    vrMenu: 'Menu',
    vrShowMeasurements: 'Show measurements',
    vrSaveLink: 'Open after VR',
    vrLinkSaved: 'Saved for later',
    vrSavedLinks: 'Saved links',
    vrSavedLinksTitle: 'Links you saved in VR',
    vrPlay: 'Play',
    vrPause: 'Pause',
    vrPhotos: 'Photos',
    vrVideo: 'Video',
    vrPdf: 'PDF',
    vrModelOutsideVr: 'The 3D model shows outside VR.',
    vrWebContent: 'Web content shows outside VR.',
};

// Cutaway strings are viewer-only and deliberately NOT part of ButtonLabels: the frozen
// legacy exporter mirrors that type as Required<ButtonLabels> and would fail to compile.
export const CUTAWAY_LABELS = {
    toggle: 'Cutaway view',
    panel: 'Cutaway plane',
    slider: 'Cutaway height',
    flip: 'Flip hidden side',
} as const;
// Photo mode strings are viewer-only for the same reason.
export const PHOTO_MODE_LABELS = {
    enter: 'Take a photo',
    take: 'Take photo',
    exit: 'Exit photo mode',
    hint: 'Frame the view, then take the photo',
} as const;
// Stereo 3D strings are viewer-only for the same reason.
export const STEREO_LABELS = {
    toggle: '3D view',
    menu: '3D view',
    off: 'Off',
    anaglyph: 'Red/cyan glasses',
    anaglyphHint: 'Any screen',
    sbs: '3D TV: side by side',
    sbsHint: 'Set the TV or projector to side-by-side 3D',
    tb: '3D TV: top and bottom',
    tbHint: 'Set the TV or projector to top-and-bottom 3D',
    sbsFull: 'Full side by side',
    sbsFullHint: 'Free viewing, 3D monitors, phone viewers',
    swapEyes: 'Swap eyes',
} as const;
/** "Ask this scene" guide (uiOptions.sceneGuide; see sceneGuide.ts). Kept out of ButtonLabels on purpose. */
export const SCENE_GUIDE_LABELS = {
    defaultName: 'Guide',
    button: 'Ask',
    /** Button + panel accessible name; {name} is the guide's name. */
    ask: 'Ask {name}',
    greeting: 'Ask me anything about this place.',
    placeholder: 'Ask a question…',
    input: 'Your question',
    send: 'Send',
    stop: 'Stop answering',
    close: 'Close',
    conversation: 'Conversation',
    suggestions: 'Suggested questions',
    thinking: 'Thinking…',
    footnote: 'AI answers can be wrong.',
    errorGeneric: "The guide can't answer right now. Please try again in a moment.",
    errorNetwork: "Couldn't reach the guide. Check your connection and try again.",
    errorInterrupted: 'The answer was cut off. Please try again.',
};
export const SCENE_GUIDE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.4 8.4 0 0 1-12.2 7.5L3 20.5l1.6-5.2A8.4 8.4 0 1 1 21 11.5z"/><path d="M8.5 11.5h.01M12.5 11.5h.01M16.5 11.5h.01" stroke-width="2.6"/></svg>';
const STEREO_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 9h20"/><path d="M3 9v5a2 2 0 0 0 2 2h3.2a2 2 0 0 0 1.8-1.1L11 13h2l1 1.9a2 2 0 0 0 1.8 1.1H19a2 2 0 0 0 2-2V9"/><path d="M3 9 5 5"/><path d="M21 9l-2-4"/></svg>';
const PHOTO_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.5"/></svg>';
const CUTAWAY_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 11h16v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z"/><path d="M4 6h16" stroke-dasharray="3 2"/><path d="M12 2v6"/><path d="M9.5 5.5 12 8l2.5-2.5"/></svg>';
const CUTAWAY_FLIP_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 4v16"/><path d="M5 7l3-3 3 3"/><path d="M16 4v16"/><path d="M13 17l3 3 3-3"/></svg>';
/**
 * Get a button label, falling back to default if not provided
 */
export function getButtonLabel(labels: ViewerButtonLabels | undefined, key: keyof ViewerButtonLabels): string {
    return labels?.[key] || DEFAULT_BUTTON_LABELS[key];
}
/**
 * Format a percentage label using the configured format
 */
function formatPercentageLabel(labels: ButtonLabels | undefined, value: number): string {
    const format = labels?.percentageFormat || DEFAULT_BUTTON_LABELS.percentageFormat;
    return format.replace('{n}', String(value));
}
export interface UIOptions {
    uiColor?: string;
    showScrollControls?: boolean;
    showModeToggle?: boolean;
    showFullscreenButton?: boolean;
    showHelpButton?: boolean;
    showMuteButton?: boolean;
    /** Show the deliberate microphone permission/stop control for mic-authored scenes. */
    showMicrophoneButton?: boolean;
    showPreloader?: boolean;
    allowedCameraModes?: string[];
    enabledExploreModes?: Array<'orbit' | 'fly' | 'walk'>;
    defaultCameraMode?: string;
    customPreloaderLogoUrl?: string;
    /** Preloader logo height in pixels (default 64 desktop, 40 mobile). */
    preloaderLogoSize?: number;
    /** Custom button labels for internationalization (i18n) */
    buttonLabels?: ViewerButtonLabels;
    /** Whether to hide the watermark (whitelabeling) */
    hideWatermark?: boolean;
    /** Custom watermark text (whitelabeling) */
    watermarkText?: string;
    /** Custom watermark link URL (whitelabeling) */
    watermarkLink?: string;
    /** Custom watermark image URL (PNG logo) for whitelabeling - used instead of text when set */
    watermarkImageUrl?: string;
    /** Max size (width & height) of the watermark image in pixels (default 100) */
    watermarkImageHeight?: number;
    /** Current scene title/name, shown in the bottom-left overlay and the "Current Space" menu header. */
    sceneTitle?: string;
    /** Show the scene title pill in the bottom-left corner (matches watermark/detail-text style). */
    showSceneTitle?: boolean;
    /** Sub-option of showSceneTitle: only reveal the bottom-left title on hover. Default false = always shown. */
    sceneTitleShowOnHover?: boolean;
    /** Initial movement-key family shown in the desktop Explore controls. */
    movementKeyDisplay?: 'wasd' | 'arrows';
    /** Scene ID for default watermark link */
    sceneId?: string;
    /** Show dropdown list for quick waypoint navigation */
    showWaypointList?: boolean;
    /** UI layout template: 'minimal' (default), 'standard' (centered), 'pro' (dark) */
    template?: 'standard' | 'minimal' | 'pro';
    /** Enable debug mode (shows FPS counter) */
    debugMode?: boolean;
    /** Hide the progress percentage text */
    hideProgressText?: boolean;
    /** Custom viewer theme overrides */
    viewerTheme?: ViewerTheme;
    /** Show relighting toggle button */
    showRelightingToggle?: boolean;
    /** Show fisheye/tiny-planet toggle button in the viewer toolbar */
    showFisheyeButton?: boolean;
    /** Show the cutaway toggle button and its plane slider */
    cutawayEnabled?: boolean;
    /** Show the stereo 3D button (3D TVs, projectors, red/cyan glasses) */
    showStereoButton?: boolean;
    /** Camera button (next to Share) that opens the clean photo mode. */
    showPhotoButton?: boolean;
    /** Show scene navigation menu (auto-enabled when portals exist or custom links exist) */
    showSceneMenu?: boolean;
    /** Custom external links for scene menu */
    sceneMenuLinks?: SceneMenuLink[];
    /** Layout style for the scene menu: 'classic' (default dropdown) or 'sidebar' (full-height left pullout) */
    sceneMenuStyle?: 'classic' | 'sidebar';
    /** When true, scene menu category folders start collapsed. Default: expanded. */
    startCategoriesCollapsed?: boolean;
    /** Show a preview image next to each connected scene in the menu. Default false (off). */
    showSceneMenuPreviewImages?: boolean;
    /** Current scene's persisted menu slot — folder + order for the synthesized "Current Space" item. */
    currentSpaceMenuPath?: string;
    currentSpaceMenuOrder?: number;
    /** Explicit render order of menu folders by full path. */
    sceneMenuFolderOrder?: string[];
    /** Names the scene menu button/header. Falls back to buttonLabels.scenes when blank. */
    menuTitle?: string;
    /** Optional logo image URL shown above the scene menu items. */
    menuTitleImageUrl?: string;
    /** Whether measurement mode is enabled for this scene (shows ruler icon) */
    measurementsEnabled?: boolean;
    /** Scene scale: real-world units per 1 scene unit */
    sceneScale?: number;
    /** Display unit for measurements */
    sceneScaleUnit?: 'meters' | 'centimeters' | 'feet' | 'inches';
    /** Custom color for measurement overlay lines/points (default: '#FF9800') */
    measurementColor?: string;
    /**
     * Title of the Layers menu (e.g. "Building History"). When set, a layers button joins the
     * top-left toolbar with an empty panel createViewer fills (layersPanel.ts).
     */
    layersTitle?: string;
    /**
     * Name of the "Ask this scene" guide. When set, an Ask button joins the toolbar with an
     * empty chat panel that createViewer fills (sceneGuide.ts). createViewer sets it only for
     * enabled guides in published scenes (never in the editor).
     */
    sceneGuideName?: string;
    /** Show share button in the top-right area */
    showShareButton?: boolean;
}
/** Registry that enforces "only one top-left tool open at a time". Each tool
 *  (help panel, measurement mode, virtual-staging picker, scene menu) registers
 *  a close() under a name, and calls closeOthers(name) when it opens so the
 *  others dismiss. They share the same top-left corner, so two open at once
 *  overlap and read as broken. */
export interface ExclusiveTools {
    register(name: string, close: () => void): void;
    closeOthers(except: string): void;
}
/**
 * The Layers menu's chrome: its toolbar button and the empty panel createViewer fills
 * (layersPanel.ts). Created with the rest of the UI when the scene opens with a Layers menu, or
 * later when the editor adds the first region to it. Returns false without a toolbar.
 */
/**
 * Show the toolbar while any of its buttons is shown and hide it when none is. createUIElements
 * hides an empty toolbar, so a button added later (the visitor directory's, the Layers menu's when
 * the editor adds the first region) or removed later must sync it, or it stays hidden.
 */
export function syncToolbarVisibility(toolbar: HTMLElement | undefined): void {
    if (!toolbar) return;
    const anyVisible = Array.from(toolbar.children).some((el) => (el as HTMLElement).style.display !== 'none');
    toolbar.style.display = anyVisible ? '' : 'none';
}

export function createLayersChrome(elements: UIElements, container: HTMLElement, title: string): boolean {
    const toolbar = elements.toolbar;
    if (!toolbar) return false;
    const layersBtn = document.createElement('button');
    layersBtn.className = 'storysplat-layers-btn';
    layersBtn.type = 'button';
    layersBtn.setAttribute('aria-label', title);
    layersBtn.setAttribute('title', title);
    const layersSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    layersSvg.setAttribute('viewBox', '0 0 24 24');
    const layersPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    layersPath.setAttribute('d', 'M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z');
    layersSvg.appendChild(layersPath);
    layersBtn.appendChild(layersSvg);
    const layersPanel = document.createElement('div');
    layersPanel.className = 'storysplat-layers-panel';
    toolbar.appendChild(layersBtn);
    container.appendChild(layersPanel);
    elements.layersButton = layersBtn;
    elements.layersPanel = layersPanel;
    return true;
}
export function createExclusiveTools(): ExclusiveTools {
    const closers = new Map<string, () => void>();
    return {
        register(name, close) { closers.set(name, close); },
        closeOthers(except) {
            closers.forEach((close, name) => {
                if (name === except) return;
                // One tool's close must never throw and block the others.
                try { close(); } catch { /* ignore */ }
            });
        },
    };
}
export interface UIElements {
    preloader?: HTMLElement;
    scrollControls?: HTMLElement;
    modeToggle?: HTMLElement;
    fullscreenButton?: HTMLElement;
    helpButton?: HTMLElement;
    helpPanel?: HTMLElement;
    performanceToggle?: HTMLInputElement;
    performanceNotice?: HTMLElement;
    performanceOffButton?: HTMLButtonElement;
    waypointInfo?: HTMLElement;
    progressBar?: HTMLElement;
    progressText?: HTMLElement;
    hotspotPopup?: HTMLElement;
    portalPopup?: HTMLElement;
    joystick?: HTMLElement;
    joystickThumb?: HTMLElement;
    lookZone?: HTMLElement;
    wasdHint?: HTMLElement;
    orbitHint?: HTMLElement;
    doubleTapHint?: HTMLElement;
    tourLookHint?: HTMLElement;
    vrButton?: HTMLElement;
    arButton?: HTMLElement;
    watermark?: HTMLElement;
    sceneTitle?: HTMLElement;
    tapForAudio?: HTMLElement;
    waypointListContainer?: HTMLElement;
    fpsCounter?: HTMLElement;
    muteButton?: HTMLElement;
    microphoneButton?: HTMLButtonElement;
    relightingButton?: HTMLElement;
    fisheyeButton?: HTMLElement;
    cutawayButton?: HTMLElement;
    cutawayPanel?: HTMLElement;
    cutawaySlider?: HTMLInputElement;
    cutawayFlipButton?: HTMLElement;
    cutawayLabel?: HTMLElement;
    stereoButton?: HTMLElement;
    stereoMenu?: HTMLElement;
    sceneMenuContainer?: HTMLElement;
    exploreControls?: HTMLElement;
    modeContainer?: HTMLElement;
    prevButton?: HTMLElement;
    playButton?: HTMLElement;
    nextButton?: HTMLElement;
    measureButton?: HTMLElement;
    measureCanvas?: HTMLCanvasElement;
    measureList?: HTMLElement;
    layersButton?: HTMLElement;
    layersPanel?: HTMLElement;
    sceneGuideButton?: HTMLButtonElement;
    sceneGuidePanel?: HTMLElement;
    toolbar?: HTMLElement;
    shareButton?: HTMLElement;
    photoButton?: HTMLElement;
    photoBar?: HTMLElement;
    photoTakeButton?: HTMLElement;
    photoExitButton?: HTMLElement;
    skinsButton?: HTMLElement;
    skinsDropdown?: HTMLElement;
    skinOverlayWrapper?: HTMLElement;
    skinOverlay?: HTMLImageElement;
    skinOpacitySlider?: HTMLInputElement;
    skinExitButton?: HTMLElement;
    /** Internal teardown for listeners/observers that createUIElements
     *  registers on document/window. Called by the viewer's destroy path. */
    _cleanup?: () => void;
    /** Re-run the top/bottom bar collision layout (after late DOM changes). */
    relayoutChrome?: () => void;
    /** Coordinates the top-left tools (help, measurement, virtual staging,
     *  scene menu) so only one is open at a time. */
    exclusiveTools?: ExclusiveTools;
}

const DEFAULT_PRELOADER_BACKGROUND = '#1e1e1e';
export const PRELOADER_FADE_MS = 500;
const preloaderDismissals = new WeakMap<HTMLElement, Promise<void>>();

/**
 * Preserve a theme's selected color while discarding any saved alpha channel.
 * Older scenes and built-in themes persisted translucent rgba/hex values, but
 * the initial loader must fully cover the scene until its dismissal begins.
 */
export function getOpaquePreloaderColor(value?: string): string {
    const color = value?.trim();
    if (!color || color.toLowerCase() === 'transparent') {
        return DEFAULT_PRELOADER_BACKGROUND;
    }

    const hex = color.match(/^#([\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i);
    if (hex) {
        const digits = hex[1];
        if (digits.length === 4) return `#${digits.slice(0, 3)}`;
        if (digits.length === 8) return `#${digits.slice(0, 6)}`;
        return color;
    }

    const functional = color.match(/^(rgba?|hsla?)\((.*)\)$/i);
    if (functional) {
        const functionName = functional[1].toLowerCase().startsWith('rgb') ? 'rgb' : 'hsl';
        const body = functional[2].trim();
        if (body.includes(',')) {
            const components = body.split(',').map(component => component.trim());
            if (components.length >= 3) {
                return `${functionName}(${components.slice(0, 3).join(', ')})`;
            }
        }
        return `${functionName}(${body.split('/')[0].trim()})`;
    }

    // Named colors and other valid opaque CSS color formats can pass through.
    // The solid fallback layer emitted by generateViewerStyles still guarantees
    // coverage if an unfamiliar functional color happens to contain alpha.
    return color;
}

/**
 * Generate CSS styles for the viewer UI.
 * Base styles are always the minimal theme. Standard and pro templates
 * are applied as CSS override blocks appended after the base.
 */
/** Theme-driven CSS value sanitizer shared by the full stylesheet and the overlay-only sheet. */
function cssSafeValue(v: string | undefined): string | undefined {
    if (typeof v !== 'string' || v.length === 0 || v.length > 200) return undefined;
    if (/[{};<>]|\/\*|\*\/|@import|expression\(|url\(|\\/i.test(v)) return undefined;
    if (/[\r\n]/.test(v)) return undefined;
    return v;
}

/**
 * The top banner: a waypoint's name and info (`.storysplat-waypoint-info`), and overlays in the
 * 'top' layout (`.storysplat-overlay-top`), which look exactly the same. `base` is hidden until
 * `shown` matches.
 */
function infoBannerCss(t: ViewerTheme, base: string, shown: string): string {
    const tc = t.globalTextColor || 'white';
    const tInfoBannerTitleFontFamily = cssSafeValue(t.infoBannerTitleFontFamily);
    const tInfoBannerTitleLetterSpacing = cssSafeValue(t.infoBannerTitleLetterSpacing);
    const tInfoBannerContentFontFamily = cssSafeValue(t.infoBannerContentFontFamily);
    const tInfoBannerContentLetterSpacing = cssSafeValue(t.infoBannerContentLetterSpacing);
    return `    ${base} {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      background: ${t.infoBannerGradientStart
        ? `linear-gradient(to bottom, ${t.infoBannerBg || 'rgba(0, 0, 0, 0.5)'} ${t.infoBannerGradientStart}%, transparent)`
        : (t.infoBannerBg || 'rgba(0, 0, 0, 0.5)')};
      color: ${tc};
      text-align: center;
      z-index: 1000;
      pointer-events: none;
      display: none;
    }

    ${shown} {
      display: block;
      padding: ${t.infoBannerPaddingTop || '50'}px 30px 30px 30px;
    }

    ${base} > h2 {
      margin: 0 0 8px 0;
      font-size: ${t.infoBannerTitleFontSize || '18px'};
      font-weight: ${t.infoBannerTitleFontWeight || 'bold'};
      ${tInfoBannerTitleFontFamily ? `font-family: ${tInfoBannerTitleFontFamily};` : ''}
      ${tInfoBannerTitleLetterSpacing ? `letter-spacing: ${tInfoBannerTitleLetterSpacing};` : ''}
      ${t.infoBannerTitleColor ? `color: ${t.infoBannerTitleColor};` : ''}
    }

    ${base} > p, ${base} > .storysplat-rich-text {
      margin: 0;
      font-size: ${t.infoBannerContentFontSize || '14px'};
      font-weight: ${t.infoBannerContentFontWeight || 'normal'};
      line-height: ${t.infoBannerContentLineHeight || '1.5'};
      opacity: ${t.infoBannerContentOpacity ?? '0.9'};
      ${tInfoBannerContentFontFamily ? `font-family: ${tInfoBannerContentFontFamily};` : ''}
      ${tInfoBannerContentLetterSpacing ? `letter-spacing: ${tInfoBannerContentLetterSpacing};` : ''}
      ${t.infoBannerContentColor ? `color: ${t.infoBannerContentColor};` : ''}
    }

    /* Responsive waypoint info
       NOTE: padding-top stays at 50px even on short viewports so the title
       clears the top-right button row (fullscreen/share/waypoint-list/mode
       toggle all sit at top:10px and are ~30-36px tall). Reduce the other
       sides + font size instead. */
    @media (max-height: 600px) {
      ${shown} {
        padding: max(${t.infoBannerPaddingTop || '50'}px, 50px) 20px 15px 20px;
        font-size: 13px;
      }
    }

    @media (max-height: 500px) {
      ${shown} {
        padding: max(${t.infoBannerPaddingTop || '50'}px, 50px) 15px 10px 15px;
        font-size: 12px;
      }
    }
`;
}

/**
 * Overlay panel styles (layer, anchors, drawer, content blocks). Part of the full viewer
 * stylesheet, and injected on their own by the editor, which skips the viewer UI but still
 * previews overlays (see injectOverlayStyles).
 */
function generateOverlayStyles(vars: { t: ViewerTheme; uiColor: string; popupTextAlign: string; tPopupContentFontFamily?: string }): string {
    const { t, uiColor, popupTextAlign, tPopupContentFontFamily } = vars;
    const blur = `blur(${t.dropdownBlur || '12px'})`;
    // Sheet rules for narrow embeds; emitted under both the container query and its
    // media-query fallback. !important beats the anchor / drawer placement rules and the
    // inline width; the drag gesture sets an important inline transform on top of these.
    //
    // Drawers become a bottom sheet (modal, with a scrim, as on desktop). Floating panels
    // become a sheet under the toolbar instead: the bottom of a phone embed holds the tour
    // controls (scroll bar, prev / next), the mode buttons and the walk joystick widget
    // (bottom 130-250px), and those sit on different rows per mode and layout, so the one
    // edge that is reliably free is the top. The height cap keeps the sheet's lower edge
    // 260px above the embed's bottom (60px toolbar + 260px = 320px), clear of all of them.
    const sheetCss = `
      .storysplat-overlay {
        left: 0 !important;
        right: 0 !important;
        width: auto !important;
        max-width: none !important;
        height: auto !important;
        border-left: none !important;
        border-right: none !important;
        backdrop-filter: ${blur};
        -webkit-backdrop-filter: ${blur};
        transition: transform 260ms cubic-bezier(0.25, 0.1, 0.25, 1), opacity 260ms ease;
      }
      .storysplat-overlay .storysplat-overlay-close { top: 10px; right: 10px; }
      .storysplat-overlay--drawer {
        top: auto !important;
        bottom: 0 !important;
        max-height: 60% !important;
        border-radius: 16px 16px 0 0 !important;
        padding: 44px 16px max(20px, env(safe-area-inset-bottom)) !important;
        box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
        transform: translateY(100%) !important;
      }
      .storysplat-overlay--drawer.visible { transform: translateY(0) !important; }
      .storysplat-overlay:not(.storysplat-overlay--drawer) {
        top: 60px !important;
        bottom: auto !important;
        left: 8px !important;
        right: 8px !important;
        max-height: max(96px, min(50%, calc(100% - 320px))) !important;
        border-radius: 14px !important;
        padding: 16px 16px 32px !important;
        transform: translateY(-12px) !important;
      }
      .storysplat-overlay.visible:not(.storysplat-overlay--drawer) { transform: translateY(0) !important; }
      .storysplat-overlay-handle {
        display: block;
        position: absolute;
        left: 0;
        right: 0;
        height: 28px;
        cursor: grab;
        touch-action: none;
      }
      .storysplat-overlay--drawer .storysplat-overlay-handle { top: 0; }
      .storysplat-overlay:not(.storysplat-overlay--drawer) .storysplat-overlay-handle { bottom: 0; }
      .storysplat-overlay-handle::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 40px;
        height: 4px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.45);
        transform: translate(-50%, -50%);
      }
      .storysplat-overlay-scrim { display: block; }
      .storysplat-overlay-layer.has-sheet .storysplat-overlay-scrim { opacity: 1; pointer-events: auto; }`;
    // 104px = the wrapped toolbar's second row (same clearance as the waypoint banner); the
    // sheet's bottom clearance (260px) is unchanged, hence 364px instead of 320px.
    const sheetWrappedCss = `
      .storysplat-tools-wrapped .storysplat-overlay:not(.storysplat-overlay--drawer),
      .storysplat-mode-wrapped .storysplat-overlay:not(.storysplat-overlay--drawer) {
        top: 104px !important;
        max-height: max(96px, min(50%, calc(100% - 364px))) !important;
      }`;
    const sheetShortCss = `
      .storysplat-overlay:not(.storysplat-overlay--drawer) { max-height: calc(100% - 68px) !important; }
      .storysplat-tools-wrapped .storysplat-overlay:not(.storysplat-overlay--drawer),
      .storysplat-mode-wrapped .storysplat-overlay:not(.storysplat-overlay--drawer) { max-height: calc(100% - 112px) !important; }`;
    return `
    ${RICH_TEXT_CSS}
    .storysplat-overlay-layer {
      position: absolute;
      inset: 0;
      pointer-events: none;
      /* Below the preloader (100000) so nothing paints over the loading screen, below the
         hotspot popup (100001) so a click-triggered overlay waits under the popup. */
      z-index: 99999;
      overflow: hidden;
      /* Overlays size and reflow against the embed, not the browser window. 'size' (not just
         inline-size) so short embeds can be queried too; the layer is sized by its inset, never
         by its content, so size containment changes nothing else. */
      container-type: size;
    }
    .storysplat-overlay {
      position: absolute;
      box-sizing: border-box;
      pointer-events: auto;
      background-color: ${t.popupBg || 'rgba(0, 0, 0, 0.75)'};
      color: ${t.popupTextColor || 'white'};
      padding: 18px 20px;
      border-radius: ${t.popupBorderRadius || '10px'};
      box-shadow: 0 4px 18px rgba(0,0,0,0.35);
      font-family: ${t.fontFamily || 'system-ui, -apple-system, sans-serif'};
      font-size: 14px;
      text-align: ${popupTextAlign};
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-width: calc(100% - 32px);
      max-height: calc(100% - 32px);
      /* The panel clips; the body / content block scrolls, so the title, close button and
         drag handle stay in place while long content scrolls under them. */
      overflow: hidden;
      overflow-wrap: anywhere;
      opacity: 0;
      transform: translateY(8px);
      transition: opacity 0.25s ease, transform 0.25s ease;
    }
    .storysplat-overlay-body {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
      min-height: 0;
      overflow-y: auto;
      /* Scrolling to the end of a panel must not scroll the host page around an inline embed. */
      overscroll-behavior: contain;
    }
    .storysplat-overlay.visible {
      opacity: 1;
      transform: translateY(0);
    }
    .storysplat-overlay--top-left { top: 64px; left: 16px; }
    .storysplat-overlay--top-center { top: 64px; left: 50%; transform: translate(-50%, 8px); }
    .storysplat-overlay--top-center.visible { transform: translate(-50%, 0); }
    .storysplat-overlay--top-right { top: 64px; right: 16px; }
    .storysplat-overlay--center-left { top: 50%; left: 16px; transform: translate(0, calc(-50% + 8px)); }
    .storysplat-overlay--center-left.visible { transform: translate(0, -50%); }
    .storysplat-overlay--center { top: 50%; left: 50%; transform: translate(-50%, calc(-50% + 8px)); }
    .storysplat-overlay--center.visible { transform: translate(-50%, -50%); }
    .storysplat-overlay--center-right { top: 50%; right: 16px; transform: translate(0, calc(-50% + 8px)); }
    .storysplat-overlay--center-right.visible { transform: translate(0, -50%); }
    .storysplat-overlay--bottom-left { bottom: 88px; left: 16px; }
    .storysplat-overlay--bottom-center { bottom: 88px; left: 50%; transform: translate(-50%, 8px); }
    .storysplat-overlay--bottom-center.visible { transform: translate(-50%, 0); }
    .storysplat-overlay--bottom-right { bottom: 88px; right: 16px; }
    /* The anchors keep a panel clear of the toolbar (64px) and the bottom controls (88px).
       Cap its height by the same margins so a tall panel scrolls inside instead of running
       into the chrome at the other end, or past the embed's edge. The 96px floor keeps a
       readable panel when those margins leave almost nothing (never taller than the embed). */
    .storysplat-overlay--top-left, .storysplat-overlay--top-center, .storysplat-overlay--top-right,
    .storysplat-overlay--bottom-left, .storysplat-overlay--bottom-center, .storysplat-overlay--bottom-right { max-height: min(calc(100% - 16px), max(96px, calc(100% - 152px))); }
    .storysplat-overlay--center-left, .storysplat-overlay--center, .storysplat-overlay--center-right { max-height: min(calc(100% - 16px), max(96px, calc(100% - 176px))); }
    .storysplat-overlay-close {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: ${t.popupCloseBtnColor || t.popupTextColor || 'white'};
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.75;
    }
    .storysplat-overlay-close:hover { opacity: 1; background: rgba(255,255,255,0.12); }
    .storysplat-overlay-title {
      margin: 0 28px 0 0;
      font-size: ${t.popupTitleFontSize || '18px'};
      font-weight: ${t.popupTitleFontWeight || '600'};
      font-family: ${t.popupTitleFontFamily || 'inherit'};
      letter-spacing: ${t.popupTitleLetterSpacing || 'normal'};
    }
    .storysplat-overlay-text {
      margin: 0;
      white-space: pre-line;
      font-size: ${t.popupContentFontSize || '14px'};
      font-weight: ${t.popupContentFontWeight || 'normal'};
      font-family: ${t.popupContentFontFamily || 'inherit'};
      letter-spacing: ${t.popupContentLetterSpacing || 'normal'};
      line-height: ${t.popupContentLineHeight || '1.5'};
      opacity: ${t.popupContentOpacity || '0.92'};
    }
    .storysplat-overlay-image, .storysplat-overlay-video {
      display: block;
      width: 100%;
      max-height: 45vh;
      object-fit: contain;
      border-radius: 6px;
      background: rgba(0,0,0,0.2);
    }
    .storysplat-overlay-link {
      align-self: flex-start;
      color: ${t.popupLinkBtnColor || uiColor};
      font-weight: 600;
      text-decoration: none;
    }
    .storysplat-overlay-link:hover { text-decoration: underline; }
    /* Edge-to-edge drawer: a full-height column docked to the left or right edge that
       slides in, mirroring the sidebar scene menu (.storysplat-scene-menu-container--sidebar). */
    .storysplat-overlay--drawer {
      top: 0;
      bottom: 0;
      height: 100%;
      max-height: 100%;
      max-width: 85%;
      border-radius: 0;
      padding: 64px 20px 24px;
      backdrop-filter: blur(${t.dropdownBlur || '12px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '12px'});
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      transition: transform 260ms cubic-bezier(0.25, 0.1, 0.25, 1), opacity 260ms ease;
    }
    .storysplat-overlay--drawer-left { left: 0; border-right: 1px solid rgba(255,255,255,0.1); transform: translateX(-100%); }
    .storysplat-overlay--drawer-right { right: 0; border-left: 1px solid rgba(255,255,255,0.1); transform: translateX(100%); }
    .storysplat-overlay--drawer.visible { transform: translateX(0); }
    .storysplat-overlay--drawer .storysplat-overlay-close { top: 14px; right: 14px; }
    /* An open drawer would cover the top bar's buttons on its side (the waypoints menu, share,
       fullscreen): they slide clear by the drawer's width (syncDrawerInsets). 'translate' adds to
       any transform a layout already uses. */
    .storysplat-toolbar, .storysplat-top-right-bar, .storysplat-top-right-group,
    .storysplat-waypoint-list-container, .storysplat-top-right-pill { transition: translate 260ms cubic-bezier(0.25, 0.1, 0.25, 1); }
    .storysplat-drawer-open-right > .storysplat-top-right-bar,
    .storysplat-drawer-open-right > .storysplat-top-right-group,
    .storysplat-drawer-open-right > .storysplat-waypoint-list-container,
    .storysplat-drawer-open-right > .storysplat-top-right-pill { translate: calc(-1 * var(--storysplat-drawer-right, 0px)) 0; }
    .storysplat-drawer-open-left > .storysplat-toolbar { translate: var(--storysplat-drawer-left, 0px) 0; }
    /* Sheet furniture, only shown in narrow embeds. */
    .storysplat-overlay-handle { display: none; }
    .storysplat-overlay-scrim {
      position: absolute;
      inset: 0;
      display: none;
      pointer-events: none;
      background: rgba(0, 0, 0, 0.45);
      opacity: 0;
      transition: opacity 0.25s ease;
    }
    /* Content blocks (galleries, videos, links, models, PDFs): the hotspot popup rules, scoped to overlays. */
    .storysplat-overlay-content { width: 100%; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
    .storysplat-overlay-content p {
      margin: 0 0 10px 0;
      white-space: pre-wrap;
      line-height: ${t.popupContentLineHeight || '1.5'};
      font-size: ${t.popupContentFontSize || '14px'};
      font-weight: ${t.popupContentFontWeight || 'normal'};
      ${tPopupContentFontFamily ? `font-family: ${tPopupContentFontFamily};` : ''}
      ${t.popupContentOpacity !== undefined ? `opacity: ${t.popupContentOpacity};` : ''}
    }
    .storysplat-overlay-content img { max-width: 100%; height: auto; border-radius: 5px; margin-bottom: 10px; display: block; }
    .storysplat-overlay-content .hotspot-gallery { width: 100%; margin-bottom: 10px; }
    .storysplat-overlay-content .hotspot-gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 6px; }
    .storysplat-overlay-content .hotspot-gallery-grid img { width: 100%; aspect-ratio: 1 / 1; object-fit: cover; margin: 0; cursor: pointer; }
    .storysplat-overlay-content .hotspot-gallery-stacked { display: flex; flex-direction: column; gap: 8px; }
    .storysplat-overlay-content .hotspot-gallery-stacked img { width: 100%; max-height: 60vh; object-fit: contain; margin: 0; cursor: pointer; }
    .storysplat-overlay-content .hotspot-gallery-carousel { display: flex; gap: 8px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }
    .storysplat-overlay-content .hotspot-gallery-carousel img { flex: 0 0 100%; width: 100%; max-height: 60vh; object-fit: contain; scroll-snap-align: center; margin: 0; cursor: pointer; }
    .storysplat-overlay-content iframe { width: 100%; max-width: 100%; aspect-ratio: 16 / 9; height: auto; min-height: 240px; border: none; border-radius: 5px; margin-bottom: 10px; }
    .storysplat-overlay-content .video-container { display: flex; align-items: center; justify-content: center; width: 100%; margin: 10px 0; }
    .storysplat-overlay-content video { max-width: 100%; max-height: 60vh; object-fit: contain; border-radius: 5px; border: 2px solid rgba(255, 255, 255, 0.3); }
    .storysplat-overlay-content .model-container { display: flex; align-items: center; justify-content: center; width: 100%; aspect-ratio: 1 / 1; min-height: 240px; max-height: 60vh; margin: 10px 0; position: relative; border-radius: 8px; overflow: hidden; background: rgba(0, 0, 0, 0.3); }
    .storysplat-overlay-content .model-container canvas { width: 100%; height: 100%; display: block; }
    .storysplat-overlay-content .model-container .model-loading { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: rgba(255, 255, 255, 0.7); font-size: 14px; }
    .storysplat-overlay-content .pdf-pdfjs-container { width: 100%; max-height: 60vh; overflow-y: auto; }
    .storysplat-overlay-content .storysplat-hotspot-popup-link {
      display: inline-block; padding: 8px 16px; margin: 4px 0 10px; border-radius: 5px; color: white;
      cursor: pointer; font-weight: 600; text-decoration: none; text-align: center;
    }
    /* Narrow embeds (phones, or a small inline embed on any page): every overlay becomes a
       sheet with a drag handle, scrolling inside. Drawers (edge-to-edge) become a bottom sheet,
       at most 60% of the embed's height, with the tap-to-dismiss scrim since they are modal on
       desktop too. Floating panels become a sheet under the toolbar that stays clear of the
       bottom controls and leaves the scene interactive (see sheetCss). Driven by the embed's
       width through a container query on the overlay layer; the media query is the fallback
       for older browsers. */
    @container (max-width: 600px) {
${sheetCss}
    }
    @supports not (container-type: inline-size) {
      @media (max-width: 600px) {
${sheetCss}
      }
    }
    /* When the top bar wraps onto a second row (layoutTopBar's .storysplat-tools-wrapped /
       .storysplat-mode-wrapped, as the waypoint banner already honours), a floating sheet starts
       below that row instead of covering share / fullscreen / help. */
    @container (max-width: 600px) {
${sheetWrappedCss}
    }
    @supports not (container-type: inline-size) {
      @media (max-width: 600px) {
${sheetWrappedCss}
      }
    }
    /* Short narrow embeds (e.g. a 16:9 inline embed on a phone, ~220px tall): clearing the
       bottom controls would leave a floating sheet a one-line strip, so it may run down to 8px
       above the bottom edge there. A visitor can still dismiss it, and a non-dismissible one
       lifts the controls it covers above the overlay layer (.storysplat-overlay-locked). */
    @container (max-width: 600px) and (max-height: 480px) {
${sheetShortCss}
    }
    @supports not (container-type: inline-size) {
      @media (max-width: 600px) and (max-height: 480px) {
${sheetShortCss}
      }
    }
    /* Short embeds (a banner-style iframe): the chrome margins would leave a panel too little
       height, so floating panels move to 8px from the edge they anchor to and may use the whole
       height. They paint over the toolbar / bottom row there, which beats an unreadable strip.
       Not !important, so the narrow-embed sheet rules above still win when both apply. */
    @container (max-height: 260px) {
      .storysplat-overlay--top-left, .storysplat-overlay--top-center, .storysplat-overlay--top-right { top: 8px; }
      .storysplat-overlay--bottom-left, .storysplat-overlay--bottom-center, .storysplat-overlay--bottom-right { bottom: 8px; }
      .storysplat-overlay:not(.storysplat-overlay--drawer) { max-height: calc(100% - 16px); }
    }
    /* A visible overlay that visitors cannot close must never hide the controls needed to end
       its trigger (walk out of its region, move the tour on): lift them above the overlay layer. */
    .storysplat-overlay-locked .storysplat-joystick-container,
    .storysplat-overlay-locked .storysplat-scroll-controls,
    .storysplat-overlay-locked .storysplat-mode-container,
    .storysplat-overlay-locked .storysplat-explore-controls { z-index: 100000 !important; }

    /* 'top' overlays: the waypoint banner's look, in the banner's place (outside the layer,
       under the toolbar buttons). The newest one wins, and the waypoint banner yields to it. */
${infoBannerCss(t, '.storysplat-overlay-top', '.storysplat-overlay-top.visible')}
    .storysplat-overlay-top.visible:has(~ .storysplat-overlay-top.visible) { display: none; }
    .storysplat-top-overlay-active .storysplat-waypoint-info { visibility: hidden; }
    .storysplat-tools-wrapped .storysplat-overlay-top.visible { padding-top: max(${t.infoBannerPaddingTop || '50'}px, 104px); }
    .storysplat-photo-mode .storysplat-overlay-top,
    .storysplat-ar-mode .storysplat-overlay-top { display: none !important; }
`;
}

/** Inject only the overlay styles (editor canvas: the viewer UI is skipped there). */
export function injectOverlayStyles(uiColor: string = '#CC5833', theme?: ViewerTheme, containerId?: string): HTMLStyleElement {
    const styleId = `storysplat-overlay-styles-${containerId || 'default'}`;
    document.getElementById(styleId)?.remove();
    const t = theme || {};
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = generateOverlayStyles({
        t,
        uiColor,
        popupTextAlign: t.popupTextAlign === 'left' ? 'left' : 'center',
        tPopupContentFontFamily: cssSafeValue(t.popupContentFontFamily),
    });
    document.head.appendChild(style);
    return style;
}

/** Full-height edge-to-edge drawer declarations for a hotspot popup selector (side rules are separate). */
function hotspotDrawerCss(selector: string, t: ViewerTheme): string {
    return `    ${selector} {
      top: 0 !important; bottom: 0 !important; height: 100% !important; max-height: 100% !important;
      width: 360px !important; max-width: 85vw !important; margin: 0 !important; border-radius: 0 !important;
      padding: 64px 20px 24px !important; transform: none !important; overflow: hidden !important;
      backdrop-filter: blur(${t.dropdownBlur || '12px'}); -webkit-backdrop-filter: blur(${t.dropdownBlur || '12px'});
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    }
    ${selector} .storysplat-hotspot-popup-content, ${selector} p { max-width: none; }`;
}

/**
 * The visitor directory (directoryPanel.ts): its toolbar button and panel, in the Layers menu's
 * look (dark glass, white text, the scene's UI colour for ticks and focus). Narrow embeds: a bottom
 * sheet with a drag handle and a tap-to-close scrim, like the guide.
 */
function directoryCss(t: ViewerTheme, tc: string, uiColor: string): string {
    const font = cssSafeValue(t.fontFamily) || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const accentText = readableTextColorOn(uiColor);
    return `    /* Visitor directory: toolbar button + panel (see directoryPanel.ts) */
    .storysplat-directory-btn {
      position: relative;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
    }
    .storysplat-directory-btn:hover { background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}; }
    .storysplat-directory-btn:focus-visible { outline: 2px solid ${uiColor}; outline-offset: 1px; }
    .storysplat-directory-btn svg { width: 20px; height: 20px; fill: ${tc}; }
    .storysplat-directory-btn.active svg { fill: ${uiColor}; }
    .storysplat-directory-panel {
      position: absolute;
      top: 52px;
      left: 10px;
      box-sizing: border-box;
      width: min(300px, calc(100% - 20px));
      max-height: calc(100% - 130px);
      flex-direction: column;
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 8px;
      padding: 8px;
      z-index: 1004;
      display: none;
      color: white;
      font-family: ${font};
      font-size: 13px;
    }
    .storysplat-directory-panel.open { display: flex; }
    .storysplat-tools-wrapped .storysplat-directory-panel { top: 98px; }
    .storysplat-directory-panel *, .storysplat-directory-panel *::before, .storysplat-directory-panel *::after { box-sizing: border-box; }
    .storysplat-directory-handle { display: none; }
    .storysplat-directory-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 2px 4px 8px;
      border-bottom: 1px solid rgba(255,255,255,0.12);
      margin-bottom: 6px;
    }
    .storysplat-directory-title {
      margin: 0;
      font-size: 11px;
      font-weight: 600;
      color: rgba(255,255,255,0.8);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .storysplat-directory-close {
      background: none;
      border: none;
      border-radius: 4px;
      color: rgba(255,255,255,0.75);
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      padding: 0 4px;
    }
    .storysplat-directory-close:hover { color: white; }
    .storysplat-directory-close:focus-visible { outline: 2px solid ${uiColor}; outline-offset: 1px; }
    .storysplat-directory-filter {
      width: 100%;
      margin: 0 0 6px;
      padding: 7px 9px;
      border-radius: 6px;
      border: 1px solid rgba(255,255,255,0.22);
      background: rgba(255,255,255,0.08);
      color: white;
      font: inherit;
      font-size: 13px;
    }
    .storysplat-directory-filter::placeholder { color: rgba(255,255,255,0.65); }
    .storysplat-directory-filter:focus { outline: 2px solid ${uiColor}; outline-offset: 0; border-color: transparent; }
    .storysplat-directory-body { overflow-y: auto; overscroll-behavior: contain; min-height: 0; flex: 1 1 auto; }
    .storysplat-directory-items { list-style: none; margin: 0; padding: 0; }
    .storysplat-directory-item {
      display: flex;
      align-items: center;
      gap: 9px;
      width: 100%;
      min-height: 34px;
      text-align: left;
      background: transparent;
      border: none;
      border-radius: 6px;
      color: white;
      padding: 6px 6px;
      cursor: pointer;
      font: inherit;
      font-size: 13px;
    }
    .storysplat-directory-item:hover { background: rgba(255,255,255,0.08); }
    .storysplat-directory-item:focus-visible { outline: 2px solid ${uiColor}; outline-offset: -2px; }
    .storysplat-directory-item.is-unavailable { cursor: default; color: rgba(255,255,255,0.6); }
    .storysplat-directory-item.is-unavailable:hover { background: transparent; }
    .storysplat-directory-marker {
      position: relative;
      flex-shrink: 0;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: var(--storysplat-marker-color, rgba(255,255,255,0.75));
      box-shadow: inset 0 0 0 1px rgba(255,255,255,0.45), 0 0 0 1px rgba(0,0,0,0.4);
      overflow: hidden;
    }
    .storysplat-directory-thumb { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .storysplat-directory-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .storysplat-directory-note { flex-shrink: 0; font-size: 11px; color: rgba(255,255,255,0.65); }
    .storysplat-directory-note[hidden] { display: none; }
    .storysplat-directory-folder { margin-top: 2px; }
    .storysplat-directory-folder-row { display: flex; align-items: center; gap: 4px; }
    .storysplat-directory-folder-toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      flex: 1;
      min-width: 0;
      min-height: 34px;
      padding: 6px 6px 6px 4px;
      background: none;
      border: none;
      border-radius: 6px;
      color: white;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      text-align: left;
      cursor: pointer;
    }
    .storysplat-directory-folder-toggle:hover { background: rgba(255,255,255,0.08); }
    .storysplat-directory-folder-toggle:focus-visible { outline: 2px solid ${uiColor}; outline-offset: -2px; }
    .storysplat-directory-chevron::before {
      content: '\\25BE';
      display: inline-block;
      width: 12px;
      color: rgba(255,255,255,0.75);
      transition: transform 0.15s ease;
    }
    .storysplat-directory-folder.is-collapsed > .storysplat-directory-folder-row .storysplat-directory-chevron::before { transform: rotate(-90deg); }
    .storysplat-directory-folder-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .storysplat-directory-count {
      flex-shrink: 0;
      padding: 0 7px;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,0.3);
      font-size: 11px;
      font-weight: 500;
      line-height: 17px;
      color: rgba(255,255,255,0.85);
      font-variant-numeric: tabular-nums;
    }
    .storysplat-directory-check { position: relative; display: grid; place-items: center; flex-shrink: 0; width: 34px; height: 34px; cursor: pointer; }
    .storysplat-directory-check-input { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; opacity: 0; cursor: pointer; }
    .storysplat-directory-check-box {
      position: relative;
      width: 18px;
      height: 18px;
      border-radius: 4px;
      border: 2px solid rgba(255,255,255,0.8);
      pointer-events: none;
    }
    .storysplat-directory-check-input:checked + .storysplat-directory-check-box { background: ${uiColor}; border-color: ${uiColor}; }
    .storysplat-directory-check-input:checked + .storysplat-directory-check-box::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 0;
      width: 5px;
      height: 10px;
      border: solid ${accentText};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
    .storysplat-directory-check-input:focus-visible + .storysplat-directory-check-box { outline: 2px solid ${uiColor}; outline-offset: 2px; }
    .storysplat-directory-check-input:indeterminate + .storysplat-directory-check-box { background: ${uiColor}; border-color: ${uiColor}; }
    .storysplat-directory-check-input:indeterminate + .storysplat-directory-check-box::after {
      content: '';
      position: absolute;
      left: 3px;
      top: 6px;
      width: 8px;
      height: 2px;
      background: ${accentText};
    }
    /* A folder's subfolders and entries, indented under it (deep folders stop indenting). */
    .storysplat-directory-folder-body { padding-left: 14px; }
    .storysplat-directory-folder-body.storysplat-directory-folder-body--flat { padding-left: 0; }
    .storysplat-directory-folder.is-collapsed > .storysplat-directory-folder-body { display: none; }
    .storysplat-directory-folder.is-folder-hidden > .storysplat-directory-folder-body > .storysplat-directory-items .storysplat-directory-item:not(.storysplat-directory-item--view) { color: rgba(255,255,255,0.65); }
    .storysplat-directory-folder.is-folder-hidden > .storysplat-directory-folder-body > .storysplat-directory-items .storysplat-directory-marker { opacity: 0.45; }
    .storysplat-directory-views { margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.12); }
    .storysplat-directory-heading {
      margin: 2px 6px 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255,255,255,0.75);
    }
    /* A location pin: picking a view takes the visitor there (an eye would read as show / hide). */
    .storysplat-directory-view-icon {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      background: currentColor;
      opacity: 0.85;
      -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z'/%3E%3C/svg%3E") center / contain no-repeat;
      mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z'/%3E%3C/svg%3E") center / contain no-repeat;
    }
    .storysplat-directory-entry.is-filtered-out, .storysplat-directory-folder.is-filtered-out, .storysplat-directory-views.is-filtered-out { display: none; }
    .storysplat-directory-empty { margin: 8px 6px; font-size: 13px; color: rgba(255,255,255,0.75); }
    .storysplat-directory-empty[hidden] { display: none; }
    .storysplat-directory-scrim {
      position: absolute;
      inset: 0;
      display: none;
      background: rgba(0, 0, 0, 0.45);
      z-index: 1003;
    }
    .storysplat-directory-scrim.visible { display: block; }
    .storysplat-directory-panel.storysplat-directory-panel--sheet {
      top: auto;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      max-height: min(70%, 560px);
      border-left: none;
      border-right: none;
      border-bottom: none;
      border-radius: 16px 16px 0 0;
      padding: 0 12px calc(12px + env(safe-area-inset-bottom));
      box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
      z-index: 1005;
      font-size: 15px;
    }
    .storysplat-directory-panel--sheet.open { animation: storysplat-directory-sheet-in 260ms cubic-bezier(0.25, 0.1, 0.25, 1); }
    @keyframes storysplat-directory-sheet-in { from { transform: translateY(100%); } to { transform: none; } }
    .storysplat-directory-panel--sheet .storysplat-directory-handle {
      display: block;
      position: relative;
      flex: 0 0 auto;
      height: 20px;
      cursor: grab;
      touch-action: none;
    }
    .storysplat-directory-panel--sheet .storysplat-directory-handle::after {
      content: '';
      position: absolute;
      top: 8px;
      left: 50%;
      width: 40px;
      height: 4px;
      border-radius: 2px;
      background: rgba(255,255,255,0.45);
      transform: translateX(-50%);
    }
    .storysplat-directory-panel--sheet .storysplat-directory-item,
    .storysplat-directory-panel--sheet .storysplat-directory-folder-toggle { min-height: 44px; font-size: 15px; }
    .storysplat-directory-panel--sheet .storysplat-directory-check { width: 44px; height: 44px; }
    .storysplat-directory-panel--sheet .storysplat-directory-filter { font-size: 16px; }
    @media (prefers-reduced-motion: reduce) {
      .storysplat-directory-panel--sheet.open { animation: none; }
    }
`;
}

/**
 * "Ask this scene" guide (sceneGuide.ts): the toolbar button and the chat panel. The panel uses
 * the hotspot popup palette so it matches each scene's branding, and the accent (uiColor) for
 * the visitor's bubbles and the send button. Desktop: a floating panel bottom-right, clear of the
 * bottom controls like a bottom-right overlay. Narrow embeds: a bottom sheet with a drag handle
 * and a tap-to-close scrim (the --sheet class is set from the embed's measured width).
 */
function sceneGuideCss(t: ViewerTheme, tc: string, uiColor: string): string {
    const accentText = readableTextColorOn(uiColor);
    const subtle = tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
    const font = cssSafeValue(t.fontFamily) || 'system-ui, -apple-system, sans-serif';
    const titleFont = cssSafeValue(t.popupTitleFontFamily);
    const contentFont = cssSafeValue(t.popupContentFontFamily);
    const blur = `blur(${t.dropdownBlur || '12px'})`;
    // currentColor mixes keep bubbles, borders and chips readable on light and dark popup colours;
    // the rgba line before each is the fallback for browsers without color-mix().
    const tint = (pct: number, fallback: string) => `${fallback}; background: color-mix(in srgb, currentColor ${pct}%, transparent)`;
    const line = (pct: number) => `rgba(127, 127, 127, 0.3); border-color: color-mix(in srgb, currentColor ${pct}%, transparent)`;
    return `    /* "Ask this scene" guide: toolbar button + chat panel (see sceneGuide.ts) */
    button.storysplat-guide-btn {
      position: relative;
      background: transparent;
      border: none;
      min-width: 28px;
      height: 28px;
      padding: 4px 8px 4px 6px;
      border-radius: 6px;
      color: ${tc};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: row;
      gap: 5px;
      font-family: ${font};
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
      transition: background 0.2s ease, color 0.2s ease;
    }
    .storysplat-guide-btn:hover { background: ${subtle}; }
    .storysplat-guide-btn svg { display: block; width: 18px; height: 18px; flex: 0 0 auto; margin: 0; }
    .storysplat-guide-btn-label { display: flex; align-items: center; height: 18px; line-height: 18px; margin: 0; }
    .storysplat-guide-btn[aria-expanded="true"] { color: ${uiColor}; background: ${subtle}; }
    @media (max-width: 480px) {
      .storysplat-guide-btn-label { display: none; }
      button.storysplat-guide-btn { padding: 4px; }
    }
    .storysplat-guide-panel {
      position: absolute;
      right: 16px;
      bottom: 88px;
      width: min(360px, calc(100% - 32px));
      height: min(520px, max(260px, calc(100% - 152px)), calc(100% - 16px));
      box-sizing: border-box;
      display: none;
      flex-direction: column;
      background-color: ${t.popupBg || 'rgba(0, 0, 0, 0.75)'};
      color: ${t.popupTextColor || 'white'};
      border: 1px solid ${line(16)};
      border-radius: ${t.popupBorderRadius || '12px'};
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
      backdrop-filter: ${blur};
      -webkit-backdrop-filter: ${blur};
      font-family: ${font};
      font-size: 14px;
      text-align: left;
      overflow: hidden;
      overflow-wrap: anywhere;
      /* Above overlay panels (99999): the visitor opened it. Below hotspot popups (100001). */
      z-index: 100000;
      outline: none;
    }
    .storysplat-guide-panel.open { display: flex; animation: storysplat-guide-in 180ms ease-out; }
    @keyframes storysplat-guide-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    .storysplat-guide-handle { display: none; }
    .storysplat-guide-header {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      gap: 9px;
      padding: 10px 8px 10px 12px;
      border-bottom: 1px solid ${line(12)};
    }
    .storysplat-guide-avatar {
      width: 26px;
      height: 26px;
      flex: 0 0 auto;
      border-radius: 50%;
      background: ${uiColor};
      color: ${accentText};
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .storysplat-guide-avatar svg { width: 15px; height: 15px; }
    .storysplat-guide-title {
      flex: 1;
      min-width: 0;
      font-size: 15px;
      font-weight: 600;
      ${titleFont ? `font-family: ${titleFont};` : ''}
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .storysplat-guide-close {
      width: 30px;
      height: 30px;
      flex: 0 0 auto;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${t.popupCloseBtnColor || t.popupTextColor || 'white'};
      opacity: 0.75;
      cursor: pointer;
    }
    .storysplat-guide-close:hover { opacity: 1; background: ${tint(12, 'rgba(127, 127, 127, 0.2)')}; }
    .storysplat-guide-close svg { width: 16px; height: 16px; display: block; }
    .storysplat-guide-messages {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 12px 12px 6px;
      scrollbar-width: thin;
    }
    .storysplat-guide-msg {
      max-width: 88%;
      padding: 8px 11px;
      border-radius: 14px;
      white-space: pre-wrap;
      font-size: ${t.popupContentFontSize || '14px'};
      line-height: ${t.popupContentLineHeight || '1.45'};
      ${contentFont ? `font-family: ${contentFont};` : ''}
    }
    .storysplat-guide-msg--assistant {
      align-self: flex-start;
      background: ${tint(11, 'rgba(127, 127, 127, 0.2)')};
      border-bottom-left-radius: 4px;
    }
    .storysplat-guide-msg--user {
      align-self: flex-end;
      background: ${uiColor};
      color: ${accentText};
      border-bottom-right-radius: 4px;
    }
    .storysplat-guide-msg.is-pending { display: inline-flex; align-items: center; gap: 4px; padding: 12px 14px; }
    .storysplat-guide-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
      opacity: 0.3;
      animation: storysplat-guide-dot 1.2s ease-in-out infinite;
    }
    .storysplat-guide-dot:nth-child(2) { animation-delay: 0.15s; }
    .storysplat-guide-dot:nth-child(3) { animation-delay: 0.3s; }
    @keyframes storysplat-guide-dot { 0%, 80%, 100% { opacity: 0.25; transform: none; } 40% { opacity: 0.9; transform: translateY(-3px); } }
    .storysplat-guide-error {
      align-self: stretch;
      padding: 8px 10px;
      border-radius: 8px;
      border: 1px solid rgba(239, 83, 80, 0.5);
      background: rgba(239, 83, 80, 0.16);
      font-size: 13px;
      line-height: 1.4;
    }
    .storysplat-guide-suggestions {
      flex: 0 0 auto;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 2px 12px 8px;
    }
    .storysplat-guide-suggestions[hidden] { display: none; }
    .storysplat-guide-chip {
      max-width: 100%;
      padding: 6px 10px;
      border-radius: 999px;
      border: 1px solid ${line(30)};
      font-size: 12.5px;
      line-height: 1.3;
      text-align: left;
      white-space: normal;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease;
    }
    .storysplat-guide-chip:hover:not(:disabled) { background: ${tint(10, 'rgba(127, 127, 127, 0.18)')}; border-color: ${uiColor}; }
    .storysplat-guide-chip:disabled { opacity: 0.5; cursor: default; }
    .storysplat-guide-compose {
      flex: 0 0 auto;
      display: flex;
      align-items: flex-end;
      gap: 8px;
      margin: 0 10px;
      padding: 5px 5px 5px 12px;
      border-radius: 18px;
      border: 1px solid ${line(24)};
      background: ${tint(6, 'rgba(127, 127, 127, 0.1)')};
      transition: border-color 0.15s ease;
    }
    .storysplat-guide-compose:focus-within { border-color: ${uiColor}; }
    .storysplat-viewer-container .storysplat-guide-input {
      flex: 1;
      min-width: 0;
      max-height: 96px;
      margin: 0;
      padding: 6px 0;
      border: none;
      outline: none;
      box-shadow: none;
      background: transparent;
      color: inherit;
      font: inherit;
      font-size: 14px;
      line-height: 1.35;
      resize: none;
      overflow-y: auto;
      -webkit-appearance: none;
      appearance: none;
    }
    .storysplat-guide-input::placeholder { color: inherit; opacity: 0.55; }
    /* 16px stops iOS zooming the page when the field takes focus. */
    @media (pointer: coarse) { .storysplat-viewer-container .storysplat-guide-input { font-size: 16px; } }
    .storysplat-guide-send {
      width: 32px;
      height: 32px;
      flex: 0 0 auto;
      border-radius: 50%;
      background: ${uiColor};
      color: ${accentText};
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: opacity 0.15s ease;
    }
    .storysplat-guide-send svg { width: 16px; height: 16px; display: block; }
    .storysplat-guide-send:disabled { opacity: 0.4; cursor: default; }
    .storysplat-guide-footnote {
      flex: 0 0 auto;
      margin: 6px 12px 9px;
      font-size: 11px;
      line-height: 1.3;
      text-align: center;
      opacity: 0.6;
    }
    .storysplat-guide-sr {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      white-space: nowrap;
    }
    .storysplat-guide-scrim {
      position: absolute;
      inset: 0;
      display: none;
      background: rgba(0, 0, 0, 0.45);
      z-index: 100000;
    }
    .storysplat-guide-scrim.visible { display: block; }
    .storysplat-guide-panel.storysplat-guide-panel--sheet {
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      height: min(70%, 560px);
      max-height: calc(100% - 48px);
      border-left: none;
      border-right: none;
      border-bottom: none;
      border-radius: 16px 16px 0 0;
      padding-bottom: env(safe-area-inset-bottom);
      box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
      transition: transform 260ms cubic-bezier(0.25, 0.1, 0.25, 1);
    }
    .storysplat-guide-panel--sheet.open { animation: storysplat-guide-sheet-in 260ms cubic-bezier(0.25, 0.1, 0.25, 1); }
    @keyframes storysplat-guide-sheet-in { from { transform: translateY(100%); } to { transform: none; } }
    .storysplat-guide-panel--sheet .storysplat-guide-handle {
      display: block;
      position: relative;
      flex: 0 0 auto;
      height: 18px;
      cursor: grab;
      touch-action: none;
    }
    .storysplat-guide-panel--sheet .storysplat-guide-handle::after {
      content: '';
      position: absolute;
      top: 8px;
      left: 50%;
      width: 40px;
      height: 4px;
      border-radius: 2px;
      background: currentColor;
      opacity: 0.35;
      transform: translateX(-50%);
    }
    .storysplat-guide-panel--sheet .storysplat-guide-header { padding-top: 2px; }
    @media (prefers-reduced-motion: reduce) {
      .storysplat-guide-panel.open, .storysplat-guide-panel--sheet.open { animation: none; }
      .storysplat-guide-dot { animation: none; opacity: 0.6; }
    }
`;
}

export function generateViewerStyles(uiColor: string = '#CC5833', template: string = 'minimal', theme?: ViewerTheme): string {
    const t = theme || {};
    const tc = t.globalTextColor || 'white';
    // Defense-in-depth sanitizer for theme-driven CSS values. The editor UI
    // constrains allowed values via dropdowns/number inputs, but scene JSON is
    // creator-authored and can be hand-edited — reject anything that could
    // break out of a declaration or inject a new rule/at-rule.
    const cssSafe = (v: string | undefined): string | undefined => {
        if (typeof v !== 'string' || v.length === 0 || v.length > 200) return undefined;
        if (/[{};<>]|\/\*|\*\/|@import|expression\(|url\(|\\/i.test(v)) return undefined;
        if (/[\r\n]/.test(v)) return undefined;
        return v;
    };
    const tPopupTitleFontFamily = cssSafe(t.popupTitleFontFamily);
    const tPopupTitleLetterSpacing = cssSafe(t.popupTitleLetterSpacing);
    const tPopupContentFontFamily = cssSafe(t.popupContentFontFamily);
    const tPopupContentLetterSpacing = cssSafe(t.popupContentLetterSpacing);
    const popupTextAlign = t.popupTextAlign === 'left' ? 'left' : 'center';
    const tShareModalFontFamily = cssSafe(t.shareModalFontFamily);
    const tProgressTextColor = cssSafe(t.progressTextColor);
    const tProgressFontFamily = cssSafe(t.progressFontFamily);
    const tProgressLetterSpacing = cssSafe(t.progressLetterSpacing);
    const preloaderBg = getOpaquePreloaderColor(cssSafe(t.preloaderBg));
    return `
    @keyframes storysplat-morph {
      0% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
      25% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
      50% { border-radius: 20% 70% 50% 50% / 30% 30% 70% 70%; }
      75% { border-radius: 70% 30% 40% 60% / 60% 30% 50% 40%; }
      100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
    }

    @keyframes storysplat-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes storysplat-float {
      0%, 100% { transform: translateY(0) scale(1); }
      50% { transform: translateY(-5px) scale(0.98); }
    }

    /* Container - CRITICAL: must be position relative and contained */
    .storysplat-viewer-container {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
      font-family: ${t.fontFamily || 'system-ui, -apple-system, sans-serif'};
      box-sizing: border-box;
    }

    .storysplat-viewer-container * {
      box-sizing: border-box;
    }

    /* Style isolation: reset common elements to prevent parent page styles from leaking in.
       Uses :where() for zero specificity so individual class rules always win. */
    .storysplat-viewer-container :where(button, a) {
      all: unset;
      box-sizing: border-box;
      display: inline-block;
      font-family: inherit;
      font-size: inherit;
      line-height: normal;
      cursor: pointer;
    }

    .storysplat-viewer-container button:focus-visible,
    .storysplat-viewer-container a:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }

    .storysplat-viewer-container canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100% !important;
      height: 100% !important;
      display: block;
      touch-action: none;
    }

    /* PDF pages drawn by the viewer's pdf.js renderer (protected media, iOS) sit in the page
       flow; the rule above would stretch each one over the whole popup as a blank sheet. */
    .storysplat-viewer-container canvas.storysplat-pdf-page {
      position: static;
      width: 100% !important;
      height: auto !important;
      flex-shrink: 0;
      touch-action: auto;
    }

    /* Preloader - fully covers the scene until the dismissal fade begins */
    .storysplat-preloader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${DEFAULT_PRELOADER_BACKGROUND};
      background-image: linear-gradient(${preloaderBg}, ${preloaderBg});
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 100000;
      transition: opacity ${PRELOADER_FADE_MS}ms ease-out;
    }

    .storysplat-preloader.hidden {
      opacity: 0;
      pointer-events: none;
    }

    .storysplat-preloader-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 40px;
      gap: 20px;
    }

    .storysplat-preloader-media {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .storysplat-preloader-image {
      height: 64px;
      width: auto;
      object-fit: contain;
    }

    .storysplat-preloader-image-inverted {
      height: 64px;
      width: auto;
      object-fit: contain;
      filter: invert(1);
      margin-right: -32px;
    }

    .storysplat-preloader-logo-text {
      font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
      font-weight: 700;
      font-size: 28px;
      color: white;
      white-space: nowrap;
      letter-spacing: -0.02em;
    }

    .storysplat-loader-container {
      position: relative;
      width: 64px;
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: storysplat-float 3s ease-in-out infinite;
    }

    .storysplat-blob {
      position: absolute;
      inset: -15%;
      background: #CC5833;
      opacity: 0.85;
      animation: storysplat-morph 4s ease-in-out infinite, storysplat-spin 12s linear infinite;
      box-shadow: inset 10px 0 20px rgba(0,0,0,0.1), 0 5px 15px rgba(204, 88, 51, 0.4);
      filter: blur(1px);
    }

    .storysplat-core {
      width: 50%;
      height: 50%;
      background-color: #e5e7eb;
      border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
      box-shadow: inset -5px -5px 10px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.2);
      position: relative;
      z-index: 2;
      animation: storysplat-morph 5s ease-in-out infinite reverse, storysplat-spin 18s linear infinite reverse;
    }

    @media (max-width: 768px) {
      .storysplat-preloader-image,
      .storysplat-preloader-image-inverted {
        height: 40px;
      }
      .storysplat-preloader-image-inverted {
        margin-right: -20px;
      }
      .storysplat-loader-container {
        height: 40px;
        width: 40px;
      }
      .storysplat-preloader-logo-text {
        font-size: 20px;
      }
    }

    .storysplat-preloader-progress {
      width: 200px;
      height: ${t.preloaderBarHeight || '4px'};
      background: rgba(255, 255, 255, 0.1);
      border-radius: 2px;
      margin-top: 20px;
      position: relative;
    }

    .storysplat-preloader-bar {
      width: 0%;
      height: 100%;
      background: ${uiColor};
      border-radius: 2px;
      transition: width 0.3s ease-out;
    }

    .storysplat-preloader-text {
      position: absolute;
      top: -25px;
      left: 50%;
      transform: translateX(-50%);
      color: ${t.preloaderTextColor || 'white'};
      font-size: 14px;
      white-space: nowrap;
    }

    @media (prefers-reduced-motion: reduce) {
      /* After the preloader rules so these win at equal specificity. */
      .storysplat-loader-container,
      .storysplat-blob,
      .storysplat-core {
        animation: none;
      }
      .storysplat-preloader-bar {
        transition: none;
      }
    }

    /* Minimal Scroll Controls */
    .storysplat-scroll-controls {
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 150px;
      padding: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      z-index: 1000;
    }

    .storysplat-scroll-content {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
    }

    .storysplat-progress-text {
      font-size: ${t.progressFontSize || '14px'};
      font-weight: ${t.progressFontWeight || 'normal'};
      color: ${tProgressTextColor || tc};${tProgressFontFamily ? `
      font-family: ${tProgressFontFamily};` : ''}${tProgressLetterSpacing ? `
      letter-spacing: ${tProgressLetterSpacing};` : ''}
      font-variant-numeric: tabular-nums;
      min-width: 40px;
      text-align: center;
    }

    .storysplat-progress-container {
      width: 90%;
      max-width: 150px;
      height: ${t.progressBarHeight || '3px'};
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'};
      border-radius: 2px;
      overflow: hidden;
    }

    .storysplat-progress-bar {
      height: 100%;
      background: ${uiColor};
      /* Compositor-only progress: scaleX instead of width so per-frame tour updates don't run
         layout. The transition keeps the same 0.3s smoothing the width version had. */
      transition: transform 0.3s ease-out;
      width: 100%;
      transform: scaleX(0);
      transform-origin: left;
    }

    .storysplat-scroll-buttons {
      display: flex;
      justify-content: center;
      gap: 5px;
      z-index: 1000;
    }

    /* Minimal Button Style */
    .storysplat-btn {
      background: ${t.buttonBg || 'rgba(0, 0, 0, 0.3)'};
      border: none;
      color: ${t.buttonTextColor || 'white'};
      padding: 4px 8px;
      font-size: ${t.buttonFontSize || '12px'};
      font-weight: ${t.buttonFontWeight || 'normal'};
      border-radius: ${t.buttonBorderRadius || '4px'};
      cursor: pointer;
      transition: background 0.2s;
      text-align: center;
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
    }

    .storysplat-btn:hover {
      background: ${t.buttonHoverBg || 'rgba(0, 0, 0, 0.5)'};
    }

    .storysplat-btn-play {
      background: ${t.buttonBg || 'rgba(0, 0, 0, 0.3)'};
      border: none;
      color: ${tc};
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
    }

    .storysplat-btn-play:hover {
      background: ${t.buttonHoverBg || 'rgba(0, 0, 0, 0.5)'};
    }

    .storysplat-btn-play svg {
      width: 12px;
      height: 12px;
      fill: ${tc};
    }

    /* Explore Controls (Orbit/Fly toggle) */
    .storysplat-explore-controls {
      display: none;
      gap: 5px;
      width: 100%;
      justify-content: center;
    }

    .storysplat-explore-controls.visible {
      display: flex;
    }

    .storysplat-explore-btn {
      flex: 1;
      background: rgba(0, 0, 0, 0.3);
      border: none;
      color: ${tc};
      padding: 6px 12px;
      font-size: 12px;
      border-radius: 4px;
      cursor: pointer;
      transition: background 0.2s;
      text-align: center;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    .storysplat-explore-btn.selected {
      background: ${uiColor} !important;
    }

    .storysplat-explore-btn:hover {
      background: rgba(0, 0, 0, 0.5);
    }

    /* Mode Toggle - Minimal */
    .storysplat-mode-container {
      margin-top: 5px;
    }

    .storysplat-mode-toggle {
      display: flex;
      gap: 5px;
    }

    .storysplat-mode-btn {
      background: rgba(0, 0, 0, 0.3);
      border: none;
      color: ${tc};
      padding: 3px 6px;
      font-size: ${t.modeBtnFontSize || '11px'};
      font-weight: ${t.modeBtnFontWeight || 'normal'};
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    .storysplat-mode-btn.selected {
      background: ${uiColor} !important;
    }

    .storysplat-mode-btn:hover {
      background: rgba(0, 0, 0, 0.5);
    }

    /* Fullscreen Button - Minimal */
    .storysplat-fullscreen-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0);
      border: none;
      padding: 5px;
      border-radius: 3px;
      cursor: pointer;
      z-index: 1000;
    }

    .storysplat-fullscreen-btn svg {
      width: 20px;
      height: 20px;
      fill: ${tc};
    }

    /* Share Button */
    .storysplat-share-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      background: ${t.shareButtonBg || t.buttonBg || 'rgba(0, 0, 0, 0.3)'};
      border: none;
      padding: 8px 10px;
      border-radius: ${t.shareButtonBorderRadius || t.buttonBorderRadius || '8px'};
      cursor: pointer;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.3s ease;
      box-sizing: border-box;
    }
    .storysplat-share-btn svg,
    .storysplat-share-btn svg path {
      fill: ${t.shareButtonIconColor || t.buttonTextColor || tc} !important;
    }
    .storysplat-share-btn:hover {
      background: ${t.shareButtonHoverBg || t.buttonHoverBg || 'rgba(0, 0, 0, 0.5)'};
    }
    .storysplat-share-btn:disabled {
      cursor: wait;
      opacity: 0.65;
    }

    /* Photo button (camera): same look as Share; standalone when there is no pill. */
    .storysplat-photo-btn {
      position: absolute;
      top: 10px;
      right: 56px;
      background: ${t.shareButtonBg || t.buttonBg || 'rgba(0, 0, 0, 0.3)'};
      border: none;
      padding: 8px 10px;
      border-radius: ${t.shareButtonBorderRadius || t.buttonBorderRadius || '8px'};
      cursor: pointer;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${t.shareButtonIconColor || t.buttonTextColor || tc};
      transition: background-color 0.3s ease;
      box-sizing: border-box;
    }
    .storysplat-photo-btn svg { width: 20px; height: 20px; display: block; }
    .storysplat-photo-btn:hover {
      background: ${t.shareButtonHoverBg || t.buttonHoverBg || 'rgba(0, 0, 0, 0.5)'};
    }
    .storysplat-top-right-pill .storysplat-photo-btn {
      position: relative;
      top: auto;
      right: auto;
      background: transparent;
      padding: 4px;
      border-radius: 6px;
      order: 0; /* [photo | share | fullscreen]: share is order 1, fullscreen order 2 */
    }
    .storysplat-top-right-pill .storysplat-photo-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }

    /* Photo mode: a clean frame. Every piece of interface chrome is hidden while the
       visitor keeps full camera control; only the viewfinder and the camera controls
       stay. Hotspot / portal markers, leader lines and the reticle are switched off on
       the render side. */
    .storysplat-photo-frame {
      position: absolute;
      inset: 18px;
      display: none;
      pointer-events: none;
      z-index: 100002;
    }
    .storysplat-photo-mode .storysplat-photo-frame { display: block; }
    .storysplat-photo-corner {
      position: absolute;
      width: 34px;
      height: 34px;
      border: 2px solid rgba(255, 255, 255, 0.9);
      filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.6));
    }
    .storysplat-photo-corner--tl { top: 0; left: 0; border-right: 0; border-bottom: 0; border-top-left-radius: 6px; }
    .storysplat-photo-corner--tr { top: 0; right: 0; border-left: 0; border-bottom: 0; border-top-right-radius: 6px; }
    .storysplat-photo-corner--bl { bottom: 0; left: 0; border-right: 0; border-top: 0; border-bottom-left-radius: 6px; }
    .storysplat-photo-corner--br { bottom: 0; right: 0; border-left: 0; border-top: 0; border-bottom-right-radius: 6px; }
    /* Shutter flash on capture. */
    .storysplat-photo-frame::after {
      content: '';
      position: absolute;
      inset: -18px;
      background: white;
      opacity: 0;
      pointer-events: none;
    }
    .storysplat-photo-frame.flash::after { animation: storysplat-photo-flash 220ms ease-out; }
    @keyframes storysplat-photo-flash { 0% { opacity: 0.85; } 100% { opacity: 0; } }
    /* Soft dark fade along the bottom edge so the hint and close read over bright scenes. */
    .storysplat-photo-fade {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 150px;
      display: none;
      pointer-events: none;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.35) 55%, rgba(0, 0, 0, 0.6) 100%);
      z-index: 100002;
    }
    .storysplat-photo-mode .storysplat-photo-fade { display: block; }
    .storysplat-photo-bar {
      position: absolute;
      left: 50%;
      bottom: 14px;
      transform: translate(-50%, 12px);
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      color: white;
      font-family: ${t.fontFamily || 'system-ui, -apple-system, sans-serif'};
      z-index: 100003;
      opacity: 0;
      transition: opacity 0.2s ease, transform 0.2s ease;
      max-width: calc(100% - 32px);
      box-sizing: border-box;
      text-align: center;
    }
    .storysplat-photo-mode .storysplat-photo-bar { display: flex; opacity: 1; transform: translate(-50%, 0); }
    .storysplat-photo-caption {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .storysplat-photo-hint {
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0.04em;
      opacity: 0.85;
      white-space: nowrap;
    }
    /* Round shutter: white ring with an inner disc, like a phone camera. */
    .storysplat-photo-take {
      width: 72px;
      height: 72px;
      padding: 0;
      border: 3px solid rgba(255, 255, 255, 0.95);
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
      transition: transform 0.12s ease;
    }
    .storysplat-photo-shutter {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: white;
      transition: transform 0.12s ease;
    }
    .storysplat-photo-take:hover .storysplat-photo-shutter { transform: scale(0.94); }
    .storysplat-photo-take:active .storysplat-photo-shutter { transform: scale(0.82); }
    .storysplat-photo-take:disabled { cursor: wait; }
    .storysplat-photo-take:disabled .storysplat-photo-shutter { transform: scale(0.82); opacity: 0.8; }
    .storysplat-photo-exit {
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: white;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0.85;
    }
    .storysplat-photo-exit svg { width: 16px; height: 16px; display: block; }
    /* Sit the whole button (cross + hover disc) on the text's optical centre, not the line box centre. */
    .storysplat-photo-caption .storysplat-photo-exit { transform: translateY(1.5px); }
    .storysplat-photo-exit:hover { opacity: 1; background: rgba(255, 255, 255, 0.12); }
    @media (max-width: 600px) {
      .storysplat-photo-frame { inset: 12px; }
      .storysplat-photo-corner { width: 26px; height: 26px; }
      .storysplat-photo-bar { bottom: 12px; gap: 6px; }
      .storysplat-photo-fade { height: 120px; }
    }
    .storysplat-photo-mode .storysplat-toolbar,
    .storysplat-photo-mode .storysplat-top-right-bar,
    .storysplat-photo-mode .storysplat-top-right-group,
    .storysplat-photo-mode .storysplat-top-right-pill,
    .storysplat-photo-mode .storysplat-fullscreen-btn,
    .storysplat-photo-mode .storysplat-share-btn,
    .storysplat-photo-mode .storysplat-photo-btn,
    .storysplat-photo-mode .storysplat-mode-container,
    .storysplat-photo-mode .storysplat-explore-controls,
    .storysplat-photo-mode .storysplat-scroll-controls,
    .storysplat-photo-mode .storysplat-waypoint-info,
    .storysplat-photo-mode .storysplat-waypoint-list-container,
    .storysplat-photo-mode .storysplat-scene-menu-container,
    .storysplat-photo-mode .storysplat-scene-menu-toggle--sidebar,
    .storysplat-photo-mode .storysplat-watermark,
    .storysplat-photo-mode .storysplat-scene-title,
    .storysplat-photo-mode .storysplat-fps-counter,
    .storysplat-photo-mode .storysplat-wasd-hint,
    .storysplat-photo-mode .storysplat-orbit-hint,
    .storysplat-photo-mode .storysplat-doubletap-hint,
    .storysplat-photo-mode .storysplat-tour-look-hint,
    .storysplat-photo-mode .storysplat-hotspot-popup,
    .storysplat-photo-mode .storysplat-portal-popup,
    .storysplat-photo-mode .storysplat-hotspot-lightbox,
    .storysplat-photo-mode .storysplat-overlay-layer,
    .storysplat-photo-mode .storysplat-help-btn,
    .storysplat-photo-mode .storysplat-help-panel,
    .storysplat-photo-mode .storysplat-mute-btn,
    .storysplat-photo-mode .storysplat-mic-btn,
    .storysplat-photo-mode .storysplat-relight-btn,
    .storysplat-photo-mode .storysplat-xr-btn,
    .storysplat-photo-mode .storysplat-measure-btn,
    .storysplat-photo-mode .storysplat-measure-canvas,
    .storysplat-photo-mode .storysplat-measure-label,
    .storysplat-photo-mode .storysplat-measure-point,
    .storysplat-photo-mode .storysplat-cutaway-btn,
    .storysplat-photo-mode .storysplat-cutaway-panel,
    .storysplat-photo-mode .storysplat-stereo-btn,
    .storysplat-photo-mode .storysplat-stereo-menu,
    .storysplat-photo-mode .storysplat-skins-btn,
    .storysplat-photo-mode .storysplat-skins-dropdown,
    .storysplat-photo-mode .storysplat-layers-btn,
    .storysplat-photo-mode .storysplat-layers-panel,
    .storysplat-photo-mode .storysplat-directory-btn,
    .storysplat-photo-mode .storysplat-directory-panel,
    .storysplat-photo-mode .storysplat-directory-scrim,
    .storysplat-photo-mode .storysplat-guide-btn,
    .storysplat-photo-mode .storysplat-guide-panel,
    .storysplat-photo-mode .storysplat-guide-scrim,
    .storysplat-photo-mode .storysplat-skin-overlay-wrapper,
    .storysplat-photo-mode .storysplat-photo-share-backdrop,
    .storysplat-photo-mode .storysplat-detail-indicator {
      display: none !important;
    }
    /* Movement controls stay usable on phones so the shot can still be framed. */

    /* Pinned toward the top (not vertically centered) so the header and buttons don't move
       when the preview decodes or an inline message appears. */
    .storysplat-photo-share-backdrop {
      position: absolute;
      inset: 0;
      z-index: 100004;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      min-height: 100%;
      padding: 10px;
      padding: max(10px, env(safe-area-inset-top)) max(10px, env(safe-area-inset-right)) max(10px, env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left));
      padding-top: max(10px, min(10vh, 96px), env(safe-area-inset-top));
      background: ${t.shareModalBackdropBg || 'rgba(0, 0, 0, 0.45)'};
      backdrop-filter: blur(${t.shareModalBackdropBlur || '6px'});
      -webkit-backdrop-filter: blur(${t.shareModalBackdropBlur || '6px'});
      box-sizing: border-box;
    }

    .storysplat-photo-share-panel {
      width: min(460px, 100%);
      max-height: min(680px, calc(100% - 20px), calc(100vh - 20px));
      max-height: min(680px, calc(100% - 20px), calc(100dvh - 20px));
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      overscroll-behavior: contain;
      background: ${t.shareModalBg || 'rgba(16, 16, 16, 0.94)'};
      border: 1px solid ${t.shareModalBorderColor || 'rgba(255, 255, 255, 0.18)'};
      border-radius: ${t.shareModalBorderRadius || '8px'};
      color: ${t.shareModalTextColor || tc};
      font-family: ${tShareModalFontFamily || t.fontFamily || 'system-ui, -apple-system, sans-serif'};
      box-shadow: 0 18px 60px rgba(0, 0, 0, 0.35);
      box-sizing: border-box;
    }

    .storysplat-photo-share-header {
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 16px 16px 12px;
    }

    .storysplat-photo-share-title {
      display: block;
      color: ${t.shareModalTitleColor || t.shareModalTextColor || tc};
      font-size: ${t.shareModalTitleFontSize || '18px'};
      font-weight: ${t.shareModalTitleFontWeight || '750'};
      line-height: 1.15;
      white-space: nowrap;
    }

    .storysplat-photo-share-close {
      border: none;
      background: transparent;
      color: ${t.shareModalCloseColor || t.shareModalTextColor || tc};
      cursor: pointer;
      font-size: 22px;
      line-height: 1;
      padding: 2px 4px;
    }

    .storysplat-photo-share-preview {
      flex: 0 1 auto;
      display: block;
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: min(340px, 38vh);
      max-height: min(340px, 38dvh);
      margin: 0;
      object-fit: contain;
      border-radius: ${t.shareModalPreviewBorderRadius || '6px'};
      background: ${t.shareModalPreviewBg || '#111'};
    }

    .storysplat-photo-share-socials {
      flex: 0 0 auto;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
      padding: 0 16px 12px;
    }
    .storysplat-photo-share-social svg {
      width: 20px;
      height: 20px;
      flex: 0 0 auto;
    }
    .storysplat-photo-share-preview-wrap {
      flex: 0 1 auto;
      min-height: 0;
      display: flex;
      justify-content: center;
      margin: 0 16px 12px;
    }
    .storysplat-photo-share-figure {
      position: relative;
      display: inline-flex;
      max-width: 100%;
      line-height: 0;
      --storysplat-photo-preview-max-h: min(340px, 38vh);
    }
    /* Known capture aspect: the figure's box is fixed up front (width limited by the panel or
       by max-height × ratio), and the image fills it once decoded. */
    .storysplat-photo-share-figure--sized {
      width: min(100%, calc(var(--storysplat-photo-preview-max-h) * var(--storysplat-photo-ratio)));
      aspect-ratio: var(--storysplat-photo-ratio);
      flex: 0 0 auto;
    }
    .storysplat-photo-share-figure--sized .storysplat-photo-share-preview {
      width: 100%;
      height: 100%;
      max-height: none;
    }

    .storysplat-photo-share-status {
      padding: 0 16px 14px;
      margin-top: -4px;
      font-size: 13px;
      line-height: 1.35;
      color: ${t.shareModalTextColor || tc};
      opacity: 0.85;
    }
    .storysplat-photo-share-status[hidden] { display: none; }

    /* In-place copy field (replaces window.prompt when the clipboard is unavailable). */
    .storysplat-copy-url-field {
      padding: 0 16px 12px;
    }
    .storysplat-copy-url-field input {
      width: 100%;
      box-sizing: border-box;
      padding: 8px 10px;
      border: 1px solid ${t.shareModalBorderColor || 'rgba(255, 255, 255, 0.22)'};
      border-radius: ${t.shareModalButtonBorderRadius || '6px'};
      background: rgba(0, 0, 0, 0.35);
      color: inherit;
      font: inherit;
      font-size: 13px;
      user-select: all;
      -webkit-user-select: all;
    }
    /* A small white "Download" over the picture's top-right corner, clickable as text. */
    .storysplat-photo-share-download {
      position: absolute;
      top: 8px;
      right: 10px;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 6px;
      border: none;
      border-radius: 4px;
      background: transparent;
      color: white;
      font: inherit;
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
      cursor: pointer;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));
    }
    .storysplat-photo-share-download svg { width: 14px; height: 14px; }
    .storysplat-photo-share-download:hover { background: rgba(0, 0, 0, 0.45); }

    .storysplat-photo-share-social {
      min-height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 10px;
      border: 1px solid ${t.shareModalBorderColor || 'rgba(255, 255, 255, 0.18)'};
      border-radius: ${t.shareModalButtonBorderRadius || '6px'};
      background: ${t.shareModalSocialButtonBg || 'rgba(255, 255, 255, 0.06)'};
      color: ${t.shareModalSocialButtonTextColor || t.shareModalTextColor || tc};
      cursor: pointer;
      font: inherit;
      font-size: ${t.shareModalSocialFontSize || '13px'};
      font-weight: ${t.shareModalSocialFontWeight || '650'};
      line-height: 1;
      text-align: center;
      text-decoration: none;
      white-space: nowrap;
    }
    .storysplat-photo-share-social:hover {
      background: ${t.shareModalSocialButtonHoverBg || t.shareModalActionButtonHoverBg || t.buttonHoverBg || 'rgba(255, 255, 255, 0.12)'};
    }

    .storysplat-photo-share-actions {
      flex: 0 0 auto;
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      padding: 0 16px 16px;
    }

    .storysplat-photo-share-action {
      min-height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 12px;
      border: 1px solid ${t.shareModalBorderColor || 'rgba(255, 255, 255, 0.22)'};
      border-radius: ${t.shareModalButtonBorderRadius || '6px'};
      background: ${t.shareModalActionButtonBg || 'rgba(255, 255, 255, 0.08)'};
      color: ${t.shareModalActionButtonTextColor || t.shareModalTextColor || tc};
      cursor: pointer;
      font: inherit;
      font-size: ${t.shareModalActionFontSize || '14px'};
      font-weight: ${t.shareModalActionFontWeight || '650'};
      line-height: 1;
      text-align: center;
      white-space: nowrap;
    }
    .storysplat-photo-share-action:hover:not(:disabled) {
      background: ${t.shareModalActionButtonHoverBg || t.buttonHoverBg || 'rgba(255, 255, 255, 0.14)'};
    }
    .storysplat-photo-share-action:disabled {
      cursor: wait;
      opacity: 0.65;
    }

    .storysplat-photo-share-action.primary {
      background: ${t.shareModalPrimaryButtonBg || uiColor};
      border-color: ${t.shareModalPrimaryButtonBg || uiColor};
      color: ${t.shareModalPrimaryButtonTextColor || t.shareModalActionButtonTextColor || t.shareModalTextColor || tc};
    }

    .storysplat-photo-share-action.primary:hover:not(:disabled) {
      background: ${t.shareModalPrimaryButtonHoverBg || t.shareModalPrimaryButtonBg || uiColor};
      border-color: ${t.shareModalPrimaryButtonHoverBg || t.shareModalPrimaryButtonBg || uiColor};
    }

    @media (max-width: 520px), (max-height: 720px) {
      .storysplat-photo-share-backdrop {
        padding: 8px;
        padding: max(8px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right)) max(8px, env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left));
      }

      .storysplat-photo-share-panel {
        width: 100%;
        max-height: min(100%, calc(100vh - 16px));
        max-height: min(100%, calc(100dvh - 16px));
      }

      .storysplat-photo-share-header {
        padding: 12px 12px 8px;
      }

      .storysplat-photo-share-title {
        max-width: calc(100% - 34px);
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .storysplat-photo-share-backdrop {
        padding-top: max(8px, min(6vh, 48px), env(safe-area-inset-top));
      }
      .storysplat-photo-share-preview {
        max-height: min(260px, 30vh);
        max-height: min(260px, 30dvh);
      }
      .storysplat-photo-share-figure {
        --storysplat-photo-preview-max-h: min(260px, 30vh);
      }
      .storysplat-photo-share-preview-wrap {
        margin: 0 12px 10px;
      }

      .storysplat-photo-share-socials {
        gap: 6px;
        padding: 0 12px 10px;
      }

      .storysplat-photo-share-actions {
        gap: 6px;
        padding: 0 12px 12px;
      }

      .storysplat-photo-share-social {
        min-height: 34px;
        padding: 0 8px;
      }

      .storysplat-photo-share-action {
        min-height: 34px;
        padding: 0 10px;
      }
    }

    @media (max-height: 600px) {
      .storysplat-photo-share-preview {
        max-height: min(180px, 24vh);
        max-height: min(180px, 24dvh);
      }
      .storysplat-photo-share-figure {
        --storysplat-photo-preview-max-h: min(180px, 24vh);
      }
    }

    /* Mute Button - Minimal (inside toolbar) */
    .storysplat-mute-btn {
      position: relative;
      top: auto;
      left: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease;
    }

    .storysplat-mute-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }

    .storysplat-mute-btn svg {
      width: 20px;
      height: 20px;
      fill: ${tc};
    }

    /* Deliberate microphone input control (only rendered for mic-authored scenes). */
    .storysplat-mic-btn {
      position: relative;
      background: transparent;
      border: none;
      min-width: 28px;
      height: 28px;
      padding: 4px 6px;
      border-radius: 6px;
      color: ${tc};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-family: ${t.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
      transition: background 0.2s ease, color 0.2s ease, opacity 0.2s ease;
    }
    .storysplat-mic-btn:hover:not(:disabled) {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-mic-btn svg {
      width: 17px;
      height: 17px;
      fill: currentColor;
      flex: 0 0 auto;
    }
    .storysplat-mic-btn.active { color: ${uiColor}; }
    .storysplat-mic-btn.denied { color: #ef9a9a; }
    .storysplat-mic-btn:disabled { cursor: default; opacity: 0.58; }

    @media (max-width: 480px) {
      .storysplat-mic-label { display: none; }
      .storysplat-mic-btn { padding: 4px; }
    }

    /* Relighting Toggle Button (inside toolbar) */
    .storysplat-relight-btn {
      position: relative;
      top: auto;
      left: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
    }

    .storysplat-relight-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }

    .storysplat-relight-btn svg {
      width: 20px;
      height: 20px;
      fill: ${tc};
      transition: fill 0.3s ease;
    }

    .storysplat-relight-btn.active svg {
      fill: ${uiColor};
    }

    /* Fisheye / Tiny Planet Toggle Button (inside toolbar) */
    .storysplat-fisheye-btn {
      position: relative;
      top: auto;
      left: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
      color: ${tc};
    }
    .storysplat-fisheye-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-fisheye-btn svg {
      width: 20px;
      height: 20px;
      transition: color 0.3s ease;
    }
    .storysplat-fisheye-btn.active {
      color: ${uiColor};
    }

    /* Stereo 3D toggle (inside toolbar) + format menu under it */
    .storysplat-stereo-btn {
      position: relative;
      top: auto;
      left: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
      color: ${tc};
    }
    .storysplat-stereo-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-stereo-btn svg {
      width: 20px;
      height: 20px;
      transition: color 0.3s ease;
    }
    .storysplat-stereo-btn.active {
      color: ${uiColor};
    }
    .storysplat-stereo-menu {
      position: absolute;
      top: 52px;
      left: 10px;
      display: none;
      flex-direction: column;
      gap: 2px;
      width: 250px;
      max-width: calc(100% - 20px);
      box-sizing: border-box;
      padding: 8px;
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      z-index: 1004;
      font-family: ${t.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
    }
    .storysplat-stereo-menu.open {
      display: flex;
    }
    .storysplat-stereo-menu-header {
      font-size: 10px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 2px 6px 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      margin-bottom: 4px;
    }
    .storysplat-stereo-option {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      width: 100%;
      padding: 7px 8px 7px 26px;
      position: relative;
      background: transparent;
      border: none;
      border-radius: 6px;
      color: white;
      font: inherit;
      font-size: 13px;
      text-align: left;
      cursor: pointer;
    }
    .storysplat-stereo-option:hover {
      background: rgba(255, 255, 255, 0.08);
    }
    .storysplat-stereo-option[aria-checked="true"]::before {
      content: '';
      position: absolute;
      left: 9px;
      top: 13px;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${uiColor};
    }
    .storysplat-stereo-option-hint {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.55);
    }
    .storysplat-stereo-swap {
      margin-top: 4px;
      padding-top: 9px;
      border-top: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 0 0 6px 6px;
    }
    .storysplat-stereo-swap[aria-checked="true"]::before {
      top: 15px;
      border-radius: 2px;
    }

    /* Split stereo formats draw the interface once per eye (see stereoUi.ts): the live interface
       in the left eye's region and a mirror of it in the right eye's, each transformed exactly
       like its eye image. The wrappers pass pointer events through except over the interface. */
    .storysplat-stereo-eye {
      position: absolute;
      inset: 0;
      transform-origin: 0 0;
      pointer-events: none;
      overflow: hidden;
      overflow: clip;
    }
    :where(.storysplat-stereo-eye) > * {
      pointer-events: auto;
    }

    /* Cutaway toggle (inside toolbar) + vertical plane slider on the right edge */
    .storysplat-cutaway-btn {
      position: relative;
      top: auto;
      left: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
      color: ${tc};
    }
    .storysplat-cutaway-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-cutaway-btn svg {
      width: 20px;
      height: 20px;
      transition: color 0.3s ease;
    }
    .storysplat-cutaway-btn.active {
      color: ${uiColor};
    }
    .storysplat-cutaway-panel {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 10px 6px;
      background: ${t.helpPanelBg || 'rgba(0, 0, 0, 0.6)'};
      color: ${tc};
      border-radius: ${t.helpPanelBorderRadius || '8px'};
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      z-index: 1002;
      touch-action: none;
      font-family: system-ui, -apple-system, sans-serif;
    }
    .storysplat-cutaway-panel.visible {
      display: flex;
    }
    .storysplat-cutaway-slider {
      /* Vertical range via writing-mode (Chrome 124+, Firefox 120+, Safari 17.4+). */
      writing-mode: vertical-lr;
      direction: rtl;
      width: 24px;
      height: min(220px, 40vh);
      margin: 0;
      cursor: pointer;
      accent-color: ${uiColor};
    }
    .storysplat-cutaway-flip {
      background: transparent;
      border: none;
      color: ${tc};
      cursor: pointer;
      padding: 4px;
      border-radius: 6px;
      display: flex;
    }
    .storysplat-cutaway-flip:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-cutaway-flip svg {
      width: 18px;
      height: 18px;
    }
    .storysplat-cutaway-label {
      font-size: 11px;
      opacity: 0.85;
      min-width: 32px;
      text-align: center;
    }

    /* Measurement Tape Measure Button (inside toolbar) */
    .storysplat-measure-btn {
      position: relative;
      top: auto;
      left: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
    }

    .storysplat-measure-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }

    .storysplat-measure-btn.active {
      opacity: 1;
    }

    .storysplat-measure-btn.active svg {
      fill: ${uiColor};
    }

    .storysplat-measure-btn svg {
      width: 20px;
      height: 20px;
      fill: ${tc};
    }

    /* Measurement distance label overlay */
    .storysplat-measure-label {
      position: absolute;
      pointer-events: none;
      background: rgba(0, 0, 0, 0.75);
      color: white;
      font-size: 12px;
      padding: 3px 8px;
      border-radius: 4px;
      white-space: nowrap;
      transform: translate(-50%, -100%) translateY(-8px);
      z-index: 1002;
      font-family: ${t.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
    }

    /* Measurement point marker */
    .storysplat-measure-point {
      position: absolute;
      width: 10px;
      height: 10px;
      background: var(--storysplat-measure-color, #FF9800);
      border: 2px solid white;
      border-radius: 50%;
      pointer-events: none;
      transform: translate(-50%, -50%);
      z-index: 1001;
    }

    /* Measurement line canvas overlay */
    .storysplat-measure-canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1001;
    }

    /* Measurement list dropdown */
    .storysplat-measure-list {
      position: absolute;
      top: 52px;
      left: 10px;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      border-radius: 6px;
      padding: 8px 0;
      min-width: 160px;
      max-height: 200px;
      overflow-y: auto;
      z-index: 1004;
      display: none;
      font-family: ${t.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
    }
    .storysplat-measure-list-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 10px 6px;
      border-bottom: 1px solid rgba(255,255,255,0.15);
      margin-bottom: 4px;
    }
    .storysplat-measure-list-title {
      font-size: 10px;
      font-weight: 600;
      color: rgba(255,255,255,0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .storysplat-measure-list-clear {
      background: none;
      border: none;
      color: rgba(255,255,255,0.5);
      font-size: 10px;
      cursor: pointer;
      padding: 2px 4px;
    }
    .storysplat-measure-list-clear:hover {
      color: #ff6b6b;
    }
    .storysplat-measure-list-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 5px 10px;
      color: white;
      font-size: 12px;
    }
    .storysplat-measure-list-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--storysplat-measure-color, #FF9800);
      flex-shrink: 0;
    }
    .storysplat-measure-list-empty {
      padding: 8px 10px;
      color: rgba(255,255,255,0.4);
      font-size: 11px;
      font-style: italic;
    }
    /* Distance / Area switch in the measurement list */
    .storysplat-measure-kind {
      display: flex;
      gap: 2px;
      margin: 0 10px 6px;
      padding: 2px;
      border-radius: 6px;
      background: rgba(255,255,255,0.08);
    }
    .storysplat-measure-kind-btn {
      flex: 1;
      background: none;
      border: none;
      border-radius: 4px;
      color: rgba(255,255,255,0.7);
      font: inherit;
      font-size: 11px;
      padding: 4px 8px;
      cursor: pointer;
    }
    .storysplat-measure-kind-btn:hover { color: white; }
    .storysplat-measure-kind-btn.active {
      background: var(--storysplat-measure-color, #FF9800);
      color: white;
    }
    .storysplat-measure-list-finish {
      display: block;
      margin: 2px 10px 6px;
      width: calc(100% - 20px);
      background: var(--storysplat-measure-color, #FF9800);
      border: none;
      border-radius: 4px;
      color: white;
      font: inherit;
      font-size: 11px;
      font-weight: 600;
      padding: 5px 8px;
      cursor: pointer;
    }
    .storysplat-measure-list-dot--area {
      border-radius: 2px;
    }
    /* Measurement sets visitors tick in the panel (measurementSetsPicker.ts) */
    .storysplat-measure-list.has-sets {
      max-height: min(420px, calc(100% - 64px));
      min-width: 200px;
      max-width: min(300px, calc(100% - 20px));
    }
    .storysplat-measure-sets {
      padding: 0 6px 6px;
      margin-bottom: 6px;
      border-bottom: 1px solid rgba(255,255,255,0.15);
    }
    .storysplat-measure-sets-title {
      padding: 2px 4px 4px;
      font-size: 10px;
      font-weight: 600;
      color: rgba(255,255,255,0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .storysplat-measure-set {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      background: none;
      border: none;
      border-radius: 4px;
      color: white;
      font: inherit;
      font-size: 12px;
      text-align: left;
      padding: 5px 4px;
      cursor: pointer;
    }
    .storysplat-measure-set:hover { background: rgba(255,255,255,0.08); }
    .storysplat-measure-set:focus-visible {
      outline: 2px solid var(--storysplat-measure-color, #FF9800);
      outline-offset: -2px;
    }
    .storysplat-measure-set--all {
      border-bottom: 1px solid rgba(255,255,255,0.1);
      border-radius: 4px 4px 0 0;
      margin-bottom: 2px;
    }
    .storysplat-measure-set-box {
      width: 14px;
      height: 14px;
      box-sizing: border-box;
      border-radius: 3px;
      border: 1.5px solid rgba(255,255,255,0.7);
      flex-shrink: 0;
      display: grid;
      place-items: center;
    }
    .storysplat-measure-set[aria-checked="true"] .storysplat-measure-set-box,
    .storysplat-measure-set[aria-checked="mixed"] .storysplat-measure-set-box {
      background: var(--storysplat-measure-color, #FF9800);
      border-color: var(--storysplat-measure-color, #FF9800);
    }
    .storysplat-measure-set[aria-checked="true"] .storysplat-measure-set-box::after {
      content: '';
      width: 3px;
      height: 7px;
      margin-top: -2px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
    .storysplat-measure-set[aria-checked="mixed"] .storysplat-measure-set-box::after {
      content: '';
      width: 8px;
      height: 2px;
      background: #fff;
    }
    .storysplat-measure-set-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .storysplat-measure-set-count {
      font-size: 11px;
      color: rgba(255,255,255,0.55);
      font-variant-numeric: tabular-nums;
    }
    @media (pointer: coarse) {
      .storysplat-measure-set { padding: 8px 4px; }
    }
    .storysplat-measure-label--area {
      transform: translate(-50%, -50%);
      font-weight: 600;
    }

    /* Annotation layers button + panel (top-left toolbar; see layersPanel.ts) */
    .storysplat-layers-btn {
      position: relative;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
    }
    .storysplat-layers-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-layers-btn svg {
      width: 20px;
      height: 20px;
      fill: ${tc};
    }
    .storysplat-layers-btn.active svg {
      fill: ${uiColor};
    }
    .storysplat-layers-panel {
      position: absolute;
      top: 52px;
      left: 10px;
      box-sizing: border-box;
      width: min(270px, calc(100% - 20px));
      max-height: calc(100% - 130px);
      overflow-y: auto;
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 8px;
      padding: 8px;
      z-index: 1004;
      display: none;
      color: white;
      font-family: ${t.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
    }
    .storysplat-layers-panel.open { display: block; }
    .storysplat-tools-wrapped .storysplat-layers-panel { top: 98px; }
    .storysplat-layers-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 2px 4px 8px;
      border-bottom: 1px solid rgba(255,255,255,0.12);
      margin-bottom: 4px;
    }
    .storysplat-layers-title {
      font-size: 11px;
      font-weight: 600;
      color: rgba(255,255,255,0.75);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .storysplat-layers-close {
      background: none;
      border: none;
      color: rgba(255,255,255,0.6);
      font-size: 18px;
      line-height: 1;
      cursor: pointer;
      padding: 0 4px;
    }
    .storysplat-layers-close:hover { color: white; }
    .storysplat-layers-item {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      box-sizing: border-box;
      text-align: left;
      background: transparent;
      border: none;
      border-radius: 6px;
      color: white;
      padding: 7px 6px;
      cursor: pointer;
      font: inherit;
      font-size: 13px;
    }
    .storysplat-layers-item:hover { background: rgba(255,255,255,0.08); }
    .storysplat-layers-item:focus-visible { outline: 2px solid ${uiColor}; outline-offset: -2px; }
    .storysplat-layers-swatch {
      position: relative;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      border-radius: 4px;
      background: var(--storysplat-layer-color, #888);
      box-shadow: inset 0 0 0 1px rgba(255,255,255,0.4);
      opacity: 0.3;
      transition: opacity 0.15s ease;
    }
    .storysplat-layers-item.is-on .storysplat-layers-swatch { opacity: 1; }
    .storysplat-layers-item[aria-checked="true"] .storysplat-layers-swatch::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      filter: drop-shadow(0 0 1px rgba(0,0,0,0.6));
    }
    .storysplat-layers-text {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }
    .storysplat-layers-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .storysplat-layers-desc {
      font-size: 11px;
      color: rgba(255,255,255,0.55);
      white-space: normal;
    }
    .storysplat-layers-area {
      flex-shrink: 0;
      font-size: 11px;
      color: rgba(255,255,255,0.7);
      font-variant-numeric: tabular-nums;
    }
    .storysplat-layers-all {
      margin-bottom: 2px;
      border-bottom: 1px solid rgba(255,255,255,0.08);
      border-radius: 6px 6px 0 0;
    }
    .storysplat-layers-section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      background: none;
      border: none;
      color: rgba(255,255,255,0.6);
      font: inherit;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 8px 6px 4px;
      cursor: pointer;
    }
    .storysplat-layers-section-header:hover { color: white; }
    .storysplat-layers-chevron::before {
      content: '\\25BE';
      display: inline-block;
      transition: transform 0.15s ease;
    }
    .storysplat-layers-section.collapsed .storysplat-layers-chevron::before { transform: rotate(-90deg); }
    .storysplat-layers-section.collapsed .storysplat-layers-section-items { display: none; }
    .storysplat-layers-section-items { padding-left: 6px; }
${directoryCss(t, tc, uiColor)}

${sceneGuideCss(t, tc, uiColor)}

    /* AI Re-skin dropdown (palette icon, inside top-left toolbar) */
    .storysplat-skins-btn {
      position: relative;
      top: auto;
      left: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease, opacity 0.3s ease;
    }
    .storysplat-skins-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-skins-btn.active svg {
      fill: ${uiColor};
    }
    .storysplat-skins-btn svg {
      width: 20px;
      height: 20px;
      fill: ${tc};
    }
    .storysplat-skins-dropdown {
      position: absolute;
      top: 52px;
      left: 10px;
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-radius: 8px;
      padding: 8px;
      width: 240px;
      max-height: 360px;
      overflow-y: auto;
      z-index: 1004;
      display: none;
      font-family: ${t.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'};
      border: 1px solid rgba(255,255,255,0.1);
    }
    .storysplat-skins-dropdown.open { display: block; }
    .storysplat-skins-dropdown-header {
      font-size: 10px;
      font-weight: 600;
      color: rgba(255,255,255,0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 2px 4px 8px;
      border-bottom: 1px solid rgba(255,255,255,0.12);
      margin-bottom: 8px;
    }
    .storysplat-skin-item {
      display: flex;
      gap: 8px;
      align-items: center;
      padding: 6px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.15s ease;
      position: relative;
      overflow: hidden;
    }
    .storysplat-skin-item:hover { background: rgba(255,255,255,0.08); }
    .storysplat-skin-item.active { background: ${uiColor}; }
    @keyframes storysplat-skin-shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    .storysplat-skin-item.loading::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.18) 50%,
        transparent 100%
      );
      animation: storysplat-skin-shimmer 1.2s ease-in-out infinite;
      pointer-events: none;
    }
    .storysplat-skin-item-thumb {
      width: 56px;
      height: 40px;
      border-radius: 4px;
      object-fit: cover;
      flex-shrink: 0;
      background: rgba(255,255,255,0.05);
    }
    .storysplat-skin-item-prompt {
      font-size: 11px;
      line-height: 1.3;
      color: rgba(255,255,255,0.9);
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .storysplat-skins-empty {
      padding: 12px 6px;
      color: rgba(255,255,255,0.4);
      font-size: 11px;
      text-align: center;
    }
    .storysplat-skin-opacity-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 4px 4px;
      margin-top: 8px;
      border-top: 1px solid rgba(255,255,255,0.12);
    }
    .storysplat-skin-opacity-row label {
      font-size: 10px;
      color: rgba(255,255,255,0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .storysplat-skin-opacity-row input[type=range] {
      flex: 1;
      accent-color: ${uiColor};
    }
    .storysplat-skin-exit-btn {
      width: 100%;
      background: rgba(255,255,255,0.08);
      color: white;
      border: none;
      padding: 6px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
      margin-top: 6px;
    }
    .storysplat-skin-exit-btn:hover { background: rgba(255,255,255,0.15); }

    /* Skin overlay (above canvas, below toolbar/dropdowns).
       The 3D camera keeps its vertical FOV constant across viewer sizes, so the
       overlay is HEIGHT-bound (height:100%) and its width comes from the skin's
       captured aspect (set inline per activation). This keeps it pixel-aligned
       with the live render (no zoom). Skins are captured WIDE (16:9, see
       skinAspect.ts), so it fills the width for windows up to that aspect; a
       window wider than the skin shows a small side gap. The wrapper owns the
       fade transition so dragging the opacity slider stays instant. */
    .storysplat-skin-overlay-wrapper {
      position: absolute;
      top: 0;
      left: 50%;
      height: 100%;
      width: auto;
      transform: translateX(-50%);
      pointer-events: none;
      z-index: 999;
      opacity: 0;
      visibility: hidden;
      transition: opacity 350ms ease-out, visibility 0s linear 350ms;
    }
    .storysplat-skin-overlay-wrapper.active {
      opacity: 1;
      visibility: visible;
      transition: opacity 350ms ease-out, visibility 0s linear 0s;
    }
    .storysplat-skin-overlay {
      display: block;
      height: 100%;
      width: 100%;
      /* The wrapper is height-bound and locked to the image's OWN aspect (set
         inline on load), so 'fill' shows it 1:1 — no crop, no zoom. Using 'cover'
         against the capture bucket instead scaled mismatched outputs up. */
      object-fit: fill;
      user-select: none;
    }

    .storysplat-ar-mode .storysplat-share-btn,
    .storysplat-ar-mode .storysplat-photo-share-backdrop,
    .storysplat-ar-mode .storysplat-skins-btn,
    .storysplat-ar-mode .storysplat-skins-dropdown,
    .storysplat-ar-mode .storysplat-layers-panel,
    .storysplat-ar-mode .storysplat-directory-btn,
    .storysplat-ar-mode .storysplat-directory-panel,
    .storysplat-ar-mode .storysplat-directory-scrim,
    .storysplat-ar-mode .storysplat-guide-btn,
    .storysplat-ar-mode .storysplat-guide-panel,
    .storysplat-ar-mode .storysplat-guide-scrim,
    .storysplat-ar-mode .storysplat-skin-overlay-wrapper {
      display: none !important;
    }

    /* Top-left toolbar container (frosted glass) */
    .storysplat-toolbar {
      position: absolute;
      top: 10px;
      left: 10px;
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 4px;
      /* Shared top-bar chip height: toolbar, top-right pill, scene-menu toggle and
         waypoint toggle are all pinned to 38px (border-box) so they line up exactly.
         38px = 4px padding ×2 + 1px border ×2 + 28px icon button. */
      box-sizing: border-box;
      height: 38px;
      background: ${t.buttonBg || 'rgba(0, 0, 0, 0.3)'};
      border-radius: 8px;
      border: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
      z-index: 1002;
    }

    /* Top-right shared-background pill (frosted glass) — holds fullscreen + share.
       Mirrors .storysplat-toolbar but anchored top-right. The pill OWNS the
       background; its children are transparent so they don't paint a second box. */
    .storysplat-top-right-pill {
      position: absolute;
      top: 10px;
      right: 10px;
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 4px;
      box-sizing: border-box;
      height: 38px; /* shared top-bar chip height (see .storysplat-toolbar) */
      background: ${t.buttonBg || 'rgba(0, 0, 0, 0.3)'};
      border-radius: 8px;
      border: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
      z-index: 1002;
    }

    /* Fullscreen + share as transparent children of the pill (mirror mute-btn).
       Visual order inside the pill is [share | fullscreen] via flex order
       (DOM append stays fullscreen-then-share). */
    .storysplat-top-right-pill .storysplat-fullscreen-btn {
      position: relative;
      top: auto;
      right: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease;
      order: 2;
    }
    .storysplat-top-right-pill .storysplat-fullscreen-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }
    .storysplat-top-right-pill .storysplat-share-btn {
      position: relative;
      top: auto;
      right: auto;
      background: transparent;
      border: none;
      padding: 4px;
      border-radius: 6px;
      align-self: auto;
      order: 1;
    }
    .storysplat-top-right-pill .storysplat-share-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }

    /* Classic: right-anchored group holds [waypoints] then the pill. */
    .storysplat-top-right-group {
      position: absolute;
      top: 10px;
      right: 10px;
      display: flex;
      gap: 8px;
      align-items: center;
      z-index: 1002;
    }
    .storysplat-top-right-group .storysplat-waypoint-list-container {
      position: relative;
      top: auto;
      right: auto;
      order: 0;
    }
    .storysplat-top-right-group .storysplat-top-right-pill {
      position: relative;
      top: auto;
      right: auto;
      order: 1;
    }

    /* Help Button - Minimal */
    .storysplat-help-btn {
      position: relative;
      top: auto;
      left: auto;
      width: 28px;
      height: 28px;
      border-radius: 6px;
      background: transparent;
      color: ${tc};
      border: none;
      cursor: pointer;
      font-size: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
    }

    .storysplat-help-btn:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    }

    .storysplat-help-btn.active {
      /* Active toggle convention (shared by relight/fisheye/measure/skins):
         the icon/glyph itself fills with the theme accent — no background fill. */
      color: ${uiColor};
    }

    .storysplat-help-panel {
      position: absolute;
      top: 52px;
      left: 10px;
      background: ${t.helpPanelBg || 'rgba(0, 0, 0, 0.85)'};
      color: ${tc};
      padding: 0;
      border-radius: ${t.helpPanelBorderRadius || '8px'};
      max-width: 260px;
      z-index: 1001;
      font-size: 11px;
      border: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
      opacity: 0;
      transform: translateY(-8px);
      pointer-events: none;
      transition: opacity 0.2s ease, transform 0.2s ease;
    }

    .storysplat-help-panel.visible {
      opacity: 1;
      transform: translateY(0);
      pointer-events: auto;
    }

    .storysplat-help-tabs {
      display: flex;
      border-bottom: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    }

    .storysplat-help-tab {
      flex: 1;
      padding: 8px 4px;
      background: none;
      border: none;
      color: ${tc};
      opacity: 0.5;
      font-size: 10px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;
      text-transform: uppercase;
      text-align: center;
      letter-spacing: 0.5px;
      border-bottom: 2px solid transparent;
    }

    .storysplat-help-tab:hover {
      opacity: 0.8;
    }

    .storysplat-help-tab.active {
      color: ${uiColor};
      opacity: 1;
      border-bottom-color: ${uiColor};
    }

    .storysplat-help-tab-content {
      display: none;
      padding: 10px 12px;
    }

    .storysplat-help-tab-content.active {
      display: block;
    }

    .storysplat-performance-notice {
      position: absolute;
      bottom: 14px;
      left: 16px;
      z-index: 1000;
      display: flex;
      align-items: center;
      gap: 8px;
      max-width: calc(100% - 32px);
      box-sizing: border-box;
      color: ${tc};
      font-size: 11px;
      line-height: 1.4;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
      opacity: 0.65;
      pointer-events: auto;
      transition: opacity 0.2s ease;
    }
    .storysplat-performance-notice[hidden] { display: none; }
    .storysplat-performance-notice:hover,
    .storysplat-performance-notice:focus-within { opacity: 1; }
    .storysplat-performance-notice button {
      flex-shrink: 0;
      min-height: 24px;
      padding: 3px 2px;
      border: none;
      background: transparent;
      color: inherit;
      font: inherit;
      text-shadow: inherit;
      text-decoration: underline;
      text-underline-offset: 3px;
      cursor: pointer;
    }
    .storysplat-performance-notice button:focus-visible { outline: 2px solid ${uiColor}; outline-offset: 2px; }
    /* Leave space for the notice below existing bottom-left overlays. */
    .storysplat-viewer-container:has(> .storysplat-performance-notice:not([hidden])) :is(.storysplat-scene-title, .storysplat-detail-indicator, .storysplat-fps-counter, .storysplat-xr-btn, .storysplat-wasd-hint, .storysplat-orbit-hint, .storysplat-doubletap-hint) {
      margin-bottom: 30px;
    }
    @media (max-width: 540px) {
      .storysplat-viewer-container:has(> .storysplat-performance-notice:not([hidden])) :is(.storysplat-watermark, .storysplat-scroll-controls) {
        margin-bottom: 30px;
      }
    }

    /* Performance toggle sits after the tab content, directly in the panel (which has
       padding: 0), so it carries the same 12px inner gutter as .storysplat-help-tab-content
       and the 3px row rhythm of .storysplat-help-row. */
    .storysplat-help-perf {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 10px 12px 3px;
      border-top: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
      font-size: 13px;
      line-height: 1.3;
      cursor: pointer;
      user-select: none;
    }
    .storysplat-help-perf input {
      accent-color: ${t.popupLinkBtnColor || '#ff6b35'};
      width: 16px;
      height: 16px;
      margin: 0;
      flex-shrink: 0;
      cursor: pointer;
    }
    /* Left inset = 12px gutter + 16px checkbox + 8px gap, so it lines up under the label text. */
    .storysplat-help-perf-desc {
      margin: 0;
      padding: 0 12px 10px 36px;
      font-size: 11px;
      line-height: 1.3;
      color: ${tc === 'white' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
    }
    .storysplat-help-row {
      display: flex;
      align-items: center;
      padding: 3px 0;
      gap: 8px;
    }

    .storysplat-help-key {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 22px;
      height: 20px;
      padding: 0 5px;
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
      border: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'};
      border-radius: 3px;
      font-size: 9px;
      font-weight: 600;
      font-family: system-ui, -apple-system, sans-serif;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .storysplat-help-desc {
      color: ${tc};
      opacity: 0.7;
      font-size: 11px;
    }

    /* XR (VR/AR) Buttons - Minimal, positioned to the right of the ? help button */
    .storysplat-xr-btn {
      position: absolute;
      bottom: 10px;
      left: 10px;
      background: rgba(0, 0, 0, 0.5);
      border: none;
      padding: 6px 10px;
      border-radius: 4px;
      cursor: pointer;
      z-index: 1000;
      color: ${tc};
      font-size: 11px;
      font-weight: bold;
      text-transform: uppercase;
      transition: all 0.2s ease;
      display: none;
    }

    .storysplat-xr-btn.available {
      display: block;
    }

    .storysplat-xr-btn.active {
      background: ${uiColor};
    }

    /* A VR session request is in flight */
    .storysplat-xr-btn.pending {
      opacity: 0.6;
      cursor: progress;
    }

    .storysplat-xr-btn:hover {
      background: rgba(0, 0, 0, 0.7);
    }

    .storysplat-xr-btn.active:hover {
      background: ${uiColor};
      opacity: 0.9;
    }

    .storysplat-vr-btn {
      left: 10px;
    }

    .storysplat-ar-btn {
      left: 10px;
    }

    /* When both XR buttons are visible, sit AR to the right of VR */
    .storysplat-vr-btn.available ~ .storysplat-ar-btn.available {
      left: 70px;
    }

    /* A page that asked for VR (?vr=1, VR mode): the VR button as a large "Enter VR" prompt */
    .storysplat-vr-btn.prompt {
      left: 50%;
      top: 50%;
      bottom: auto;
      transform: translate(-50%, -50%);
      padding: 14px 28px;
      border-radius: 999px;
      font-size: 18px;
      letter-spacing: 0.04em;
      background: rgba(0, 0, 0, 0.72);
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
    }
    .storysplat-vr-btn.prompt:hover {
      background: rgba(0, 0, 0, 0.85);
    }
    .storysplat-vr-btn.prompt ~ .storysplat-ar-btn.available {
      left: 10px;
    }

    .storysplat-help-panel h3 {
      margin: 0;
      padding: 10px 12px;
      font-size: 12px;
      font-weight: 600;
      text-align: center;
      border-bottom: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    }

    .storysplat-help-panel p {
      margin: 3px 0;
      line-height: 1.3;
    }

    /* Waypoint Info - Top Banner Style (matching BabylonJS minimal export) */
${infoBannerCss(t, '.storysplat-waypoint-info', '.storysplat-waypoint-info.hasContent')}
    /* Hotspot Popup - Matching BabylonJS HTML export styles */
    /* Uses position: absolute so popup stays within container when embedded */
    /* Overlays: floating information panels shown by triggers (not attached to a marker).
       Same palette as hotspot popups so a themed scene stays coherent. */
${generateOverlayStyles({ t, uiColor, popupTextAlign, tPopupContentFontFamily })}

    .storysplat-hotspot-popup {
      position: absolute;
      background-color: ${t.popupBg || 'rgba(0, 0, 0, 0.75)'};
      color: ${t.popupTextColor || 'white'};
      padding: 20px;
      border-radius: ${t.popupBorderRadius || '10px'};
      z-index: 100001;
      box-shadow: 0 0 10px rgba(0,0,0,0.5);
      display: none;
      font-size: 14px;
      flex-direction: column;
      font-family: ${t.fontFamily || 'system-ui, -apple-system, sans-serif'};
      /* Default positioning: horizontally centered, anchored near the TOP (not vertically
         centered) so the title and close button stay put while images/media load and the
         popup grows downward. */
      top: 10%;
      left: 50%;
      transform: translateX(-50%);
      max-width: 80%;
      max-height: 85%;
      text-align: ${popupTextAlign};
      /* Clip the popup and let the CONTENT scroll instead, so tall content never
         forces the popup past max-height. Title + close stay pinned. */
      overflow: hidden;
    }

    .storysplat-hotspot-popup.visible {
      display: flex;
    }

    /* Per-hotspot placement (HotspotData.popupAnchor / popupLayout): the nine overlay
       positions and the edge-to-edge drawer. Two classes + !important so they beat the
       global Theme Editor layout override on .storysplat-hotspot-popup. */
    .storysplat-hotspot-popup[class*="storysplat-hotspot-popup--anchor-"] { max-width: min(90%, 840px) !important; max-height: calc(100% - 100px) !important; margin: 0 !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-top-left { top: 64px !important; left: 16px !important; right: auto !important; bottom: auto !important; transform: none !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-top-center { top: 64px !important; left: 50% !important; right: auto !important; bottom: auto !important; transform: translateX(-50%) !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-top-right { top: 64px !important; right: 16px !important; left: auto !important; bottom: auto !important; transform: none !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-center-left { top: 50% !important; left: 16px !important; right: auto !important; bottom: auto !important; transform: translateY(-50%) !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-center { top: 50% !important; left: 50% !important; right: auto !important; bottom: auto !important; transform: translate(-50%, -50%) !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-center-right { top: 50% !important; right: 16px !important; left: auto !important; bottom: auto !important; transform: translateY(-50%) !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-bottom-left { bottom: 88px !important; left: 16px !important; right: auto !important; top: auto !important; transform: none !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-bottom-center { bottom: 88px !important; left: 50% !important; right: auto !important; top: auto !important; transform: translateX(-50%) !important; }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--anchor-bottom-right { bottom: 88px !important; right: 16px !important; left: auto !important; top: auto !important; transform: none !important; }
${hotspotDrawerCss('.storysplat-hotspot-popup.storysplat-hotspot-popup--drawer', t)}
    .storysplat-hotspot-popup.storysplat-hotspot-popup--drawer-left { left: 0 !important; right: auto !important; border-right: 1px solid rgba(255,255,255,0.1); }
    .storysplat-hotspot-popup.storysplat-hotspot-popup--drawer-right { right: 0 !important; left: auto !important; border-left: 1px solid rgba(255,255,255,0.1); }
${t.hotspotPopupLayout === 'drawer' ? `
    /* Theme default: every hotspot without its own placement opens as a drawer on the
       ${t.hotspotPopupDrawerSide === 'left' ? 'left' : 'right'}. A hotspot's --anchor-* / --drawer-* / --floating class opts out. */
${hotspotDrawerCss('.storysplat-hotspot-popup.storysplat-hotspot-popup:not([class*="storysplat-hotspot-popup--"])', t)}
    .storysplat-hotspot-popup.storysplat-hotspot-popup:not([class*="storysplat-hotspot-popup--"]) { ${t.hotspotPopupDrawerSide === 'left' ? 'left: 0 !important; right: auto !important; border-right: 1px solid rgba(255,255,255,0.1);' : 'right: 0 !important; left: auto !important; border-left: 1px solid rgba(255,255,255,0.1);'} }
` : ''}
    .storysplat-hotspot-popup-handle { display: none; }
    .storysplat-hotspot-popup-scrim {
      position: absolute;
      inset: 0;
      display: none;
      background: rgba(0, 0, 0, 0.45);
      z-index: 100000;
    }
    /* Phones: popup drawers become a bottom sheet (at most 60% of the embed's height) with a
       drag handle, swipe-down and tap-outside to close. */
    @media (max-width: 600px) {
      .storysplat-hotspot-popup.storysplat-hotspot-popup--drawer .storysplat-hotspot-popup-handle${t.hotspotPopupLayout === 'drawer' ? ',\n      .storysplat-hotspot-popup:not([class*="storysplat-hotspot-popup--"]) .storysplat-hotspot-popup-handle' : ''} {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 28px;
        cursor: grab;
        touch-action: none;
      }
      .storysplat-hotspot-popup-handle::after {
        content: '';
        position: absolute;
        top: 8px;
        left: 50%;
        width: 40px;
        height: 4px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.45);
        transform: translateX(-50%);
      }
      .storysplat-hotspot-popup.visible.storysplat-hotspot-popup--drawer + .storysplat-hotspot-popup-scrim${t.hotspotPopupLayout === 'drawer' ? ',\n      .storysplat-hotspot-popup.visible:not([class*="storysplat-hotspot-popup--"]) + .storysplat-hotspot-popup-scrim' : ''} { display: block; }
      .storysplat-hotspot-popup.storysplat-hotspot-popup--drawer${t.hotspotPopupLayout === 'drawer' ? ',\n      .storysplat-hotspot-popup.storysplat-hotspot-popup:not([class*="storysplat-hotspot-popup--"])' : ''} {
        padding-bottom: max(20px, env(safe-area-inset-bottom)) !important;
        top: auto !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: auto !important;
        max-width: none !important;
        height: auto !important;
        max-height: 60% !important;
        border-left: none !important;
        border-right: none !important;
        border-radius: 16px 16px 0 0 !important;
        padding: 44px 16px 20px !important;
        transform: none !important;
      }
    }

    /* Fullscreen mode for click activation with image/iframe content */
    /* Fits content, horizontally centered and anchored near the top (not vertically centered)
       so the title/close don't jump when images load and the popup grows downward. */
    .storysplat-hotspot-popup.fullscreen {
      top: 20px !important;
      left: 0 !important;
      right: 0 !important;
      bottom: auto !important;
      transform: none !important;
      width: min(calc(100% - 40px), 840px) !important;
      height: fit-content !important;
      max-height: calc(100% - 40px) !important;
      margin: 0 auto !important;
      border-radius: 10px !important;
      display: flex !important;
      align-items: center !important;
      /* safe center: centers when it fits but falls back to flex-start when the content is taller
         than the popup, so a too-tall popup scrolls from the top instead of clipping its head. */
      justify-content: safe center !important;
      flex-direction: column !important;
      padding: 20px !important;
      overflow: auto !important;
      box-sizing: border-box !important;
    }

    .storysplat-hotspot-popup h2,
    .storysplat-hotspot-popup-title {
      margin: 0 0 10px 0;
      font-size: ${t.popupTitleFontSize || '18px'};
      font-weight: ${t.popupTitleFontWeight || '600'};
      ${tPopupTitleFontFamily ? `font-family: ${tPopupTitleFontFamily};` : ''}
      ${tPopupTitleLetterSpacing ? `letter-spacing: ${tPopupTitleLetterSpacing};` : ''}
      text-align: ${popupTextAlign};
      padding-right: 30px;
      flex-shrink: 0;
    }

    .storysplat-hotspot-popup p {
      margin: 0 0 10px 0;
      line-height: ${t.popupContentLineHeight || '1.5'};
      font-size: ${t.popupContentFontSize || '14px'};
      font-weight: ${t.popupContentFontWeight || 'normal'};
      ${tPopupContentFontFamily ? `font-family: ${tPopupContentFontFamily};` : ''}
      ${tPopupContentLetterSpacing ? `letter-spacing: ${tPopupContentLetterSpacing};` : ''}
      ${t.popupContentOpacity !== undefined ? `opacity: ${t.popupContentOpacity};` : ''}
      text-align: ${popupTextAlign};
      white-space: pre-wrap;
      max-width: 500px;
    }

    .storysplat-hotspot-popup-content {
      max-width: 500px;
      text-align: ${popupTextAlign};
      /* Scrollable middle region: shrinks within the popup's max-height and scrolls
         internally so the popup stays centered and capped no matter how tall. */
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      width: 100%;
    }

    .storysplat-hotspot-popup.fullscreen .storysplat-hotspot-popup-title {
      text-align: ${popupTextAlign};
      width: 100%;
      padding: 0 0 10px 0;
      flex-shrink: 0;
    }

    .storysplat-hotspot-popup.fullscreen .storysplat-hotspot-popup-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: ${popupTextAlign};
      width: 100%;
      max-width: none;
      min-height: 0;
      overflow-y: auto;
    }

    .storysplat-hotspot-popup.fullscreen p {
      text-align: ${popupTextAlign};
      max-width: 80%;
    }

    .storysplat-hotspot-popup.fullscreen .storysplat-hotspot-popup-link {
      margin: 4px auto;
    }

    .storysplat-hotspot-popup.fullscreen .storysplat-hotspot-popup-close {
      display: block;
      margin: 10px auto 0 auto;
      flex-shrink: 0;
    }

    .storysplat-hotspot-popup img {
      max-width: 100%;
      height: auto;
      border-radius: 5px;
      margin-bottom: 10px;
      display: block;
    }

    .storysplat-hotspot-popup.fullscreen img {
      width: auto !important;
      height: auto !important;
      max-width: 100% !important;
      max-height: 70vh !important;
      object-fit: contain !important;
      border-radius: 8px;
      margin: 10px 0;
      flex-shrink: 1;
      min-height: 0;
    }

    /* Space reserved for a popup image until it loads (reservePopupImageSpace). */
    .storysplat-hotspot-popup img.storysplat-img-pending {
      width: 100% !important;
      min-height: min(240px, 35vh) !important;
      background: rgba(255, 255, 255, 0.06);
      color: transparent;
      border-radius: 8px;
    }

    /* Multi-image gallery layouts for hotspot contentBlocks */
    .storysplat-hotspot-popup .hotspot-gallery {
      width: 100%;
      margin-bottom: 10px;
    }
    .storysplat-hotspot-popup .hotspot-gallery-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
      gap: 6px;
    }
    .storysplat-hotspot-popup .hotspot-gallery-grid img {
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      aspect-ratio: 1 / 1;
      object-fit: cover !important;
      margin: 0 !important;
      cursor: pointer;
    }
    /* A lone grid image stays a small thumbnail (click to open the lightbox),
       matching the editor — use the 'stacked' layout for a full-width image. */
    .storysplat-hotspot-popup .hotspot-gallery-stacked {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .storysplat-hotspot-popup .hotspot-gallery-stacked img {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 70vh !important;
      object-fit: contain !important;
      margin: 0 !important;
      cursor: pointer;
    }
    .storysplat-hotspot-popup .hotspot-gallery-carousel {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
    }
    .storysplat-hotspot-popup .hotspot-gallery-carousel img {
      flex: 0 0 100%;
      width: 100% !important;
      max-width: 100% !important;
      max-height: 70vh !important;
      object-fit: contain !important;
      scroll-snap-align: center;
      margin: 0 !important;
      cursor: pointer;
    }

    .storysplat-hotspot-popup iframe {
      width: 100%;
      max-width: 100%;
      aspect-ratio: 16 / 9;
      height: auto;
      /* Floor the height so the embed stays readable when sibling blocks are
         removed and the scrolling flex column tries to crush it. */
      min-height: 400px;
      border: none;
      border-radius: 5px;
      margin-bottom: 10px;
    }

    .storysplat-hotspot-popup.fullscreen iframe {
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      aspect-ratio: 16 / 9 !important;
      max-height: 70vh !important;
      margin: 10px 0;
      border-radius: 8px;
      flex-shrink: 1;
      min-height: 0;
    }

    /* Portal confirmation popup */
    .storysplat-portal-popup {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: ${t.portalPopupBg || 'rgba(0, 0, 0, 0.85)'};
      color: ${tc};
      padding: 20px;
      border-radius: 10px;
      z-index: 100002;
      display: none;
      flex-direction: column;
      gap: 15px;
      min-width: 240px;
      max-width: 80%;
      text-align: center;
      box-shadow: 0 0 12px rgba(0, 0, 0, 0.6);
    }

    .storysplat-portal-popup.visible {
      display: flex;
    }

    .storysplat-portal-popup-title {
      font-size: ${t.portalPopupTitleFontSize || '16px'};
      font-weight: ${t.portalPopupTitleFontWeight || '600'};
    }

    .storysplat-portal-popup-actions {
      display: flex;
      justify-content: center;
      gap: 10px;
    }

    .storysplat-portal-popup-btn {
      padding: 8px 14px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: ${t.portalPopupBtnFontSize || '13px'};
      font-weight: ${t.portalPopupBtnFontWeight || '500'};
    }

    .storysplat-portal-popup-confirm {
      background: ${uiColor};
      color: white;
    }

    .storysplat-portal-popup-cancel {
      background: rgba(255, 255, 255, 0.15);
      color: ${tc};
    }

    /* Panorama portal loading spinner */
    .storysplat-panorama-loading {
      position: absolute;
      inset: 0;
      z-index: 999;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      opacity: 0;
      animation: storysplat-pano-loading-fadein 180ms ease-out 120ms forwards;
      transition: opacity 200ms ease-out;
      pointer-events: auto;
    }
    .storysplat-panorama-loading--hiding {
      opacity: 0;
      animation: none;
    }
    .storysplat-panorama-loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.2);
      border-top-color: white;
      border-radius: 50%;
      animation: storysplat-pano-spin 0.8s linear infinite;
    }
    @keyframes storysplat-pano-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes storysplat-pano-loading-fadein {
      to { opacity: 1; }
    }

    /* Panorama portal exit button */
    .storysplat-panorama-exit-btn {
      position: absolute;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1000;
      padding: 10px 24px;
      background: rgba(0, 0, 0, 0.65);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 24px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s ease, transform 0.2s ease;
      animation: storysplat-panorama-exit-fadein 0.5s ease 0.8s both;
    }
    .storysplat-panorama-exit-btn:hover {
      background: rgba(0, 0, 0, 0.85);
      transform: translateX(-50%) scale(1.05);
    }
    @keyframes storysplat-panorama-exit-fadein {
      from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
      to { opacity: 1; transform: translateX(-50%) translateY(0); }
    }

    /* PDF drawn by pdf.js: full width (a fullscreen popup's centred column would shrink it to nothing) */
    .storysplat-hotspot-popup .pdf-pdfjs-container {
      width: 100%;
    }

    /* Model container for 3D model lightbox */
    .storysplat-hotspot-popup .model-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      aspect-ratio: 1 / 1;
      /* Floor the height so the 3D model viewport stays usable when sibling
         blocks are removed and the scrolling flex column tries to crush it. */
      min-height: 360px;
      max-height: 70vh;
      margin: 10px 0;
      position: relative;
      border-radius: 8px;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.3);
    }
    .storysplat-hotspot-popup .model-container canvas {
      width: 100% !important;
      height: 100% !important;
      display: block;
      border-radius: 8px;
    }
    .storysplat-hotspot-popup .model-container .model-loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: rgba(255, 255, 255, 0.7);
      font-size: 14px;
    }

    /* Video container - matches HTML export sizing */
    .storysplat-hotspot-popup .video-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 100%;
      height: auto;
      margin: 10px 0;
    }

    .storysplat-hotspot-popup video {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 5px;
      border: 2px solid rgba(255, 255, 255, 0.3);
    }

    /* Fullscreen popup video - constrained so title+description+buttons always fit */
    .storysplat-hotspot-popup.fullscreen .video-container {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 70vh !important;
      height: auto !important;
      margin: 10px 0 !important;
      /* Never collapse the video box. It used to shrink toward 0 at short window heights while the
         <video> kept up to 70vh, so the video spilled out of its box and overlapped the gallery.
         Keep its natural size and let the scrollable content region handle the overflow instead. */
      flex-shrink: 0;
      min-height: 0;
    }

    .storysplat-hotspot-popup.fullscreen video {
      max-width: 100% !important;
      max-height: 70vh !important;
      object-fit: contain !important;
      border-radius: 8px;
    }

    /* Galleries also keep their natural height in fullscreen, so the popup content scrolls rather
       than compressing them or letting the following media overlap them. */
    .storysplat-hotspot-popup.fullscreen .hotspot-gallery {
      flex-shrink: 0;
    }

    /* Close button - matching BabylonJS export (green button at bottom).
       The media popup's fullscreen toggle shares this styling but keeps its own
       class so analytics can tell the two buttons apart. */
    .storysplat-hotspot-popup-close,
    .storysplat-hotspot-popup-fullscreen {
      display: inline-block;
      width: auto;
      align-self: center;
      flex-shrink: 0;
      margin-top: 4px;
      padding: 10px 20px;
      background-color: ${t.popupCloseBtnColor || uiColor};
      border: none;
      color: white;
      cursor: pointer;
      border-radius: ${t.buttonBorderRadius || '5px'};
      margin: 5px 0 0 0;
      font-size: 14px;
      font-weight: 500;
      font-family: ${t.fontFamily || 'system-ui, -apple-system, sans-serif'};
      text-align: center;
      transition: background-color 0.2s;
      flex-shrink: 0;
    }

    .storysplat-hotspot-popup-close:hover,
    .storysplat-hotspot-popup-fullscreen:hover {
      opacity: 0.85;
    }

    /* Error Popup - for outdated/broken scenes */
    .storysplat-error-popup {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: ${t.errorPopupBg || 'rgba(0, 0, 0, 0.95)'};
      border-radius: ${t.errorPopupBorderRadius || '12px'};
      padding: 30px 40px;
      max-width: 450px;
      width: 90%;
      text-align: center;
      z-index: 10001;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(255, 100, 100, 0.3);
    }

    .storysplat-error-popup-icon {
      font-size: 48px;
      margin-bottom: 15px;
    }

    .storysplat-error-popup-title {
      color: ${t.errorTitleColor || '#ff6b6b'};
      font-size: 20px;
      font-weight: 600;
      margin: 0 0 15px 0;
    }

    .storysplat-error-popup-message {
      color: #ccc;
      font-size: 14px;
      line-height: 1.6;
      margin: 0 0 20px 0;
    }

    .storysplat-error-popup-action {
      display: inline-block;
      padding: 12px 24px;
      background: linear-gradient(135deg, #CC5833, #b54d2d);
      color: white;
      text-decoration: none;
      border-radius: 6px;
      font-weight: 500;
      font-size: 14px;
      transition: transform 0.2s, box-shadow 0.2s;
    }

    .storysplat-error-popup-action:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
    }

    .storysplat-hotspot-popup-link {
      display: block;
      padding: 8px 16px;
      background: ${t.popupLinkBtnColor || '#007bff'};
      color: white;
      text-decoration: none;
      border-radius: ${t.buttonBorderRadius || '4px'};
      text-align: center;
      font-weight: 500;
      margin: 0 0 4px 0;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .storysplat-hotspot-popup-link:hover {
      background: #0056b3;
    }

    /* Mobile Responsive */
    @media (max-width: 768px) {
      .storysplat-scroll-controls {
        margin-bottom: 10px;
      }

      .storysplat-hotspot-popup {
        max-width: calc(100% - 40px);
      }

      .storysplat-hotspot-popup.fullscreen {
        top: 10px !important;
        width: min(calc(100% - 20px), 840px) !important;
        max-height: calc(100% - 20px) !important;
        padding: 15px !important;
      }

      .storysplat-hotspot-popup-title {
        font-size: 16px !important;
      }
    }

    @media (max-width: 540px) {
      /* No extra bottom margin here: the navigator keeps the same 10px lift as
         wider mobile widths. Bumping it up at this width pushed it into the
         walk-mode move/jump hints. */
      .storysplat-hotspot-popup {
        max-width: calc(100% - 20px);
      }

      .storysplat-hotspot-popup.fullscreen {
        top: 5px !important;
        width: min(calc(100% - 10px), 840px) !important;
        max-height: calc(100% - 10px) !important;
        padding: 10px !important;
      }
    }

    /* Virtual Joystick Overlay - visual indicator only, touches pass through to canvas */
    .storysplat-joystick-container {
      position: absolute;
      bottom: 130px;
      left: 40px;
      width: 120px;
      height: 120px;
      z-index: 2000;
      pointer-events: none;
      touch-action: none;
      display: none;
    }

    .storysplat-joystick-container.visible {
      display: block;
    }

    .storysplat-joystick-base {
      position: absolute;
      top: 0;
      left: 0;
      width: 120px;
      height: 120px;
      background: ${t.joystickBaseColor || 'rgba(255, 255, 255, 0.2)'};
      border: 3px solid rgba(255, 255, 255, 0.4);
      border-radius: 50%;
      pointer-events: none;
    }

    .storysplat-joystick-thumb {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 50px;
      height: 50px;
      margin-left: -25px;
      margin-top: -25px;
      background: ${t.joystickThumbColor || 'rgba(255, 255, 255, 0.6)'};
      border: 3px solid rgba(255, 255, 255, 0.8);
      border-radius: 50%;
      pointer-events: none;
      will-change: transform;
    }

    .storysplat-joystick-thumb.active {
      background: rgba(255, 255, 255, 0.8);
      border-color: white;
    }

    /* Look Zone Indicator (right side) */
    .storysplat-look-zone {
      position: absolute;
      bottom: 130px;
      right: 40px;
      width: 100px;
      height: 100px;
      z-index: 1000;
      pointer-events: none;
      display: none;
    }

    .storysplat-look-zone.visible {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .storysplat-look-zone-icon {
      width: 60px;
      height: 60px;
      background: rgba(255, 255, 255, 0.15);
      border: 2px solid rgba(255, 255, 255, 0.25);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .storysplat-look-zone-icon svg {
      width: 30px;
      height: 30px;
      fill: rgba(255, 255, 255, 0.5);
    }

    /* Look Zone Active State */
    .storysplat-look-zone.active .storysplat-look-zone-icon {
      background: rgba(255, 255, 255, 0.25);
      border-color: rgba(255, 255, 255, 0.5);
    }

    .storysplat-look-zone.active .storysplat-look-zone-icon svg {
      fill: rgba(255, 255, 255, 0.8);
    }

    /* WASD Key Hint (desktop fly mode) */
    @keyframes storysplat-hint-fadein {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    /* Hints centred with translateX(-50%) fade in where they sit: a keyframe transform would
       replace the centring while it runs, drawing the card half its width to the right until it
       snapped back into place. */
    @keyframes storysplat-hint-fadein-in-place {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .storysplat-wasd-hint {
      position: absolute;
      bottom: 54px; /* sits above the bottom-left scene-title + "Adding detail…" LOD indicator (was 40px; its Space/Jump row overlapped that text) */
      left: 20px;
      z-index: 2000;
      pointer-events: none;
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      animation: storysplat-hint-fadein 0.4s ease-out;
    }

    /* Mode-specific rows: fly shows Q/E + "refocus"; walk shows just W/A/S/D + Space-to-jump. */
    .storysplat-wasd-hint.mode-walk .storysplat-wasd-fly-only { display: none !important; }
    /* !important so the generic .storysplat-wasd-hint-row (display:flex, defined later) can't
       override and leak the Space/Jump row into fly mode. */
    .storysplat-wasd-walk-only { display: none !important; }
    .storysplat-wasd-hint.mode-walk .storysplat-wasd-walk-only { display: flex !important; }
    .storysplat-wasd-key--space {
      width: auto;
      min-width: 84px;
      height: 28px; /* shorter than the 36px square keys so the Jump row is less tall */
      padding: 0 14px;
      letter-spacing: 0.5px;
    }

    .storysplat-wasd-hint.visible {
      display: flex;
      pointer-events: auto;
    }

    .storysplat-wasd-hint-row {
      display: flex;
      gap: 4px;
      justify-content: center;
    }

    .storysplat-wasd-key {
      width: 36px;
      height: 36px;
      background: rgba(255, 255, 255, 0.1);
      border: 1.5px solid rgba(255, 255, 255, 0.25);
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
      transition: background 0.1s ease, border-color 0.1s ease, color 0.1s ease, box-shadow 0.1s ease;
    }

    .storysplat-wasd-key:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.35);
      color: rgba(255, 255, 255, 0.7);
    }

    .storysplat-wasd-key.active {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.6);
      color: rgba(255, 255, 255, 0.95);
      box-shadow: 0 0 8px rgba(255, 255, 255, 0.15);
    }

    .storysplat-wasd-hint-label {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 10px;
      color: rgba(255, 255, 255, 0.35);
      margin-top: 2px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Orbit hint (explore orbit mode — drag to rotate / double-click to refocus) */
    .storysplat-orbit-hint {
      position: absolute;
      bottom: 54px; /* same offset as the WASD hint — clears the bottom-left scene title + LOD indicator */
      left: 20px;
      z-index: 2000;
      pointer-events: none;
      display: none;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      animation: storysplat-hint-fadein 0.4s ease-out;
    }

    .storysplat-orbit-hint--refocus-locked .storysplat-orbit-hint-refocus {
      display: none;
    }
    .storysplat-orbit-hint.visible {
      display: flex;
    }

    .storysplat-orbit-hint-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .storysplat-orbit-hint-icon {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.1);
      border: 1.5px solid rgba(255, 255, 255, 0.25);
      border-radius: 6px;
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      flex-shrink: 0;
    }

    .storysplat-orbit-hint-icon svg {
      width: 16px;
      height: 16px;
      fill: rgba(255, 255, 255, 0.5);
    }

    .storysplat-orbit-hint-label {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.45);
      white-space: nowrap;
      letter-spacing: 0.3px;
    }

    /* Tap for audio overlay */
    .storysplat-tap-for-audio {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 50;
      display: flex;
      align-items: center;
      gap: 6px;
      color: white;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: 0.3px;
      line-height: 1;
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6), 0 0 12px rgba(0, 0, 0, 0.3);
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.4s ease;
      user-select: none;
    }
    .storysplat-tap-for-audio svg {
      display: block;
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
      /* Optical correction: geometric centering leaves the glyph ~1px high
         against the label's cap height, since the text box's center sits
         above its visual center (baseline + descender space). */
      transform: translateY(1px);
      fill: currentColor;
      filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.6));
    }
    .storysplat-tap-for-audio.visible {
      opacity: 1;
    }

    /* Double-tap hint (explore mode) */
    .storysplat-doubletap-hint {
      position: absolute;
      bottom: 90px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 2000;
      pointer-events: none;
      display: none;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 20px;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      animation: storysplat-hint-fadein-in-place 0.4s ease-out;
      transition: opacity 0.4s ease-out;
    }

    .storysplat-doubletap-hint.visible {
      display: flex;
    }

    .storysplat-doubletap-hint.fading {
      opacity: 0;
    }

    .storysplat-doubletap-hint-icon {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    .storysplat-doubletap-hint-icon svg {
      width: 20px;
      height: 20px;
      fill: rgba(255, 255, 255, 0.5);
    }

    .storysplat-doubletap-hint-text {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.6);
      white-space: nowrap;
    }

    /* Controls card — shown once after the loading screen is gone, in tour and
       explore modes. Desktop explains mouse/keyboard controls while mobile
       focuses on the touch gesture. Fades after seven seconds or when closed
       with its X; the rest of the card lets pointer input through. */
    .storysplat-tour-look-hint {
      position: absolute;
      bottom: 132px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 2000;
      pointer-events: none;
      display: none;
      width: min(580px, calc(100% - 40px));
      color: #fff;
      background:
        linear-gradient(145deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.035) 44%, rgba(9, 12, 18, 0.38)),
        rgba(8, 10, 15, 0.32);
      border: 1px solid rgba(255, 255, 255, 0.27);
      border-radius: 20px;
      box-shadow:
        0 18px 52px rgba(0, 0, 0, 0.26),
        inset 0 1px 0 rgba(255, 255, 255, 0.34),
        inset 0 -1px 0 rgba(255, 255, 255, 0.06);
      backdrop-filter: blur(24px) saturate(155%) brightness(1.08);
      -webkit-backdrop-filter: blur(24px) saturate(155%) brightness(1.08);
      animation: storysplat-hint-fadein-in-place 0.5s ease-out;
      transition: opacity 0.4s ease-out;
      overflow: hidden;
      container-type: inline-size;
      container-name: storysplat-tour-hint;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .storysplat-tour-look-hint::before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      background:
        radial-gradient(ellipse at 18% -20%, rgba(255, 255, 255, 0.28), transparent 48%),
        linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.055) 45%, transparent 64%);
    }
    .storysplat-tour-look-hint.visible { display: block; }
    .storysplat-tour-look-hint.fading { opacity: 0; }
    .storysplat-tour-look-hint-close {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      color: rgba(255, 255, 255, 0.82);
      background: rgba(255, 255, 255, 0.1);
      border: 0;
      border-radius: 50%;
      cursor: pointer;
      pointer-events: auto;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .storysplat-tour-look-hint.fading .storysplat-tour-look-hint-close { pointer-events: none; }
    .storysplat-tour-look-hint-close:hover { color: #fff; background: rgba(255, 255, 255, 0.2); }
    .storysplat-tour-look-hint-close:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.85); outline-offset: 2px; }
    .storysplat-tour-look-hint-close svg {
      width: 14px;
      height: 14px;
      fill: none;
      stroke: currentColor;
      stroke-width: 2.4;
      stroke-linecap: round;
    }
    .storysplat-tour-look-hint--no-wheel .storysplat-tour-look-hint-wheel-callout,
    .storysplat-tour-look-hint--no-wheel .storysplat-tour-look-hint-narrow-action--wheel { display: none; }
    .storysplat-tour-look-hint--no-wheel .storysplat-tour-look-hint-wheel { animation: none; opacity: 0.45; }
    .storysplat-tour-look-hint--no-keys .storysplat-tour-look-hint-keyboard { display: none; }
    .storysplat-tour-look-hint-desktop {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      gap: 14px;
      padding: 18px 20px 16px;
    }
    .storysplat-tour-look-hint-title {
      padding: 0 30px; /* clear the close button */
      color: rgba(255, 255, 255, 0.96);
      font-size: 15px;
      font-weight: 650;
      line-height: 1.3;
      letter-spacing: 0.01em;
      text-align: center;
    }
    .storysplat-tour-look-hint-controls {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 18px;
    }
    .storysplat-tour-look-hint--no-keys .storysplat-tour-look-hint-controls {
      grid-template-columns: minmax(0, 1fr);
      gap: 0;
    }
    .storysplat-tour-look-hint-mouse {
      display: block;
      width: 100%;
      height: auto;
      max-height: 122px;
      overflow: visible;
    }
    .storysplat-tour-look-hint-mouse-outline,
    .storysplat-tour-look-hint-mouse-detail,
    .storysplat-tour-look-hint-callout-line {
      fill: none;
      stroke: rgba(255, 255, 255, 0.86);
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .storysplat-tour-look-hint-mouse-outline { fill: rgba(255, 255, 255, 0.035); }
    .storysplat-tour-look-hint-mouse-button {
      fill: rgba(255, 255, 255, 0.25);
      stroke: rgba(255, 255, 255, 0.96);
      stroke-width: 1.8;
    }
    .storysplat-tour-look-hint-wheel {
      fill: rgba(255, 255, 255, 0.92);
      animation: storysplat-tour-wheel 1.8s ease-in-out infinite;
      transform-origin: center;
      transform-box: fill-box;
    }
    .storysplat-tour-look-hint-callout-line {
      stroke: rgba(255, 255, 255, 0.96);
      stroke-width: 2;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
    }
    .storysplat-tour-look-hint-callout-arrowhead {
      fill: rgba(255, 255, 255, 0.98);
    }
    .storysplat-tour-look-hint-callout {
      fill: rgba(255, 255, 255, 0.92);
      font: 650 12px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      letter-spacing: 0.01em;
      paint-order: stroke;
      stroke: rgba(0, 0, 0, 0.34);
      stroke-width: 2px;
      stroke-linejoin: round;
    }
    .storysplat-tour-look-hint-narrow-actions {
      display: none;
      gap: 8px;
    }
    .storysplat-tour-look-hint-narrow-action {
      display: grid;
      grid-template-columns: 46px minmax(0, 1fr);
      align-items: center;
      min-height: 58px;
      padding: 7px 12px;
      color: rgba(255, 255, 255, 0.94);
      background: rgba(255, 255, 255, 0.065);
      border: 1px solid rgba(255, 255, 255, 0.13);
      border-radius: 12px;
      font-size: 13px;
      font-weight: 650;
      line-height: 1.25;
    }
    .storysplat-tour-look-hint-narrow-mouse {
      width: 34px;
      height: 46px;
      justify-self: center;
      overflow: visible;
    }
    .storysplat-tour-look-hint-narrow-mouse .storysplat-tour-look-hint-mouse-outline,
    .storysplat-tour-look-hint-narrow-mouse .storysplat-tour-look-hint-mouse-detail {
      stroke-width: 1.6;
    }
    .storysplat-tour-look-hint-narrow-mouse .storysplat-tour-look-hint-mouse-button {
      stroke-width: 1.4;
    }
    .storysplat-tour-look-hint-narrow-action--wheel .storysplat-tour-look-hint-wheel {
      filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.45));
    }
    .storysplat-tour-look-hint-keyboard {
      display: flex;
      align-items: center;
      padding-left: 18px;
      border-left: 1px solid rgba(255, 255, 255, 0.14);
    }
    .storysplat-tour-look-hint-keyset {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }
    .storysplat-tour-look-hint-key-row {
      display: flex;
      justify-content: center;
      gap: 5px;
    }
    .storysplat-tour-look-hint-key {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 45px;
      height: 30px;
      padding: 0;
      color: rgba(255, 255, 255, 0.88);
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.07));
      border: 1px solid rgba(255, 255, 255, 0.22);
      border-bottom-color: rgba(255, 255, 255, 0.12);
      border-radius: 6px;
      box-shadow: 0 2px 0 rgba(0, 0, 0, 0.25);
      gap: 3px;
      font: 650 11px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .storysplat-tour-look-hint-key-caption {
      margin-top: 2px;
      color: rgba(255, 255, 255, 0.72);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .storysplat-tour-look-hint-key-slash { color: rgba(255, 255, 255, 0.38); }
    .storysplat-tour-look-hint-key-arrow { font-size: 15px; font-weight: 500; }
    .storysplat-tour-look-hint-mobile {
      position: relative;
      z-index: 1;
      display: none;
      align-items: center;
      justify-content: center;
      gap: 14px;
      min-height: 82px;
      padding: 14px 42px 14px 18px; /* right: clear the close button */
    }
    .storysplat-tour-look-hint-finger {
      width: 68px;
      height: 62px;
      flex: 0 0 68px;
      color: rgba(255, 255, 255, 0.94);
      overflow: visible;
      filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.42));
    }
    .storysplat-tour-look-hint-gesture-icon {
      fill: currentColor;
      animation: storysplat-tour-drag 2.2s ease-in-out infinite;
      transform-origin: center;
      transform-box: fill-box;
    }
    .storysplat-tour-look-hint-mobile-text {
      max-width: 220px;
      color: rgba(255, 255, 255, 0.94);
      font-size: 14px;
      font-weight: 600;
      line-height: 1.35;
    }
    .storysplat-tour-look-hint.storysplat-tour-look-hint--mobile {
      bottom: 148px;
      width: min(350px, calc(100% - 32px));
      border-radius: 18px;
    }
    .storysplat-tour-look-hint--mobile .storysplat-tour-look-hint-desktop { display: none; }
    .storysplat-tour-look-hint--mobile .storysplat-tour-look-hint-mobile { display: flex; }
    /* Skinny desktop windows: the stacked narrow layout below is taller, so
       lift the hint a little further off the bottom navigator. */
    @media (max-width: 540px) {
      .storysplat-tour-look-hint:not(.storysplat-tour-look-hint--mobile) { bottom: 160px; }
    }
    @container storysplat-tour-hint (max-width: 470px) {
      .storysplat-tour-look-hint:not(.storysplat-tour-look-hint--mobile) .storysplat-tour-look-hint-desktop {
        gap: 12px;
        padding: 16px;
      }
      .storysplat-tour-look-hint:not(.storysplat-tour-look-hint--mobile) .storysplat-tour-look-hint-controls {
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
      }
      .storysplat-tour-look-hint:not(.storysplat-tour-look-hint--mobile) .storysplat-tour-look-hint-mouse { display: none; }
      .storysplat-tour-look-hint:not(.storysplat-tour-look-hint--mobile) .storysplat-tour-look-hint-narrow-actions { display: grid; }
      .storysplat-tour-look-hint:not(.storysplat-tour-look-hint--mobile) .storysplat-tour-look-hint-keyboard {
        justify-content: center;
        padding: 12px 0 0;
        border-top: 1px solid rgba(255, 255, 255, 0.14);
        border-left: 0;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .storysplat-tour-look-hint-wheel,
      .storysplat-tour-look-hint-gesture-icon { animation: none; }
    }
    @keyframes storysplat-tour-wheel {
      0%, 100% { transform: translateY(-2px); opacity: 0.7; }
      50% { transform: translateY(3px); opacity: 1; }
    }
    @keyframes storysplat-tour-drag {
      0%, 100% { transform: translateX(-2px); }
      50% { transform: translateX(3px); }
    }

    /* Lazy Load Container */
    .storysplat-lazy-load-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: ${t.lazyLoadBg || '#1a1a1a'};
      z-index: 10001;
    }

    .storysplat-lazy-load-thumbnail {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0.7;
    }

    .storysplat-lazy-load-thumbnail-video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0.7;
    }

    .storysplat-lazy-load-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.3) 0%,
        rgba(0, 0, 0, 0.5) 50%,
        rgba(0, 0, 0, 0.7) 100%
      );
    }

    .storysplat-lazy-load-content {
      position: relative;
      z-index: 1;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }

    .storysplat-lazy-load-start-btn {
      padding: 16px 32px;
      background: ${uiColor};
      border: none;
      border-radius: ${t.lazyLoadBtnBorderRadius || '50px'};
      color: white;
      font-size: 18px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 12px;
      transition: all 0.3s ease;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }

    .storysplat-lazy-load-start-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 30px rgba(0, 0, 0, 0.4);
    }

    .storysplat-lazy-load-start-btn:active {
      transform: scale(0.98);
    }

    .storysplat-lazy-load-start-btn svg {
      width: 24px;
      height: 24px;
      fill: white;
    }

    @media (max-width: 768px) {
      .storysplat-lazy-load-start-btn {
        padding: 12px 24px;
        font-size: 16px;
      }
    }

    /* Waypoint List Dropdown - Glassmorphic style */
    .storysplat-waypoint-list-container {
      position: absolute;
      top: 10px;
      right: 45px;
      z-index: 1000;
    }

    .storysplat-waypoint-list-toggle {
      padding: 8px 16px;
      box-sizing: border-box;
      height: 38px; /* shared top-bar chip height (see .storysplat-toolbar) */
      background: ${t.buttonBg || 'rgba(0, 0, 0, 0.3)'};
      border: none;
      border-radius: 8px;
      color: ${t.buttonTextColor || 'white'};
      cursor: pointer;
      font-size: ${t.waypointListFontSize || '14px'};
      font-weight: ${t.waypointListFontWeight || 'normal'};
      line-height: 1;
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
      transition: background-color 0.3s ease;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .storysplat-waypoint-list-toggle:hover {
      background: ${t.buttonHoverBg || 'rgba(0, 0, 0, 0.5)'};
    }

    .storysplat-waypoint-list-toggle svg {
      width: 16px;
      height: 16px;
      fill: ${tc};
      transition: transform 0.3s ease;
    }

    .storysplat-waypoint-list-toggle.open svg {
      transform: rotate(180deg);
    }

    .storysplat-waypoint-list-dropdown {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 8px;
      min-width: 200px;
      max-height: 300px;
      overflow-y: auto;
      background: ${t.dropdownBg || 'rgba(0, 0, 0, 0.8)'};
      border-radius: ${t.dropdownBorderRadius || '8px'};
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
      display: none;
      flex-direction: column;
    }

    .storysplat-waypoint-list-dropdown.open {
      display: flex;
    }

    .storysplat-waypoint-item {
      padding: 12px 16px;
      color: ${tc};
      cursor: pointer;
      border-bottom: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
      transition: background-color 0.2s ease;
      font-size: ${t.waypointListFontSize || '14px'};
      font-weight: ${t.waypointListFontWeight || 'normal'};
    }

    .storysplat-waypoint-item:last-child {
      border-bottom: none;
    }

    .storysplat-waypoint-item:hover {
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    }

    .storysplat-waypoint-item.active {
      background: ${uiColor}40;
    }

    /* Between the tour waypoints and the extra waypoints listed after them. */
    .storysplat-waypoint-list-divider {
      flex-shrink: 0;
      height: 1px;
      background: ${tc === 'white' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
    }

    /* Watermark - matches BabylonJS HTML export */
    .storysplat-watermark {
      position: absolute;
      bottom: 10px;
      right: 10px;
      background: ${t.watermarkBg || 'rgba(0, 0, 0, 0.3)'};
      color: ${tc};
      padding: 5px 10px;
      border-radius: 8px;
      border: 1px solid ${tc === 'white' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
      font-size: ${t.watermarkFontSize || '12px'};
      font-weight: ${t.watermarkFontWeight || 'normal'};
      z-index: 1000;
      pointer-events: auto;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
      cursor: default;
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
    }

    .storysplat-watermark a {
      color: ${uiColor};
      text-decoration: none;
      cursor: pointer;
    }

    .storysplat-watermark a:hover {
      text-decoration: underline;
    }

    .storysplat-watermark.storysplat-watermark-image {
      background: transparent;
      border: none;
      padding: 0;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }

    .storysplat-watermark-logo {
      display: block;
      max-height: var(--storysplat-watermark-img-size, 100px);
      max-width: var(--storysplat-watermark-img-size, 100px);
      object-fit: contain;
    }

    /* Scene title — bottom-left, styled to match the subtle "Adding detail…" LOD
       indicator (.storysplat-detail-indicator): plain faded white text, no
       background / border / pill / blur. It's persistent, so no pulse animation. */
    .storysplat-scene-title {
      position: absolute;
      bottom: 14px;
      left: 16px;
      max-width: calc(100% - 32px);
      box-sizing: border-box;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #fff;
      font-size: 12px;
      line-height: 1;
      font-family: system-ui, -apple-system, sans-serif;
      letter-spacing: 0.02em;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
      z-index: 1000;
      pointer-events: none;
    }

    /* Hover mode: hidden until the bottom-left corner is hovered, then fades in.
       pointer-events:auto only here so the (otherwise invisible) text can receive
       hover; in always-on mode it stays pointer-events:none and never intercepts
       canvas input. */
    .storysplat-scene-title--hover {
      opacity: 0;
      pointer-events: auto;
      transition: opacity 0.2s ease;
    }

    .storysplat-scene-title--hover:hover {
      opacity: 1;
    }

    .storysplat-fps-counter {
      position: absolute;
      bottom: 8px;
      left: 8px;
      background: rgba(0, 0, 0, 0.6);
      color: #0f0;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-family: monospace;
      z-index: 1000;
      pointer-events: none;
      line-height: 1;
    }

    /* Scene Navigation Menu */
    .storysplat-scene-menu-container {
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 1001;
      font-family: ${t.fontFamily || 'inherit'};
    }

    .storysplat-scene-menu-toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      box-sizing: border-box;
      height: 38px; /* shared top-bar chip height (see .storysplat-toolbar) */
      background: ${t.buttonBg || 'rgba(0, 0, 0, 0.5)'};
      backdrop-filter: blur(${t.dropdownBlur || '10px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '10px'});
      color: ${t.buttonTextColor || tc};
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: ${t.buttonBorderRadius || '8px'};
      padding: 8px 12px;
      cursor: pointer;
      font-size: ${t.sceneMenuFontSize || '13px'};
      font-weight: ${t.sceneMenuFontWeight || 'normal'};
      font-family: inherit;
      transition: background 0.2s, border-color 0.2s;
    }

    .storysplat-scene-menu-toggle:hover {
      background: ${t.buttonHoverBg || 'rgba(0, 0, 0, 0.7)'};
      border-color: rgba(255, 255, 255, 0.2);
    }

    .storysplat-scene-menu-toggle svg {
      width: 16px;
      height: 16px;
      fill: currentColor;
      flex-shrink: 0;
    }

    .storysplat-scene-menu-toggle.open svg {
      transform: rotate(90deg);
    }

    .storysplat-scene-menu-label {
      white-space: nowrap;
      line-height: 1;
      /* Font ascender/descender space puts the visible glyphs below a centered
         SVG. Align the text optically with the icon, including themed sizes. */
      transform: translateY(-0.08em);
    }

    .storysplat-scene-menu-dropdown {
      display: none;
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      min-width: 220px;
      max-width: 320px;
      max-height: 60vh;
      overflow-y: auto;
      background: ${t.dropdownBg || 'rgba(0, 0, 0, 0.75)'};
      backdrop-filter: blur(${t.dropdownBlur || '12px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '12px'});
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: ${t.dropdownBorderRadius || '10px'};
      padding: 6px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    }

    .storysplat-scene-menu-dropdown.open {
      display: block;
    }

    .storysplat-scene-menu-dropdown::-webkit-scrollbar {
      width: 4px;
    }

    .storysplat-scene-menu-dropdown::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }

    /* ===== Scene Menu - Sidebar Pullout Style ===== */
    /* Authored AFTER the base .storysplat-scene-menu-container rule so the
       modifier overrides position/top/left. A true slide-out drawer: the panel
       is a fixed 340px column parked off-screen-left (translateX(-100%)) and
       slides in on .open. z-index sits ABOVE the toolbar (1002) so the opening
       panel sweeps over / "consumes" the help, mute, relight, measure buttons. */
    .storysplat-scene-menu-container--sidebar {
      /* absolute (not fixed) so it spans the VIEWER container's height, not the whole
         browser viewport — correct when the viewer is embedded in a sub-region. The
         container is position:relative + overflow:hidden, so the closed slide-out
         (translateX(-100%)) is clipped to the embed and doesn't peek out the left. */
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 340px;
      max-width: 85%; /* of the embed, not the browser window */
      transform: translateX(-100%);
      z-index: 1003;
      background: ${t.dropdownBg || 'rgba(0, 0, 0, 0.75)'};
      backdrop-filter: blur(${t.dropdownBlur || '12px'});
      -webkit-backdrop-filter: blur(${t.dropdownBlur || '12px'});
      border-right: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      transition: transform 260ms cubic-bezier(0.25, 0.1, 0.25, 1);
      display: flex;
      flex-direction: column;
    }

    .storysplat-scene-menu-container--sidebar.open {
      transform: translateX(0);
    }

    /* The open sidebar slides over the lower-left navigation hints (WASD keys /
       orbit drag). Hide them while it's open — the hints are appended to the
       viewer container AFTER the sidebar panel, so a sibling selector covers
       every close path (toggle, X, outside click, Escape, exclusiveTools)
       without any JS bookkeeping. */
    .storysplat-scene-menu-container--sidebar.open ~ .storysplat-wasd-hint,
    .storysplat-scene-menu-container--sidebar.open ~ .storysplat-orbit-hint {
      display: none !important;
    }

    /* The sidebar toggle is appended directly to the viewer container (not inside the
       positioned scene-menu panel like the classic toggle), so it needs its own
       positioning or it lays out at 0,0 behind the canvas and is invisible. Pin it
       top-left, above the canvas, below the open panel (z 1003). */
    .storysplat-scene-menu-toggle--sidebar {
      position: absolute;
      top: 10px;
      left: 10px;
      z-index: 1002;
    }

    .storysplat-scene-menu-sidebar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px 12px 15.5px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      flex-shrink: 0;
      color: ${t.buttonTextColor || tc};
      font-size: ${t.sceneMenuFontSize || '15px'};
      font-weight: 600;
      /* Fill the panel, never a fixed width: in a narrow embed the panel is capped at 85%
         of the embed and a 340px minimum spilled the header and list onto the scene. */
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    .storysplat-scene-menu-sidebar-close {
      width: 28px;
      height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      border-radius: 6px;
      color: inherit;
      cursor: pointer;
      flex-shrink: 0;
    }

    .storysplat-scene-menu-sidebar-close:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .storysplat-scene-menu-sidebar-body {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 6px;
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    .storysplat-scene-menu-sidebar-body::-webkit-scrollbar {
      width: 4px;
    }

    .storysplat-scene-menu-sidebar-body::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }

    /* Optional logo at the top of the scene menu list. Shared by both the
       classic dropdown and the sidebar body (inserted before the menu items). */
    .storysplat-scene-menu-title {
      padding: 8px 10px 10px;
      margin-bottom: 4px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      color: ${tc};
      font-size: ${t.sceneMenuFontSize || '15px'};
      font-weight: 700;
      letter-spacing: 0.3px;
    }

    .storysplat-scene-menu-title--image {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    .storysplat-scene-menu-title-img {
      max-width: 100%;
      max-height: 56px;
      object-fit: contain;
      display: block;
    }

    /* "Current Space" header — pinned at the very top of the scene menu list,
       above all category folders and the spaces/scenes items. */
    .storysplat-scene-menu-current-space {
      padding: 8px 10px 10px;
      margin-bottom: 4px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .storysplat-scene-menu-current-space-label {
      color: ${tc};
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.6px;
      opacity: 0.6;
      margin-bottom: 2px;
    }

    .storysplat-scene-menu-current-space-name {
      color: ${tc};
      font-size: ${t.sceneMenuFontSize || '15px'};
      font-weight: 700;
      letter-spacing: 0.3px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .storysplat-scene-menu-folder {
      margin-bottom: 2px;
    }

    .storysplat-scene-menu-folder-header {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 8px;
      color: ${tc};
      /* Folder headers stay small + bold regardless of sceneMenuFontSize/Weight —
         their job is to visually separate sections, not match item text. */
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      opacity: 0.7;
      cursor: pointer;
      border-radius: 6px;
      transition: opacity 0.15s;
      user-select: none;
    }

    .storysplat-scene-menu-folder-header:hover {
      opacity: 1;
    }

    .storysplat-scene-menu-folder-header svg {
      width: 20px;
      height: 20px;
      fill: currentColor;
      transition: transform 0.15s;
      flex-shrink: 0;
    }

    .storysplat-scene-menu-folder-header.collapsed svg {
      transform: rotate(-90deg);
    }

    .storysplat-scene-menu-folder-children {
      padding-left: 12px;
    }

    .storysplat-scene-menu-folder-children.collapsed {
      display: none;
    }

    .storysplat-scene-menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      color: ${tc};
      font-size: ${t.sceneMenuFontSize || '13px'};
      font-weight: ${t.sceneMenuFontWeight || 'normal'};
      cursor: pointer;
      border-radius: 6px;
      transition: background 0.15s;
      text-decoration: none;
    }

    .storysplat-scene-menu-item:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .storysplat-scene-menu-item-thumb {
      width: 32px;
      height: 32px;
      border-radius: 4px;
      object-fit: cover;
      flex-shrink: 0;
      background: rgba(255, 255, 255, 0.05);
    }

    .storysplat-scene-menu-item-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Current scene shown in-list (carried from the profile) — a non-clickable "you are here" item. */
    .storysplat-scene-menu-item.current-space-selected,
    .storysplat-scene-menu-item.current-space-selected:hover {
      cursor: default;
      background: rgba(255, 255, 255, 0.12);
      font-weight: 700;
    }
    .storysplat-scene-menu-current-badge {
      margin-left: auto;
      flex-shrink: 0;
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 2px 6px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.18);
      color: ${tc};
      opacity: 0.85;
    }
    /* "Been here this session" — deliberately subtle; resets on refresh. */
    .storysplat-scene-menu-item.visited .storysplat-scene-menu-item-label {
      opacity: 0.45;
    }
    .storysplat-scene-menu-item.visited::after {
      content: '';
      margin-left: auto;
      flex-shrink: 0;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: currentColor;
      opacity: 0.3;
    }

    .storysplat-scene-menu-link {
      text-decoration: none;
      color: ${t.sceneMenuLinkColor || 'white'};
    }

    .storysplat-scene-menu-link-icon {
      width: 16px;
      height: 16px;
      fill: currentColor;
      flex-shrink: 0;
      opacity: 0.7;
    }

    .storysplat-scene-menu-link-ext {
      width: 12px;
      height: 12px;
      fill: currentColor;
      flex-shrink: 0;
      opacity: 0.4;
      margin-left: auto;
    }

    @media (max-width: 768px) {
      .storysplat-scene-menu-label {
        display: none;
      }
    }

    /* ===== 8th Wall AR Mode Overrides ===== */
    /* Applied when container has .storysplat-ar-mode class (camera feed visible behind scene) */
    .storysplat-ar-mode .storysplat-waypoint-info {
      background: rgba(0, 0, 0, 0.75);
    }

    .storysplat-ar-mode .storysplat-hotspot-popup {
      background: rgba(0, 0, 0, 0.85) !important;
    }

    .storysplat-ar-mode .storysplat-custom-menu-toggle,
    .storysplat-ar-mode .storysplat-scene-menu-toggle {
      background: rgba(0, 0, 0, 0.7);
    }

    .storysplat-ar-mode .storysplat-toolbar .storysplat-btn {
      display: none;
    }

    .storysplat-ar-mode .storysplat-toolbar .storysplat-xr-btn {
      display: block;
    }

    .storysplat-ar-mode .storysplat-help-btn {
      display: none !important;
    }

    .storysplat-ar-mode .storysplat-measure-canvas {
      display: none !important;
    }

    /* Waypoint navigation (prev/next/play/progress) and explore-mode toggles
       don't apply when the scene is anchored to the real world. */
    .storysplat-ar-mode .storysplat-scroll-controls,
    .storysplat-ar-mode .storysplat-explore-controls,
    .storysplat-ar-mode .storysplat-waypoint-list-container,
    .storysplat-ar-mode .storysplat-waypoint-info,
    .storysplat-ar-mode .storysplat-scene-menu-container,
    .storysplat-ar-mode .storysplat-top-right-bar,
    .storysplat-ar-mode .storysplat-top-right-group,
    .storysplat-ar-mode .storysplat-top-right-pill,
    .storysplat-ar-mode .storysplat-fullscreen-btn,
    .storysplat-ar-mode .storysplat-mute-btn,
    .storysplat-ar-mode .storysplat-mode-container {
      display: none !important;
    }

    /* Keep the AR button itself visible so the user can exit AR. */
    .storysplat-ar-mode .storysplat-ar-btn.available {
      display: block !important;
    }


    /* ===== Top-bar collision layout (classes set by layoutTopBar in createUIElements) =====
       No fixed breakpoint: .storysplat-tools-wrapped drops the tool chips to a second row
       under the menu button only when they would run into the right-hand group; the pro
       template's .storysplat-mode-wrapped drops the Tour/Explore toggle to the second row on
       the right; .storysplat-watermark-tight hides the watermark's "Created with" prefix when
       the bottom navigator would run into it. All measured against the embed. */
    .storysplat-tools-wrapped .storysplat-toolbar {
      top: 56px !important;
      left: 10px !important;
    }
    .storysplat-tools-wrapped .storysplat-waypoint-list-toggle {
      max-width: 150px;
      padding-left: 12px;
      padding-right: 10px;
    }
    .storysplat-tools-wrapped .storysplat-waypoint-list-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }
    .storysplat-tools-wrapped .storysplat-waypoint-list-toggle svg { flex-shrink: 0; }
    .storysplat-tools-wrapped .storysplat-waypoint-info.hasContent { padding-top: max(${t.infoBannerPaddingTop || '50'}px, 104px); }
    .storysplat-mode-wrapped .storysplat-top-right-bar .storysplat-mode-container {
      position: absolute;
      top: 46px;
      right: 0;
      margin-top: 0;
    }
    .storysplat-watermark-tight .storysplat-watermark-prefix { display: none; }
    .storysplat-watermark-tight .storysplat-watermark:not(.storysplat-watermark-image) {
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .storysplat-watermark-lifted .storysplat-watermark {
      /* Just above the navigator's top edge (distance from the embed's bottom). */
      bottom: calc(var(--storysplat-nav-top, 110px) + 8px);
    }
  ` + PROTECT_MEDIA_CSS + PSEUDO_FULLSCREEN_CSS + generateTemplateOverrides(uiColor, template, t) + generateLayoutOverrides(t?.uiLayout);
}

/**
 * Generate template-specific CSS overrides.
 * These are appended after the minimal base styles with higher cascade priority.
 */
function generateTemplateOverrides(uiColor: string, template: string, _theme?: ViewerTheme): string {
    if (template === 'standard') {
        return `
    /* ===== STANDARD (Centered) Template Overrides ===== */
    .storysplat-scroll-controls {
      width: auto;
      max-width: 350px;
      padding: 15px 20px;
      background: rgba(0, 0, 0, 0.7);
      border-radius: 10px;
      bottom: 20px;
      gap: 8px;
    }

    .storysplat-scroll-content {
      gap: 8px;
    }

    .storysplat-progress-container {
      max-width: 300px;
      height: 10px;
      border-radius: 5px;
    }

    .storysplat-progress-bar {
      border-radius: 5px;
    }

    .storysplat-progress-text {
      font-size: 16px;
    }

    .storysplat-scroll-buttons {
      gap: 8px;
    }

    .storysplat-btn {
      background: ${uiColor};
      padding: 10px 20px;
      font-size: 16px;
      border-radius: 5px;
    }

    .storysplat-btn:hover {
      opacity: 0.85;
      background: ${uiColor};
    }

    .storysplat-btn-play {
      padding: 10px 12px;
      background: ${uiColor};
      border-radius: 5px;
    }

    .storysplat-btn-play svg {
      width: 16px;
      height: 16px;
    }

    .storysplat-btn-play:hover {
      opacity: 0.85;
      background: ${uiColor};
    }

    .storysplat-mode-container {
      margin-top: 8px;
    }

    .storysplat-mode-toggle {
      gap: 8px;
    }

    .storysplat-mode-btn {
      padding: 6px 14px;
      font-size: 14px;
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .storysplat-explore-btn {
      padding: 8px 16px;
      font-size: 14px;
      border-radius: 4px;
    }

    @media (max-width: 768px) {
      .storysplat-scroll-controls {
        max-width: 280px;
        padding: 12px 15px;
      }

      .storysplat-btn {
        padding: 8px 16px;
        font-size: 14px;
      }

      .storysplat-progress-container {
        height: 8px;
      }

      .storysplat-progress-text {
        font-size: 14px;
      }
    }
    `;
    }

    if (template === 'pro') {
        return `
    /* ===== PRO (Dark) Template Overrides ===== */

    /* Top-right bar: holds mode toggle + waypoints + fullscreen/share pill */
    .storysplat-top-right-bar {
      position: absolute;
      top: 10px;
      right: 10px;
      display: flex;
      align-items: center;
      gap: 8px;
      z-index: 1000;
    }

    /* Visual order within the bar: [mode/Explore] [Waypoints] [pill].
       Share left the bar (it lives in the pill now), so its old order:1 is gone.
       Applied via flex order so the JS append sequence stays untouched. */
    .storysplat-top-right-bar .storysplat-mode-container { order: 0; }
    .storysplat-top-right-bar .storysplat-waypoint-list-container { order: 1; }
    .storysplat-top-right-bar .storysplat-top-right-pill { order: 2; }

    /* Override absolute positioning when inside top-right-bar */
    .storysplat-top-right-bar .storysplat-waypoint-list-container,
    .storysplat-top-right-bar .storysplat-top-right-pill {
      position: relative;
      top: auto;
      right: auto;
    }

    .storysplat-scroll-controls {
      width: auto;
      padding: 8px 12px;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 8px;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      gap: 6px;
    }

    .storysplat-scroll-content {
      gap: 6px;
    }

    .storysplat-progress-text {
      display: none;
    }

    .storysplat-progress-container {
      width: 100%;
      max-width: 100%;
      height: 2px;
      border-radius: 1px;
    }

    .storysplat-progress-bar {
      border-radius: 1px;
    }

    .storysplat-scroll-buttons {
      gap: 6px;
    }

    .storysplat-btn {
      background: rgba(0, 0, 0, 0.5);
      padding: 8px 16px;
      font-size: 14px;
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .storysplat-btn:hover {
      background: rgba(0, 0, 0, 0.7);
      border-color: rgba(255, 255, 255, 0.2);
    }

    .storysplat-btn-play {
      padding: 8px 10px;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 4px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .storysplat-btn-play svg {
      width: 14px;
      height: 14px;
    }

    .storysplat-btn-play:hover {
      background: rgba(0, 0, 0, 0.7);
      border-color: rgba(255, 255, 255, 0.2);
    }

    /* Pro: mode toggle in top-right bar, horizontal layout */
    .storysplat-mode-container {
      position: relative;
      bottom: auto;
      left: auto;
      margin-top: 0;
    }

    .storysplat-mode-toggle {
      flex-direction: row;
      gap: 4px;
    }

    .storysplat-mode-btn {
      padding: 6px 12px;
      font-size: 13px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.3);
      border: none;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    .storysplat-mode-btn:hover {
      background: rgba(0, 0, 0, 0.5);
    }

    .storysplat-explore-controls {
      flex-direction: row;
    }

    .storysplat-explore-btn {
      padding: 6px 14px;
      font-size: 13px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .storysplat-explore-btn:hover {
      background: rgba(0, 0, 0, 0.7);
    }

    @media (max-width: 768px) {
      .storysplat-scroll-controls {
        padding: 6px 10px;
        bottom: 10px;
      }

      .storysplat-top-right-bar {
        right: 10px;
      }

      .storysplat-btn {
        padding: 6px 12px;
        font-size: 12px;
      }
    }

    @media (max-width: 540px) {
      .storysplat-wasd-hint {
        transform: translateY(-50%);
      }
      .storysplat-orbit-hint {
        transform: translateY(-50%);
      }
    }
    `;
    }

    // 'minimal' (default) — no overrides needed
    return '';
}

/**
 * Map of UIElementId to CSS selector(s).
 */
const LAYOUT_CLASS_MAP: Record<UIElementId, string> = {
    helpButton: '.storysplat-help-btn',
    muteButton: '.storysplat-mute-btn',
    fullscreenButton: '.storysplat-fullscreen-btn',
    watermark: '.storysplat-watermark',
    waypointDropdown: '.storysplat-waypoint-list-container',
    scrollControls: '.storysplat-scroll-controls',
    waypointBanner: '.storysplat-waypoint-info, .storysplat-overlay-top',
    progressText: '.storysplat-progress-text',
    modeToggle: '.storysplat-mode-container',
    exploreControls: '.storysplat-explore-controls',
    prevButton: '.storysplat-btn-prev',
    playButton: '.storysplat-btn-play',
    nextButton: '.storysplat-btn-next',
    hotspotPopup: '.storysplat-hotspot-popup',
    portalPopup: '.storysplat-portal-popup',
    relightingButton: '.storysplat-relight-btn',
    sceneMenu: '.storysplat-scene-menu-container',
};

/** Sub-elements that are normally position:static children and need absolute positioning when custom-placed */
const SUB_ELEMENTS: Set<UIElementId> = new Set([
    'progressText', 'modeToggle', 'exploreControls', 'prevButton', 'playButton', 'nextButton'
]);

// Touch-verb hints. iPadOS 13+ Safari sends a desktop 'Macintosh' UA, so the
// regex alone misses those iPads; MacIntel platform + multi-touch is the same
// pair createViewer's isDesktopUAiPad uses.
function isTouchLikeDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Convert an anchor + offsets into CSS positioning declarations.
 * Returns a string of CSS property declarations (no selector/braces).
 */
function anchorToCSS(pos: ElementPosition, elementId: UIElementId, opts?: { clampOffsets?: boolean }): string {
    const { anchor, offsetX, offsetY } = pos;
    const [vAnchor, hAnchor] = anchor.split('-') as [string, string | undefined];
    // For single-word anchors like 'center', split gives ['center', undefined]
    const v = vAnchor === 'center' && !hAnchor ? 'center' : vAnchor;
    const h = hAnchor || (vAnchor === 'center' ? 'center' : 'left');
    // Clamped offsets keep a placement inherited from a wider layout reachable:
    // a 400px desktop offset on a 390px-wide phone would otherwise push the
    // element entirely off screen. min() leaves at least 80px of the container
    // between the anchored edge and the element.
    const off = (px: number) => opts?.clampOffsets ? `min(${px}px, calc(100% - 80px))` : `${px}px`;

    const rules: string[] = [
        'top: auto !important',
        'right: auto !important',
        'bottom: auto !important',
        'left: auto !important',
    ];

    // Vertical positioning
    if (v === 'top') {
        rules.push(`top: ${off(offsetY)} !important`);
    } else if (v === 'bottom') {
        rules.push(`bottom: ${off(offsetY)} !important`);
    } else {
        // center vertically
        rules.push(`top: 50% !important`);
    }

    // Horizontal positioning
    // waypointBanner is full-width by default — keep left:0;right:0 unless user picks a non-center horizontal anchor
    if (elementId === 'waypointBanner') {
        if (h === 'center') {
            rules.push('left: 0 !important', 'right: 0 !important');
        } else if (h === 'left') {
            rules.push(`left: ${off(offsetX)} !important`);
            rules.push('right: auto !important');
        } else {
            rules.push(`right: ${off(offsetX)} !important`);
            rules.push('left: auto !important');
        }
    } else if (h === 'left') {
        rules.push(`left: ${off(offsetX)} !important`);
    } else if (h === 'right') {
        rules.push(`right: ${off(offsetX)} !important`);
    } else {
        // center horizontally
        rules.push(`left: 50% !important`);
    }

    // Transform for centering
    const needsTranslateX = h === 'center' && elementId !== 'waypointBanner';
    const needsTranslateY = v === 'center';

    // scrollControls normally has transform: translateX(-50%). Reset it when not center-column.
    if (elementId === 'scrollControls') {
        if (needsTranslateX && needsTranslateY) {
            rules.push('transform: translate(-50%, -50%) !important');
        } else if (needsTranslateX) {
            rules.push('transform: translateX(-50%) !important');
        } else if (needsTranslateY) {
            rules.push('transform: translateY(-50%) !important');
        } else {
            rules.push('transform: none !important');
        }
    } else {
        if (needsTranslateX && needsTranslateY) {
            rules.push('transform: translate(-50%, -50%) !important');
        } else if (needsTranslateX) {
            rules.push('transform: translateX(-50%) !important');
        } else if (needsTranslateY) {
            rules.push('transform: translateY(-50%) !important');
        } else if (elementId === 'hotspotPopup' || elementId === 'portalPopup') {
            // Both popups are centered by default via a base translate(-50%, -50%).
            // Without an explicit reset that translate survives the cascade and
            // re-centers the popup ON its anchor point instead of cornering it.
            // Mirrors popupPositionStyle's transform: 'none' in the editor canvas.
            rules.push('transform: none !important');
        }
    }

    // Width / Height overrides
    if (pos.width != null && pos.width > 0) rules.push(`width: ${pos.width}px !important`);
    if (pos.height != null && pos.height > 0) rules.push(`height: ${pos.height}px !important`);

    return rules.join('; ');
}

/**
 * Generate CSS overrides for custom UI element positions.
 * Desktop rules are in @media (min-width: 769px), mobile in @media (max-width: 768px).
 */
function generateLayoutOverrides(layout?: UILayout): string {
    if (!layout) return '';

    const desktopElements = layout.elements;
    const mobileElements = layout.mobile;

    if (!desktopElements && !mobileElements) return '';

    let css = '\n/* ===== Custom UI Layout Overrides ===== */\n';

    const emitRules = (elements: Partial<Record<string, ElementPosition>>, clampIds?: ReadonlySet<string>) => {
        let block = '';
        for (const [id, pos] of Object.entries(elements)) {
            if (!pos) continue;
            const selector = LAYOUT_CLASS_MAP[id as UIElementId];
            if (!selector) continue;
            const rules = anchorToCSS(pos, id as UIElementId, { clampOffsets: clampIds?.has(id) });
            // Sub-elements that are normally position:static need absolute + z-index
            const extra = SUB_ELEMENTS.has(id as UIElementId) ? 'position: absolute !important; z-index: 1001 !important; ' : '';
            block += `  ${selector} { ${extra}${rules}; }\n`;
            if (id === 'waypointDropdown') {
                block += `  ${selector}.with-fullscreen { ${rules}; }\n`;
                block += `  ${selector}.no-fullscreen { ${rules}; }\n`;
            }
            // The hotspot popup switches to a `.fullscreen` variant for media content, and
            // that rule is more specific than the single-class selector above — so without
            // this companion rule the global position silently applies only to text popups.
            if (id === 'hotspotPopup') {
                block += `  ${selector}.fullscreen { ${rules}; margin: 0 !important; }\n`;
            }
        }
        return block;
    };

    // Desktop overrides
    if (desktopElements && Object.keys(desktopElements).length > 0) {
        css += '@media (min-width: 769px) {\n';
        css += emitRules(desktopElements);
        css += '}\n';
    }

    // Mobile overrides. The hotspot popup falls back to the desktop placement
    // when no mobile one is set — creators position popups on the Desktop tab,
    // and without the fallback every viewport <= 768px (iPad portrait, Split
    // View, narrow embeds) reverts to the stylesheet's centered default. This
    // mirrors the editor preview's `mobile ?? elements` (HotspotContentDisplay).
    // Deliberately popup-only: other controls keep their mobile default layout
    // unless a mobile position is configured for them.
    const effectiveMobile: Partial<Record<string, ElementPosition>> = { ...(mobileElements ?? {}) };
    const inheritedIds = new Set<string>();
    if (!effectiveMobile.hotspotPopup && desktopElements?.hotspotPopup) {
        effectiveMobile.hotspotPopup = desktopElements.hotspotPopup;
        // Inherited desktop offsets get clamped so a placement tuned on a wide
        // screen can't land off a phone viewport; explicit mobile placements
        // are the creator's choice and pass through untouched.
        inheritedIds.add('hotspotPopup');
    }
    if (Object.keys(effectiveMobile).length > 0) {
        css += '@media (max-width: 768px) {\n';
        css += emitRules(effectiveMobile, inheritedIds);
        css += '}\n';
    }

    return css;
}

/**
 * Inject CSS styles into the document
 */
export function injectStyles(uiColor: string = '#CC5833', template: string = 'minimal', theme?: ViewerTheme, containerId?: string): HTMLStyleElement {
    const styleId = containerId
        ? `storysplat-viewer-styles-${containerId}`
        : `storysplat-viewer-styles-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
        existingStyle.remove();
    }
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = generateViewerStyles(uiColor, template, theme);
    document.head.appendChild(style);
    return style;
}
// StorySplat default assets
/**
 * Load Plus Jakarta Sans from Google Fonts for the preloader logo text
 */
function ensurePreloaderFont(): void {
    const offlineAssets = getOfflineAssets();
    if (offlineAssets?.preloaderFontUrl) {
        if (document.getElementById('storysplat-preloader-font')) return;
        const style = document.createElement('style');
        style.id = 'storysplat-preloader-font';
        style.textContent = `@font-face { font-family: 'Plus Jakarta Sans'; src: url('${offlineAssets.preloaderFontUrl}') format('woff2'); font-weight: 700; font-style: normal; font-display: swap; }`;
        document.head.appendChild(style);
        return;
    }
    // Strict offline exports intentionally use the system-font fallback when
    // no packaged brand font is supplied; never create a Google Fonts request.
    if (offlineAssets?.disableRemoteFonts) return;
    if (document.querySelector('link[href*="Plus+Jakarta+Sans"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700&display=swap';
    document.head.appendChild(link);
}
/**
 * Create preloader element
 */
export function createPreloader(container: HTMLElement, customLogoUrl?: string, buttonLabels?: ButtonLabels, logoSize?: number): HTMLElement {
    const preloader = document.createElement('div');
    preloader.className = 'storysplat-preloader';
    const useCustomLogo = !!customLogoUrl;
    // Build preloader content via DOM methods
    const content = document.createElement('div');
    content.className = 'storysplat-preloader-content';
    const media = document.createElement('div');
    media.className = 'storysplat-preloader-media';
    if (useCustomLogo) {
        const mediaType = detectMediaType(customLogoUrl!);
        const el: HTMLImageElement | HTMLVideoElement =
            mediaType === 'video' ? document.createElement('video') : document.createElement('img');
        el.className = 'storysplat-preloader-image';
        el.src = customLogoUrl!;
        if (el instanceof HTMLVideoElement) {
            el.autoplay = true;
            el.loop = true;
            el.muted = true;
            el.playsInline = true;
        } else {
            el.alt = 'Custom Logo';
        }
        // Inline style overrides the CSS-fixed height so authors can size the logo.
        // Applied as height so aspect ratio is preserved via max-width CSS.
        if (typeof logoSize === 'number' && logoSize > 0) {
            el.style.height = `${logoSize}px`;
        }
        media.appendChild(el);
    } else {
        const logoText = document.createElement('span');
        logoText.className = 'storysplat-preloader-logo-text';
        logoText.textContent = 'StorySplat';
        media.appendChild(logoText);
        const loaderContainer = document.createElement('div');
        loaderContainer.className = 'storysplat-loader-container';
        const blob = document.createElement('div');
        blob.className = 'storysplat-blob';
        const core = document.createElement('div');
        core.className = 'storysplat-core';
        loaderContainer.appendChild(blob);
        loaderContainer.appendChild(core);
        media.appendChild(loaderContainer);
    }
    content.appendChild(media);
    const progress = document.createElement('div');
    progress.className = 'storysplat-preloader-progress';
    const textEl = document.createElement('div');
    textEl.className = 'storysplat-preloader-text';
    textEl.textContent = `${getButtonLabel(buttonLabels, 'loading')} 0%`;
    const bar = document.createElement('div');
    bar.className = 'storysplat-preloader-bar';
    progress.appendChild(textEl);
    progress.appendChild(bar);
    content.appendChild(progress);
    preloader.appendChild(content);
    container.appendChild(preloader);
    // Load Google Font for logo text if using default logo
    if (!useCustomLogo) {
        ensurePreloaderFont();
    }
    return preloader;
}
/**
 * Update preloader progress
 */
const preloaderDisplayedPercent = new WeakMap<HTMLElement, number>();
/**
 * Update preloader progress. The displayed value only ever moves forward (a URL fallback or a
 * later phase reporting a lower number never rewinds the bar), and the text always shows the
 * SAME rounded number the bar is drawn at. Returns the displayed fraction (0..1).
 *
 * `text` is kept for API compatibility but the visible label is always "<Loading> N%" — phase
 * labels ("Ready!", "Adding detail…") made the loader read as several different loaders.
 */
export function updatePreloaderProgress(preloader: HTMLElement, progress: number, _text?: string, loadingLabel?: string): number {
    const bar = preloader.querySelector('.storysplat-preloader-bar') as HTMLElement;
    const textEl = preloader.querySelector('.storysplat-preloader-text') as HTMLElement;
    const requested = Number.isFinite(progress) ? Math.max(0, Math.min(100, progress * 100)) : 0;
    const percent = Math.round(Math.max(preloaderDisplayedPercent.get(preloader) ?? 0, requested));
    preloaderDisplayedPercent.set(preloader, percent);
    if (bar)
        bar.style.width = `${percent}%`;
    if (textEl)
        textEl.textContent = `${loadingLabel || DEFAULT_BUTTON_LABELS.loading} ${percent}%`;
    return percent / 100;
}
/**
 * Hide preloader
 */
export function hidePreloader(preloader: HTMLElement): Promise<void> {
    const existingDismissal = preloaderDismissals.get(preloader);
    if (existingDismissal) return existingDismissal;

    preloader.classList.add('hidden');
    const dismissal = new Promise<void>((resolve) => {
        setTimeout(() => {
            preloader.remove();
            resolve();
        }, PRELOADER_FADE_MS);
    });
    preloaderDismissals.set(preloader, dismissal);
    return dismissal;
}

/**
 * Non-blocking "adding detail" indicator for LOD-streaming scenes: subtle plain white text in the
 * bottom-left corner shown as static dim text while higher LODs stream in. No background,
 * no spinner — just faded text. Idempotent show; hide fades out and removes.
 */
export function showDetailIndicator(container: HTMLElement, label = 'Adding detail…'): void {
    let el = container.querySelector('.storysplat-detail-indicator') as HTMLElement | null;
    if (!el) {
        if (!document.getElementById('storysplat-detail-indicator-style')) {
            const style = document.createElement('style');
            style.id = 'storysplat-detail-indicator-style';
            style.textContent = '@keyframes storysplat-di-fadeout{from{opacity:0.45}to{opacity:0}}@media (prefers-reduced-motion: reduce){.storysplat-detail-indicator{animation:none!important;transition:none!important}}';
            document.head.appendChild(style);
        }
        el = document.createElement('div');
        el.className = 'storysplat-detail-indicator';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        // Subtle: bottom-left, plain white faded text (no background/spinner), static.
        el.style.cssText = 'position:absolute;bottom:14px;left:16px;color:#fff;font-size:12px;line-height:1;font-family:system-ui,-apple-system,sans-serif;letter-spacing:0.02em;text-shadow:0 1px 3px rgba(0,0,0,0.55);z-index:100004;pointer-events:none;opacity:0;';
        container.appendChild(el);
        void el.offsetWidth; // force reflow before animating
    }
    el.textContent = label;
    // The persistent scene-title overlay claims the base bottom-left spot
    // (bottom:14px). When it's enabled, slide this indicator up so the two stack
    // without overlapping. The title element is in the DOM whenever the feature is
    // on (even in show-on-hover mode), so key off its presence — not hover state.
    el.style.bottom = container.querySelector('.storysplat-scene-title') ? '38px' : '14px';
    // Static, dim text — no pulse. A short opacity transition eases it in without drawing the eye.
    el.style.animation = '';
    el.style.transition = 'opacity 0.3s ease';
    el.style.opacity = '0.45';
}

export function hideDetailIndicator(container: HTMLElement): void {
    const el = container.querySelector('.storysplat-detail-indicator') as HTMLElement | null;
    if (!el) return;
    el.style.animation = 'storysplat-di-fadeout 0.45s ease forwards';
    setTimeout(() => el.remove(), 460);
}

const AUTOPLAY_DETAIL_STYLE_ID = 'storysplat-autoplay-detail-style';
const AUTOPLAY_DETAIL_FADE_MS = 300;
// Self-contained (like the detail indicator) so it also works when the viewer UI stylesheet is
// not injected (showUI: false). Per-viewer theming comes in through custom properties set on the
// element, so two viewers on one page keep their own colours.
const AUTOPLAY_DETAIL_CSS = `
.storysplat-autoplay-detail {
  position: absolute;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  pointer-events: none;
}
.storysplat-autoplay-detail.storysplat-autoplay-detail--below-audio-prompt {
  padding-top: 112px;
}
.storysplat-autoplay-detail-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: min(320px, 100%);
  box-sizing: border-box;
  padding: 14px 20px;
  color: var(--storysplat-autoplay-detail-text, #fff);
  background: rgba(12, 14, 18, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  font-family: var(--storysplat-autoplay-detail-font, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
  text-align: center;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity ${AUTOPLAY_DETAIL_FADE_MS}ms ease, transform ${AUTOPLAY_DETAIL_FADE_MS}ms ease;
}
.storysplat-autoplay-detail.visible .storysplat-autoplay-detail-card {
  opacity: 1;
  transform: none;
}
.storysplat-autoplay-detail-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}
/* The button row grows open (0fr -> 1fr) so the card expands smoothly instead of jumping. */
.storysplat-autoplay-detail-action {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows ${AUTOPLAY_DETAIL_FADE_MS}ms ease;
}
.storysplat-autoplay-detail-action.visible {
  grid-template-rows: 1fr;
}
.storysplat-autoplay-detail-action > div {
  min-height: 0;
  overflow: hidden;
}
.storysplat-autoplay-detail-start {
  margin-top: 12px;
  pointer-events: auto;
  appearance: none;
  -webkit-appearance: none;
  min-height: 40px;
  padding: 10px 18px;
  border: 0;
  border-radius: 999px;
  background: var(--storysplat-autoplay-detail-accent, #CC5833);
  color: var(--storysplat-autoplay-detail-accent-text, #fff);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  opacity: 0;
  transition: opacity ${AUTOPLAY_DETAIL_FADE_MS}ms ease, filter 0.15s ease;
}
/* The viewer's button reset sets display, which beats the UA [hidden] rule. */
.storysplat-autoplay-detail-start[hidden] {
  display: none !important;
}
.storysplat-autoplay-detail-start.visible {
  opacity: 1;
}
.storysplat-autoplay-detail-start:hover {
  filter: brightness(1.08);
}
.storysplat-autoplay-detail-start:focus-visible {
  /* Inset so the row's overflow clipping keeps the whole ring visible. */
  outline: 2px solid currentColor;
  outline-offset: -5px;
}
.storysplat-photo-mode .storysplat-autoplay-detail {
  display: none !important;
}
@media (prefers-reduced-motion: reduce) {
  .storysplat-autoplay-detail-card,
  .storysplat-autoplay-detail-action,
  .storysplat-autoplay-detail-start {
    transition: none;
    transform: none;
  }
}
`;

/**
 * Text colour that stays readable on `background` (hex or rgb[a]()): near-black on light accents
 * (the scene default uiColor is white), white otherwise or when the colour can't be parsed.
 */
export function readableTextColorOn(background: string | undefined): string {
    const value = (background || '').trim();
    let rgb: number[] | null = null;
    const hex = /^#([0-9a-f]{3,8})$/i.exec(value)?.[1];
    if (hex && (hex.length === 3 || hex.length === 4)) rgb = [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16));
    else if (hex && (hex.length === 6 || hex.length === 8)) rgb = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
    else {
        const m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value);
        if (m) rgb = [m[1], m[2], m[3]].map(Number);
    }
    if (!rgb || rgb.some((c) => !Number.isFinite(c))) return '#fff';
    const [r, g, b] = rgb.map((c) => {
        const v = Math.min(255, Math.max(0, c)) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    // Pick whichever of white / near-black has the higher contrast ratio.
    return (luminance + 0.05) / 0.05 > 1.05 / (luminance + 0.05) ? '#111' : '#fff';
}

export interface AutoplayDetailOverlayOptions {
    /** Status text ("Adding in more detail"). */
    label: string;
    /** Button text ("Start the tour anyways"). */
    startLabel: string;
    /** Scene accent colour for the button. */
    uiColor?: string;
    theme?: ViewerTheme;
    /** Called when the visitor presses the button. */
    onStart: () => void;
}

/**
 * Centred status shown while the initial autoplay waits for levels of detail. The text is a polite
 * live region; the "start anyway" button stays hidden (out of the accessibility tree) until
 * showAutoplayDetailStartButton(). Idempotent: an existing overlay is reused.
 */
export function showAutoplayDetailOverlay(container: HTMLElement, options: AutoplayDetailOverlayOptions): HTMLElement {
    if (!document.getElementById(AUTOPLAY_DETAIL_STYLE_ID)) {
        const style = document.createElement('style');
        style.id = AUTOPLAY_DETAIL_STYLE_ID;
        style.textContent = AUTOPLAY_DETAIL_CSS;
        document.head.appendChild(style);
    }
    const existing = container.querySelector('.storysplat-autoplay-detail') as HTMLElement | null;
    if (existing && !existing.dataset.removing) return existing;
    existing?.remove();

    const root = document.createElement('div');
    root.className = 'storysplat-autoplay-detail';
    const theme = options.theme || {};
    // setProperty ignores invalid values, so creator-supplied theme strings cannot break out.
    if (options.uiColor) {
        root.style.setProperty('--storysplat-autoplay-detail-accent', options.uiColor);
        root.style.setProperty('--storysplat-autoplay-detail-accent-text', readableTextColorOn(options.uiColor));
    }
    if (theme.globalTextColor) root.style.setProperty('--storysplat-autoplay-detail-text', theme.globalTextColor);
    if (theme.fontFamily) root.style.setProperty('--storysplat-autoplay-detail-font', theme.fontFamily);
    // "Tap for audio" also sits at the centre; stack below it instead of overlapping.
    if (container.querySelector('.storysplat-tap-for-audio.visible')) {
        root.classList.add('storysplat-autoplay-detail--below-audio-prompt');
    }

    const card = document.createElement('div');
    card.className = 'storysplat-autoplay-detail-card';
    const text = document.createElement('p');
    text.className = 'storysplat-autoplay-detail-text';
    text.setAttribute('role', 'status');
    text.setAttribute('aria-live', 'polite');
    text.textContent = options.label;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'storysplat-autoplay-detail-start';
    button.hidden = true;
    button.textContent = options.startLabel;
    // The click bubbles on purpose: it is a user gesture, so the container's "tap for audio"
    // handler can unlock sound for the tour that is about to start.
    button.addEventListener('click', () => options.onStart());
    // Keep presses on the button from steering the camera underneath it.
    for (const type of ['pointerdown', 'wheel'] as const) {
        button.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
    }
    const action = document.createElement('div');
    action.className = 'storysplat-autoplay-detail-action';
    const actionInner = document.createElement('div');
    actionInner.appendChild(button);
    action.appendChild(actionInner);
    card.appendChild(text);
    card.appendChild(action);
    root.appendChild(card);
    container.appendChild(root);
    void root.offsetWidth; // commit the hidden state so the fade-in runs
    root.classList.add('visible');
    return root;
}

/** Reveal the "Start the tour anyways" button of the current overlay. */
export function showAutoplayDetailStartButton(container: HTMLElement): void {
    const button = container.querySelector('.storysplat-autoplay-detail-start') as HTMLButtonElement | null;
    if (!button || !button.hidden) return;
    button.hidden = false;
    void button.offsetWidth; // commit the collapsed row so it grows open
    button.classList.add('visible');
    button.closest('.storysplat-autoplay-detail-action')?.classList.add('visible');
}

/** Remove the overlay: fades out, or removes at once when `immediate` (viewer teardown). */
export function hideAutoplayDetailOverlay(container: HTMLElement, immediate = false): void {
    const root = container.querySelector('.storysplat-autoplay-detail') as HTMLElement | null;
    if (!root) return;
    if (immediate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        root.remove();
        return;
    }
    root.dataset.removing = 'true';
    root.classList.remove('visible');
    root.querySelector('.storysplat-autoplay-detail-start')?.setAttribute('disabled', '');
    setTimeout(() => root.remove(), AUTOPLAY_DETAIL_FADE_MS + 20);
}

/** Trigger a browser download for an object URL and confirm it with the photo toast. */
function downloadObjectUrl(container: HTMLElement, objectUrl: string, fileName: string, buttonLabels?: ButtonLabels): void {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    showViewerToast(container, getButtonLabel(buttonLabels, 'photoDownloaded'));
}

/** Download a captured image (photo mode). The object URL is released after the click. */
export function downloadPhotoBlob(container: HTMLElement, blob: Blob, fileName: string, buttonLabels?: ButtonLabels): void {
    const objectUrl = URL.createObjectURL(blob);
    try {
        downloadObjectUrl(container, objectUrl, fileName, buttonLabels);
    } finally {
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    }
}

export function showViewerToast(container: HTMLElement, message: string, timeoutMs = 2000): void {
    const existing = container.querySelector('.storysplat-share-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'storysplat-share-toast';
    toast.textContent = message;
    toast.style.cssText = 'position:absolute;top:50px;right:10px;background:rgba(0,0,0,0.82);color:white;padding:8px 16px;border-radius:8px;font-size:14px;z-index:100005;pointer-events:none;font-family:system-ui,-apple-system,sans-serif;';
    container.appendChild(toast);
    setTimeout(() => toast.remove(), timeoutMs);
}

/** Filled check glyph for the Share button (its CSS forces `fill` on svg paths). */
const SHARE_CHECK_ICON = '<svg viewBox="0 0 24 24" style="width:20px;height:20px;fill:white" aria-hidden="true"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';

/** Remove the Share button's anchored popover (and its listeners), if open. */
export function closeShareLinkPopover(host: HTMLElement): void {
    const el = host.querySelector('.storysplat-share-link-popover') as (HTMLElement & { __storysplatClose?: () => void }) | null;
    if (!el) return;
    if (el.__storysplatClose) el.__storysplatClose();
    else el.remove();
}

/**
 * Small panel anchored under the Share button. Shows either the link in a selectable field
 * (clipboard unavailable — replaces window.prompt) or a short failure message. Persists until
 * dismissed (× button, Escape, or a click elsewhere).
 */
export function showShareLinkPopover(host: HTMLElement, anchor: HTMLElement, opts: { url?: string; message?: string; label?: string; closeLabel?: string }): void {
    closeShareLinkPopover(host);
    const pop = document.createElement('div') as HTMLElement & { __storysplatClose?: () => void };
    pop.className = 'storysplat-share-link-popover';
    pop.setAttribute('role', opts.url ? 'dialog' : 'alert');
    if (opts.label) pop.setAttribute('aria-label', opts.label);
    const hostRect = host.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const top = Math.max(8, anchorRect.bottom - hostRect.top + 8);
    const right = Math.max(8, hostRect.right - anchorRect.right);
    pop.style.cssText = `position:absolute;top:${top}px;right:${right}px;z-index:100003;display:flex;align-items:center;gap:6px;width:max-content;max-width:min(340px, calc(100% - 16px));box-sizing:border-box;padding:8px;background:rgba(16,16,16,0.94);color:white;border:1px solid rgba(255,255,255,0.18);border-radius:8px;box-shadow:0 8px 30px rgba(0,0,0,0.35);font-family:system-ui,-apple-system,sans-serif;font-size:13px;line-height:1.35;`;
    let field: HTMLElement | null = null;
    if (opts.url) {
        field = createCopyUrlField(opts.url, opts.label);
        field.style.cssText = 'flex:1 1 auto;min-width:0;padding:0;';
        const input = field.querySelector('input');
        if (input) input.style.cssText = 'width:min(260px, 60vw);max-width:100%;box-sizing:border-box;padding:6px 8px;border:1px solid rgba(255,255,255,0.22);border-radius:6px;background:rgba(0,0,0,0.35);color:inherit;font:inherit;';
        pop.appendChild(field);
    } else {
        const msg = document.createElement('span');
        msg.textContent = opts.message || '';
        msg.style.cssText = 'padding:0 4px;';
        pop.appendChild(msg);
    }
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = '\u00d7';
    closeBtn.setAttribute('aria-label', opts.closeLabel || 'Close');
    closeBtn.style.cssText = 'flex:0 0 auto;border:none;background:transparent;color:inherit;cursor:pointer;font-size:18px;line-height:1;padding:2px 4px;';
    pop.appendChild(closeBtn);
    // Keep clicks inside from reaching the canvas / camera controls.
    for (const type of ['pointerdown', 'touchstart', 'wheel'] as const) {
        pop.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
    }
    const onKeyDown = (event: KeyboardEvent) => {
        if (!pop.isConnected) { close(); return; }
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation(); // don't also dismiss an overlay/popup underneath
        close();
    };
    const onOutside = (event: Event) => {
        if (!pop.isConnected) { close(); return; }
        const target = event.target as Node | null;
        if (target && (pop.contains(target) || anchor.contains(target))) return;
        close();
    };
    const close = () => {
        document.removeEventListener('keydown', onKeyDown, true);
        document.removeEventListener('pointerdown', onOutside, true);
        pop.remove();
    };
    pop.__storysplatClose = close;
    closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onOutside, true);
    host.appendChild(pop);
    if (field) focusCopyUrlField(field);
}

export interface PhotoSharePanelOptions {
    blob: Blob;
    fileName: string;
    shareUrl: string;
    shareText?: string;
    /** The scene's title: email subject, tweet text and the native share sheet use it. */
    sceneTitle?: string;
    /** Panel heading. */
    title?: string;
    buttonLabels?: ButtonLabels;
    sharePhoto?: () => Promise<void>;
    /** width / height of the captured photo, so the preview's box is reserved before it decodes. */
    aspectRatio?: number;
}

/**
 * Read-only, pre-selected URL field — the in-place replacement for `window.prompt('Copy this
 * link:')` when the Clipboard API is unavailable or refused. The visitor copies it by hand.
 */
export function createCopyUrlField(url: string, label = 'Copy this link'): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'storysplat-copy-url-field';
    const input = document.createElement('input');
    input.type = 'text';
    input.readOnly = true;
    input.value = url;
    input.setAttribute('aria-label', label);
    input.spellcheck = false;
    const selectAll = () => { try { input.select(); } catch { /* non-fatal */ } };
    input.addEventListener('focus', selectAll);
    input.addEventListener('click', selectAll);
    wrap.appendChild(input);
    return wrap;
}

/** Focus + select the URL inside a copy field (after it's in the DOM). */
function focusCopyUrlField(field: HTMLElement): void {
    const input = field.querySelector('input');
    if (!input) return;
    try { input.focus({ preventScroll: true }); input.select(); } catch { /* non-fatal */ }
}

/** Close the photo-share panel if open, running its cleanup (listeners, object URL, focus). */
export function closePhotoSharePanel(container: HTMLElement): void {
    const existing = container.querySelector('.storysplat-photo-share-backdrop') as (HTMLElement & { __storysplatClose?: () => void }) | null;
    if (!existing) return;
    if (existing.__storysplatClose) existing.__storysplatClose();
    else existing.remove();
}

export function showPhotoSharePanel(container: HTMLElement, options: PhotoSharePanelOptions): void {
    // Re-opening replaces the panel; run the old one's cleanup (listeners, object URL, focus).
    closePhotoSharePanel(container);

    const { blob, fileName, shareUrl, shareText, sceneTitle, title, buttonLabels, sharePhoto, aspectRatio } = options;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const resolvedTitle = title || 'Share StorySplat';
    const resolvedSceneTitle = sceneTitle || 'StorySplat Scene';
    const resolvedShareText = shareText || `Check out "${resolvedSceneTitle}" on StorySplat.`;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(resolvedShareText);
    const encodedSubject = encodeURIComponent(resolvedSceneTitle);
    const encodedEmailBody = encodeURIComponent(`${resolvedShareText}\n\n${shareUrl}`);
    const objectUrl = URL.createObjectURL(blob);
    let activeObjectUrl: string | null = objectUrl;
    const backdrop = document.createElement('div');
    backdrop.className = 'storysplat-photo-share-backdrop';

    const panel = document.createElement('div');
    panel.className = 'storysplat-photo-share-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', resolvedTitle);

    const header = document.createElement('div');
    header.className = 'storysplat-photo-share-header';
    const heading = document.createElement('span');
    heading.className = 'storysplat-photo-share-title';
    heading.textContent = resolvedTitle;
    const closeBtn = document.createElement('button');
    closeBtn.className = 'storysplat-photo-share-close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', getButtonLabel(buttonLabels, 'close'));
    closeBtn.textContent = '\u00d7';
    header.appendChild(heading);
    header.appendChild(closeBtn);

    const img = document.createElement('img');
    img.className = 'storysplat-photo-share-preview';
    img.src = objectUrl;
    img.alt = resolvedTitle;
    img.addEventListener('error', () => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                img.src = reader.result;
                if (activeObjectUrl) {
                    URL.revokeObjectURL(activeObjectUrl);
                    activeObjectUrl = null;
                }
            }
        };
        reader.readAsDataURL(blob);
    }, { once: true });

    const socials = document.createElement('div');
    socials.className = 'storysplat-photo-share-socials';
    const X_ICON = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>';
    const MAIL_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>';
    const CHECK_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
    const LINK_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/></svg>';
    // Icon-only tiles; the label lives in aria-label / title for screen readers and hover.
    const tile = (el: HTMLElement, icon: string, label: string) => {
        el.className = 'storysplat-photo-share-social';
        el.innerHTML = icon;
        el.setAttribute('aria-label', label);
        el.setAttribute('title', label);
        return el;
    };
    const xLink = tile(document.createElement('a'), X_ICON, 'Share on X') as HTMLAnchorElement;
    xLink.href = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
    xLink.target = '_blank';
    xLink.rel = 'noopener noreferrer';
    const mailLink = tile(document.createElement('a'), MAIL_ICON, 'Share by email') as HTMLAnchorElement;
    mailLink.href = `mailto:?subject=${encodedSubject}&body=${encodedEmailBody}`;
    const copyBtn = tile(document.createElement('button'), LINK_ICON, getButtonLabel(buttonLabels, 'copyLink')) as HTMLButtonElement;
    copyBtn.type = 'button';
    copyBtn.classList.add('storysplat-photo-share-copy');
    socials.append(xLink, mailLink, copyBtn);
    const actions = document.createElement('div');
    actions.className = 'storysplat-photo-share-actions';
    // Inline status line under the actions (share failure) — shown in place, stays until the
    // next attempt, instead of a corner toast.
    const status = document.createElement('div');
    status.className = 'storysplat-photo-share-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    let primaryAction: HTMLElement = copyBtn;
    if (sharePhoto) {
        const shareBtn = document.createElement('button');
        primaryAction = shareBtn;
        shareBtn.className = 'storysplat-photo-share-action primary';
        shareBtn.type = 'button';
        shareBtn.textContent = getButtonLabel(buttonLabels, 'share');
        shareBtn.addEventListener('click', async () => {
            shareBtn.disabled = true;
            status.hidden = true;
            try {
                await sharePhoto();
            } catch (err) {
                console.warn('[StorySplat Viewer] Native photo share failed:', err);
                status.textContent = getButtonLabel(buttonLabels, 'shareFailed');
                status.hidden = false;
            } finally {
                shareBtn.disabled = false;
            }
        });
        actions.appendChild(shareBtn);
    }
    // Download is a small icon in the top-right corner of the preview: photo mode is the
    // primary way to get a clean picture, so it stays out of the main actions.
    const previewWrap = document.createElement('div');
    previewWrap.className = 'storysplat-photo-share-preview-wrap';
    // The figure shrink-wraps the image so the link overlays the picture itself.
    const figure = document.createElement('div');
    figure.className = 'storysplat-photo-share-figure';
    const downloadBtn = document.createElement('button');
    downloadBtn.className = 'storysplat-photo-share-download';
    downloadBtn.type = 'button';
    downloadBtn.setAttribute('title', getButtonLabel(buttonLabels, 'downloadPhoto'));
    downloadBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v11"/><path d="m7 10 5 5 5-5"/><path d="M5 19h14"/></svg><span>Download</span>';
    figure.append(img, downloadBtn);
    // Reserve the preview's box from the capture's aspect ratio so the panel doesn't grow
    // (and the buttons below don't move) when the image decodes.
    if (typeof aspectRatio === 'number' && Number.isFinite(aspectRatio) && aspectRatio > 0) {
        figure.classList.add('storysplat-photo-share-figure--sized');
        figure.style.setProperty('--storysplat-photo-ratio', String(aspectRatio));
    }
    previewWrap.appendChild(figure);

    panel.appendChild(header);
    panel.appendChild(previewWrap);
    panel.appendChild(socials);
    if (actions.childElementCount > 0) panel.appendChild(actions);
    panel.appendChild(status);
    backdrop.appendChild(panel);
    container.appendChild(backdrop);

    let closed = false;
    let copyResetTimer: ReturnType<typeof setTimeout> | undefined;
    // Escape closes the panel. Capture phase + stopPropagation so the same keypress doesn't
    // also dismiss an overlay / hotspot popup underneath (their handlers listen on document
    // in the bubble phase).
    const onKeyDown = (event: KeyboardEvent) => {
        if (!backdrop.isConnected) { close(); return; } // removed externally (viewer destroy)
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        close();
    };
    const close = () => {
        if (closed) return;
        closed = true;
        document.removeEventListener('keydown', onKeyDown, true);
        clearTimeout(copyResetTimer);
        if (activeObjectUrl) {
            URL.revokeObjectURL(activeObjectUrl);
            activeObjectUrl = null;
        }
        const hadFocus = backdrop.contains(document.activeElement);
        backdrop.remove();
        // Return focus to whatever opened the panel (the share button), if it's still there.
        if (hadFocus && previouslyFocused && previouslyFocused.isConnected) {
            try { previouslyFocused.focus({ preventScroll: true }); } catch { /* non-fatal */ }
        }
    };
    (backdrop as HTMLElement & { __storysplatClose?: () => void }).__storysplatClose = close;
    document.addEventListener('keydown', onKeyDown, true);
    try { primaryAction.focus({ preventScroll: true }); } catch { /* non-fatal */ }

    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', (event) => {
        if (event.target === backdrop) close();
    });

    downloadBtn.addEventListener('click', () => {
        downloadObjectUrl(container, objectUrl, fileName, buttonLabels);
    });

    let copyField: HTMLElement | null = null;
    copyBtn.addEventListener('click', async () => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
            await navigator.clipboard.writeText(shareUrl);
            // Confirm on the button itself rather than with a toast elsewhere on screen.
            const copiedLabel = getButtonLabel(buttonLabels, 'linkCopied');
            const copyLabel = getButtonLabel(buttonLabels, 'copyLink');
            copyBtn.innerHTML = CHECK_ICON;
            copyBtn.setAttribute('aria-label', copiedLabel);
            copyBtn.setAttribute('title', copiedLabel);
            clearTimeout(copyResetTimer);
            copyResetTimer = setTimeout(() => {
                copyBtn.innerHTML = LINK_ICON;
                copyBtn.setAttribute('aria-label', copyLabel);
                copyBtn.setAttribute('title', copyLabel);
            }, 1500);
        } catch {
            // No clipboard access: show the link in place, selected, for a manual copy.
            if (!copyField) {
                copyField = createCopyUrlField(shareUrl, getButtonLabel(buttonLabels, 'copyLink'));
                socials.insertAdjacentElement('afterend', copyField);
            }
            focusCopyUrlField(copyField);
        }
    });
}

/**
 * A calm, neutral notice (not a blocking popup) pinned to the top of the viewer. It
 * stays until the visitor dismisses it, so it can't vanish mid-read.
 */
function showViewerNoticeBanner(container: HTMLElement, message: string): void {
    container.querySelector('.storysplat-outdated-banner')?.remove();
    const banner = document.createElement('div');
    banner.className = 'storysplat-outdated-banner';
    banner.setAttribute('role', 'status');
    banner.style.cssText = 'position:absolute;top:10px;left:50%;transform:translateX(-50%);z-index:10000;display:flex;align-items:center;gap:10px;max-width:calc(100% - 20px);box-sizing:border-box;padding:8px 8px 8px 14px;background:rgba(20,20,20,0.82);color:rgba(255,255,255,0.9);border:1px solid rgba(255,255,255,0.14);border-radius:8px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:13px;line-height:1.35;font-weight:400;backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);';
    const msgSpan = document.createElement('span');
    msgSpan.textContent = message;
    banner.appendChild(msgSpan);
    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.textContent = '\u00d7';
    dismissBtn.setAttribute('aria-label', 'Dismiss');
    dismissBtn.setAttribute('title', 'Dismiss');
    dismissBtn.style.cssText = 'flex:0 0 auto;background:transparent;border:none;color:inherit;padding:0 6px;cursor:pointer;font-size:18px;line-height:1;';
    dismissBtn.addEventListener('click', () => banner.remove());
    banner.appendChild(dismissBtn);
    container.appendChild(banner);
}

/**
 * Notice for hosts that replace a failed scene with its legacy (BabylonJS) export.
 * Only they know a fallback exists, so they show this after swapping in the iframe.
 */
export function showLegacyViewerBanner(container: HTMLElement): void {
    showViewerNoticeBanner(container, 'This scene was made with an older version of StorySplat. Some features may not be available.');
}

/**
 * Show error popup for load failures (e.g., outdated scenes that need re-export)
 */
export function showErrorPopup(container: HTMLElement, errorMessage: string): void {
    // Never show error popups in editor mode — the editor handles its own error states
    if (container.hasAttribute('data-storysplat-editor')) {
        return;
    }
    // Remove any existing error popup
    container.querySelector('.storysplat-error-popup')?.remove();
    // Also hide the preloader if visible
    const preloader = container.querySelector('.storysplat-preloader') as HTMLElement;
    if (preloader) {
        hidePreloader(preloader);
    }
    // The splat never loaded. Keep it non-blocking: hotspots, meshes and menus can still work.
    if (errorMessage.includes('Failed to load splat from any URL')) {
        showViewerNoticeBanner(container, "This scene's 3D model couldn't be loaded. Try refreshing the page.");
        return;
    }
    const popup = document.createElement('div');
    popup.className = 'storysplat-error-popup';
    const iconDiv = document.createElement('div');
    iconDiv.className = 'storysplat-error-popup-icon';
    iconDiv.textContent = '\u274C';
    popup.appendChild(iconDiv);
    const titleEl = document.createElement('h3');
    titleEl.className = 'storysplat-error-popup-title';
    titleEl.textContent = 'Failed to Load Scene';
    popup.appendChild(titleEl);
    const msgEl = document.createElement('p');
    msgEl.className = 'storysplat-error-popup-message';
    msgEl.textContent = errorMessage || 'An error occurred while loading this scene. Please try refreshing the page.';
    popup.appendChild(msgEl);
    container.appendChild(popup);
}
/** Which controls the card explains: the tour, or one of the explore sub-modes. */
export type ControlsCardMode = 'tour' | 'orbit' | 'fly' | 'walk';

/**
 * Split a mouse-diagram callout over two lines at the space nearest its middle, so translated
 * text keeps the shape of "Progress through / the tour". Short text stays on one line.
 */
export function splitCalloutText(text: string, maxLineLength = 18): [string, string] {
    const t = text.trim();
    if (t.length <= maxLineLength) return [t, ''];
    const middle = t.length / 2;
    let best = -1;
    for (let i = 0; i < t.length; i++) {
        if (t[i] === ' ' && (best < 0 || Math.abs(i - middle) <= Math.abs(best - middle))) best = i;
    }
    return best < 0 ? [t, ''] : [t.slice(0, best), t.slice(best + 1)];
}

/**
 * Build both onboarding variants once so the viewer's device classification
 * can select the correct one without rebuilding this DOM. The text is filled
 * in by setTourOnboardingMode, so it follows the camera mode and the scene's
 * label overrides (translations).
 *
 * @license Mobile gesture artwork derives from the open-source Google Material
 * Icons "swipe" glyph, distributed via react-icons under the Apache License 2.0.
 * https://github.com/google/material-design-icons/blob/master/LICENSE
 */
export function createTourOnboardingHint(
    showMovementKeys = true,
    buttonLabels?: ViewerButtonLabels,
    mode: ControlsCardMode = 'tour',
): HTMLElement {
    const hint = document.createElement('div');
    hint.className = 'storysplat-tour-look-hint';
    // Tour-only scenes (no explore/walk mode) deliberately ignore WASD/arrows,
    // so the keyset must not be advertised on their tour card.
    hint.dataset.tourMovementKeys = showMovementKeys ? 'true' : 'false';
    hint.setAttribute('role', 'status');
    hint.setAttribute('aria-live', 'polite');
    const movementKeysMarkup = [
        '    <div class="storysplat-tour-look-hint-keyboard" aria-label="W A S D or arrow keys">',
        '      <div class="storysplat-tour-look-hint-keyset">',
        '        <div class="storysplat-tour-look-hint-key-row"><kbd class="storysplat-tour-look-hint-key" aria-label="W or Up arrow"><span>W</span><span class="storysplat-tour-look-hint-key-slash" aria-hidden="true">/</span><span class="storysplat-tour-look-hint-key-arrow" aria-hidden="true">&#8593;</span></kbd></div>',
        '        <div class="storysplat-tour-look-hint-key-row"><kbd class="storysplat-tour-look-hint-key" aria-label="A or Left arrow"><span>A</span><span class="storysplat-tour-look-hint-key-slash" aria-hidden="true">/</span><span class="storysplat-tour-look-hint-key-arrow" aria-hidden="true">&#8592;</span></kbd><kbd class="storysplat-tour-look-hint-key" aria-label="S or Down arrow"><span>S</span><span class="storysplat-tour-look-hint-key-slash" aria-hidden="true">/</span><span class="storysplat-tour-look-hint-key-arrow" aria-hidden="true">&#8595;</span></kbd><kbd class="storysplat-tour-look-hint-key" aria-label="D or Right arrow"><span>D</span><span class="storysplat-tour-look-hint-key-slash" aria-hidden="true">/</span><span class="storysplat-tour-look-hint-key-arrow" aria-hidden="true">&#8594;</span></kbd></div>',
        '        <div class="storysplat-tour-look-hint-key-caption"></div>',
        '      </div>',
        '    </div>',
    ].join('');
    // Static artwork only: every label is set with textContent below, never parsed as HTML.
    hint.innerHTML = [
        '<button type="button" class="storysplat-tour-look-hint-close">',
        '  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>',
        '</button>',
        '<div class="storysplat-tour-look-hint-desktop">',
        '  <div class="storysplat-tour-look-hint-title"></div>',
        '  <div class="storysplat-tour-look-hint-controls">',
        '    <svg class="storysplat-tour-look-hint-mouse" viewBox="0 0 350 120" role="img" aria-label="Mouse controls">',
        '      <defs><marker id="storysplat-tour-callout-arrow" viewBox="0 0 7 7" refX="6" refY="3.5" markerWidth="7" markerHeight="7" orient="auto"><path class="storysplat-tour-look-hint-callout-arrowhead" d="M0 0 L7 3.5 L0 7 Z" /></marker></defs>',
        '      <text class="storysplat-tour-look-hint-callout storysplat-tour-look-hint-callout--drag" x="79" y="28" text-anchor="end"></text>',
        '      <path class="storysplat-tour-look-hint-callout-line" d="M82 24 C104 24 119 31 136 42" marker-end="url(#storysplat-tour-callout-arrow)" />',
        '      <rect class="storysplat-tour-look-hint-mouse-outline" x="139" y="8" width="72" height="104" rx="36" />',
        '      <path class="storysplat-tour-look-hint-mouse-button" d="M174 9 H173 C155 9 140 24 140 43 V51 H174 Z" />',
        '      <path class="storysplat-tour-look-hint-mouse-detail" d="M175 9 V51 M139 51 H211" />',
        '      <rect class="storysplat-tour-look-hint-wheel" x="171" y="19" width="8" height="21" rx="4" />',
        '      <g class="storysplat-tour-look-hint-wheel-callout">',
        '        <path class="storysplat-tour-look-hint-callout-line" d="M232 42 C211 42 197 36 181 29" marker-end="url(#storysplat-tour-callout-arrow)" />',
        '        <text class="storysplat-tour-look-hint-callout storysplat-tour-look-hint-callout--wheel" x="235" y="42"></text>',
        '        <text class="storysplat-tour-look-hint-callout storysplat-tour-look-hint-callout--wheel" x="235" y="58"></text>',
        '      </g>',
        '    </svg>',
        '    <div class="storysplat-tour-look-hint-narrow-actions" aria-label="Mouse controls">',
        '      <div class="storysplat-tour-look-hint-narrow-action">',
        '        <svg class="storysplat-tour-look-hint-narrow-mouse" viewBox="0 0 40 52" aria-hidden="true">',
        '          <rect class="storysplat-tour-look-hint-mouse-outline" x="5" y="2" width="30" height="48" rx="15" />',
        '          <path class="storysplat-tour-look-hint-mouse-button" d="M19.5 3H19C11.8 3 6 8.8 6 16v7h13.5z" />',
        '          <path class="storysplat-tour-look-hint-mouse-detail" d="M20 3v20M5 23h30" />',
        '        </svg>',
        '        <span class="storysplat-tour-look-hint-narrow-text--drag"></span>',
        '      </div>',
        '      <div class="storysplat-tour-look-hint-narrow-action storysplat-tour-look-hint-narrow-action--wheel">',
        '        <svg class="storysplat-tour-look-hint-narrow-mouse" viewBox="0 0 40 52" aria-hidden="true">',
        '          <rect class="storysplat-tour-look-hint-mouse-outline" x="5" y="2" width="30" height="48" rx="15" />',
        '          <path class="storysplat-tour-look-hint-mouse-detail" d="M20 3v20M5 23h30" />',
        '          <rect class="storysplat-tour-look-hint-wheel" x="17.5" y="8" width="5" height="13" rx="2.5" />',
        '        </svg>',
        '        <span class="storysplat-tour-look-hint-narrow-text--wheel"></span>',
        '      </div>',
        '    </div>',
        movementKeysMarkup,
        '  </div>',
        '</div>',
        '<div class="storysplat-tour-look-hint-mobile">',
        '  <svg class="storysplat-tour-look-hint-finger" viewBox="0 0 24 24" role="img" aria-label="One finger swiping left and right">',
        '    <g class="storysplat-tour-look-hint-gesture-icon">',
        '      <path d="m18.89 14.75-4.09-2.04c-.28-.14-.58-.21-.89-.21H13v-6c0-.83-.67-1.5-1.5-1.5S10 5.67 10 6.5v10.74l-3.25-.74a.99.99 0 0 0-.92.28l-.83.84 4.54 4.79c.38.38 1.14.59 1.67.59h6.16c1 0 1.84-.73 1.98-1.72l.63-4.46c.12-.85-.32-1.68-1.09-2.07z" />',
        '      <path d="M20.13 3.87C18.69 2.17 15.6 1 12 1S5.31 2.17 3.87 3.87L2 2v5h5L4.93 4.93c1-1.29 3.7-2.43 7.07-2.43s6.07 1.14 7.07 2.43L17 7h5V2l-1.87 1.87z" />',
        '    </g>',
        '  </svg>',
        '  <div class="storysplat-tour-look-hint-mobile-text"></div>',
        '</div>',
    ].join('');
    setTourOnboardingMode(hint, mode, buttonLabels);
    return hint;
}

/**
 * Point the controls card at a camera mode: tour (look around, scroll through the tour), orbit
 * (drag to rotate, scroll to zoom) or fly/walk (look around, keys to move). Text comes from the
 * label overrides, so creators can translate it; it is always set as text, never as HTML.
 */
export function setTourOnboardingMode(hint: HTMLElement, mode: ControlsCardMode, buttonLabels?: ViewerButtonLabels): void {
    const label = (key: keyof ViewerButtonLabels) => getButtonLabel(buttonLabels, key);
    const tourKeys = hint.dataset.tourMovementKeys !== 'false';
    // Orbit has no keyboard movement; the tour advertises keys only when they can leave it.
    const showKeys = mode === 'tour' ? tourKeys : mode !== 'orbit';
    // The wheel does nothing in fly and walk.
    const showWheel = mode === 'tour' || mode === 'orbit';
    const title = label(showKeys ? 'controlsTitleMouseKeyboard' : 'controlsTitleMouse');
    const drag = label(mode === 'orbit' ? 'controlsOrbitRotate' : 'controlsLookAround');
    const wheel = label(mode === 'orbit' ? 'controlsOrbitZoom' : 'controlsTourScroll');
    const touch = label(mode === 'tour' ? 'controlsTouchTour' : mode === 'orbit' ? 'controlsTouchOrbit' : 'controlsTouchMove');

    hint.dataset.mode = mode;
    hint.classList.toggle('storysplat-tour-look-hint--no-keys', !showKeys);
    hint.classList.toggle('storysplat-tour-look-hint--no-wheel', !showWheel);
    hint.setAttribute('aria-label', title);
    const setText = (selector: string, text: string) => {
        const el = hint.querySelector(selector);
        if (el) el.textContent = text;
    };
    setText('.storysplat-tour-look-hint-title', title);
    setText('.storysplat-tour-look-hint-callout--drag', drag);
    const wheelLines = splitCalloutText(wheel);
    hint.querySelectorAll('.storysplat-tour-look-hint-callout--wheel').forEach((el, i) => {
        el.textContent = wheelLines[i] ?? '';
    });
    setText('.storysplat-tour-look-hint-narrow-text--drag', drag);
    setText('.storysplat-tour-look-hint-narrow-text--wheel', wheel);
    setText('.storysplat-tour-look-hint-key-caption', label('controlsMove'));
    setText('.storysplat-tour-look-hint-mobile-text', touch);
    const close = hint.querySelector<HTMLButtonElement>('.storysplat-tour-look-hint-close');
    if (close) {
        close.setAttribute('aria-label', label('close'));
        close.title = label('close');
    }
}

/**
 * Create UI elements for the viewer
 */
export function createUIElements(container: HTMLElement, config: ViewerConfig, options: UIOptions = {}): UIElements {
    const { uiColor = '#4CAF50', showScrollControls = true, showModeToggle = true, showFullscreenButton = true, showHelpButton = false, // Minimal: hide by default
    showMuteButton = false, showMicrophoneButton = false, showPreloader = true, allowedCameraModes = ['tour', 'explore'], enabledExploreModes = ['orbit', 'fly'], defaultCameraMode = 'tour', customPreloaderLogoUrl, preloaderLogoSize, buttonLabels, hideWatermark = false, watermarkText, watermarkLink, watermarkImageUrl, watermarkImageHeight, sceneId, showWaypointList = true, template = 'minimal', hideProgressText = true, viewerTheme, showRelightingToggle = false, showFisheyeButton = false, cutawayEnabled = false, showStereoButton = false, showPhotoButton = true, showSceneMenu, sceneMenuLinks, sceneMenuStyle = 'classic', startCategoriesCollapsed = false, showSceneMenuPreviewImages = false, currentSpaceMenuPath, currentSpaceMenuOrder, sceneMenuFolderOrder, menuTitle, menuTitleImageUrl, measurementsEnabled = false, measurementColor, layersTitle, sceneGuideName, showShareButton = true, sceneTitle, showSceneTitle = false, sceneTitleShowOnHover = false } = options;
    const movementKeyDisplay = options.movementKeyDisplay ?? 'wasd';
    // Trimmed scene title used by the bottom-left overlay + "Current Space" menu header.
    const sceneTitleText = (sceneTitle || '').trim();
    // Get localized labels
    const labels = {
        tour: getButtonLabel(buttonLabels, 'tour'),
        explore: getButtonLabel(buttonLabels, 'explore'),
        walk: getButtonLabel(buttonLabels, 'walk'),
        orbit: getButtonLabel(buttonLabels, 'orbit'),
        fly: getButtonLabel(buttonLabels, 'fly'),
        previous: getButtonLabel(buttonLabels, 'previous'),
        next: getButtonLabel(buttonLabels, 'next'),
        fullscreen: getButtonLabel(buttonLabels, 'fullscreen'),
        waypoints: getButtonLabel(buttonLabels, 'waypoints'),
        close: getButtonLabel(buttonLabels, 'close'),
        yes: getButtonLabel(buttonLabels, 'yes'),
        cancel: getButtonLabel(buttonLabels, 'cancel'),
        vr: getButtonLabel(buttonLabels, 'vr'),
        ar: getButtonLabel(buttonLabels, 'ar'),
        loading: getButtonLabel(buttonLabels, 'loading'),
        helpTitle: getButtonLabel(buttonLabels, 'helpTitle'),
        helpCameraModes: getButtonLabel(buttonLabels, 'helpCameraModes'),
        helpTourDesc: getButtonLabel(buttonLabels, 'helpTourDesc'),
        helpExploreDesc: getButtonLabel(buttonLabels, 'helpExploreDesc'),
        helpWalkDesc: getButtonLabel(buttonLabels, 'helpWalkDesc'),
        helpTourControls: getButtonLabel(buttonLabels, 'helpTourControls'),
        helpTourScroll: getButtonLabel(buttonLabels, 'helpTourScroll'),
        helpTourDrag: getButtonLabel(buttonLabels, 'helpTourDrag'),
        helpExploreControls: getButtonLabel(buttonLabels, 'helpExploreControls'),
        helpExploreLMB: getButtonLabel(buttonLabels, 'helpExploreLMB'),
        helpExploreRMB: getButtonLabel(buttonLabels, 'helpExploreRMB'),
        helpExploreWASD: getButtonLabel(buttonLabels, 'helpExploreWASD'),
        helpExploreShift: getButtonLabel(buttonLabels, 'helpExploreShift'),
        helpExploreScroll: getButtonLabel(buttonLabels, 'helpExploreScroll'),
        helpExploreDblClick: getButtonLabel(buttonLabels, 'helpExploreDblClick'),
        helpWalkControls: getButtonLabel(buttonLabels, 'helpWalkControls'),
        helpWalkClick: getButtonLabel(buttonLabels, 'helpWalkClick'),
        helpWalkWASD: getButtonLabel(buttonLabels, 'helpWalkWASD'),
        helpWalkMouse: getButtonLabel(buttonLabels, 'helpWalkMouse'),
        helpWalkShift: getButtonLabel(buttonLabels, 'helpWalkShift'),
        helpWalkSpace: getButtonLabel(buttonLabels, 'helpWalkSpace'),
        hotspotDefaultTitle: getButtonLabel(buttonLabels, 'hotspotDefaultTitle'),
        openExternalLink: getButtonLabel(buttonLabels, 'openExternalLink'),
        mute: getButtonLabel(buttonLabels, 'mute'),
        unmute: getButtonLabel(buttonLabels, 'unmute'),
        share: getButtonLabel(buttonLabels, 'share'),
        scenes: getButtonLabel(buttonLabels, 'scenes'),
    };
    const elements: UIElements = {};
    // Coordinator so only one top-left tool (help / measurement / virtual
    // staging / scene menu) is open at a time — they share the same corner.
    elements.exclusiveTools = createExclusiveTools();
    // Clean up any existing UI elements from previous renders
    container.querySelectorAll('.storysplat-preloader, .storysplat-scroll-controls, .storysplat-waypoint-info, .storysplat-fullscreen-btn, .storysplat-mute-btn, .storysplat-mic-btn, .storysplat-help-btn, .storysplat-help-panel, .storysplat-performance-notice, .storysplat-watermark, .storysplat-waypoint-list-container, .storysplat-scene-menu-container, .storysplat-scene-menu-toggle--sidebar, .storysplat-hotspot-popup, .storysplat-overlay-layer, .storysplat-hotspot-lightbox, .storysplat-portal-popup, .storysplat-wasd-hint, .storysplat-orbit-hint, .storysplat-doubletap-hint, .storysplat-look-zone, .storysplat-joystick-container, .storysplat-relight-btn, .storysplat-fps-counter, .storysplat-xr-btn, .storysplat-mode-container, .storysplat-explore-controls, .storysplat-top-right-bar, .storysplat-top-right-group, .storysplat-top-right-pill, .storysplat-measure-btn, .storysplat-cutaway-btn, .storysplat-cutaway-panel, .storysplat-stereo-btn, .storysplat-stereo-menu, .storysplat-measure-canvas, .storysplat-measure-label, .storysplat-measure-point, .storysplat-toolbar, .storysplat-share-btn, .storysplat-photo-btn, .storysplat-photo-bar, .storysplat-photo-frame, .storysplat-photo-fade, .storysplat-hotspot-popup-scrim, .storysplat-photo-share-backdrop, .storysplat-share-link-popover, .storysplat-ar-error, .storysplat-share-toast, .storysplat-scene-title, .storysplat-skins-btn, .storysplat-skins-dropdown, .storysplat-skin-overlay-wrapper, .storysplat-layers-btn, .storysplat-layers-panel, .storysplat-directory-btn, .storysplat-directory-panel, .storysplat-directory-scrim, .storysplat-guide-btn, .storysplat-guide-panel, .storysplat-guide-scrim').forEach(el => el.remove());
    // Inject styles (template-aware, with optional theme overrides)
    const viewerStyleElement = injectStyles(uiColor, template, viewerTheme, container.id);
    elements._cleanup = () => viewerStyleElement.remove();
    // Add container class
    container.classList.add('storysplat-viewer-container');
    // Create Preloader
    if (showPreloader) {
        elements.preloader = createPreloader(container, customPreloaderLogoUrl, buttonLabels, preloaderLogoSize);
    }
    // Create Waypoint Info Panel (top banner style)
    const waypointInfo = document.createElement('div');
    waypointInfo.className = 'storysplat-waypoint-info';
    waypointInfo.innerHTML = `
    <h2 class="storysplat-waypoint-title"></h2>
    <div class="storysplat-waypoint-description storysplat-rich-text"></div>
  `;
    container.appendChild(waypointInfo);
    elements.waypointInfo = waypointInfo;
    // Create "Tap for audio" overlay (hidden by default, shown when AudioContext is suspended)
    const tapForAudio = document.createElement('div');
    tapForAudio.className = 'storysplat-tap-for-audio';
    // Same volume-up glyph as the toolbar mute button — an emoji here renders
    // differently on every platform and clashes with the rest of the UI.
    tapForAudio.innerHTML = `<span>Tap for audio</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>`;
    tapForAudio.style.display = 'none';
    container.appendChild(tapForAudio);
    elements.tapForAudio = tapForAudio;
    // Create Scroll Controls
    if (showScrollControls && config.waypoints && config.waypoints.length > 0) {
        const scrollControls = document.createElement('div');
        scrollControls.className = 'storysplat-scroll-controls';
        // Explore sub-mode buttons (orbit/fly/walk): only render the bar when 2+ are available — a
        // single-sub-mode bar is a pointless one-button row (entering explore auto-resolves to it).
        const showOrbitBtn = allowedCameraModes.includes('explore') && enabledExploreModes.includes('orbit');
        const showFlyBtn = allowedCameraModes.includes('explore') && enabledExploreModes.includes('fly');
        const showWalkExploreBtn = allowedCameraModes.includes('walk') || (allowedCameraModes.includes('explore') && enabledExploreModes.includes('walk'));
        const exploreBtnCount = [showOrbitBtn, showFlyBtn, showWalkExploreBtn].filter(Boolean).length;
        scrollControls.innerHTML = `
      <div class="storysplat-scroll-content">
        <div class="storysplat-progress-text">0%</div>
        <div class="storysplat-progress-container">
          <div class="storysplat-progress-bar"></div>
        </div>
        <div class="storysplat-scroll-buttons">
          <button class="storysplat-btn storysplat-btn-prev">${labels.previous}</button>
          <button class="storysplat-btn storysplat-btn-play">
            <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <button class="storysplat-btn storysplat-btn-next">${labels.next}</button>
        </div>
        ${exploreBtnCount >= 2 ? `
        <div class="storysplat-explore-controls">
          ${showOrbitBtn ? `<button class="storysplat-explore-btn" data-explore-mode="orbit">${labels.orbit}</button>` : ''}
          ${showFlyBtn ? `<button class="storysplat-explore-btn" data-explore-mode="fly">${labels.fly}</button>` : ''}
          ${showWalkExploreBtn ? `<button class="storysplat-explore-btn" data-explore-mode="walk">${labels.walk}</button>` : ''}
        </div>
        ` : ''}
        ${showModeToggle && allowedCameraModes.includes('tour') && (allowedCameraModes.includes('explore') || allowedCameraModes.includes('walk')) ? `
        <div class="storysplat-mode-container">
          <div class="storysplat-mode-toggle">
            ${allowedCameraModes.includes('tour') ? `<button class="storysplat-mode-btn ${defaultCameraMode === 'tour' ? 'selected' : ''}" data-mode="tour">${labels.tour}</button>` : ''}
            ${allowedCameraModes.includes('explore') && allowedCameraModes.includes('walk') ? `<button class="storysplat-mode-btn ${defaultCameraMode === 'explore' || defaultCameraMode === 'walk' ? 'selected' : ''}" data-mode="explore">${labels.explore}</button>` : ''}
            ${allowedCameraModes.includes('explore') && !allowedCameraModes.includes('walk') ? `<button class="storysplat-mode-btn ${defaultCameraMode === 'explore' ? 'selected' : ''}" data-mode="explore">${labels.explore}</button>` : ''}
            ${!allowedCameraModes.includes('explore') && allowedCameraModes.includes('walk') ? `<button class="storysplat-mode-btn ${defaultCameraMode === 'walk' ? 'selected' : ''}" data-mode="walk">${labels.walk}</button>` : ''}
          </div>
        </div>
        ` : ''}
      </div>
    `;
        container.appendChild(scrollControls);
        elements.scrollControls = scrollControls;
        elements.progressBar = scrollControls.querySelector('.storysplat-progress-bar') as HTMLElement;
        elements.progressText = scrollControls.querySelector('.storysplat-progress-text') as HTMLElement;
        elements.exploreControls = scrollControls.querySelector('.storysplat-explore-controls') as HTMLElement;
        elements.modeContainer = scrollControls.querySelector('.storysplat-mode-container') as HTMLElement;
        elements.prevButton = scrollControls.querySelector('.storysplat-btn-prev') as HTMLElement;
        elements.playButton = scrollControls.querySelector('.storysplat-btn-play') as HTMLElement;
        elements.nextButton = scrollControls.querySelector('.storysplat-btn-next') as HTMLElement;

        // Pro template: create a top-right bar and move mode container into it
        // The waypoint list will also be appended here later
        if (template === 'pro') {
            const topBar = document.createElement('div');
            topBar.className = 'storysplat-top-right-bar';
            container.appendChild(topBar);
            if (elements.modeContainer) {
                topBar.appendChild(elements.modeContainer);
            }
        }

        // Extract sub-elements to container root when custom positions exist
        const layout = viewerTheme?.uiLayout;
        const hasPos = (id: string) => layout?.elements?.[id as UIElementId] || layout?.mobile?.[id as UIElementId];

        if (hasPos('progressText') && elements.progressText) container.appendChild(elements.progressText);
        if (hasPos('exploreControls') && elements.exploreControls) container.appendChild(elements.exploreControls);
        if (hasPos('prevButton') && elements.prevButton) container.appendChild(elements.prevButton);
        if (hasPos('playButton') && elements.playButton) container.appendChild(elements.playButton);
        if (hasPos('nextButton') && elements.nextButton) container.appendChild(elements.nextButton);
        // modeToggle: pro already extracts; for non-pro, extract when positioned
        if (template !== 'pro' && hasPos('modeToggle') && elements.modeContainer) {
            container.appendChild(elements.modeContainer);
        }

        // Apply hideProgressText
        if (hideProgressText && elements.progressText) elements.progressText.style.display = 'none';
    }
    // Create the top-right shared-background pill (holds fullscreen + share).
    // Built BEFORE fullscreen + share so both can append into it as flex children.
    // Classic: lives inside a right-anchored `.storysplat-top-right-group` so the
    //   waypoint dropdown can sit to its LEFT. Pro: appended into the existing
    //   `.storysplat-top-right-bar` (after mode toggle + before waypoints by flex order).
    // Custom uiLayout fullscreen placement: when fullscreen is custom-positioned it is
    //   extracted back to `container` below, leaving the pill to hold only share (or empty).
    // A fullscreen button needs either the Fullscreen API or the window-filling fallback
    // (fullscreen.ts). Decided here, before the pill is built, so a browser that can do
    // neither never gets a button that is hidden later and leaves an empty chip behind.
    const canFullscreen = showFullscreenButton && detectFullscreenMode(container, document) !== 'none';
    let topRightPill: HTMLDivElement | null = null;
    if (canFullscreen || showShareButton || showPhotoButton) {
        topRightPill = document.createElement('div');
        topRightPill.className = 'storysplat-top-right-pill';
        const proBar = template === 'pro'
            ? (container.querySelector('.storysplat-top-right-bar') as HTMLElement | null)
            : null;
        if (proBar) {
            proBar.appendChild(topRightPill);
        } else {
            // Classic (or pro without a bar): wrap in a right-anchored flex group so
            // waypoints can be appended to its LEFT later.
            const topRightGroup = document.createElement('div');
            topRightGroup.className = 'storysplat-top-right-group';
            topRightGroup.appendChild(topRightPill);
            container.appendChild(topRightGroup);
        }
    }
    // Create Fullscreen Button
    if (canFullscreen) {
        const fullscreenBtn = document.createElement('button');
        fullscreenBtn.className = 'storysplat-fullscreen-btn';
        fullscreenBtn.setAttribute('aria-label', labels.fullscreen);
        fullscreenBtn.innerHTML = `
      <svg class="storysplat-expand-icon" viewBox="0 0 24 24">
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
      </svg>
      <svg class="storysplat-compress-icon" viewBox="0 0 24 24" style="display: none;">
        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
      </svg>
    `;
        // Custom uiLayout placement: extract to container so the base absolute rule +
        // generated position overrides anchor it to the viewer, not the pill.
        const fsLayout = viewerTheme?.uiLayout;
        const fsHasCustomPos = !!(fsLayout?.elements?.fullscreenButton || fsLayout?.mobile?.fullscreenButton);
        ((!fsHasCustomPos && topRightPill) ? topRightPill : container).appendChild(fullscreenBtn);
        elements.fullscreenButton = fullscreenBtn;
    }
    // Create top-left toolbar container (frosted glass, holds help/mute/relight/measure icons)
    const toolbar = document.createElement('div');
    toolbar.className = 'storysplat-toolbar';
    container.appendChild(toolbar);
    elements.toolbar = toolbar;
    // Create Mute Button (shown when scene has audio)
    if (showMuteButton) {
        const muteBtn = document.createElement('button');
        muteBtn.className = 'storysplat-mute-btn';
        muteBtn.setAttribute('aria-label', labels.mute);
        // Volume on icon
        const unmutedIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        unmutedIcon.classList.add('storysplat-unmuted-icon');
        unmutedIcon.setAttribute('viewBox', '0 0 24 24');
        const unmutedPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        unmutedPath.setAttribute('d', 'M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z');
        unmutedIcon.appendChild(unmutedPath);
        muteBtn.appendChild(unmutedIcon);
        // Volume off icon (hidden by default)
        const mutedIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        mutedIcon.classList.add('storysplat-muted-icon');
        mutedIcon.setAttribute('viewBox', '0 0 24 24');
        mutedIcon.style.display = 'none';
        const mutedPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        mutedPath.setAttribute('d', 'M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z');
        mutedIcon.appendChild(mutedPath);
        muteBtn.appendChild(mutedIcon);
        toolbar.appendChild(muteBtn);
        elements.muteButton = muteBtn;
    }
    if (showMicrophoneButton) {
        const micBtn = document.createElement('button');
        micBtn.className = 'storysplat-mic-btn';
        micBtn.type = 'button';
        micBtn.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.92V20h2a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2h2v-2.08A7 7 0 0 1 5 11a1 1 0 1 1 2 0 5 5 0 0 0 10 0Z"/></svg>
          <span class="storysplat-mic-label">Use mic</span>`;
        toolbar.appendChild(micBtn);
        elements.microphoneButton = micBtn;
    }
    // Create Relighting Toggle Button (lightbulb icon)
    if (showRelightingToggle) {
        const relightBtn = document.createElement('button');
        relightBtn.className = 'storysplat-relight-btn active';
        relightBtn.setAttribute('aria-label', 'Toggle Relighting');
        // Lightbulb icon
        const bulbIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        bulbIcon.setAttribute('viewBox', '0 0 24 24');
        const bulbPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        bulbPath.setAttribute('d', 'M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z');
        bulbIcon.appendChild(bulbPath);
        relightBtn.appendChild(bulbIcon);
        toolbar.appendChild(relightBtn);
        elements.relightingButton = relightBtn;
    }
    // Create Fisheye / Tiny Planet toggle button
    if (showFisheyeButton) {
        const fisheyeBtn = document.createElement('button');
        fisheyeBtn.className = 'storysplat-fisheye-btn';
        fisheyeBtn.setAttribute('aria-label', 'Toggle Tiny Planet');
        fisheyeBtn.setAttribute('title', 'Tiny Planet');
        const fisheyeIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        fisheyeIcon.setAttribute('viewBox', '0 0 24 24');
        const outer = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        outer.setAttribute('cx', '12'); outer.setAttribute('cy', '12'); outer.setAttribute('r', '9');
        outer.setAttribute('fill', 'none'); outer.setAttribute('stroke', 'currentColor'); outer.setAttribute('stroke-width', '2');
        const inner = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        inner.setAttribute('cx', '12'); inner.setAttribute('cy', '12'); inner.setAttribute('r', '3.5');
        inner.setAttribute('fill', 'currentColor');
        fisheyeIcon.appendChild(outer);
        fisheyeIcon.appendChild(inner);
        fisheyeBtn.appendChild(fisheyeIcon);
        toolbar.appendChild(fisheyeBtn);
        elements.fisheyeButton = fisheyeBtn;
    }
    // Create Cutaway toggle button + plane slider (hide splats above/below a movable plane)
    if (cutawayEnabled) {
        const cutawayBtn = document.createElement('button');
        cutawayBtn.className = 'storysplat-cutaway-btn';
        cutawayBtn.setAttribute('aria-label', CUTAWAY_LABELS.toggle);
        cutawayBtn.setAttribute('title', CUTAWAY_LABELS.toggle);
        cutawayBtn.setAttribute('aria-pressed', 'false');
        cutawayBtn.innerHTML = CUTAWAY_ICON;
        toolbar.appendChild(cutawayBtn);

        const cutawayPanel = document.createElement('div');
        cutawayPanel.className = 'storysplat-cutaway-panel';
        cutawayPanel.setAttribute('role', 'group');
        cutawayPanel.setAttribute('aria-label', CUTAWAY_LABELS.panel);
        const flipBtn = document.createElement('button');
        flipBtn.type = 'button';
        flipBtn.className = 'storysplat-cutaway-flip';
        flipBtn.setAttribute('aria-label', CUTAWAY_LABELS.flip);
        flipBtn.setAttribute('title', CUTAWAY_LABELS.flip);
        flipBtn.innerHTML = CUTAWAY_FLIP_ICON;
        const slider = document.createElement('input');
        slider.type = 'range';
        slider.className = 'storysplat-cutaway-slider';
        slider.min = '0';
        slider.max = '1000';
        slider.step = '1';
        slider.value = '1000';
        slider.setAttribute('aria-label', CUTAWAY_LABELS.slider);
        slider.setAttribute('aria-orientation', 'vertical');
        const cutawayLabel = document.createElement('span');
        cutawayLabel.className = 'storysplat-cutaway-label';
        cutawayLabel.textContent = '100%';
        cutawayPanel.append(flipBtn, slider, cutawayLabel);
        container.appendChild(cutawayPanel);
        elements.cutawayButton = cutawayBtn;
        elements.cutawayPanel = cutawayPanel;
        elements.cutawaySlider = slider;
        elements.cutawayFlipButton = flipBtn;
        elements.cutawayLabel = cutawayLabel;
    }
    // Create Stereo 3D button + format menu (opt-in: the Discover scene page has its own control)
    if (showStereoButton) {
        const stereoBtn = document.createElement('button');
        stereoBtn.className = 'storysplat-stereo-btn';
        stereoBtn.setAttribute('aria-label', STEREO_LABELS.toggle);
        stereoBtn.setAttribute('title', STEREO_LABELS.toggle);
        stereoBtn.setAttribute('aria-haspopup', 'menu');
        stereoBtn.setAttribute('aria-expanded', 'false');
        stereoBtn.innerHTML = STEREO_ICON;
        toolbar.appendChild(stereoBtn);

        const stereoMenu = document.createElement('div');
        stereoMenu.className = 'storysplat-stereo-menu';
        stereoMenu.setAttribute('role', 'menu');
        stereoMenu.setAttribute('aria-label', STEREO_LABELS.menu);
        const header = document.createElement('div');
        header.className = 'storysplat-stereo-menu-header';
        header.textContent = STEREO_LABELS.menu;
        stereoMenu.appendChild(header);
        const options: Array<[string, string, string]> = [
            ['off', STEREO_LABELS.off, ''],
            ['anaglyph', STEREO_LABELS.anaglyph, STEREO_LABELS.anaglyphHint],
            ['sbs', STEREO_LABELS.sbs, STEREO_LABELS.sbsHint],
            ['tb', STEREO_LABELS.tb, STEREO_LABELS.tbHint],
            ['sbs-full', STEREO_LABELS.sbsFull, STEREO_LABELS.sbsFullHint],
        ];
        for (const [format, title, hint] of options) {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = 'storysplat-stereo-option';
            item.setAttribute('role', 'menuitemradio');
            item.setAttribute('aria-checked', format === 'off' ? 'true' : 'false');
            item.dataset.stereoFormat = format;
            const titleEl = document.createElement('span');
            titleEl.className = 'storysplat-stereo-option-title';
            titleEl.textContent = title;
            item.appendChild(titleEl);
            if (hint) {
                const hintEl = document.createElement('span');
                hintEl.className = 'storysplat-stereo-option-hint';
                hintEl.textContent = hint;
                item.appendChild(hintEl);
            }
            stereoMenu.appendChild(item);
        }
        const swap = document.createElement('button');
        swap.type = 'button';
        swap.className = 'storysplat-stereo-option storysplat-stereo-swap';
        swap.setAttribute('role', 'menuitemcheckbox');
        swap.setAttribute('aria-checked', 'false');
        swap.dataset.stereoSwap = 'true';
        swap.textContent = STEREO_LABELS.swapEyes;
        stereoMenu.appendChild(swap);
        container.appendChild(stereoMenu);
        elements.stereoButton = stereoBtn;
        elements.stereoMenu = stereoMenu;
    }
    // Create Waypoint List Dropdown (for quick navigation to waypoints). Waypoints marked
    // hideInMenu are left out (the tour still visits them); items keep their tour index. Extra
    // waypoints with showInMenu follow the tour waypoints, after a divider.
    const menuWaypoints = waypointMenuEntries(config.waypoints);
    const menuExtraWaypoints = extraWaypointMenuEntries(config.extraWaypoints);
    if (showWaypointList && menuWaypoints.length + menuExtraWaypoints.length > 0) {
        const waypointListContainer = document.createElement('div');
        waypointListContainer.className = 'storysplat-waypoint-list-container';
        waypointListContainer.innerHTML = `
      <button class="storysplat-waypoint-list-toggle" aria-label="${labels.waypoints}">
        <span class="storysplat-waypoint-list-label">${labels.waypoints}</span>
        <svg viewBox="0 0 24 24">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </button>
      <div class="storysplat-waypoint-list-dropdown"></div>
    `;
        // Items are built with textContent: names are authored text, never markup.
        const waypointDropdown = waypointListContainer.querySelector('.storysplat-waypoint-list-dropdown')!;
        for (const { index, name } of menuWaypoints) {
            const item = document.createElement('div');
            item.className = 'storysplat-waypoint-item';
            item.dataset.waypointIndex = String(index);
            item.textContent = name;
            waypointDropdown.appendChild(item);
        }
        if (menuWaypoints.length && menuExtraWaypoints.length) {
            const divider = document.createElement('div');
            divider.className = 'storysplat-waypoint-list-divider';
            divider.setAttribute('role', 'separator');
            waypointDropdown.appendChild(divider);
        }
        for (const { id, name } of menuExtraWaypoints) {
            const item = document.createElement('div');
            item.className = 'storysplat-waypoint-item storysplat-waypoint-item--extra';
            item.dataset.extraWaypointId = id;
            item.textContent = name;
            waypointDropdown.appendChild(item);
        }
        // Placement: pro top-right bar > classic top-right group (LEFT of the pill) >
        // container. When the dropdown has a custom uiLayout position it is anchored
        // absolutely to the viewer, so it must live on `container`, not inside the
        // positioned group/bar.
        const wpLayout = viewerTheme?.uiLayout;
        const wpHasCustomPos = !!(wpLayout?.elements?.waypointDropdown || wpLayout?.mobile?.waypointDropdown);
        const topBar = container.querySelector('.storysplat-top-right-bar');
        const topGroup = container.querySelector('.storysplat-top-right-group');
        if (wpHasCustomPos) {
            container.appendChild(waypointListContainer);
        } else if (topBar) {
            topBar.appendChild(waypointListContainer);
        } else if (topGroup) {
            topGroup.appendChild(waypointListContainer);
        } else {
            container.appendChild(waypointListContainer);
        }
        elements.waypointListContainer = waypointListContainer;
        // Setup toggle behavior
        const toggleBtn = waypointListContainer.querySelector('.storysplat-waypoint-list-toggle');
        const dropdown = waypointListContainer.querySelector('.storysplat-waypoint-list-dropdown');
        toggleBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleBtn.classList.toggle('open');
            dropdown?.classList.toggle('open');
        });
        // Close dropdown when clicking outside
        const onWaypointListOutsideClick = (e: MouseEvent) => {
            if (!waypointListContainer.contains(e.target as Node)) {
                toggleBtn?.classList.remove('open');
                dropdown?.classList.remove('open');
            }
        };
        document.addEventListener('click', onWaypointListOutsideClick);
        // Chain into the shared teardown so portal navigation / destroy releases the document
        // listener (otherwise one leaks per scene switch, pinning the detached UI subtree).
        {
            const prevCleanup = elements._cleanup;
            elements._cleanup = () => {
                if (prevCleanup) prevCleanup();
                document.removeEventListener('click', onWaypointListOutsideClick);
            };
        }
    }
    // Create Share Button
    if (showShareButton) {
        const shareBtn = document.createElement('button');
        shareBtn.className = 'storysplat-share-btn';
        shareBtn.setAttribute('aria-label', labels.share);
        shareBtn.setAttribute('title', labels.share);
        // Share icon (standard share/export icon) - built with DOM APIs
        const shareSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        shareSvg.setAttribute('viewBox', '0 0 24 24');
        shareSvg.style.width = '20px';
        shareSvg.style.height = '20px';
        shareSvg.style.fill = 'white';
        const sharePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        sharePath.setAttribute('d', 'M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z');
        shareSvg.appendChild(sharePath);
        shareBtn.appendChild(shareSvg);

        // Share lives in the top-right pill (created above), after fullscreen →
        // [fullscreen | share]. The pill owns the background; share is transparent.
        (topRightPill || container).appendChild(shareBtn);
        elements.shareButton = shareBtn;
    }
    // Photo button: opens the clean photo mode (see .storysplat-photo-mode). Sits left of
    // Share in the pill → [photo | share | fullscreen] (CSS order).
    if (showPhotoButton) {
        const photoBtn = document.createElement('button');
        photoBtn.className = 'storysplat-photo-btn';
        photoBtn.setAttribute('aria-label', PHOTO_MODE_LABELS.enter);
        photoBtn.setAttribute('title', PHOTO_MODE_LABELS.enter);
        photoBtn.innerHTML = PHOTO_ICON;
        (topRightPill || container).appendChild(photoBtn);
        elements.photoButton = photoBtn;
        // Viewfinder corner brackets, drawn over the scene only while photo mode is on.
        const frame = document.createElement('div');
        frame.className = 'storysplat-photo-frame';
        frame.setAttribute('aria-hidden', 'true');
        for (const corner of ['tl', 'tr', 'bl', 'br']) {
            const mark = document.createElement('span');
            mark.className = `storysplat-photo-corner storysplat-photo-corner--${corner}`;
            frame.appendChild(mark);
        }
        container.appendChild(frame);
        // Camera-style controls, stacked at the bottom: a subtle hint, the round shutter,
        // and the close button underneath it.
        const bar = document.createElement('div');
        bar.className = 'storysplat-photo-bar';
        bar.setAttribute('role', 'toolbar');
        bar.setAttribute('aria-label', PHOTO_MODE_LABELS.enter);
        const hint = document.createElement('span');
        hint.className = 'storysplat-photo-hint';
        hint.textContent = PHOTO_MODE_LABELS.hint;
        const take = document.createElement('button');
        take.type = 'button';
        take.className = 'storysplat-photo-take';
        take.setAttribute('aria-label', PHOTO_MODE_LABELS.take);
        take.setAttribute('title', PHOTO_MODE_LABELS.take);
        take.innerHTML = '<span class="storysplat-photo-shutter"></span>';
        const exit = document.createElement('button');
        exit.type = 'button';
        exit.className = 'storysplat-photo-exit';
        exit.setAttribute('aria-label', PHOTO_MODE_LABELS.exit);
        exit.setAttribute('title', PHOTO_MODE_LABELS.exit);
        // An SVG cross centres exactly inside the round hover background, unlike the × glyph.
        exit.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
        // Hint and close sit together under the shutter, over a soft fade at the bottom edge.
        const fade = document.createElement('div');
        fade.className = 'storysplat-photo-fade';
        fade.setAttribute('aria-hidden', 'true');
        container.appendChild(fade);
        const caption = document.createElement('div');
        caption.className = 'storysplat-photo-caption';
        caption.append(hint, exit);
        bar.append(take, caption);
        container.appendChild(bar);
        elements.photoBar = bar;
        elements.photoTakeButton = take;
        elements.photoExitButton = exit;
    }
    // Hide the pill if it ended up empty (e.g. both fullscreen + share hidden, or
    // fullscreen was extracted for custom placement leaving nothing). connectUIToViewer
    // re-checks after it hides buttons.
    syncButtonGroupVisibility(topRightPill);

    // Create Scene Navigation Menu (when portals or custom links exist)
    const hasPortals = config.portals && config.portals.length > 0;
    const hasCustomLinks = sceneMenuLinks && sceneMenuLinks.length > 0;
    // Optional logo shown at the top of the menu list (both classic and
    // sidebar modes). Text titles name the button and header instead.
    const buildSceneMenuLogo = (): HTMLElement | null => {
        if (menuTitleImageUrl) {
            const titleEl = document.createElement('div');
            titleEl.className = 'storysplat-scene-menu-title storysplat-scene-menu-title--image';
            const img = document.createElement('img');
            img.className = 'storysplat-scene-menu-title-img';
            img.src = menuTitleImageUrl;
            img.alt = menuTitle || '';
            // Eager (not lazy): the menu is built into a display:none container at scene load, so
            // 'lazy' would defer the fetch until first open → ~1s download + a visible top-to-bottom
            // draw. Eager fetches on insertion even while hidden; createViewer also warms decode().
            img.loading = 'eager';
            img.decoding = 'async';
            titleEl.appendChild(img);
            return titleEl;
        }

        return null;
    };
    if ((hasPortals || hasCustomLinks) && showSceneMenu !== false) {
      if (sceneMenuStyle === 'sidebar') {
        // ─── SIDEBAR PULLOUT ───
        // Full-height, left-edge slide-out panel mirroring the editor's settings
        // pullout. Reuses the same data + buildSceneMenuDom() body renderer; only
        // the chrome around it differs from the classic dropdown.
        const scenesLabel = menuTitle?.trim() || labels.scenes;

        // Toggle button — top-left hamburger, same affordance as classic.
        // Appended to `container` (not the panel) so it stays pinned + clickable
        // when the panel is collapsed.
        const toggleBtn = document.createElement('button');
        toggleBtn.className = 'storysplat-scene-menu-toggle storysplat-scene-menu-toggle--sidebar';
        toggleBtn.setAttribute('aria-label', scenesLabel);
        const hamburgerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        hamburgerSvg.setAttribute('viewBox', '0 0 24 24');
        const hamburgerPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        hamburgerPath.setAttribute('d', 'M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z');
        hamburgerSvg.appendChild(hamburgerPath);
        toggleBtn.appendChild(hamburgerSvg);
        const labelSpan = document.createElement('span');
        labelSpan.className = 'storysplat-scene-menu-label';
        labelSpan.textContent = scenesLabel;
        toggleBtn.appendChild(labelSpan);

        // Panel — reuse the scene-menu-container class (so AR-hide + teardown
        // selectors still match) plus a sidebar modifier.
        const sidebar = document.createElement('div');
        sidebar.className = 'storysplat-scene-menu-container storysplat-scene-menu-container--sidebar';

        // Header row: title left, X close right (mirrors Sidebar.tsx ContentHeader).
        const header = document.createElement('div');
        header.className = 'storysplat-scene-menu-sidebar-header';
        const headerTitle = document.createElement('span');
        headerTitle.textContent = scenesLabel;
        header.appendChild(headerTitle);
        const closeBtn = document.createElement('button');
        closeBtn.className = 'storysplat-scene-menu-sidebar-close';
        closeBtn.setAttribute('aria-label', 'Close');
        const closeSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        closeSvg.setAttribute('viewBox', '0 0 24 24');
        closeSvg.setAttribute('width', '18');
        closeSvg.setAttribute('height', '18');
        const closePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        closePath.setAttribute('d', 'M18 6L6 18M6 6l12 12');
        closePath.setAttribute('stroke', 'currentColor');
        closePath.setAttribute('stroke-width', '2');
        closePath.setAttribute('stroke-linecap', 'round');
        closePath.setAttribute('fill', 'none');
        closeSvg.appendChild(closePath);
        closeBtn.appendChild(closeSvg);
        header.appendChild(closeBtn);
        sidebar.appendChild(header);

        // Body — scrollable, populated by the SAME renderer as classic.
        const body = document.createElement('div');
        body.className = 'storysplat-scene-menu-sidebar-body';
        const sidebarTitle = buildSceneMenuLogo();
        if (menuTitleImageUrl && sidebarTitle) body.appendChild(sidebarTitle);
        buildSceneMenuDom(body, config.portals || [], sceneMenuLinks || [], startCategoriesCollapsed, sceneTitleText, showSceneMenuPreviewImages, sceneId, currentSpaceMenuPath, currentSpaceMenuOrder, sceneMenuFolderOrder);
        sidebar.appendChild(body);

        container.appendChild(sidebar);
        container.appendChild(toggleBtn);
        elements.sceneMenuContainer = sidebar;

        // Only-one-open coordination: register a close, dismiss others on open.
        const closeSidebarMenu = () => {
            sidebar.classList.remove('open');
            toggleBtn.classList.remove('open');
        };
        elements.exclusiveTools?.register('sceneMenu', closeSidebarMenu);

        // Open via hamburger toggle
        toggleBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const willOpen = !sidebar.classList.contains('open');
            sidebar.classList.toggle('open', willOpen);
            toggleBtn.classList.toggle('open', willOpen);
            if (willOpen) elements.exclusiveTools?.closeOthers('sceneMenu');
        });

        // Close via X button
        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            closeSidebarMenu();
        });

        // Folder collapse/expand
        body.querySelectorAll('.storysplat-scene-menu-folder-header').forEach((folderHeader) => {
            folderHeader.addEventListener('click', (e) => {
                e.stopPropagation();
                folderHeader.classList.toggle('collapsed');
                const children = (folderHeader as HTMLElement).nextElementSibling;
                if (children) children.classList.toggle('collapsed');
            });
        });

        // Close on outside click (include the toggle in the contains-check so
        // clicking the hamburger doesn't immediately re-close the panel).
        const onOutsideClick = (e: MouseEvent) => {
            if (!sidebar.contains(e.target as Node) && !toggleBtn.contains(e.target as Node)) {
                sidebar.classList.remove('open');
                toggleBtn.classList.remove('open');
            }
        };
        document.addEventListener('click', onOutsideClick);

        // Close on Escape
        const onEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                sidebar.classList.remove('open');
                toggleBtn.classList.remove('open');
            }
        };
        document.addEventListener('keydown', onEscape);

        // Register listener teardown alongside the toolbar-reflow cleanup so the
        // viewer's destroy path releases them across portal-navigation transitions.
        const prevCleanup = elements._cleanup;
        elements._cleanup = () => {
            if (prevCleanup) prevCleanup();
            document.removeEventListener('click', onOutsideClick);
            document.removeEventListener('keydown', onEscape);
        };
      } else {
        const sceneMenuContainer = document.createElement('div');
        sceneMenuContainer.className = 'storysplat-scene-menu-container';
        // Position is set by the top-left button positioning block below
        const scenesLabel = menuTitle?.trim() || labels.scenes;

        // Toggle button: hamburger icon + label
        const toggleBtn = document.createElement('button');
        toggleBtn.className = 'storysplat-scene-menu-toggle';
        toggleBtn.setAttribute('aria-label', scenesLabel);
        const hamburgerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        hamburgerSvg.setAttribute('viewBox', '0 0 24 24');
        const hamburgerPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        hamburgerPath.setAttribute('d', 'M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z');
        hamburgerSvg.appendChild(hamburgerPath);
        toggleBtn.appendChild(hamburgerSvg);
        const labelSpan = document.createElement('span');
        labelSpan.className = 'storysplat-scene-menu-label';
        labelSpan.textContent = scenesLabel;
        toggleBtn.appendChild(labelSpan);

        const dropdown = document.createElement('div');
        dropdown.className = 'storysplat-scene-menu-dropdown';
        const dropdownTitle = buildSceneMenuLogo();
        if (menuTitleImageUrl && dropdownTitle) dropdown.appendChild(dropdownTitle);
        buildSceneMenuDom(dropdown, config.portals || [], sceneMenuLinks || [], startCategoriesCollapsed, sceneTitleText, showSceneMenuPreviewImages, sceneId, currentSpaceMenuPath, currentSpaceMenuOrder, sceneMenuFolderOrder);

        sceneMenuContainer.appendChild(toggleBtn);
        sceneMenuContainer.appendChild(dropdown);
        container.appendChild(sceneMenuContainer);
        elements.sceneMenuContainer = sceneMenuContainer;

        // Only-one-open coordination: register a close, dismiss others on open.
        elements.exclusiveTools?.register('sceneMenu', () => {
            toggleBtn.classList.remove('open');
            dropdown.classList.remove('open');
        });

        // Toggle open/close
        toggleBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const willOpen = !dropdown.classList.contains('open');
            toggleBtn.classList.toggle('open', willOpen);
            dropdown.classList.toggle('open', willOpen);
            if (willOpen) elements.exclusiveTools?.closeOthers('sceneMenu');
        });

        // Folder collapse/expand
        dropdown.querySelectorAll('.storysplat-scene-menu-folder-header').forEach((header) => {
            header.addEventListener('click', (e) => {
                e.stopPropagation();
                header.classList.toggle('collapsed');
                const children = (header as HTMLElement).nextElementSibling;
                if (children) children.classList.toggle('collapsed');
            });
        });

        // Close on outside click
        const onSceneMenuOutsideClick = (e: MouseEvent) => {
            if (!sceneMenuContainer.contains(e.target as Node)) {
                toggleBtn.classList.remove('open');
                dropdown.classList.remove('open');
            }
        };
        document.addEventListener('click', onSceneMenuOutsideClick);
        // Same teardown chain as the sidebar variant above — this dropdown branch previously
        // leaked its document listener on every portal hop / scene switch.
        {
            const prevCleanup = elements._cleanup;
            elements._cleanup = () => {
                if (prevCleanup) prevCleanup();
                document.removeEventListener('click', onSceneMenuOutsideClick);
            };
        }
      }
    }

    // Create Measurement Tape Measure Button (top-left row, next to help/mute/relight)
    if (measurementsEnabled) {
        // Set custom measurement color CSS property
        if (measurementColor) {
            container.style.setProperty('--storysplat-measure-color', measurementColor);
        }
        const measureBtn = document.createElement('button');
        measureBtn.className = 'storysplat-measure-btn';
        measureBtn.setAttribute('aria-label', getButtonLabel(buttonLabels, 'measureButton'));
        // Position is set by the top-left button positioning block below
        // Tape measure / ruler SVG icon
        const rulerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        rulerSvg.setAttribute('viewBox', '0 0 24 24');
        const rulerPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        rulerPath.setAttribute('d', 'M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h2v4h2V8h2v4h2V8h2v4h2V8h2v4h2V8h2v8z');
        rulerSvg.appendChild(rulerPath);
        measureBtn.appendChild(rulerSvg);
        // Measurement overlay canvas (for drawing lines)
        const measureCanvas = document.createElement('canvas');
        measureCanvas.className = 'storysplat-measure-canvas';
        // Measurement list dropdown
        const measureList = document.createElement('div');
        measureList.className = 'storysplat-measure-list';
        toolbar.appendChild(measureBtn);
        container.appendChild(measureCanvas);
        container.appendChild(measureList);
        elements.measureButton = measureBtn;
        elements.measureCanvas = measureCanvas;
        elements.measureList = measureList;
    }

    // Create the Layers menu Button + Panel (top-left toolbar). createViewer renders the
    // panel's contents and wires the clicks (layersPanel.ts); this only places the chrome.
    if (layersTitle) createLayersChrome(elements, container, layersTitle);

    // Create Skins Button + Dropdown (AI re-skins toggle, shown when config.skins has entries)
    if (config.skins && Array.isArray(config.skins) && config.skins.length > 0) {
        const skinsBtn = document.createElement('button');
        skinsBtn.className = 'storysplat-skins-btn';
        skinsBtn.setAttribute('aria-label', 'Virtual Staging');
        // Photo / image icon (rectangle with sun + mountain)
        const photoSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        photoSvg.setAttribute('viewBox', '0 0 24 24');
        const photoPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        photoPath.setAttribute('d', 'M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z');
        photoSvg.appendChild(photoPath);
        skinsBtn.appendChild(photoSvg);
        toolbar.appendChild(skinsBtn);

        const skinsDropdown = document.createElement('div');
        skinsDropdown.className = 'storysplat-skins-dropdown';
        const header = document.createElement('div');
        header.className = 'storysplat-skins-dropdown-header';
        header.textContent = 'Virtual Staging';
        skinsDropdown.appendChild(header);

        const list = document.createElement('div');
        list.className = 'storysplat-skins-list';
        skinsDropdown.appendChild(list);

        config.skins.forEach((skin, idx) => {
            const item = document.createElement('div');
            item.className = 'storysplat-skin-item';
            item.setAttribute('data-skin-id', skin.id);
            item.setAttribute('data-skin-index', String(idx));
            // -1 / absent means "available at every waypoint"; otherwise the item
            // is only surfaced when the tour is on that waypoint.
            if (typeof skin.waypointIndex === 'number') {
                item.setAttribute('data-skin-waypoint', String(skin.waypointIndex));
            }
            const thumb = document.createElement('img');
            thumb.className = 'storysplat-skin-item-thumb';
            // Lazy: the dropdown is display:none until opened, so eager thumbs (or the
            // full-resolution imageUrl fallback) would race the splat/chunk downloads at startup.
            thumb.loading = 'lazy';
            thumb.decoding = 'async';
            thumb.src = skin.thumbnailUrl || skin.imageUrl;
            thumb.alt = '';
            thumb.draggable = false;
            const promptEl = document.createElement('div');
            promptEl.className = 'storysplat-skin-item-prompt';
            promptEl.textContent = skin.name || skin.prompt || `Staging ${idx + 1}`;
            item.appendChild(thumb);
            item.appendChild(promptEl);
            list.appendChild(item);
        });

        const opacityRow = document.createElement('div');
        opacityRow.className = 'storysplat-skin-opacity-row';
        const opacityLabel = document.createElement('label');
        opacityLabel.textContent = 'Blend';
        const opacityInput = document.createElement('input');
        opacityInput.type = 'range';
        opacityInput.min = '0';
        opacityInput.max = '100';
        opacityInput.value = '100';
        opacityRow.appendChild(opacityLabel);
        opacityRow.appendChild(opacityInput);
        skinsDropdown.appendChild(opacityRow);

        const exitBtn = document.createElement('button');
        exitBtn.className = 'storysplat-skin-exit-btn';
        exitBtn.textContent = 'Exit overlay';
        exitBtn.style.display = 'none';
        skinsDropdown.appendChild(exitBtn);

        container.appendChild(skinsDropdown);

        // Skin overlay (created once, src swapped on activation).
        // Wrapper owns the fade transition + aspect-ratio; img carries the
        // slider-controlled opacity without a transition so slider is snappy.
        const skinOverlayWrapper = document.createElement('div');
        skinOverlayWrapper.className = 'storysplat-skin-overlay-wrapper';
        const skinOverlay = document.createElement('img');
        skinOverlay.className = 'storysplat-skin-overlay';
        skinOverlay.alt = '';
        skinOverlay.draggable = false;
        skinOverlay.addEventListener('error', () => {
            console.error('[StorySplat Viewer] Skin overlay image failed to load:', skinOverlay.src);
        });
        skinOverlayWrapper.appendChild(skinOverlay);
        container.appendChild(skinOverlayWrapper);

        elements.skinsButton = skinsBtn;
        elements.skinsDropdown = skinsDropdown;
        elements.skinOverlayWrapper = skinOverlayWrapper;
        elements.skinOverlay = skinOverlay;
        elements.skinOpacitySlider = opacityInput;
        elements.skinExitButton = exitBtn;
    }

    // "Ask this scene" guide: toolbar button + empty chat panel. createViewer renders the
    // panel's contents and runs the conversation (sceneGuide.ts); this only places the chrome.
    if (sceneGuideName) {
        const guideLabel = SCENE_GUIDE_LABELS.ask.replace('{name}', sceneGuideName);
        const guideBtn = document.createElement('button');
        guideBtn.type = 'button';
        guideBtn.className = 'storysplat-guide-btn';
        guideBtn.setAttribute('aria-label', guideLabel);
        guideBtn.setAttribute('title', guideLabel);
        guideBtn.setAttribute('aria-haspopup', 'dialog');
        guideBtn.setAttribute('aria-expanded', 'false');
        guideBtn.innerHTML = SCENE_GUIDE_ICON;
        const guideBtnLabel = document.createElement('span');
        guideBtnLabel.className = 'storysplat-guide-btn-label';
        guideBtnLabel.textContent = SCENE_GUIDE_LABELS.button;
        guideBtn.appendChild(guideBtnLabel);
        toolbar.appendChild(guideBtn);
        const guidePanel = document.createElement('div');
        guidePanel.className = 'storysplat-guide-panel';
        container.appendChild(guidePanel);
        elements.sceneGuideButton = guideBtn;
        elements.sceneGuidePanel = guidePanel;
    }

    // Create VR Button (hidden by default, shown when VR is available: every scene offers it)
    const vrBtn = document.createElement('button');
    vrBtn.className = 'storysplat-xr-btn storysplat-vr-btn';
    vrBtn.setAttribute('aria-label', labels.vr);
    vrBtn.title = labels.vr;
    vrBtn.textContent = labels.vr;
    container.appendChild(vrBtn);
    elements.vrButton = vrBtn;
    // Create AR Button (hidden by default, shown when AR is available)
    const arBtn = document.createElement('button');
    arBtn.className = 'storysplat-xr-btn storysplat-ar-btn';
    arBtn.setAttribute('aria-label', labels.ar);
    arBtn.title = `${labels.ar} (beta)`;
    arBtn.textContent = labels.ar;
    container.appendChild(arBtn);
    elements.arButton = arBtn;
    // Create Help Button and Panel (optional in minimal)
    if (showHelpButton) {
        const helpBtn = document.createElement('button');
        helpBtn.className = 'storysplat-help-btn';
        helpBtn.setAttribute('title', labels.helpTitle);
        helpBtn.textContent = '?';
        toolbar.insertBefore(helpBtn, toolbar.firstChild);
        elements.helpButton = helpBtn;
        const helpPanel = document.createElement('div');
        helpPanel.className = 'storysplat-help-panel';
        // Build help panel with tabbed layout using safe DOM methods
        const helpTitle = document.createElement('h3');
        helpTitle.textContent = labels.helpTitle;
        helpPanel.appendChild(helpTitle);
        // Tab bar
        const tabBar = document.createElement('div');
        tabBar.className = 'storysplat-help-tabs';
        const tabData: { key: string; label: string }[] = [
            { key: 'tour', label: labels.tour },
            { key: 'explore', label: labels.explore },
        ];
        tabData.forEach((td, i) => {
            const tab = document.createElement('button');
            tab.className = 'storysplat-help-tab' + (i === 0 ? ' active' : '');
            tab.setAttribute('data-tab', td.key);
            tab.textContent = td.label;
            tabBar.appendChild(tab);
        });
        helpPanel.appendChild(tabBar);
        // Helper to build a key-description row
        const makeRow = (key: string, desc: string) => {
            const row = document.createElement('div');
            row.className = 'storysplat-help-row';
            const keySpan = document.createElement('span');
            keySpan.className = 'storysplat-help-key';
            keySpan.textContent = key;
            const descSpan = document.createElement('span');
            descSpan.className = 'storysplat-help-desc';
            descSpan.textContent = desc.replace(/^[\u2022\s]+/, '');
            row.appendChild(keySpan);
            row.appendChild(descSpan);
            return row;
        };
        // Helper to build a tab content section
        const makeTabContent = (tabKey: string, description: string, rows: [string, string][], isActive: boolean) => {
            const content = document.createElement('div');
            content.className = 'storysplat-help-tab-content' + (isActive ? ' active' : '');
            content.setAttribute('data-tab', tabKey);
            const desc = document.createElement('p');
            desc.style.cssText = 'margin-bottom:6px;color:rgba(255,255,255,0.5)';
            desc.textContent = description;
            content.appendChild(desc);
            rows.forEach(([k, d]) => content.appendChild(makeRow(k, d)));
            return content;
        };
        helpPanel.appendChild(makeTabContent('tour', labels.helpTourDesc, [
            ['Scroll', labels.helpTourScroll],
            ['Drag', labels.helpTourDrag],
        ], true));
        const movementHelpKey = movementKeyDisplay === 'arrows' ? 'Arrows' : 'WASD';
        const exploreMovementHelp = movementKeyDisplay === 'arrows'
            && labels.helpExploreWASD === DEFAULT_BUTTON_LABELS.helpExploreWASD
            ? 'Arrows/WASD/QE - Move camera'
            : labels.helpExploreWASD;
        const walkMovementHelp = movementKeyDisplay === 'arrows'
            && labels.helpWalkWASD === DEFAULT_BUTTON_LABELS.helpWalkWASD
            ? 'Arrows/WASD - Move'
            : labels.helpWalkWASD;
        const exploreRows: [string, string][] = [
            ['LMB', labels.helpExploreLMB],
            ['RMB', labels.helpExploreRMB],
            [movementHelpKey, exploreMovementHelp],
            ['Shift', labels.helpExploreShift],
            ['Scroll', labels.helpExploreScroll],
            ['Dbl-click', labels.helpExploreDblClick],
        ];
        // Append walk controls under explore when walk mode is available
        if (allowedCameraModes.includes('walk')) {
            exploreRows.push(
                ['', `— ${labels.walk} —`],
                ['Click', labels.helpWalkClick],
                [movementHelpKey, walkMovementHelp],
                ['Mouse', labels.helpWalkMouse],
                ['Shift', labels.helpWalkShift],
                ['Space', labels.helpWalkSpace],
            );
        }
        helpPanel.appendChild(makeTabContent('explore', labels.helpExploreDesc, exploreRows, false));
        // Performance mode toggle (end-user, per-device). Wired in connectUIToViewer.
        const perfLabel = document.createElement('label');
        perfLabel.className = 'storysplat-help-perf';
        const perfInput = document.createElement('input');
        perfInput.type = 'checkbox';
        perfInput.setAttribute('aria-label', PERFORMANCE_MODE_LABEL);
        const perfText = document.createElement('span');
        perfText.textContent = PERFORMANCE_MODE_LABEL;
        perfLabel.appendChild(perfInput);
        perfLabel.appendChild(perfText);
        helpPanel.appendChild(perfLabel);
        const perfDesc = document.createElement('p');
        perfDesc.className = 'storysplat-help-perf-desc';
        perfDesc.textContent = PERFORMANCE_MODE_DESC;
        helpPanel.appendChild(perfDesc);
        elements.performanceToggle = perfInput;
        container.appendChild(helpPanel);
        elements.helpPanel = helpPanel;
        // Wire up help tab switching
        const helpTabs = helpPanel.querySelectorAll('.storysplat-help-tab');
        const helpTabContents = helpPanel.querySelectorAll('.storysplat-help-tab-content');
        helpTabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                const target = (e.currentTarget as HTMLElement).getAttribute('data-tab');
                helpTabs.forEach(t => t.classList.remove('active'));
                helpTabContents.forEach(c => c.classList.remove('active'));
                (e.currentTarget as HTMLElement).classList.add('active');
                helpPanel.querySelector(`.storysplat-help-tab-content[data-tab="${target}"]`)?.classList.add('active');
            });
        });
    }
    // Independent of Help visibility so visitors can always see and disable performance mode.
    const performanceNotice = document.createElement('div');
    performanceNotice.className = 'storysplat-performance-notice';
    performanceNotice.hidden = true;
    const performanceStatus = document.createElement('span');
    performanceStatus.setAttribute('role', 'status');
    performanceStatus.textContent = 'Performance mode is on';
    const performanceOffButton = document.createElement('button');
    performanceOffButton.type = 'button';
    performanceOffButton.textContent = 'Turn off';
    performanceOffButton.setAttribute('aria-label', 'Turn off performance mode');
    performanceOffButton.title = 'Restore full quality';
    performanceNotice.append(performanceStatus, performanceOffButton);
    container.appendChild(performanceNotice);
    elements.performanceNotice = performanceNotice;
    elements.performanceOffButton = performanceOffButton;
    const previousCleanup = elements._cleanup;
    elements._cleanup = () => {
        previousCleanup?.();
        performanceNotice.remove();
    };

    // Create Hotspot Popup (always created, shown when needed)
    // Note: No overlay - matching BabylonJS export behavior
    const hotspotPopup = document.createElement('div');
    hotspotPopup.className = 'storysplat-hotspot-popup';
    hotspotPopup.id = 'hotspotContent'; // Match BabylonJS export ID
    hotspotPopup.innerHTML = `
    <div class="storysplat-hotspot-popup-handle" aria-hidden="true"></div>
    <h2 class="storysplat-hotspot-popup-title"></h2>
    <div class="storysplat-hotspot-popup-content"></div>
    <button class="storysplat-hotspot-popup-close">${labels.close}</button>
  `;
    container.appendChild(hotspotPopup);
    // Scrim behind a popup rendered as a bottom sheet (phones); tapping it closes the popup.
    const popupScrim = document.createElement('div');
    popupScrim.className = 'storysplat-hotspot-popup-scrim';
    container.appendChild(popupScrim);
    elements.hotspotPopup = hotspotPopup;
    // Close popup on close button click
    const closeBtn = hotspotPopup.querySelector('.storysplat-hotspot-popup-close') as HTMLElement | null;
    closeBtn?.addEventListener('click', () => {
        hotspotPopup.classList.remove('visible', 'fullscreen');
        syncPopupDrawerInsets(container);
    });
    // A popup docked as a drawer pushes the top-right buttons aside (syncDrawerInsets). Every way
    // it opens or closes (this button, the scrim, drag-to-dismiss, closeHotspot(), another hotspot)
    // changes its class, so re-measure on that: a missed close left the buttons stuck mid-screen.
    if (typeof MutationObserver !== 'undefined') {
        let wasVisible = hotspotPopup.classList.contains('visible');
        new MutationObserver(() => {
            const visible = hotspotPopup.classList.contains('visible');
            if (visible === wasVisible) return;
            wasVisible = visible;
            syncPopupDrawerInsets(container);
        }).observe(hotspotPopup, { attributes: true, attributeFilter: ['class'] });
    }
    popupScrim.addEventListener('click', () => closeBtn?.click());
    const popupHandle = hotspotPopup.querySelector('.storysplat-hotspot-popup-handle') as HTMLElement;
    attachSheetDrag(hotspotPopup, popupHandle, () => getComputedStyle(popupHandle).display !== 'none', () => closeBtn?.click());
    // Create Portal Confirmation Popup
    const portalPopup = document.createElement('div');
    portalPopup.className = 'storysplat-portal-popup';
    portalPopup.innerHTML = `
    <div class="storysplat-portal-popup-title"></div>
    <div class="storysplat-portal-popup-actions">
      <button class="storysplat-portal-popup-btn storysplat-portal-popup-confirm">${labels.yes}</button>
      <button class="storysplat-portal-popup-btn storysplat-portal-popup-cancel">${labels.cancel}</button>
    </div>
  `;
    container.appendChild(portalPopup);
    elements.portalPopup = portalPopup;
    // Create Virtual Joystick (for mobile explore mode)
    const joystick = document.createElement('div');
    joystick.className = 'storysplat-joystick-container';
    joystick.innerHTML = `
    <div class="storysplat-joystick-base"></div>
    <div class="storysplat-joystick-thumb"></div>
  `;
    container.appendChild(joystick);
    elements.joystick = joystick;
    elements.joystickThumb = joystick.querySelector('.storysplat-joystick-thumb') as HTMLElement;
    // Create Look Zone indicator (right side)
    const lookZone = document.createElement('div');
    lookZone.className = 'storysplat-look-zone';
    lookZone.innerHTML = `
    <div class="storysplat-look-zone-icon">
      <svg viewBox="0 0 24 24">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
      </svg>
    </div>
  `;
    container.appendChild(lookZone);
    elements.lookZone = lookZone;
    // Create WASD+QE Key Hint (desktop fly mode - replaces joystick position)
    const wasdHint = document.createElement('div');
    wasdHint.className = 'storysplat-wasd-hint';
    wasdHint.dataset.movementKeyDisplay = movementKeyDisplay;
    // Double-click to refocus row (above keyboard keys, orbit-hint style)
    const dblClickRow = document.createElement('div');
    dblClickRow.className = 'storysplat-orbit-hint-row';
    dblClickRow.style.marginBottom = '6px';
    const flyDblIcon = document.createElement('div');
    flyDblIcon.className = 'storysplat-orbit-hint-icon';
    const flyDblSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    flyDblSvg.setAttribute('viewBox', '0 0 24 24');
    const flyDblPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    flyDblPath.setAttribute('d', 'M13.64 21.97C13.14 22.21 12.54 22 12.31 21.5L10.13 16.76L7.62 18.78C7.45 18.92 7.24 19 7 19C6.44 19 6 18.56 6 18V3C6 2.44 6.44 2 7 2C7.24 2 7.47 2.09 7.64 2.23L7.65 2.22L19.14 11.86C19.57 12.22 19.31 12.92 18.76 12.96L14.78 13.21L16.97 17.95C17.21 18.45 17 19.05 16.5 19.28L13.64 21.97Z');
    flyDblSvg.appendChild(flyDblPath);
    flyDblIcon.appendChild(flyDblSvg);
    dblClickRow.appendChild(flyDblIcon);
    const isTouchForFly = isTouchLikeDevice();
    const flyTapMode = config.refocusTapMode || 'single';
    const clickVerb = isTouchForFly
        ? (flyTapMode === 'single' ? 'Tap' : 'Double-tap')
        : (flyTapMode === 'single' ? 'Click' : 'Double-click');
    // The row + mouse icon show in BOTH modes; only the label text swaps:
    // fly = "… to refocus", walk = "… to move" (click-to-move). Toggled by fly-only/walk-only classes.
    const flyDblLabel = document.createElement('div');
    flyDblLabel.className = 'storysplat-orbit-hint-label storysplat-wasd-fly-only';
    flyDblLabel.textContent = `${clickVerb} to refocus`;
    dblClickRow.appendChild(flyDblLabel);
    const walkDblLabel = document.createElement('div');
    walkDblLabel.className = 'storysplat-orbit-hint-label storysplat-wasd-walk-only';
    walkDblLabel.textContent = `${clickVerb} to move`;
    dblClickRow.appendChild(walkDblLabel);
    wasdHint.appendChild(dblClickRow);
    // Build WASD+QE hint using DOM methods
    const movementKeyLabels: Record<string, { arrow: string; arrowCode: string; direction: string }> = {
        W: { arrow: '\u2191', arrowCode: 'ArrowUp', direction: 'Up' },
        A: { arrow: '\u2190', arrowCode: 'ArrowLeft', direction: 'Left' },
        S: { arrow: '\u2193', arrowCode: 'ArrowDown', direction: 'Down' },
        D: { arrow: '\u2192', arrowCode: 'ArrowRight', direction: 'Right' },
    };
    const setMovementKeyLabel = (keyEl: HTMLElement, key: string) => {
        const movement = movementKeyLabels[key];
        if (!movement) {
            keyEl.textContent = key;
            return;
        }
        keyEl.dataset.wasdLabel = key;
        keyEl.dataset.arrowLabel = movement.arrow;
        keyEl.dataset.arrowKeycode = movement.arrowCode;
        keyEl.textContent = movementKeyDisplay === 'arrows' ? movement.arrow : key;
        keyEl.setAttribute('aria-label', movementKeyDisplay === 'arrows'
            ? `${movement.direction} arrow movement key; ${key} also supported`
            : `${key} movement key; ${movement.direction} arrow also supported`);
    };
    // Row 1: Q W E
    const topRow = document.createElement('div');
    topRow.className = 'storysplat-wasd-hint-row';
    ['Q', 'W', 'E'].forEach(key => {
        const keyEl = document.createElement('div');
        // Q/E are fly-only (up/down). Hidden in walk mode, leaving the classic W-over-ASD shape.
        keyEl.className = key === 'W' ? 'storysplat-wasd-key' : 'storysplat-wasd-key storysplat-wasd-fly-only';
        setMovementKeyLabel(keyEl, key);
        keyEl.setAttribute('data-keycode', `Key${key}`);
        topRow.appendChild(keyEl);
    });
    wasdHint.appendChild(topRow);
    // Row 2: A S D
    const bottomRow = document.createElement('div');
    bottomRow.className = 'storysplat-wasd-hint-row';
    ['A', 'S', 'D'].forEach(key => {
        const keyEl = document.createElement('div');
        keyEl.className = 'storysplat-wasd-key';
        setMovementKeyLabel(keyEl, key);
        keyEl.setAttribute('data-keycode', `Key${key}`);
        bottomRow.appendChild(keyEl);
    });
    wasdHint.appendChild(bottomRow);
    const moveLabel = document.createElement('div');
    moveLabel.className = 'storysplat-wasd-hint-label';
    moveLabel.textContent = 'Move';
    wasdHint.appendChild(moveLabel);
    // Space → Jump row (walk mode ONLY; the !important display rules keep it out of fly mode).
    // "Jump" sits inline to the right of the Space key.
    const jumpRow = document.createElement('div');
    jumpRow.className = 'storysplat-wasd-hint-row storysplat-wasd-walk-only';
    jumpRow.style.marginTop = '6px';
    jumpRow.style.alignItems = 'center';
    const spaceKey = document.createElement('div');
    spaceKey.className = 'storysplat-wasd-key storysplat-wasd-key--space';
    spaceKey.textContent = 'Space';
    spaceKey.setAttribute('data-keycode', 'Space');
    jumpRow.appendChild(spaceKey);
    const jumpText = document.createElement('span');
    jumpText.className = 'storysplat-wasd-hint-label';
    jumpText.textContent = 'Jump';
    jumpText.style.margin = '0 0 0 6px';
    jumpRow.appendChild(jumpText);
    wasdHint.appendChild(jumpRow);
    container.appendChild(wasdHint);
    elements.wasdHint = wasdHint;
    // Create Orbit Hint (explore orbit mode — replaces WASD hint position)
    const orbitHint = document.createElement('div');
    orbitHint.className = 'storysplat-orbit-hint';
    // Row 1: drag icon + "Drag to rotate"
    const orbitRow1 = document.createElement('div');
    orbitRow1.className = 'storysplat-orbit-hint-row';
    const dragIcon = document.createElement('div');
    dragIcon.className = 'storysplat-orbit-hint-icon';
    const dragSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    dragSvg.setAttribute('viewBox', '0 0 24 24');
    const dragPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    // Hand/drag cursor icon
    dragPath.setAttribute('d', 'M13 6v5h5V7.75L22.25 12 18 16.25V13h-5v5h3.25L12 22.25 7.75 18H11v-5H6v3.25L1.75 12 6 7.75V11h5V6H7.75L12 1.75 16.25 6H13z');
    dragSvg.appendChild(dragPath);
    dragIcon.appendChild(dragSvg);
    orbitRow1.appendChild(dragIcon);
    const dragLabel = document.createElement('div');
    dragLabel.className = 'storysplat-orbit-hint-label';
    dragLabel.textContent = 'Drag to rotate';
    orbitRow1.appendChild(dragLabel);
    orbitHint.appendChild(orbitRow1);
    // Row 2: double-click icon + "Double-click to refocus" (hidden while the pivot is locked)
    const orbitRow2 = document.createElement('div');
    orbitRow2.className = 'storysplat-orbit-hint-row storysplat-orbit-hint-refocus';
    const dblIcon = document.createElement('div');
    dblIcon.className = 'storysplat-orbit-hint-icon';
    const dblSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    dblSvg.setAttribute('viewBox', '0 0 24 24');
    const dblPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    // Mouse/click icon
    dblPath.setAttribute('d', 'M13.64 21.97C13.14 22.21 12.54 22 12.31 21.5L10.13 16.76L7.62 18.78C7.45 18.92 7.24 19 7 19C6.44 19 6 18.56 6 18V3C6 2.44 6.44 2 7 2C7.24 2 7.47 2.09 7.64 2.23L7.65 2.22L19.14 11.86C19.57 12.22 19.31 12.92 18.76 12.96L14.78 13.21L16.97 17.95C17.21 18.45 17 19.05 16.5 19.28L13.64 21.97Z');
    dblSvg.appendChild(dblPath);
    dblIcon.appendChild(dblSvg);
    orbitRow2.appendChild(dblIcon);
    const dblLabel = document.createElement('div');
    dblLabel.className = 'storysplat-orbit-hint-label';
    const isTouchForOrbit = isTouchLikeDevice();
    const orbitTapMode = config.refocusTapMode || 'single';
    dblLabel.textContent = isTouchForOrbit
        ? (orbitTapMode === 'single' ? 'Tap to refocus' : 'Double-tap to refocus')
        : (orbitTapMode === 'single' ? 'Click to refocus' : 'Double-click to refocus');
    orbitRow2.appendChild(dblLabel);
    orbitHint.appendChild(orbitRow2);
    container.appendChild(orbitHint);
    elements.orbitHint = orbitHint;

    // Create Double-tap Hint (explore mode - dismissed after first double-click)
    const doubleTapHint = document.createElement('div');
    doubleTapHint.className = 'storysplat-doubletap-hint';
    const dtIcon = document.createElement('div');
    dtIcon.className = 'storysplat-doubletap-hint-icon';
    const dtSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    dtSvg.setAttribute('viewBox', '0 0 24 24');
    const dtPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    dtPath.setAttribute('d', 'M13.64 21.97C13.14 22.21 12.54 22 12.31 21.5L10.13 16.76L7.62 18.78C7.45 18.92 7.24 19 7 19C6.44 19 6 18.56 6 18V3C6 2.44 6.44 2 7 2C7.24 2 7.47 2.09 7.64 2.23L7.65 2.22L19.14 11.86C19.57 12.22 19.31 12.92 18.76 12.96L14.78 13.21L16.97 17.95C17.21 18.45 17 19.05 16.5 19.28L13.64 21.97Z');
    dtSvg.appendChild(dtPath);
    dtIcon.appendChild(dtSvg);
    doubleTapHint.appendChild(dtIcon);
    const dtText = document.createElement('div');
    dtText.className = 'storysplat-doubletap-hint-text';
    const isTouchDevice = isTouchLikeDevice();
    const dtTapMode = config.refocusTapMode || 'single';
    dtText.textContent = isTouchDevice
        ? (dtTapMode === 'single' ? 'Tap to focus on a point' : 'Double-tap to focus on a point')
        : (dtTapMode === 'single' ? 'Click to focus on a point' : 'Double-click to focus on a point');
    doubleTapHint.appendChild(dtText);
    container.appendChild(doubleTapHint);
    elements.doubleTapHint = doubleTapHint;
    // Controls card: device-specific; the viewer points it at the current camera mode when it
    // shows it. On the tour card, movement keys are advertised only when the viewer can actually
    // leave tour mode with them (same allowedModes condition as _movementKeydownHandler).
    const tourLookHint = createTourOnboardingHint(allowedCameraModes.includes('explore') || allowedCameraModes.includes('walk'), buttonLabels);
    container.appendChild(tourLookHint);
    elements.tourLookHint = tourLookHint;
    // Whether the bottom-left scene title overlay will be rendered.
    const willShowSceneTitle = showSceneTitle && sceneTitleText.length > 0;
    // Create Watermark (unless hidden for whitelabeling)
    if (!hideWatermark) {
        const watermark = document.createElement('div');
        watermark.className = 'storysplat-watermark';
        // Build watermark content - matches BabylonJS HTML export behavior
        const defaultWatermarkLink = sceneId
            ? `https://storysplat.com?ref=${sceneId}`
            : 'https://storysplat.com';
        const finalLink = watermarkLink || defaultWatermarkLink;
        const createWatermarkLink = (href: string): HTMLAnchorElement => {
            const a = document.createElement('a');
            a.href = href;
            a.target = '_blank';
            return a;
        };
        if (watermarkImageUrl) {
            // Custom image/logo provided (whitelabeling)
            watermark.classList.add('storysplat-watermark-image');
            if (watermarkImageHeight) {
                watermark.style.setProperty('--storysplat-watermark-img-size', `${watermarkImageHeight}px`);
            }
            const a = createWatermarkLink(finalLink);
            const img = document.createElement('img');
            img.src = watermarkImageUrl;
            img.alt = 'Logo';
            img.className = 'storysplat-watermark-logo';
            a.appendChild(img);
            watermark.appendChild(a);
        }
        else if (watermarkText) {
            // Custom text provided (whitelabeling)
            const a = createWatermarkLink(finalLink);
            a.textContent = watermarkText;
            watermark.appendChild(a);
        }
        else {
            // Default StorySplat watermark
            const prefix = document.createElement('span');
            prefix.className = 'storysplat-watermark-prefix';
            prefix.textContent = 'Created with ';
            watermark.appendChild(prefix);
            const a = createWatermarkLink(finalLink);
            a.textContent = 'StorySplat';
            watermark.appendChild(a);
        }
        container.appendChild(watermark);
        elements.watermark = watermark;
    }
    // Create the bottom-left scene title overlay (matches the subtle "Adding
    // detail…" LOD indicator style). In "show on hover" mode it stays hidden until
    // the corner is hovered; otherwise it is shown all the time.
    if (willShowSceneTitle) {
        const titleEl = document.createElement('div');
        titleEl.className = sceneTitleShowOnHover
            ? 'storysplat-scene-title storysplat-scene-title--hover'
            : 'storysplat-scene-title';
        titleEl.textContent = sceneTitleText;
        titleEl.title = sceneTitleText;
        container.appendChild(titleEl);
        elements.sceneTitle = titleEl;
    }
    // Create FPS counter (debug mode)
    if (options.debugMode) {
        const fpsCounter = document.createElement('div');
        fpsCounter.className = 'storysplat-fps-counter';
        fpsCounter.textContent = '-- FPS';
        container.appendChild(fpsCounter);
        elements.fpsCounter = fpsCounter;
    }
    // ═══ Top-left toolbar positioning ═══
    // When the scene menu exists, the toolbar shifts right to sit beside it.
    // Re-layout on resize because the scene-menu label hides below 768px,
    // which changes the toggle's width and therefore the toolbar's left edge.
    {
        const sceneMenuEl = elements.sceneMenuContainer;
        const baseTop = 10;
        if (sceneMenuEl) {
            // In sidebar mode the container is a fixed full-height panel pinned at
            // 0,0 and its hamburger toggle is a sibling appended to `container`,
            // not a child of the panel. So skip the inline left/top override
            // (it would fight the panel's fixed positioning) and find the toggle
            // on the container instead.
            const isSidebar = sceneMenuEl.classList.contains('storysplat-scene-menu-container--sidebar');
            if (!isSidebar) {
                sceneMenuEl.style.left = '10px';
                sceneMenuEl.style.top = `${baseTop}px`;
            }
            const toggleEl = (isSidebar
                ? container.querySelector('.storysplat-scene-menu-toggle--sidebar')
                : sceneMenuEl.querySelector('.storysplat-scene-menu-toggle')) as HTMLElement;
            if (toggleEl) {
                const reflow = () => {
                    const menuRight = 10 + toggleEl.offsetWidth + 8;
                    toolbar.style.left = `${menuRight}px`;
                    const toggleH = toggleEl.offsetHeight || 36;
                    const toolbarH = toolbar.offsetHeight || 36;
                    toolbar.style.top = `${baseTop + (toggleH - toolbarH) / 2}px`;
                };
                reflow();
                let ro: ResizeObserver | null = null;
                if (typeof ResizeObserver !== 'undefined') {
                    ro = new ResizeObserver(reflow);
                    ro.observe(toggleEl);
                    ro.observe(toolbar);
                }
                window.addEventListener('resize', reflow);
                // Expose teardown so the viewer's destroy path can release
                // the observer + window listener instead of leaking them
                // across portal-navigation transitions.
                const prevCleanup = elements._cleanup;
                elements._cleanup = () => {
                    if (prevCleanup) prevCleanup();
                    if (ro) ro.disconnect();
                    window.removeEventListener('resize', reflow);
                };
            }
        }

        // Hide toolbar if it has no icon children
        if (toolbar.children.length === 0) {
            toolbar.style.display = 'none';
        }
    }

    // Top-bar collision layout. Nothing here is a fixed breakpoint: the tool chips drop to
    // a second row only when they would actually run into the right-hand group (waypoints +
    // photo/share/fullscreen), the pro template's Tour/Explore toggle drops to the second
    // row only when the bar would run into the menu button, and the watermark loses its
    // "Created with" prefix only when the bottom navigator would run into it. Measured
    // against the embed, re-run on every size change of the parts involved.
    let layingOut = false;
    const layoutTopBar = () => {
        if (!container.isConnected || layingOut) return;
        layingOut = true;
        try { layoutTopBarNow(); } finally { layingOut = false; }
    };
    const layoutTopBarNow = () => {
        const c = container.getBoundingClientRect();
        if (c.width === 0) return;
        // Always measure the natural, single-row layout. The wrapped state shrinks the
        // waypoint label, and deciding from that shrunken width flip-flopped the toolbar
        // between rows at the threshold. Sizes end up identical when the decision holds, so
        // the resize observers stay quiet.
        container.classList.remove('storysplat-tools-wrapped', 'storysplat-mode-wrapped');
        const toggle = container.querySelector('.storysplat-scene-menu-toggle--sidebar, .storysplat-scene-menu-container:not(.storysplat-scene-menu-container--sidebar) .storysplat-scene-menu-toggle') as HTMLElement | null;
        const leftEdge = toggle && toggle.offsetWidth > 0 ? toggle.getBoundingClientRect().right - c.left + 8 : 10;
        const group = container.querySelector('.storysplat-top-right-group, .storysplat-top-right-bar') as HTMLElement | null;
        const modeInBar = group?.classList.contains('storysplat-top-right-bar') ? group.querySelector('.storysplat-mode-container') as HTMLElement | null : null;
        let otherW = 0;
        let modeW = 0;
        if (group) {
            const kids = Array.from(group.children) as HTMLElement[];
            const visible = kids.filter((k) => k.offsetWidth > 0);
            for (const k of visible) {
                if (k === modeInBar) modeW = k.offsetWidth + 8;
                else otherW += k.offsetWidth + 8;
            }
            if (otherW > 0) otherW -= 8; // no trailing gap
        }
        const wrapMode = !!modeInBar && leftEdge + modeW + otherW + 10 > c.width;
        container.classList.toggle('storysplat-mode-wrapped', wrapMode);
        const groupLeft = group ? c.width - 10 - otherW - (wrapMode ? 0 : modeW) : c.width - 10;
        const toolbarW = toolbar.offsetWidth;
        const wrapTools = toolbar.style.display !== 'none' && toolbarW > 0 && leftEdge + toolbarW + 8 > groupLeft;
        container.classList.toggle('storysplat-tools-wrapped', wrapTools);
        // Bottom row: navigator (Prev / Play / Next + Tour / Explore) vs. the watermark.
        const nav = container.querySelector('.storysplat-scroll-controls') as HTMLElement | null;
        const wm = elements.watermark;
        if (nav && wm && nav.offsetWidth > 0 && wm.offsetWidth > 0) {
            const n = nav.getBoundingClientRect();
            // Both axes: the pro navigator sits higher than the classic one, so a purely
            // horizontal test would miss (or invent) a collision.
            const collides = () => {
                const w = wm.getBoundingClientRect();
                return n.right + 8 > w.left && w.right + 8 > n.left && n.bottom > w.top && w.bottom > n.top;
            };
            // Measure from the full layout so the decision does not flip-flop, then step
            // down: hide the prefix; if it still collides, lift the watermark above the
            // navigator (extreme widths only).
            container.classList.remove('storysplat-watermark-tight', 'storysplat-watermark-lifted');
            const tight = collides();
            container.classList.toggle('storysplat-watermark-tight', tight);
            if (tight) {
                container.style.setProperty('--storysplat-nav-top', `${c.height - (n.top - c.top)}px`);
                const lifted = collides();
                container.classList.toggle('storysplat-watermark-lifted', lifted);
            }
        }
    };
    elements.relayoutChrome = layoutTopBar;
    layoutTopBar();
    if (typeof ResizeObserver !== 'undefined') {
        const barObserver = new ResizeObserver(() => layoutTopBar());
        barObserver.observe(container);
        barObserver.observe(toolbar);
        for (const sel of ['.storysplat-top-right-group', '.storysplat-top-right-bar', '.storysplat-scroll-controls', '.storysplat-watermark', '.storysplat-mode-container', '.storysplat-waypoint-list-container']) {
            const el = container.querySelector(sel);
            if (el) barObserver.observe(el);
        }
        const prevCleanup = elements._cleanup;
        elements._cleanup = () => {
            if (prevCleanup) prevCleanup();
            barObserver.disconnect();
        };
    }
    // Keep the layout in sync when the toolbar reflow (menu toggle offset) runs.
    window.addEventListener('resize', layoutTopBar);
    {
        const prevCleanup = elements._cleanup;
        elements._cleanup = () => {
            if (prevCleanup) prevCleanup();
            window.removeEventListener('resize', layoutTopBar);
        };
    }

    return elements;
}
/**
 * Update the FPS counter display
 */
export function updateFpsCounter(elements: UIElements, fps: number): void {
    if (elements.fpsCounter) {
        elements.fpsCounter.textContent = `${Math.round(fps)} FPS`;
    }
}
/**
 * What the waypoint banner shows: the name and info of the waypoint the tour is at, in tour mode
 * only. Explore (orbit / fly), walk, FPV and panorama show nothing, so leaving the tour takes the
 * banner down and roaming near a waypoint never brings it back. Info meant to stay up, or to show
 * in an area whatever the mode, is an overlay (Scene starts / Camera enters an area triggers).
 */
export function getWaypointBannerContent(cameraMode: string | undefined, waypoint: WaypointData | undefined): { title: string; info: string; infoHtml?: string } | null {
    if (cameraMode !== 'tour' || !waypoint) return null;
    // "Show its name at the top of the viewer" unticked: the name stays in the waypoints menu only.
    const title = waypoint.hideNameInViewer === true ? '' : waypoint.name || '';
    const info = waypoint.info || '';
    return title || info ? { title, info, ...(waypoint.infoHtml !== undefined ? { infoHtml: waypoint.infoHtml } : {}) } : null;
}
/**
 * Connect UI elements to viewer controls
 */
export function connectUIToViewer(elements: UIElements, viewer: ViewerUIController, defaultCameraMode: string = 'tour', buttonLabels?: ButtonLabels, opts?: { container?: HTMLElement; hideProgressText?: boolean }): void {
    const container = opts?.container;
    const hideProgressText = opts?.hideProgressText;
    // Prev/Next buttons — use stored refs (may have been extracted to container root)
    const prevBtn = elements.prevButton || elements.scrollControls?.querySelector('.storysplat-btn-prev');
    const nextBtn = elements.nextButton || elements.scrollControls?.querySelector('.storysplat-btn-next');
    const playBtn = elements.playButton || elements.scrollControls?.querySelector('.storysplat-btn-play');
    if (prevBtn) {
        prevBtn.addEventListener('click', () => viewer.prevWaypoint());
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', () => viewer.nextWaypoint());
    }
    if (playBtn) {
        // Update button icon based on state
        const updatePlayButtonIcon = (isPlaying: boolean) => {
            if (isPlaying) {
                playBtn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M6 4h4v16H6zm8 0h4v16h-4z"/></svg>'; // Pause icon
            }
            else {
                playBtn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>'; // Play icon
            }
        };
        playBtn.addEventListener('click', () => {
            if (viewer.isPlaying()) {
                viewer.pause();
            }
            else {
                viewer.play();
            }
            // Icon will be updated by event listeners below
        });
        // Listen for playback events to update button state (handles autoplay and programmatic play/pause)
        viewer.on('playbackStart', () => updatePlayButtonIcon(true));
        viewer.on('playbackStop', () => updatePlayButtonIcon(false));
        // Set initial state based on current playing status
        updatePlayButtonIcon(viewer.isPlaying());
    }
    // Help button
    if (elements.helpButton && elements.helpPanel) {
        // Only-one-open coordination: register a close, dismiss others on open.
        elements.exclusiveTools?.register('help', () => {
            elements.helpPanel!.classList.remove('visible');
            elements.helpButton!.classList.remove('active');
        });
        elements.helpButton.addEventListener('click', () => {
            const willOpen = !elements.helpPanel!.classList.contains('visible');
            elements.helpPanel!.classList.toggle('visible', willOpen);
            elements.helpButton!.classList.toggle('active', willOpen);
            if (willOpen) elements.exclusiveTools?.closeOthers('help');
        });
    }
    // Keep the notice and Help checkbox in sync for startup, user actions, and API changes.
    if (viewer.setPerformanceMode && viewer.getPerformanceMode) {
        const syncPerformanceUI = (enabled: boolean) => {
            if (elements.performanceToggle) elements.performanceToggle.checked = !!enabled;
            if (elements.performanceNotice) elements.performanceNotice.hidden = !enabled;
        };
        syncPerformanceUI(viewer.getPerformanceMode());
        elements.performanceToggle?.addEventListener('change', () => {
            viewer.setPerformanceMode!(elements.performanceToggle!.checked);
        });
        elements.performanceOffButton?.addEventListener('pointerdown', (event) => event.stopPropagation());
        elements.performanceOffButton?.addEventListener('click', (event) => {
            event.stopPropagation();
            viewer.setPerformanceMode!(false);
        });
        viewer.on('performanceModeChange', syncPerformanceUI);
    } else {
        elements.performanceNotice?.remove();
        const label = elements.performanceToggle?.parentElement;
        label?.nextElementSibling?.remove();
        label?.remove();
    }
    // Share button
    if (elements.shareButton) {
        const shareBtn = elements.shareButton as HTMLButtonElement;
        const popoverHost = container || shareBtn.parentElement || document.body;
        // 'copied' → confirm on the button; 'manual' → show the link in place for a manual copy.
        const shareUrlFallback = async (): Promise<'shared' | 'copied' | 'manual'> => {
            const shareUrl = window.location.href;
            if (navigator.share) {
                try {
                    await navigator.share({ url: shareUrl });
                    return 'shared';
                } catch (err) {
                    // The user dismissed the share sheet — respect that; don't copy behind their back.
                    if ((err as { name?: string } | null)?.name === 'AbortError') return 'shared';
                    // Share failed for another reason — continue to clipboard fallback.
                }
            }
            try {
                if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
                await navigator.clipboard.writeText(shareUrl);
                return 'copied';
            } catch {
                return 'manual';
            }
        };
        // The button's own icon is the confirmation: swap to a check for 1.5s (no corner toast).
        const shareIconHtml = shareBtn.innerHTML;
        let shareCheckTimer: ReturnType<typeof setTimeout> | undefined;
        const flashShareCopied = () => {
            const copiedLabel = getButtonLabel(buttonLabels, 'linkCopied');
            const restoreLabel = getButtonLabel(buttonLabels, 'share');
            shareBtn.innerHTML = SHARE_CHECK_ICON;
            shareBtn.setAttribute('aria-label', copiedLabel);
            shareBtn.setAttribute('title', copiedLabel);
            clearTimeout(shareCheckTimer);
            shareCheckTimer = setTimeout(() => {
                if (!shareBtn.isConnected) return;
                shareBtn.innerHTML = shareIconHtml;
                shareBtn.setAttribute('aria-label', restoreLabel);
                shareBtn.setAttribute('title', restoreLabel);
            }, 1500);
        };

        shareBtn.addEventListener('click', async () => {
            if (shareBtn.disabled) return;
            shareBtn.disabled = true;
            closeShareLinkPopover(popoverHost);
            const originalLabel = getButtonLabel(buttonLabels, 'share');
            shareBtn.setAttribute('aria-label', getButtonLabel(buttonLabels, 'capturingPhoto'));
            shareBtn.setAttribute('title', getButtonLabel(buttonLabels, 'capturingPhoto'));
            let outcome: 'shared' | 'copied' | 'manual' | 'failed' = 'shared';
            try {
                if (viewer.shareCurrentView) {
                    await viewer.shareCurrentView();
                } else {
                    outcome = await shareUrlFallback();
                }
            } catch (err) {
                console.warn('[StorySplat Viewer] Share failed:', err);
                outcome = 'failed';
            } finally {
                shareBtn.disabled = false;
                shareBtn.setAttribute('aria-label', originalLabel);
                shareBtn.setAttribute('title', originalLabel);
            }
            if (outcome === 'copied') flashShareCopied();
            else if (outcome === 'manual') showShareLinkPopover(popoverHost, shareBtn, { url: window.location.href, label: getButtonLabel(buttonLabels, 'copyLink'), closeLabel: getButtonLabel(buttonLabels, 'close') });
            else if (outcome === 'failed') showShareLinkPopover(popoverHost, shareBtn, { message: getButtonLabel(buttonLabels, 'shareFailed'), closeLabel: getButtonLabel(buttonLabels, 'close') });
        });
    }
    // Fullscreen button
    if (elements.fullscreenButton) {
        // Fullscreen targets the viewer container itself. The button lives inside the
        // top-right pill, so parentElement would be the pill — use `container` directly.
        const fsTarget = container ?? null;
        const fsButton = elements.fullscreenButton;
        const setFullscreenIcons = (isFullscreen: boolean) => {
            const expandIcon = fsButton.querySelector('.storysplat-expand-icon') as HTMLElement | null;
            const compressIcon = fsButton.querySelector('.storysplat-compress-icon') as HTMLElement | null;
            if (expandIcon) expandIcon.style.display = isFullscreen ? 'none' : 'block';
            if (compressIcon) compressIcon.style.display = isFullscreen ? 'block' : 'none';
        };
        // Feature detection, not the user agent: iPads have the Fullscreen API, iPhones
        // don't (only <video> can go fullscreen there) and get the window-filling fallback
        // when ENABLE_PSEUDO_FULLSCREEN is on (fullscreen.ts).
        const fsMode = detectFullscreenMode(fsTarget, document);
        if (fsMode === 'none' || !fsTarget) {
            fsButton.style.display = 'none';
            syncButtonGroupVisibility(fsButton.closest('.storysplat-top-right-pill') as HTMLElement | null);
        }
        else if (fsMode === 'pseudo') {
            const pseudo = createPseudoFullscreen(fsTarget, setFullscreenIcons);
            fsButton.addEventListener('click', () => {
                pseudo.toggle();
            });
            const prevCleanup = elements._cleanup;
            elements._cleanup = () => {
                if (prevCleanup) prevCleanup();
                pseudo.destroy();
            };
        }
        else {
            fsButton.addEventListener('click', () => {
                // Use webkit-prefixed API for Safari compatibility
                const doc = document as WebkitFullscreenDocument;
                const fullscreenElement = doc.fullscreenElement || doc.webkitFullscreenElement;
                if (!fullscreenElement) {
                    // Enter fullscreen - try standard API first, then webkit
                    if (fsTarget.requestFullscreen) {
                        // Older engines return undefined instead of a promise.
                        fsTarget.requestFullscreen()?.catch?.(() => { /* denied (embed without allowfullscreen) */ });
                    }
                    else if ((fsTarget as WebkitFullscreenElement).webkitRequestFullscreen) {
                        (fsTarget as WebkitFullscreenElement).webkitRequestFullscreen?.();
                    }
                    setFullscreenIcons(true);
                }
                else {
                    // Exit fullscreen - try standard API first, then webkit
                    if (doc.exitFullscreen) {
                        doc.exitFullscreen();
                    }
                    else if (doc.webkitExitFullscreen) {
                        doc.webkitExitFullscreen();
                    }
                    setFullscreenIcons(false);
                }
            });
            // Listen for fullscreen change events (including webkit prefix)
            const handleFullscreenChange = () => {
                const doc = document as WebkitFullscreenDocument;
                setFullscreenIcons(!!(doc.fullscreenElement || doc.webkitFullscreenElement));
            };
            document.addEventListener('fullscreenchange', handleFullscreenChange);
            document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
            // handleFullscreenChange closes over the whole UIElements record — without teardown
            // each scene switch pinned the full detached UI. Chain into the shared cleanup.
            {
                const prevCleanup = elements._cleanup;
                elements._cleanup = () => {
                    if (prevCleanup) prevCleanup();
                    document.removeEventListener('fullscreenchange', handleFullscreenChange);
                    document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
                };
            }
        }
    }
    // Helper to show/hide tour navigation controls based on mode
    const setTourControlsVisible = (visible: boolean) => {
        const progressContainer = elements.scrollControls?.querySelector('.storysplat-progress-container') as HTMLElement;
        const scrollButtons = elements.scrollControls?.querySelector('.storysplat-scroll-buttons') as HTMLElement;
        const display = visible ? '' : 'none';
        // Use stored ref (may have been extracted to container root)
        if (elements.progressText)
            elements.progressText.style.display = visible && !hideProgressText ? '' : 'none';
        if (progressContainer)
            progressContainer.style.display = display;
        if (scrollButtons)
            scrollButtons.style.display = display;
        // Also show/hide extracted prev/play/next buttons (at the container root, which in split
        // stereo is the live eye wrapper, see stereoUi.ts)
        const atRoot = (el: HTMLElement) => el.parentElement === container || !!el.parentElement?.classList.contains(STEREO_EYE_CLASS);
        if (elements.prevButton && atRoot(elements.prevButton))
            elements.prevButton.style.display = display;
        if (elements.playButton && atRoot(elements.playButton))
            elements.playButton.style.display = display;
        if (elements.nextButton && atRoot(elements.nextButton))
            elements.nextButton.style.display = display;
    };
    // Helper to show/hide explore controls (orbit/fly buttons)
    const setExploreControlsVisible = (visible: boolean) => {
        // Use stored ref (may have been extracted to container root)
        if (elements.exploreControls) {
            if (visible) {
                elements.exploreControls.classList.add('visible');
            }
            else {
                elements.exploreControls.classList.remove('visible');
            }
        }
    };
    // Helper to update orbit/fly/walk button selected state
    const queryRoot = container || elements.scrollControls?.parentElement;
    const updateExploreButtons = (activeMode: 'orbit' | 'fly' | 'walk') => {
        // Search from container root since explore buttons may have been extracted
        const exploreBtns = queryRoot?.querySelectorAll('.storysplat-explore-btn');
        exploreBtns?.forEach(btn => {
            const mode = btn.getAttribute('data-explore-mode');
            btn.classList.toggle('selected', mode === activeMode);
        });
    };
    // Set initial visibility based on default mode
    setTourControlsVisible(defaultCameraMode === 'tour');
    setExploreControlsVisible(defaultCameraMode === 'explore' || defaultCameraMode === 'walk');
    // Mode toggle buttons
    // Query from container root since mode container may have been extracted (pro template or custom position)
    if (viewer.setCameraMode) {
        const modeButtons = queryRoot?.querySelectorAll('.storysplat-mode-btn');
        modeButtons?.forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.getAttribute('data-mode');
                if (mode && viewer.setCameraMode) {
                    viewer.setCameraMode(mode);
                    // Update selected state
                    modeButtons.forEach(b => b.classList.remove('selected'));
                    btn.classList.add('selected');
                    // Show/hide tour vs explore controls based on mode
                    setTourControlsVisible(mode === 'tour');
                    setExploreControlsVisible(mode === 'explore' || mode === 'walk');
                }
            });
        });
        // Listen for mode changes
        // Check if a dedicated walk button exists (walk-only mode, no explore)
        const hasWalkModeBtn = !!queryRoot?.querySelector('.storysplat-mode-btn[data-mode="walk"]');
        viewer.on('modeChange', ({ mode }: {
            mode: string;
        }) => {
            setTourControlsVisible(mode === 'tour');
            setExploreControlsVisible(mode === 'explore' || mode === 'walk');
            // Map walk to the correct button: dedicated walk btn or grouped under explore
            const displayMode = mode === 'walk' ? (hasWalkModeBtn ? 'walk' : 'explore') : mode;
            modeButtons?.forEach(btn => {
                const btnMode = btn.getAttribute('data-mode');
                btn.classList.toggle('selected', btnMode === displayMode);
            });
        });
    }
    // Update progress continuously (throttled to prevent visual artifacts)
    let lastProgressUpdate = 0;
    let lastDisplayedPercentage = -1;
    viewer.on('progressUpdate', ({ progress }: {
        progress: number;
    }) => {
        // Clamp progress to 0-1 range
        const clampedProgress = Math.max(0, Math.min(1, progress));
        const percentage = clampedProgress * 100;
        const roundedPercentage = Math.round(percentage);
        // Throttle UI updates to ~30fps to prevent visual glitches
        const now = performance.now();
        if (now - lastProgressUpdate < 33)
            return;
        lastProgressUpdate = now;
        if (elements.progressBar) {
            elements.progressBar.style.transform = `scaleX(${clampedProgress})`;
        }
        // Only update text if the percentage actually changed (prevents flickering)
        if (elements.progressText && roundedPercentage !== lastDisplayedPercentage) {
            lastDisplayedPercentage = roundedPercentage;
            elements.progressText.textContent = formatPercentageLabel(buttonLabels, roundedPercentage);
        }
    });
    // Waypoint banner: the current waypoint's name and info, in tour mode only (see
    // getWaypointBannerContent). Re-evaluated when the tour's waypoint changes and when the camera
    // mode changes: leaving the tour hides it, coming back shows the current waypoint's again.
    // Panorama switches to explore without a modeChange, so it re-evaluates on that event too.
    let bannerWaypointIndex = viewer.getCurrentWaypointIndex();
    let bannerWaypoint: WaypointData | undefined;
    let bannerCameraMode = defaultCameraMode;
    const refreshWaypointBanner = () => {
        if (!elements.waypointInfo)
            return;
        const titleEl = elements.waypointInfo.querySelector('.storysplat-waypoint-title') as HTMLElement;
        const descEl = elements.waypointInfo.querySelector('.storysplat-waypoint-description') as HTMLElement;
        if (!titleEl || !descEl)
            return;
        const wp = bannerWaypoint || viewer.getWaypoints?.()?.[bannerWaypointIndex];
        const content = getWaypointBannerContent(viewer.getCameraMode?.() ?? bannerCameraMode, wp);
        titleEl.textContent = content?.title ?? '';
        descEl.innerHTML = richTextHtml(content?.info, content?.infoHtml);
        elements.waypointInfo.classList.toggle('hasContent', !!content);
    };
    viewer.on('waypointChange', ({ index, waypoint }: {
        index: number;
        waypoint?: WaypointData;
    }) => {
        bannerWaypointIndex = index;
        bannerWaypoint = waypoint;
        refreshWaypointBanner();
    });
    viewer.on('modeChange', ({ mode }: { mode: string }) => {
        bannerCameraMode = mode;
        refreshWaypointBanner();
    });
    viewer.on('panoramaModeChange', refreshWaypointBanner);
}
/**
 * Initialize a mini PlayCanvas app to render a 3D model (GLB/GLTF) inside a hotspot popup.
 * Only called when the popup is opened (model loads on click, not preloaded).
 */
function initModelViewer(canvas: HTMLCanvasElement, modelUrl: string, popup: HTMLElement): void {
    const container = canvas.parentElement;
    if (!container) return;

    // Size the canvas to match its container
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;

    // Create a standalone mini PlayCanvas app
    const app = new pc.Application(canvas, {
        graphicsDeviceOptions: {
            alpha: true,
            antialias: true,
            preserveDrawingBuffer: false,
        },
    });
    app.setCanvasResolution(pc.RESOLUTION_AUTO);
    app.setCanvasFillMode(pc.FILLMODE_NONE);

    // Camera
    const cameraEntity = new pc.Entity('popup-camera');
    cameraEntity.addComponent('camera', {
        clearColor: new pc.Color(0, 0, 0, 0),
        fov: 45,
        nearClip: 0.01,
        farClip: 1000,
    });
    app.root.addChild(cameraEntity);

    // Key light
    const keyLight = new pc.Entity('popup-key-light');
    keyLight.addComponent('light', {
        type: 'directional',
        color: new pc.Color(1, 1, 1),
        intensity: 1.2,
        castShadows: false,
    });
    keyLight.setEulerAngles(45, 30, 0);
    app.root.addChild(keyLight);

    // Fill light
    const fillLight = new pc.Entity('popup-fill-light');
    fillLight.addComponent('light', {
        type: 'directional',
        color: new pc.Color(0.7, 0.75, 0.85),
        intensity: 0.6,
        castShadows: false,
    });
    fillLight.setEulerAngles(-30, -60, 0);
    app.root.addChild(fillLight);

    app.start();

    // Orbit state
    let orbitYaw = 0;
    let orbitPitch = -20;
    let orbitDist = 5;
    let orbitCenter = new pc.Vec3(0, 0, 0);
    let isDragging = false;
    let lastX = 0;
    let lastY = 0;

    function updateCamera(): void {
        const yawRad = orbitYaw * pc.math.DEG_TO_RAD;
        const pitchRad = orbitPitch * pc.math.DEG_TO_RAD;
        const x = orbitDist * Math.cos(pitchRad) * Math.sin(yawRad);
        const y = orbitDist * Math.sin(pitchRad);
        const z = orbitDist * Math.cos(pitchRad) * Math.cos(yawRad);
        cameraEntity.setPosition(
            orbitCenter.x + x,
            orbitCenter.y + y,
            orbitCenter.z + z
        );
        cameraEntity.lookAt(orbitCenter);
    }

    // Mouse orbit controls
    canvas.addEventListener('mousedown', (e: MouseEvent) => {
        isDragging = true;
        lastX = e.clientX;
        lastY = e.clientY;
        e.preventDefault();
    });
    canvas.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isDragging) return;
        const dx = e.clientX - lastX;
        const dy = e.clientY - lastY;
        orbitYaw -= dx * 0.4;
        orbitPitch = Math.max(-89, Math.min(89, orbitPitch - dy * 0.4));
        lastX = e.clientX;
        lastY = e.clientY;
        updateCamera();
    });
    canvas.addEventListener('mouseup', () => { isDragging = false; });
    canvas.addEventListener('mouseleave', () => { isDragging = false; });
    canvas.addEventListener('wheel', (e: WheelEvent) => {
        orbitDist = Math.max(0.1, orbitDist * (1 + e.deltaY * 0.001));
        updateCamera();
        e.preventDefault();
    }, { passive: false });

    // Touch orbit controls
    let touchStartDist = 0;
    let touchStartOrbitDist = 0;
    canvas.addEventListener('touchstart', (e: TouchEvent) => {
        if (e.touches.length === 1) {
            isDragging = true;
            lastX = e.touches[0].clientX;
            lastY = e.touches[0].clientY;
        } else if (e.touches.length === 2) {
            isDragging = false;
            const dx = e.touches[1].clientX - e.touches[0].clientX;
            const dy = e.touches[1].clientY - e.touches[0].clientY;
            touchStartDist = Math.sqrt(dx * dx + dy * dy);
            touchStartOrbitDist = orbitDist;
        }
        e.preventDefault();
    }, { passive: false });
    canvas.addEventListener('touchmove', (e: TouchEvent) => {
        if (e.touches.length === 1 && isDragging) {
            const dx = e.touches[0].clientX - lastX;
            const dy = e.touches[0].clientY - lastY;
            orbitYaw -= dx * 0.4;
            orbitPitch = Math.max(-89, Math.min(89, orbitPitch - dy * 0.4));
            lastX = e.touches[0].clientX;
            lastY = e.touches[0].clientY;
            updateCamera();
        } else if (e.touches.length === 2) {
            const dx = e.touches[1].clientX - e.touches[0].clientX;
            const dy = e.touches[1].clientY - e.touches[0].clientY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (touchStartDist > 0) {
                orbitDist = Math.max(0.1, touchStartOrbitDist * (touchStartDist / dist));
                updateCamera();
            }
        }
        e.preventDefault();
    }, { passive: false });
    canvas.addEventListener('touchend', () => {
        isDragging = false;
        touchStartDist = 0;
    });

    // ResizeObserver for responsive canvas sizing
    const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            const { width, height } = entry.contentRect;
            if (width > 0 && height > 0) {
                canvas.width = width * window.devicePixelRatio;
                canvas.height = height * window.devicePixelRatio;
                app.resizeCanvas(width, height);
            }
        }
    });
    resizeObserver.observe(container);

    // Load GLB model
    const asset = new pc.Asset('popup-model', 'container', { url: modelUrl });
    asset.on('load', () => {
        const loadingEl = container.querySelector('.model-loading');
        if (loadingEl) loadingEl.remove();

        const modelEntity = (asset.resource as any).instantiateRenderEntity();
        app.root.addChild(modelEntity);

        // Auto-frame: compute AABB of all mesh instances
        const meshInstances: pc.MeshInstance[] = [];
        modelEntity.findComponents('render').forEach((render: any) => {
            meshInstances.push(...render.meshInstances);
        });

        if (meshInstances.length > 0) {
            const aabb = new pc.BoundingBox();
            aabb.copy(meshInstances[0].aabb);
            for (let i = 1; i < meshInstances.length; i++) {
                aabb.add(meshInstances[i].aabb);
            }

            orbitCenter.copy(aabb.center);
            const halfExtents = aabb.halfExtents;
            const maxExtent = Math.max(halfExtents.x, halfExtents.y, halfExtents.z);
            orbitDist = maxExtent * 2.5;
        }

        updateCamera();
    });
    asset.on('error', (err: string) => {
        const loadingEl = container.querySelector('.model-loading');
        if (loadingEl) loadingEl.textContent = 'Failed to load model';
        console.error('Model hotspot load error:', err);
    });
    app.assets.add(asset);
    app.assets.load(asset);

    updateCamera();

    // Store cleanup function on popup for later destruction.
    // Support multiple model blocks: keep the legacy single ref (back-compat) AND
    // push into an array so N model canvases can each be torn down in hideHotspotPopup.
    const cleanup = () => {
        resizeObserver.disconnect();
        app.destroy();
    };
    (popup as any).__modelViewerCleanup = cleanup;
    const modelCleanups = ((popup as any).__modelViewerCleanups ||= []);
    modelCleanups.push(cleanup);
}

/**
 * Open a dependency-free full-screen image lightbox (prev/next + close) for a hotspot
 * gallery. The overlay is appended to the popup and removed on close; the reference is
 * stored on the popup so hideHotspotPopup can tear it down with the popup.
 */
function openHotspotLightbox(popup: HTMLElement, urls: string[], startIndex: number): void {
    const safeUrls = urls.filter(u => !!u);
    if (safeUrls.length === 0) return;
    // Replace any existing lightbox first.
    closeHotspotLightbox(popup);
    let index = startIndex;

    // Append the overlay to the popup's POSITIONED parent (the viewer container),
    // NOT the popup itself: the popup is transform:translate-centered, which makes
    // it a containing block for position:fixed/absolute descendants and clips them
    // to its (often short) height. Hosting the overlay on the container lets it
    // fill the whole viewer/embed so the opened image reaches its max height.
    const host = popup.parentElement || popup;
    const overlay = document.createElement('div');
    overlay.className = 'storysplat-hotspot-lightbox';
    // pointer-events:auto so the lightbox stays clickable when hosted in the overlay layer (pointer-events:none).
    overlay.style.cssText = 'position:absolute;inset:0;z-index:200000;pointer-events:auto;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center;gap:12px;padding:16px;box-sizing:border-box;';

    const img = document.createElement('img');
    img.style.cssText = 'max-width:90%;max-height:90%;object-fit:contain;border-radius:8px;';
    img.src = safeUrls[index];

    const navBtnCss = 'background:rgba(0,0,0,0.5);color:#fff;border:none;border-radius:50%;width:44px;height:44px;font-size:22px;cursor:pointer;line-height:1;flex-shrink:0;display:flex;align-items:center;justify-content:center;padding:0;box-sizing:border-box;';

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.style.cssText = navBtnCss + 'position:absolute;top:16px;right:16px;';

    const show = (i: number) => { index = (i + safeUrls.length) % safeUrls.length; img.src = safeUrls[index]; };

    const prevBtn = document.createElement('button');
    prevBtn.textContent = '‹';
    prevBtn.setAttribute('aria-label', 'Previous');
    prevBtn.style.cssText = navBtnCss;

    const nextBtn = document.createElement('button');
    nextBtn.textContent = '›';
    nextBtn.setAttribute('aria-label', 'Next');
    nextBtn.style.cssText = navBtnCss;

    const close = () => closeHotspotLightbox(popup);
    overlay.addEventListener('click', close);
    closeBtn.addEventListener('click', (e) => { e.stopPropagation(); close(); });
    img.addEventListener('click', (e) => e.stopPropagation());
    prevBtn.addEventListener('click', (e) => { e.stopPropagation(); show(index - 1); });
    nextBtn.addEventListener('click', (e) => { e.stopPropagation(); show(index + 1); });
    // Swallow wheel/touch so the scrollable popup content beneath doesn't move.
    overlay.addEventListener('wheel', (e) => e.stopPropagation());
    overlay.addEventListener('touchmove', (e) => e.stopPropagation());

    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') show(index - 1);
        else if (e.key === 'ArrowRight') show(index + 1);
    };
    document.addEventListener('keydown', onKey);

    // Lock scroll while the lightbox is open: freeze both the popup's scrollable
    // content area and the page body. Restored in the cleanup (closeHotspotLightbox).
    const contentEl = popup.querySelector('.storysplat-hotspot-popup-content') as HTMLElement | null;
    const prevContentOverflow = contentEl ? contentEl.style.overflow : '';
    const prevBodyOverflow = document.body.style.overflow;
    if (contentEl) contentEl.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    if (safeUrls.length > 1) overlay.appendChild(prevBtn);
    overlay.appendChild(img);
    if (safeUrls.length > 1) overlay.appendChild(nextBtn);
    overlay.appendChild(closeBtn);
    host.appendChild(overlay);

    (popup as any).__lightboxCleanup = () => {
        document.removeEventListener('keydown', onKey);
        // Restore scroll locks.
        if (contentEl) contentEl.style.overflow = prevContentOverflow;
        document.body.style.overflow = prevBodyOverflow;
        if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
    };
}

/** Tear down the hotspot lightbox overlay if present. */
function closeHotspotLightbox(popup: HTMLElement): void {
    const cleanup = (popup as any).__lightboxCleanup;
    if (cleanup) {
        cleanup();
        delete (popup as any).__lightboxCleanup;
    }
}

/** PDF.js CDN version for iOS Safari fallback (iframe PDF embedding doesn't work on iOS) */
const PDFJS_CDN_VERSION = '4.8.69';
const PDFJS_CDN_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_CDN_VERSION}`;

let pdfjsLibPromise: Promise<any> | null = null;

/** Load pdf.js library from CDN (cached across calls) */
export function loadPdfJs(): Promise<any> {
    if (!pdfjsLibPromise) {
        const offlineAssets = getOfflineAssets();
        const moduleUrl = offlineAssets?.pdfJsModuleUrl || `${PDFJS_CDN_BASE}/pdf.min.mjs`;
        const workerUrl = offlineAssets?.pdfJsWorkerUrl || `${PDFJS_CDN_BASE}/pdf.worker.min.mjs`;
        pdfjsLibPromise = import(/* @vite-ignore */ moduleUrl).then(lib => {
            lib.GlobalWorkerOptions.workerSrc = workerUrl;
            return lib;
        }).catch(err => {
            pdfjsLibPromise = null; // allow retry on next call
            return Promise.reject(err);
        });
    }
    return pdfjsLibPromise;
}

/** Detect iOS (iPhone, iPad, iPod Touch) */
function isIOSDevice(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Render a PDF using pdf.js into a scrollable container of canvas elements.
 * Used as fallback on iOS Safari where iframe PDF embedding doesn't work.
 */
function renderPdfWithPdfJs(url: string, container: HTMLElement): () => void {
    let cancelled = false;
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'width: 100%; height: 70vh; min-height: 500px; max-height: 600px; overflow-y: auto; border-radius: 8px; background: #525659; display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 8px 0;';

    const loading = document.createElement('div');
    loading.textContent = 'Loading PDF...';
    loading.style.cssText = 'color: white; padding: 20px; font-size: 14px;';
    wrapper.appendChild(loading);
    container.appendChild(wrapper);

    loadPdfJs().then(async (pdfjsLib) => {
        try {
            const pdf = await pdfjsLib.getDocument(url).promise;
            if (cancelled) return;
            loading.remove();
            for (let i = 1; i <= pdf.numPages; i++) {
                if (cancelled) return;
                const page = await pdf.getPage(i);
                if (cancelled) return;
                // Scale to fit wrapper width (~900px max, minus padding). A wrapper with no
                // width yet would give a negative scale and a blank default-size canvas.
                const available = wrapper.clientWidth - 16;
                const desiredWidth = Math.min(available > 0 ? available : 600, 850);
                const unscaledViewport = page.getViewport({ scale: 1 });
                const scale = desiredWidth / unscaledViewport.width;
                const viewport = page.getViewport({ scale });

                const canvas = document.createElement('canvas');
                // Sized by the .storysplat-pdf-page rule (full width, height from the page's shape).
                canvas.className = 'storysplat-pdf-page';
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                canvas.style.maxWidth = `${Math.floor(viewport.width)}px`;
                wrapper.appendChild(canvas);

                const ctx = canvas.getContext('2d');
                if (ctx) {
                    await page.render({ canvasContext: ctx, viewport }).promise;
                }
            }
        } catch (err) {
            if (cancelled) return;
            loading.textContent = 'Failed to load PDF.';
            console.error('[StorySplat] PDF.js render error:', err);
            // Add fallback download link
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.textContent = 'Open PDF in new tab';
            link.style.cssText = 'color: #4CAF50; margin-top: 8px; font-size: 14px;';
            wrapper.appendChild(link);
        }
    }).catch(() => {
        if (cancelled) return;
        loading.textContent = 'PDF viewer unavailable.';
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = 'Open PDF in new tab';
        link.style.cssText = 'color: #4CAF50; margin-top: 8px; font-size: 14px;';
        wrapper.appendChild(link);
    });

    return () => { cancelled = true; };
}

/**
 * Re-apply a CSS colour at a given alpha. Accepts `#rgb`, `#rrggbb`, `rgb()` and
 * `rgba()` (the forms getComputedStyle returns and the editor writes). Anything
 * else — named colours, gradients — is returned untouched so we never mangle a
 * theme value we can't parse.
 */
function withAlpha(color: string, alpha: number): string {
    const value = color.trim();
    const hex = value.startsWith('#') ? value.slice(1) : '';
    if (hex.length === 3 || hex.length === 6) {
        const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
        const r = parseInt(full.substring(0, 2), 16);
        const g = parseInt(full.substring(2, 4), 16);
        const b = parseInt(full.substring(4, 6), 16);
        if (![r, g, b].some(Number.isNaN))
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        return value;
    }
    const rgbMatch = value.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (rgbMatch) {
        return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${alpha})`;
    }
    return value;
}

/**
 * Build the attribute list for a popup <video>. Autoplay implies muted unless the
 * author explicitly unmutes — browsers block audible autoplay, and the hydration
 * pass below falls back to muted playback if play() is rejected anyway.
 */
function popupVideoAttrs(opts: { autoplay?: boolean; loop?: boolean; muted?: boolean }): string {
    const attrs = ['controls', 'playsinline', 'webkit-playsinline'];
    const muted = opts.muted ?? !!opts.autoplay;
    if (opts.autoplay) {
        attrs.push('autoplay', 'data-autoplay="true"', 'preload="auto"');
    } else {
        attrs.push('preload="metadata"');
    }
    if (opts.loop)
        attrs.push('loop');
    if (muted)
        attrs.push('muted');
    return attrs.join(' ');
}

/**
 * HTML for a list of content blocks, shared by hotspot popups and overlays. Text and link
 * labels are escaped; every media / iframe / pdf URL is protocol-checked and escaped as an
 * attribute value (a quote in an authored URL must never close the attribute); the link
 * button opens through a data-href wired in hydratePopupMedia(), never inline script.
 */
function buildContentBlocksHtml(
    blocks: HotspotContentBlock[],
    title: string | undefined,
    buttonLabels?: ButtonLabels,
    // Draw PDFs with the viewer's pdf.js renderer instead of the browser's PDF viewer (which
    // has download/print buttons): always on iOS, where the iframe doesn't work, and when the
    // scene protects its media.
    opts: { pdfInViewer?: boolean } = {},
): string {
    let contentHtml = '';
    for (const block of blocks) {
        switch (block.kind) {
            case 'iframe': {
                const src = attrUrl(block.url, 'web');
                if (src) contentHtml += `<iframe src="${src}" title="${escapeHtml(title || 'Embedded content')}"></iframe>`;
                break;
            }
            case 'video': {
                const src = attrUrl(block.url, 'media');
                if (src) contentHtml += `<div class="video-container"><video src="${src}" ${popupVideoAttrs(block)}></video></div>`;
                break;
            }
            case 'image': {
                // Gallery: one <img> per safe url.
                const urls = (Array.isArray(block.urls) ? block.urls : []).map(u => attrUrl(u, 'media')).filter(u => !!u);
                if (urls.length === 0) break;
                const layout = block.layout || 'grid';
                const alt = escapeHtml(title || 'Hotspot image');
                const imgs = urls.map(u => `<img src="${u}" alt="${alt}" />`).join('');
                if (layout === 'stacked') {
                    contentHtml += `<div class="hotspot-gallery hotspot-gallery-stacked">${imgs}</div>`;
                } else if (layout === 'carousel') {
                    contentHtml += `<div class="hotspot-gallery hotspot-gallery-carousel">${imgs}</div>`;
                } else {
                    contentHtml += `<div class="hotspot-gallery hotspot-gallery-grid">${imgs}</div>`;
                }
                break;
            }
            case 'model': {
                // Carry the per-block model url so the hydration loop can read it.
                const src = attrUrl(block.url, 'media');
                if (src) contentHtml += `<div class="model-container"><div class="model-loading">Loading 3D model...</div><canvas class="model-canvas" data-model-url="${src}"></canvas></div>`;
                break;
            }
            case 'pdf': {
                const src = attrUrl(block.url, 'media');
                if (!src) break;
                if (opts.pdfInViewer || isIOSDevice()) {
                    contentHtml += `<div class="pdf-pdfjs-container" data-pdf-url="${src}"></div>`;
                } else {
                    contentHtml += `<iframe src="${src}" style="width: 100%; height: 70vh; min-height: 500px; max-height: 600px; aspect-ratio: auto; border: none; border-radius: 8px;" title="${escapeHtml(title || 'PDF Document')}"></iframe>`;
                }
                break;
            }
            case 'text':
                contentHtml += `<div class="storysplat-rich-text">${richTextHtml(block.text, block.textHtml)}</div>`;
                break;
            case 'link': {
                const href = attrUrl(block.url, 'link');
                if (!href) break;
                const btnColor = safeCssColor(block.buttonColor, '#007bff');
                const escapedLinkText = escapeHtml(block.text || getButtonLabel(buttonLabels, 'openExternalLink'));
                contentHtml += `
      <div role="link" tabindex="0" data-href="${href}"
           class="storysplat-hotspot-popup-link" style="background-color: ${btnColor}">
        ${escapedLinkText}
      </div>
    `;
                break;
            }
        }
    }
    return contentHtml;
}

/** True when at least one block has something to render. */
export function contentBlocksHaveContent(blocks: HotspotContentBlock[] | undefined): boolean {
    if (!Array.isArray(blocks)) return false;
    return blocks.some((b) => {
        switch (b.kind) {
            case 'text': return !!b.text;
            case 'image': return Array.isArray(b.urls) && b.urls.some(u => !!u);
            default: return !!b.url;
        }
    });
}

/**
 * After content HTML is inserted: start autoplay videos, wire galleries to the lightbox,
 * boot 3D model viewers and pdf.js fallbacks. Cleanups are stashed on `host` (the popup
 * or overlay panel) for teardownPopupMedia(). `legacy` supplies the single-content model /
 * pdf urls for canvases and containers that carry no data attribute.
 */
function hydratePopupMedia(host: HTMLElement, contentEl: HTMLElement, legacy: { modelUrl?: string; pdfUrl?: string } = {}): void {
    // Link buttons: open the authored destination in a new tab. The target lives in
    // data-href (never inline script) and is protocol-checked again here.
    contentEl.querySelectorAll<HTMLElement>('.storysplat-hotspot-popup-link[data-href]').forEach((el) => {
        if (el.dataset.wired) return;
        el.dataset.wired = '1';
        const open = () => {
            const href = safeUrl(el.dataset.href, 'link');
            if (href) window.open(href, '_blank', 'noopener,noreferrer');
        };
        el.addEventListener('click', open);
        el.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(); } });
    });
    // Kick off autoplay videos. The `autoplay` attribute alone is unreliable for
    // elements inserted after load, so call play() explicitly and fall back to muted
    // playback when the browser rejects an audible autoplay.
    const autoplayVideos = contentEl.querySelectorAll('video[data-autoplay="true"]') as NodeListOf<HTMLVideoElement>;
    autoplayVideos.forEach((video) => {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(() => {
                video.muted = true;
                const retry = video.play();
                if (retry && typeof retry.catch === 'function')
                    retry.catch(() => { /* user gesture required — controls are visible */ });
            });
        }
    });
    // Wire image-gallery thumbnails to a lightbox overlay (block galleries only).
    const galleries = contentEl.querySelectorAll('.hotspot-gallery');
    galleries.forEach((gallery) => {
        const imgs = Array.from(gallery.querySelectorAll('img')) as HTMLImageElement[];
        const urls = imgs.map(img => img.getAttribute('src') || '');
        imgs.forEach((img, i) => {
            img.style.cursor = 'pointer';
            img.addEventListener('click', () => openHotspotLightbox(host, urls, i));
        });
    });
    // Initialize 3D model viewer(s) — single legacy canvas OR N block canvases.
    // Multi-instance: querySelectorAll so every model block hydrates independently.
    const canvases = contentEl.querySelectorAll('.model-canvas') as NodeListOf<HTMLCanvasElement>;
    canvases.forEach((canvas) => {
        const url = canvas.getAttribute('data-model-url') || legacy.modelUrl;
        if (url) {
            initModelViewer(canvas, url, host);
        }
    });
    // Initialize pdf.js renderer(s) for iOS fallback — single legacy OR N block containers.
    // Store cancel callbacks as an ARRAY so multiple pdf renders are all drained on hide.
    const pdfContainers = contentEl.querySelectorAll('.pdf-pdfjs-container') as NodeListOf<HTMLElement>;
    const pdfCleanups: Array<() => void> = [];
    pdfContainers.forEach((pdfContainer) => {
        const url = pdfContainer.getAttribute('data-pdf-url') || legacy.pdfUrl;
        if (url) {
            pdfCleanups.push(renderPdfWithPdfJs(url, pdfContainer));
        }
    });
    if (pdfCleanups.length > 0) {
        // Keep the legacy single ref for back-compat AND the new array.
        (host as any).__pdfCleanup = pdfCleanups[0];
        (host as any).__pdfCleanups = pdfCleanups;
    }
}

/** Stop videos, close the lightbox and destroy model viewers / pdf renders hosted on `host`. */
function teardownPopupMedia(host: HTMLElement): void {
    // Tear down the image lightbox overlay if open
    closeHotspotLightbox(host);
    // Stop popup videos — an autoplaying video would otherwise keep playing
    // (audibly) behind a closed popup until the next popup rebuilds the content.
    const videos = host.querySelectorAll('video') as NodeListOf<HTMLVideoElement>;
    videos.forEach((video) => {
        try {
            video.pause();
            video.currentTime = 0;
        }
        catch { /* detached or unseekable stream */ }
    });
    // Clean up model viewer mini app(s). Drain the array (N model blocks) AND the
    // legacy single ref. The array entries are the same functions as the single ref,
    // so clear the single ref first to avoid double-destroy.
    const modelCleanups = (host as any).__modelViewerCleanups as Array<() => void> | undefined;
    if (modelCleanups && modelCleanups.length > 0) {
        modelCleanups.forEach(fn => { try { fn(); } catch { /* already destroyed */ } });
        delete (host as any).__modelViewerCleanups;
        delete (host as any).__modelViewerCleanup;
    } else {
        const modelCleanup = (host as any).__modelViewerCleanup;
        if (modelCleanup) {
            modelCleanup();
            delete (host as any).__modelViewerCleanup;
        }
    }
    // Cancel in-progress PDF rendering(s). Drain the array (N pdf blocks) AND the
    // legacy single ref (which points at the first array entry, so clear it too).
    const pdfCleanups = (host as any).__pdfCleanups as Array<() => void> | undefined;
    if (pdfCleanups && pdfCleanups.length > 0) {
        pdfCleanups.forEach(fn => { try { fn(); } catch { /* noop */ } });
        delete (host as any).__pdfCleanups;
        delete (host as any).__pdfCleanup;
    } else {
        const pdfCleanup = (host as any).__pdfCleanup;
        if (pdfCleanup) {
            pdfCleanup();
            delete (host as any).__pdfCleanup;
        }
    }
}

/**
 * Show hotspot popup with content - matching BabylonJS HTML export behavior
 */
export function showHotspotPopup(container: HTMLElement, hotspot: HotspotData, buttonLabels?: ButtonLabels): void {
    const popup = container.querySelector('.storysplat-hotspot-popup') as HTMLElement;
    if (!popup)
        return;
    const hasContentBlocks = Array.isArray(hotspot.contentBlocks) && hotspot.contentBlocks.length > 0;
    if (hotspot.contentType === 'none' && !hasContentBlocks) {
        popup.classList.remove('visible');
        const overlay = container.querySelector('.storysplat-hotspot-overlay') as HTMLElement;
        if (overlay)
            overlay.classList.remove('visible');
        return;
    }
    const titleEl = popup.querySelector('.storysplat-hotspot-popup-title') as HTMLElement;
    const contentEl = popup.querySelector('.storysplat-hotspot-popup-content') as HTMLElement;
    const closeBtn = popup.querySelector('.storysplat-hotspot-popup-close') as HTMLElement;
    // Which hotspot it shows (a hidden hotspot folder closes its popup).
    if (typeof hotspot.id === 'string') popup.dataset.hotspotId = hotspot.id;
    else delete popup.dataset.hotspotId;
    // Reset any previous custom styles and classes
    popup.style.cssText = '';
    popup.classList.remove('fullscreen');
    for (const cls of Array.from(popup.classList)) if (cls.startsWith('storysplat-hotspot-popup--')) popup.classList.remove(cls);
    // Determine activation mode (default to 'click')
    const activationMode = hotspot.activationMode || 'click';
    // Load-bearing legacy-fallback guard: render contentBlocks ONLY when the array is
    // present and non-empty; otherwise the existing single-contentType path runs unchanged.
    const blocks = hotspot.contentBlocks;
    const useBlocks = Array.isArray(blocks) && blocks.length > 0;
    const hasBlockMedia = useBlocks && blocks.some(b =>
        b.kind === 'image' || b.kind === 'video' || b.kind === 'iframe' || b.kind === 'model' || b.kind === 'pdf'
    );
    const hasMediaContent = hasBlockMedia || hotspot.photoUrl || hotspot.popupVideoUrl || (hotspot.contentType === 'iframe' && hotspot.iframeUrl) || (hotspot.contentType === 'model' && hotspot.modelUrl) || (hotspot.contentType === 'pdf' && hotspot.pdfUrl);
    // Apply fullscreen mode for click activation with photo, video, or iframe (matching BabylonJS export)
    if (activationMode === 'click' && hasMediaContent) {
        popup.classList.add('fullscreen');
    }
    // Per-hotspot placement: the nine overlay positions or an edge-to-edge drawer. Both
    // replace the centered / fullscreen presentation (see the --anchor / --drawer rules).
    if (hotspot.popupLayout === 'drawer') {
        popup.classList.remove('fullscreen');
        popup.classList.add('storysplat-hotspot-popup--drawer', `storysplat-hotspot-popup--drawer-${popupDrawerSide(hotspot.popupAnchor)}`);
    } else if (hotspot.popupAnchor && OVERLAY_ANCHORS.has(hotspot.popupAnchor)) {
        popup.classList.remove('fullscreen');
        popup.classList.add(`storysplat-hotspot-popup--anchor-${hotspot.popupAnchor}`);
    } else if (hotspot.popupLayout === 'floating') {
        // Keep the popup even when the theme makes drawers the default.
        popup.classList.add('storysplat-hotspot-popup--floating');
    }
    // Apply per-hotspot styling ONLY where the hotspot actually overrides something.
    // Anything left undefined must fall through to the global theme stylesheet
    // (.storysplat-hotspot-popup rules built from ViewerTheme.popup*), so clear the
    // inline styles the previous hotspot may have left on the shared popup elements.
    if (titleEl)
        titleEl.style.color = '';
    if (closeBtn)
        closeBtn.style.backgroundColor = '';
    // Background: a hotspot may override the colour, the opacity, or both. With only an
    // opacity override we keep the theme's popup colour and re-apply it at that alpha.
    const hasBgColorOverride = typeof hotspot.backgroundColor === 'string' && hotspot.backgroundColor !== '';
    const hasBgAlphaOverride = typeof hotspot.backgroundAlpha === 'number';
    if (hasBgColorOverride) {
        popup.style.backgroundColor = withAlpha(hotspot.backgroundColor as string, hotspot.backgroundAlpha ?? 0.75);
    } else if (hasBgAlphaOverride) {
        const themeBg = getComputedStyle(popup).backgroundColor;
        popup.style.backgroundColor = withAlpha(themeBg, hotspot.backgroundAlpha as number);
    }
    if (hotspot.textColor) {
        popup.style.color = hotspot.textColor;
        if (titleEl)
            titleEl.style.color = hotspot.textColor;
    }
    if (hotspot.fontFamily) {
        popup.style.fontFamily = hotspot.fontFamily;
    }
    if (hotspot.fontSize) {
        popup.style.fontSize = `${hotspot.fontSize}px`;
    }
    // Apply close button color if provided
    if (closeBtn && hotspot.closeButtonColor) {
        closeBtn.style.backgroundColor = hotspot.closeButtonColor;
    }
    // Set title
    if (titleEl) {
        titleEl.textContent = hotspot.title || getButtonLabel(buttonLabels, 'hotspotDefaultTitle');
    }
    // Build content HTML - order matches BabylonJS export: title, iframe, photo, info, link
    let contentHtml = '';
    if (useBlocks) {
        contentHtml += buildContentBlocksHtml(blocks, hotspot.title, buttonLabels, { pdfInViewer: isMediaProtected(container) });
    } else {
    // Iframe content (before photo in BabylonJS export)
    if (hotspot.contentType === 'iframe' && hotspot.iframeUrl) {
        contentHtml += `<iframe src="${hotspot.iframeUrl}" title="${hotspot.title || 'Embedded content'}"></iframe>`;
    }
    // Video content - show when contentType is 'video' or unset (backwards compat)
    if (hotspot.popupVideoUrl && (!hotspot.contentType || hotspot.contentType === 'video')) {
        const attrs = popupVideoAttrs({
            autoplay: hotspot.popupVideoAutoplay,
            loop: hotspot.popupVideoLoop,
            muted: hotspot.popupVideoMuted,
        });
        contentHtml += `<div class="video-container"><video src="${hotspot.popupVideoUrl}" ${attrs}></video></div>`;
    }
    // Image/GIF content - show when contentType is 'image', 'gif', or unset (backwards compat)
    if (hotspot.photoUrl && (!hotspot.contentType || hotspot.contentType === 'image' || hotspot.contentType === 'gif')) {
        contentHtml += `<img src="${hotspot.photoUrl}" alt="${hotspot.title || 'Hotspot image'}" />`;
    }
    // 3D Model content - canvas-based lightbox viewer (loaded on click)
    if (hotspot.contentType === 'model' && hotspot.modelUrl) {
        contentHtml += `<div class="model-container"><div class="model-loading">Loading 3D model...</div><canvas class="model-canvas"></canvas></div>`;
    }
    // PDF content — iframe on desktop, pdf.js on iOS (where iframe PDF doesn't work) and when
    // the scene protects its media (the browser's PDF viewer has a download button)
    if (hotspot.contentType === 'pdf' && hotspot.pdfUrl) {
        if (isIOSDevice() || isMediaProtected(container)) {
            contentHtml += `<div class="pdf-pdfjs-container"></div>`;
        } else {
            contentHtml += `<iframe src="${escapeHtml(hotspot.pdfUrl)}" style="width: 100%; height: 70vh; min-height: 500px; max-height: 600px; aspect-ratio: auto; border: none; border-radius: 8px;" title="${escapeHtml(hotspot.title || 'PDF Document')}"></iframe>`;
        }
    }
    // Information text
    if (hotspot.information) {
        contentHtml += `<div class="storysplat-rich-text">${richTextHtml(hotspot.information, hotspot.informationHtml)}</div>`;
    }
    // External link
    if (hotspot.externalLinkUrl) {
        const btnColor = hotspot.externalLinkButtonColor || '#007bff';
        const escapedUrl = escapeHtml(hotspot.externalLinkUrl);
        const escapedLinkText = escapeHtml(hotspot.externalLinkText || getButtonLabel(buttonLabels, 'openExternalLink'));
        contentHtml += `
      <div role="link" tabindex="0" data-href="${escapedUrl}"
           class="storysplat-hotspot-popup-link" style="background-color: ${safeCssColor(btnColor, '#007bff')}">
        ${escapedLinkText}
      </div>
    `;
    }
    } // end legacy single-contentType path
    if (contentEl) {
        // Text fields (information, link text) are escaped via escapeHtml()
        contentEl.innerHTML = contentHtml; // eslint-disable-line -- structured HTML with escaped user text
        hydratePopupMedia(popup, contentEl, { modelUrl: hotspot.modelUrl, pdfUrl: hotspot.pdfUrl });
        reservePopupImageSpace(contentEl);
    }
    // Show popup (no overlay - matching BabylonJS export)
    popup.classList.add('visible');
    syncPopupDrawerInsets(container);
    // Fix: when a video inside the popup enters native fullscreen,
    // the container's overflow:hidden clips the fullscreen content.
    // Temporarily remove overflow:hidden while a child is in fullscreen.
    const video = popup.querySelector('video');
    if (video) {
        const onFullscreenChange = () => {
            const doc = document as any;
            const fsEl = doc.fullscreenElement || doc.webkitFullscreenElement;
            if (fsEl && container.contains(fsEl)) {
                container.style.overflow = 'visible';
            } else {
                container.style.overflow = 'hidden';
            }
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', onFullscreenChange);
        // Store cleanup reference on the popup element
        (popup as any).__fullscreenCleanup = () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
            container.style.overflow = 'hidden';
        };
    }
}
/**
 * Reserve a box for popup images that haven't loaded yet (hotspot data carries no saved
 * dimensions), so the content below doesn't jump when they arrive. The placeholder is
 * released on load/error. Grid thumbnails already have a fixed 1:1 box and are skipped.
 */
function reservePopupImageSpace(contentEl: HTMLElement): void {
    const imgs = contentEl.querySelectorAll('img') as NodeListOf<HTMLImageElement>;
    imgs.forEach((img) => {
        if (img.closest('.hotspot-gallery-grid')) return;
        if (img.complete && img.naturalWidth > 0) return;
        img.classList.add('storysplat-img-pending');
        const release = () => img.classList.remove('storysplat-img-pending');
        img.addEventListener('load', release, { once: true });
        img.addEventListener('error', release, { once: true });
    });
}
/**
 * Hide hotspot popup
 */
export function hideHotspotPopup(container: HTMLElement): void {
    const popup = container.querySelector('.storysplat-hotspot-popup') as HTMLElement;
    if (popup) {
        teardownPopupMedia(popup);
        // Clean up fullscreen listener if present
        const cleanup = (popup as any).__fullscreenCleanup;
        if (cleanup) {
            cleanup();
            delete (popup as any).__fullscreenCleanup;
        }
        popup.classList.remove('visible', 'fullscreen');
        syncPopupDrawerInsets(container);
    }
}
/**
 * Show/hide virtual joystick overlay
 */
export function setJoystickVisible(elements: UIElements, visible: boolean): void {
    if (elements.joystick) {
        if (visible) {
            elements.joystick.classList.add('visible');
            // Reset thumb position to prevent stuck state
            if (elements.joystickThumb) {
                elements.joystickThumb.classList.remove('active');
                elements.joystickThumb.style.transform = 'translate(0, 0)';
            }
        }
        else {
            elements.joystick.classList.remove('visible');
        }
    }
    if (elements.lookZone) {
        if (visible) {
            elements.lookZone.classList.add('visible');
        }
        else {
            elements.lookZone.classList.remove('visible');
        }
    }
}
/**
 * Update joystick thumb position based on touch input
 */
export function updateJoystickPosition(elements: UIElements, active: boolean, dx: number, dy: number, maxRadius: number): void {
    if (!elements.joystickThumb)
        return;
    if (active) {
        elements.joystickThumb.classList.add('active');
        // Clamp the offset to maxRadius
        const distance = Math.sqrt(dx * dx + dy * dy);
        const clampedDistance = Math.min(distance, maxRadius);
        const scale = distance > 0 ? clampedDistance / distance : 0;
        const clampedX = dx * scale;
        const clampedY = dy * scale;
        elements.joystickThumb.style.transform = `translate(${clampedX}px, ${clampedY}px)`;
    }
    else {
        elements.joystickThumb.classList.remove('active');
        elements.joystickThumb.style.transform = 'translate(0, 0)';
    }
}
/**
 * Update look zone visual state based on touch input
 */
export function updateLookZoneState(elements: UIElements, active: boolean): void {
    if (!elements.lookZone)
        return;
    if (active) {
        elements.lookZone.classList.add('active');
    }
    else {
        elements.lookZone.classList.remove('active');
    }
}
/**
 * Build the scene navigation menu DOM from portal data.
 * Organizes portals into a folder hierarchy based on menuPath.
 */
/** SVG icon paths for scene menu link icons */
const SCENE_MENU_LINK_ICONS: Record<string, string> = {
    link: 'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
    map: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
    calendar: 'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z',
    phone: 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z',
    cart: 'M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z',
    globe: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z',
    download: 'M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z',
};

/**
 * Scenes visited this session — scene ids the user has navigated AWAY from via a portal. Kept at
 * MODULE scope so it survives in-page portal navigation (the viewer is recreated in the same page,
 * not reloaded) yet resets on a real page refresh — a deliberately session-local "I've been here"
 * cue. markSceneVisited() is called by createViewer on portal activation.
 */
const visitedScenes = new Set<string>();
export function markSceneVisited(sceneId?: string): void {
    if (sceneId) visitedScenes.add(sceneId);
}

function buildSceneMenuDom(parent: HTMLElement, portals: PortalData[], customLinks: SceneMenuLink[] = [], startCollapsed = false, currentSpaceTitle = '', showPreviewImages = false, currentSceneId = '', currentSpaceMenuPath?: string, currentSpaceMenuOrder?: number, folderOrder?: string[]): void {
    // The current scene shown IN the menu (synthesized below, or an already-marked portal) replaces
    // the "Current Space" header above; otherwise we fall back to that header.
    let hasInlineCurrentSpace = portals.some((p) => p.isCurrentScene);
    type MenuItem = { type: 'portal'; data: PortalData } | { type: 'link'; data: SceneMenuLink };

    interface FolderNode {
        name: string;
        children: Map<string, FolderNode>;
        items: Array<{ item: MenuItem; order: number }>;
        minOrder: number; // smallest item order anywhere in this subtree — positions folders vs loose items
    }

    const root: FolderNode = { name: '', children: new Map(), items: [], minOrder: Infinity };

    function addToTree(item: MenuItem, order: number, menuPath?: string): void {
        const path = menuPath?.trim();
        root.minOrder = Math.min(root.minOrder, order);
        if (!path) {
            root.items.push({ item, order });
            return;
        }
        const parts = path.split('/').map(s => s.trim()).filter(Boolean);
        let node = root;
        for (const part of parts) {
            if (!node.children.has(part)) {
                node.children.set(part, { name: part, children: new Map(), items: [], minOrder: Infinity });
            }
            node = node.children.get(part)!;
            node.minOrder = Math.min(node.minOrder, order);
        }
        node.items.push({ item, order });
    }

    // Build menu portal list: if a targetSceneId has any menuOnly portal, suppress
    // physical (non-menuOnly) portals for that target from the menu (they're visible
    // in the 3D scene already). Multiple menuOnly portals for the same target are
    // allowed (user may want different labels/folders). Story chain portals are
    // injected upstream and are always menuOnly; they self-filter by existing targets.
    const targetsWithMenuOnly = new Set<string>();
    for (const portal of portals) {
        if (portal.menuOnly && portal.targetSceneId) {
            targetsWithMenuOnly.add(portal.targetSceneId);
        }
    }

    // Collect the items that should appear in the menu, paired with a resolved
    // sort key. Portals and custom links share one unified order via `menuOrder`.
    //
    // Backward compat: existing published scenes have no `menuOrder` on any item.
    // When NOTHING is ordered we fall back to the legacy layout — every portal
    // first (in array order), then every custom link (in array order) — so those
    // menus render exactly as before. When ANY item carries a `menuOrder`, that
    // numeric key drives the order; un-ordered stragglers fall back to a synthetic
    // key that still keeps portals ahead of links and preserves array order.
    type OrderedItem = { item: MenuItem; order: number; seq: number };
    const collected: OrderedItem[] = [];
    let anyMenuOrder = false;
    let seq = 0; // running index across portals then links — the legacy/fallback order

    for (const portal of portals) {
        // 3D portals can opt out of the menu while keeping their in-world presence.
        // Menu-only portals are inherently menu items and ignore showInMenu.
        if (!portal.menuOnly && portal.showInMenu === false) {
            continue;
        }
        // Skip physical portals whose target already has a menuOnly entry
        if (!portal.menuOnly && portal.targetSceneId && targetsWithMenuOnly.has(portal.targetSceneId)) {
            continue;
        }
        const hasOrder = typeof portal.menuOrder === 'number' && Number.isFinite(portal.menuOrder);
        if (hasOrder) anyMenuOrder = true;
        // Fallback key === seq, so un-ordered portals keep array order ahead of links.
        collected.push({ item: { type: 'portal', data: portal }, order: hasOrder ? portal.menuOrder! : seq, seq });
        seq++;
    }
    for (const link of customLinks) {
        const hasOrder = typeof link.menuOrder === 'number' && Number.isFinite(link.menuOrder);
        if (hasOrder) anyMenuOrder = true;
        // Un-ordered links sort after every un-ordered portal (seq keeps growing).
        collected.push({ item: { type: 'link', data: link }, order: hasOrder ? link.menuOrder! : seq, seq });
        seq++;
    }

    // The current scene never links to itself, so its menu slot can't come from connections —
    // synthesize it here so it ALWAYS appears as an in-list item (rendered selected, "Current
    // Space"), unless a real connection to it already exists (then mark that one). A persisted
    // currentSpaceMenuOrder gives it a STABLE position (default: pinned to the top) so it doesn't
    // drift between scenes; currentSpaceMenuPath optionally places it inside a folder.
    if (currentSceneId && !hasInlineCurrentSpace) {
        const selfIdx = collected.findIndex((c) => c.item.type === 'portal' && c.item.data.targetSceneId === currentSceneId);
        if (selfIdx >= 0) {
            (collected[selfIdx].item.data as PortalData).isCurrentScene = true;
        } else {
            const hasOrder = typeof currentSpaceMenuOrder === 'number' && Number.isFinite(currentSpaceMenuOrder);
            const synthesized = {
                id: '__current_space__',
                targetSceneId: currentSceneId,
                title: currentSpaceTitle || undefined,
                type: 'sphere',
                menuOnly: true,
                isCurrentScene: true,
                showInMenu: true,
                menuPath: currentSpaceMenuPath,
                menuOrder: hasOrder ? currentSpaceMenuOrder : undefined,
            } as unknown as PortalData;
            collected.push({
                item: { type: 'portal', data: synthesized },
                order: hasOrder ? currentSpaceMenuOrder! : -1e9, // default: pin to top
                seq: -1, // ties resolve ahead of everything
            });
            anyMenuOrder = true; // force the sort so the slot / top-pin is honored
        }
        hasInlineCurrentSpace = true;
    }

    // With no menuOrder anywhere, `collected` is already in legacy order
    // (portals-then-links, array order) and we skip sorting entirely. When any
    // item carries a menuOrder, sort by it; `seq` breaks ties stably and also
    // anchors any un-ordered stragglers to their legacy slot.
    if (anyMenuOrder) {
        collected.sort((a, b) => (a.order - b.order) || (a.seq - b.seq));
    }

    for (const { item, order } of collected) {
        addToTree(item, order, item.data.menuPath);
    }

    function createPortalItem(portal: PortalData): HTMLElement {
        const item = document.createElement('div');
        item.className = 'storysplat-scene-menu-item';
        if (portal.isCurrentScene) {
            // The current scene at its own menu slot — a non-navigating "selected" entry (no
            // data-portal-id, so setupSceneMenuClickHandlers skips it).
            item.classList.add('current-space-selected');
        } else {
            item.setAttribute('data-portal-id', portal.id);
            item.setAttribute('data-target-scene-id', portal.targetSceneId);
            // Subtle "been here this session" cue (resets on refresh).
            if (visitedScenes.has(portal.targetSceneId)) item.classList.add('visited');
        }

        if (showPreviewImages && portal.targetSceneThumbnail) {
            const thumb = document.createElement('img');
            thumb.className = 'storysplat-scene-menu-item-thumb';
            thumb.src = portal.targetSceneThumbnail;
            thumb.alt = '';
            thumb.loading = 'lazy';
            item.appendChild(thumb);
        }

        const label = document.createElement('span');
        label.className = 'storysplat-scene-menu-item-label';
        label.textContent = portal.title || portal.targetSceneName || 'Untitled';
        item.appendChild(label);

        if (portal.isCurrentScene) {
            const badge = document.createElement('span');
            badge.className = 'storysplat-scene-menu-current-badge';
            badge.textContent = 'Current Space';
            item.appendChild(badge);
        }
        return item;
    }

    function createLinkItem(link: SceneMenuLink): HTMLElement {
        // Splat-swap links are non-navigating action items: clicking swaps the displayed
        // splat in place (handled via delegation in setupSceneMenuClickHandlers).
        const isSwapLink = link.swapAction === 'splat-swap';
        const isMediaLink = !isSwapLink && link.contentType && link.contentType !== 'link';
        const isAnchor = !isSwapLink && !isMediaLink;
        const mediaUrl = link.mediaUrl || link.url;

        // For media + swap links, use a div instead of an anchor tag
        const item = isAnchor ? document.createElement('a') : document.createElement('div');
        item.className = 'storysplat-scene-menu-item storysplat-scene-menu-link';
        if (isAnchor) {
            item.setAttribute('href', link.url);
            item.setAttribute('target', link.openInNewTab === false ? '_self' : '_blank');
            item.setAttribute('rel', 'noopener noreferrer');
        }
        item.setAttribute('data-link-id', link.id);
        if (isMediaLink) {
            item.setAttribute('data-content-type', link.contentType!);
            item.setAttribute('data-media-url', mediaUrl);
            item.style.cursor = 'pointer';
        }
        if (isSwapLink) {
            item.setAttribute('data-action', 'splat-swap');
            item.setAttribute('data-swap-splat-url', link.targetSplatUrl || '');
            item.style.cursor = 'pointer';
        }

        // Icon
        const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        iconSvg.setAttribute('viewBox', '0 0 24 24');
        iconSvg.classList.add('storysplat-scene-menu-link-icon');
        const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        iconPath.setAttribute('d', SCENE_MENU_LINK_ICONS[link.icon || 'link']);
        iconSvg.appendChild(iconPath);
        item.appendChild(iconSvg);

        const label = document.createElement('span');
        label.className = 'storysplat-scene-menu-item-label';
        label.textContent = link.label;
        item.appendChild(label);

        if (isAnchor) {
            // External link indicator
            const extSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            extSvg.setAttribute('viewBox', '0 0 24 24');
            extSvg.classList.add('storysplat-scene-menu-link-ext');
            const extPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            extPath.setAttribute('d', 'M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z');
            extSvg.appendChild(extPath);
            item.appendChild(extSvg);
        }

        // Prevent menu close bubbling — let the link navigate naturally or handle media popup
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            if (isMediaLink) {
                e.preventDefault();
                showMenuLinkMediaPopup(link.label, link.contentType!, mediaUrl, parent.closest('.storysplat-viewer-container') as HTMLElement || document.body);
            }
        });

        return item;
    }

    /** Show a fullscreen media popup for menu link content (PDF, image, video, iframe) */
    function showMenuLinkMediaPopup(title: string, contentType: string, url: string, container: HTMLElement): void {
        // Remove any existing media popup
        const existing = container.querySelector('.storysplat-menu-media-popup');
        if (existing) existing.remove();

        const overlay = document.createElement('div');
        overlay.className = 'storysplat-menu-media-popup storysplat-hotspot-popup fullscreen';
        overlay.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 100001; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.85);';

        // Native-fullscreen support (button hidden where the API is missing \u2014 iOS
        // Safari only fullscreens <video>). Vendor-prefixed fallbacks cover older Safari.
        const overlayFs = overlay as HTMLElement & { webkitRequestFullscreen?: () => void };
        const docFs = document as Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
        const canFullscreen = typeof overlay.requestFullscreen === 'function' || typeof overlayFs.webkitRequestFullscreen === 'function';
        const isOwnFullscreen = () => document.fullscreenElement === overlay || docFs.webkitFullscreenElement === overlay;

        let cancelPdfRender: (() => void) | null = null;
        const closePopup = () => {
            if (cancelPdfRender) cancelPdfRender();
            const v = overlay.querySelector('video') as HTMLVideoElement | null;
            if (v) { v.pause(); v.src = ''; }
            if (isOwnFullscreen()) {
                if (document.exitFullscreen) document.exitFullscreen().catch(() => { /* already exiting */ });
                else docFs.webkitExitFullscreen?.();
            }
            overlay.remove();
            document.removeEventListener('keydown', onEscape);
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange' as 'fullscreenchange', onFullscreenChange);
        };

        const onEscape = (e: KeyboardEvent) => {
            // In fullscreen, Escape exits fullscreen (browser-native) \u2014 keep the popup
            // open so a second Escape closes it, instead of both happening at once.
            if (e.key === 'Escape' && !isOwnFullscreen()) closePopup();
        };
        document.addEventListener('keydown', onEscape);

        // Close + fullscreen buttons \u2014 pinned as a row to the top-right of the modal box
        // (content), at the title's level, instead of centered above it. (The shared
        // .fullscreen rule makes the overlay a centered column, so as direct overlay
        // children the buttons stack above the title; absolute-positioning the row
        // inside `content` puts it top-right. Inline styles win \u2014 no shared close rule
        // uses !important.)
        const closeBtn = document.createElement('button');
        closeBtn.className = 'storysplat-hotspot-popup-close';
        closeBtn.textContent = '\u00D7';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.addEventListener('click', closePopup);
        closeBtn.style.cssText = 'margin: 0;';

        const expandIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>';
        const compressIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7"/></svg>';

        let fullscreenBtn: HTMLButtonElement | null = null;
        if (canFullscreen) {
            fullscreenBtn = document.createElement('button');
            // Distinct class from the close button. It used to reuse
            // `storysplat-hotspot-popup-close` purely to inherit its styling,
            // which made every fullscreen click record as "hotspot popup close"
            // in analytics (describeButton matches class before aria-label) and
            // made `querySelector('.storysplat-hotspot-popup-close')` ambiguous
            // inside this popup. The shared CSS rules list both classes.
            fullscreenBtn.className = 'storysplat-hotspot-popup-fullscreen';
            fullscreenBtn.setAttribute('aria-label', 'Toggle fullscreen');
            fullscreenBtn.title = 'Fullscreen';
            fullscreenBtn.innerHTML = expandIcon;
            fullscreenBtn.style.cssText = 'margin: 0; display: inline-flex; align-items: center; justify-content: center;';
            fullscreenBtn.addEventListener('click', () => {
                if (isOwnFullscreen()) {
                    if (document.exitFullscreen) document.exitFullscreen().catch(() => { /* not in fullscreen */ });
                    else docFs.webkitExitFullscreen?.();
                } else if (overlay.requestFullscreen) {
                    overlay.requestFullscreen().catch(() => { /* denied (e.g. iframe without allowfullscreen) */ });
                } else {
                    overlayFs.webkitRequestFullscreen?.();
                }
            });
        }

        const buttonRow = document.createElement('div');
        buttonRow.style.cssText = 'position: absolute; top: 0; right: 0; display: flex; gap: 8px; z-index: 2;';
        if (fullscreenBtn) buttonRow.appendChild(fullscreenBtn);
        buttonRow.appendChild(closeBtn);

        // Content wrapper (position:relative so the button row anchors to its top-right corner)
        const content = document.createElement('div');
        content.className = 'storysplat-hotspot-popup-content';
        content.style.cssText = 'position: relative; width: 90%; max-width: 900px; max-height: 85vh; display: flex; flex-direction: column; align-items: center;';
        content.appendChild(buttonRow);

        // While the overlay is fullscreen, let the media use the whole viewport \u2014 the
        // default 70vh / 600px caps would leave a small PDF floating on a huge screen.
        const applyFullscreenLayout = (active: boolean) => {
            content.style.maxWidth = active ? '1400px' : '900px';
            content.style.maxHeight = active ? '96vh' : '85vh';
            const media = content.querySelector('iframe, img, video') as HTMLElement | null;
            if (media) {
                if (media.tagName === 'IFRAME') media.style.height = active ? 'calc(100vh - 90px)' : '70vh';
                media.style.maxHeight = active ? 'calc(100vh - 90px)' : (media.tagName === 'IFRAME' ? '600px' : '70vh');
            }
            if (fullscreenBtn) fullscreenBtn.innerHTML = active ? compressIcon : expandIcon;
        };
        const onFullscreenChange = () => applyFullscreenLayout(isOwnFullscreen());
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('webkitfullscreenchange' as 'fullscreenchange', onFullscreenChange);

        // Title
        if (title) {
            const titleEl = document.createElement('h3');
            titleEl.className = 'storysplat-hotspot-popup-title';
            titleEl.textContent = title;
            titleEl.style.cssText = 'color: white; margin: 0 0 12px 0; font-size: 18px;';
            content.appendChild(titleEl);
        }

        // Media content — use pdf.js on iOS Safari since iframe PDF embedding doesn't work, and
        // when the scene protects its media (the browser's PDF viewer has a download button)
        if (contentType === 'pdf') {
            if (isIOSDevice() || isMediaProtected(container)) {
                cancelPdfRender = renderPdfWithPdfJs(url, content);
            } else {
                const iframe = document.createElement('iframe');
                iframe.src = url;
                iframe.title = title || 'PDF Document';
                iframe.style.cssText = 'width: 100%; height: 70vh; max-height: 600px; border: none; border-radius: 8px;';
                content.appendChild(iframe);
            }
        } else if (contentType === 'iframe') {
            const iframe = document.createElement('iframe');
            iframe.src = url;
            iframe.title = title || 'Content';
            iframe.style.cssText = 'width: 100%; height: 70vh; max-height: 600px; border: none; border-radius: 8px;';
            content.appendChild(iframe);
        } else if (contentType === 'image') {
            const img = document.createElement('img');
            img.src = url;
            img.alt = title || 'Image';
            img.style.cssText = 'max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 8px;';
            content.appendChild(img);
        } else if (contentType === 'video') {
            const video = document.createElement('video');
            video.src = url;
            video.controls = true;
            video.setAttribute('playsinline', '');
            video.setAttribute('preload', 'metadata');
            video.style.cssText = 'max-width: 100%; max-height: 70vh; border-radius: 8px;';
            content.appendChild(video);
        }

        overlay.appendChild(content);

        // Close on background click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) closePopup();
        });

        container.appendChild(overlay);
    }

    function renderFolder(container: HTMLElement, node: FolderNode, pathPrefix = ''): void {
        // Render sub-folders, honoring explicit folder order (uiOptions.sceneMenuFolderOrder) when
        // set: listed full-paths first in their listed order, unlisted folders keep insertion order
        // after, stable tiebreak on insertion index — mirrors the item-level menuOrder fallback.
        let childList = [...node.children.values()];
        if (folderOrder && folderOrder.length) {
            const rank = (name: string): number => {
                const full = pathPrefix ? pathPrefix + '/' + name : name;
                const i = folderOrder.indexOf(full);
                return i < 0 ? Number.MAX_SAFE_INTEGER : i;
            };
            childList = childList
                .map((c, i) => ({ c, i }))
                .sort((a, b) => (rank(a.c.name) - rank(b.c.name)) || (a.i - b.i))
                .map(({ c }) => c);
        }
        // A loose (non-foldered) item whose order is ABOVE the earliest folder item renders above the
        // folders — a "home base" at the very top; the rest render below. Folders render as a block.
        const folderMinOrder = childList.reduce((m, c) => Math.min(m, c.minOrder), Infinity);
        const renderEntry = (entry: { item: MenuItem; order: number }) => {
            container.appendChild(entry.item.type === 'portal' ? createPortalItem(entry.item.data) : createLinkItem(entry.item.data));
        };
        node.items.filter((e) => e.order < folderMinOrder).forEach(renderEntry);
        for (const child of childList) {
            const folder = document.createElement('div');
            folder.className = 'storysplat-scene-menu-folder';

            const header = document.createElement('div');
            header.className = 'storysplat-scene-menu-folder-header';
            const chevronSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            chevronSvg.setAttribute('viewBox', '0 0 24 24');
            const chevronPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            chevronPath.setAttribute('d', 'M7 10l5 5 5-5z');
            chevronSvg.appendChild(chevronPath);
            header.appendChild(chevronSvg);
            header.appendChild(document.createTextNode(child.name));
            // When configured, categories start collapsed. The same `collapsed`
            // class the click-toggle uses is applied up front to both header
            // (chevron rotation) and children (display:none); the existing
            // per-mode toggle handlers still flip it open afterwards.
            if (startCollapsed) header.classList.add('collapsed');
            folder.appendChild(header);

            const childrenContainer = document.createElement('div');
            childrenContainer.className = 'storysplat-scene-menu-folder-children';
            if (startCollapsed) childrenContainer.classList.add('collapsed');
            renderFolder(childrenContainer, child, pathPrefix ? pathPrefix + '/' + child.name : child.name);
            folder.appendChild(childrenContainer);
            container.appendChild(folder);
        }
        // Loose items that fall at/after the folders render below them.
        node.items.filter((e) => e.order >= folderMinOrder).forEach(renderEntry);
    }

    // "Current Space" header — pinned at the very top, above every category
    // folder and the spaces/scenes list.
    const spaceTitle = (currentSpaceTitle || '').trim();
    if (spaceTitle && !hasInlineCurrentSpace) {
        const header = document.createElement('div');
        header.className = 'storysplat-scene-menu-current-space';
        const label = document.createElement('div');
        label.className = 'storysplat-scene-menu-current-space-label';
        label.textContent = 'Current Space';
        const nameEl = document.createElement('div');
        nameEl.className = 'storysplat-scene-menu-current-space-name';
        nameEl.textContent = spaceTitle;
        nameEl.title = spaceTitle;
        header.appendChild(label);
        header.appendChild(nameEl);
        parent.appendChild(header);
    }

    renderFolder(parent, root);
}

/**
 * Render an EXACT, read-only preview of the slide-out (sidebar) scene menu into `container`, using
 * the REAL menu renderer (buildSceneMenuDom) and the REAL injected CSS — so the editor's "Preview
 * menu" matches the published menu (folders, order, items, thumbnails, the in-list Current Space
 * marker and the session "visited" cue). The sidebar panel is position:absolute, so `container` is
 * forced position:relative + overflow:hidden to frame it the same way the viewer embeds it. Safe to
 * call repeatedly; it clears and rebuilds.
 */
export function renderSceneMenuPreview(
    container: HTMLElement,
    opts: {
        portals: PortalData[];
        customLinks?: SceneMenuLink[];
        menuTitle?: string;
        buttonLabels?: ViewerButtonLabels;
        startCollapsed?: boolean;
        currentSpaceTitle?: string;
        currentSceneId?: string;
        currentSpaceMenuPath?: string;
        currentSpaceMenuOrder?: number;
        folderOrder?: string[];
        showPreviewImages?: boolean;
        uiColor?: string;
        template?: string;
        viewerTheme?: ViewerTheme;
    },
): void {
    // Real viewer menu CSS (re-applied each call so theme changes take effect).
    injectStyles(opts.uiColor ?? '#CC5833', opts.template ?? 'minimal', opts.viewerTheme, 'scene-menu-preview');
    container.style.position = 'relative';
    container.style.overflow = 'hidden';
    container.innerHTML = '';
    const panel = document.createElement('div');
    panel.className = 'storysplat-scene-menu-container storysplat-scene-menu-container--sidebar open';
    const header = document.createElement('div');
    header.className = 'storysplat-scene-menu-sidebar-header';
    // Preview-only: undo the real menu's +3.5px title nudge (looks too far right in the small frame).
    header.style.paddingLeft = '12px';
    const titleSpan = document.createElement('span');
    titleSpan.textContent = (opts.menuTitle || '').trim() || getButtonLabel(opts.buttonLabels, 'scenes');
    header.appendChild(titleSpan);
    panel.appendChild(header);
    const body = document.createElement('div');
    body.className = 'storysplat-scene-menu-sidebar-body';
    // Preview-only: extra breathing room on the right so items aren't flush against the frame edge.
    body.style.paddingRight = '16px';
    buildSceneMenuDom(
        body,
        opts.portals || [],
        opts.customLinks || [],
        !!opts.startCollapsed,
        opts.currentSpaceTitle || '',
        !!opts.showPreviewImages,
        opts.currentSceneId || '',
        opts.currentSpaceMenuPath,
        opts.currentSpaceMenuOrder,
        opts.folderOrder,
    );
    panel.appendChild(body);
    container.appendChild(panel);
}

/**
 * Setup scene menu click handlers for portal navigation
 */
export function setupSceneMenuClickHandlers(
    elements: UIElements,
    onSceneSelect: (portalId: string) => void,
    onSplatSwap?: (url: string) => void,
    /** Any custom item (link, media or swap) was clicked, alongside what it does itself. */
    onLinkClick?: (linkId: string) => void
): void {
    if (!elements.sceneMenuContainer) return;

    const menuContainer = elements.sceneMenuContainer;
    const items = menuContainer.querySelectorAll('.storysplat-scene-menu-item[data-portal-id]');
    // Sidebar mode: the panel itself carries the `open` class and the toggle is a
    // sibling on the parent container; classic mode: the dropdown carries `open`
    // and the toggle lives inside the container. closeMenu() handles both shapes.
    const isSidebar = menuContainer.classList.contains('storysplat-scene-menu-container--sidebar');
    const toggleBtn = isSidebar
        ? menuContainer.parentElement?.querySelector('.storysplat-scene-menu-toggle--sidebar')
        : menuContainer.querySelector('.storysplat-scene-menu-toggle');
    const dropdown = menuContainer.querySelector('.storysplat-scene-menu-dropdown');
    const closeMenu = () => {
        toggleBtn?.classList.remove('open');
        dropdown?.classList.remove('open');
        if (isSidebar) menuContainer.classList.remove('open');
    };

    items.forEach((item) => {
        item.addEventListener('click', () => {
            const portalId = item.getAttribute('data-portal-id') || '';
            onSceneSelect(portalId);
            // Close menu after selection
            closeMenu();
        });
    });

    // Wire splat-swap links: an in-place swap of the displayed splat (no navigation/teardown).
    const swapItems = menuContainer.querySelectorAll('.storysplat-scene-menu-item[data-swap-splat-url]');
    swapItems.forEach((item) => {
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            const url = item.getAttribute('data-swap-splat-url') || '';
            if (url) onSplatSwap?.(url);
            // Close menu after selection (mirror portal close logic)
            closeMenu();
        });
    });

    // Every custom item: the item's own listener (link, media popup) still runs; stopPropagation
    // there only keeps the click from closing the menu through its ancestors.
    if (onLinkClick) {
        menuContainer.querySelectorAll('.storysplat-scene-menu-item[data-link-id]').forEach((item) => {
            item.addEventListener('click', () => {
                const linkId = item.getAttribute('data-link-id');
                if (linkId) onLinkClick(linkId);
            });
        });
    }
}

/**
 * Show/hide WASD key hint overlay (desktop fly mode only)
 */
export function setWasdHintVisible(elements: UIElements, visible: boolean, hintMode: 'fly' | 'walk' = 'fly'): void {
    if (elements.wasdHint) {
        // Walk mode hides Q/E + the refocus row and shows the Space-to-jump row (see CSS).
        elements.wasdHint.classList.toggle('mode-walk', hintMode === 'walk');
        if (visible) {
            elements.wasdHint.classList.add('visible');
        }
        else {
            elements.wasdHint.classList.remove('visible');
        }
    }
}
/**
 * Show/hide orbit hint overlay (desktop orbit mode)
 */
export function setOrbitHintVisible(elements: UIElements, visible: boolean, refocusLocked?: boolean): void {
    if (refocusLocked !== undefined) setOrbitHintRefocusLocked(elements, refocusLocked);
    if (elements.orbitHint) {
        if (visible) {
            elements.orbitHint.classList.add('visible');
        }
        else {
            elements.orbitHint.classList.remove('visible');
        }
    }
}
/**
 * "Limit orbit rotation" locks the orbit pivot, so click-to-refocus does nothing: drop the
 * "Click to refocus" row from the orbit hint instead of advertising it.
 */
export function setOrbitHintRefocusLocked(elements: UIElements, locked: boolean): void {
    elements.orbitHint?.classList.toggle('storysplat-orbit-hint--refocus-locked', locked);
}
/**
 * Setup keyboard highlight and click-to-simulate interaction for WASD+QE keys.
 * - Highlights keys when physically pressed on keyboard.
 * - Switches the four movement labels between WASD and arrows based on real input.
 * - Allows clicking/tapping keys to simulate keyboard input
 * Returns a cleanup function to remove event listeners.
 */
export function setupWasdKeyInteraction(elements: UIElements): (() => void) | null {
    if (!elements.wasdHint) return null;

    const keyElements = elements.wasdHint.querySelectorAll<HTMLElement>('.storysplat-wasd-key[data-keycode]');
    if (keyElements.length === 0) return null;

    // Map keycode -> DOM element for fast lookup
    const keyMap = new Map<string, HTMLElement>();
    const movementKeyElements: HTMLElement[] = [];
    keyElements.forEach(el => {
        const code = el.getAttribute('data-keycode');
        if (code) keyMap.set(code, el);
        const arrowCode = el.dataset.arrowKeycode;
        if (arrowCode) {
            keyMap.set(arrowCode, el);
            movementKeyElements.push(el);
        }
    });

    type MovementKeyDisplay = 'wasd' | 'arrows';
    const renderMovementKeys = (display: MovementKeyDisplay) => {
        if (elements.wasdHint!.dataset.movementKeyDisplay === display) return;
        elements.wasdHint!.dataset.movementKeyDisplay = display;
        movementKeyElements.forEach(el => {
            const wasdLabel = el.dataset.wasdLabel;
            const arrowLabel = el.dataset.arrowLabel;
            const arrowCode = el.dataset.arrowKeycode;
            if (!wasdLabel || !arrowLabel || !arrowCode) return;
            const direction = arrowCode.replace('Arrow', '');
            el.textContent = display === 'arrows' ? arrowLabel : wasdLabel;
            el.setAttribute('aria-label', display === 'arrows'
                ? `${direction} arrow movement key; ${wasdLabel} also supported`
                : `${wasdLabel} movement key; ${direction} arrow also supported`);
        });
    };
    const initialDisplay = elements.wasdHint.dataset.movementKeyDisplay === 'arrows' ? 'arrows' : 'wasd';
    renderMovementKeys(initialDisplay);

    // Track real and click-simulated codes separately. Multiple codes can map
    // to the same visual key (for example KeyW and ArrowUp), so the element
    // stays highlighted until every aliased source has been released.
    const simulatedKeys = new Set<string>();
    const heldKeys = new Set<string>();
    const syntheticKeydowns = new Set<string>();
    const syntheticKeyups = new Set<string>();
    const updateKeyHighlight = (el: HTMLElement) => {
        const active = Array.from(keyMap).some(([code, mappedEl]) =>
            mappedEl === el && (heldKeys.has(code) || simulatedKeys.has(code))
        );
        el.classList.toggle('active', active);
    };

    // Highlight on real keyboard press
    const onKeyDown = (e: KeyboardEvent) => {
        // Same guard as createViewer's movement handlers: keys typed into a text
        // field don't move the camera, so they must not flip the label family or
        // light up the hint either.
        const focused = document.activeElement as HTMLElement | null;
        if (focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' || focused.isContentEditable)) return;
        if (!syntheticKeydowns.has(e.code)) {
            heldKeys.add(e.code);
            if (e.code.startsWith('Arrow') && keyMap.has(e.code)) renderMovementKeys('arrows');
            else if (/^Key[WASD]$/.test(e.code)) renderMovementKeys('wasd');
        }
        const el = keyMap.get(e.code);
        if (el) updateKeyHighlight(el);
    };
    const onKeyUp = (e: KeyboardEvent) => {
        if (!syntheticKeyups.has(e.code)) heldKeys.delete(e.code);
        const el = keyMap.get(e.code);
        if (el) updateKeyHighlight(el);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    // Click/tap to simulate key press
    const activePointers = new Map<string, number>(); // keycode -> pointerId

    keyElements.forEach(el => {
        const code = el.getAttribute('data-keycode');
        if (!code) return;

        const startSimulation = (pointerId: number) => {
            if (activePointers.has(code)) return;
            activePointers.set(code, pointerId);
            simulatedKeys.add(code);
            updateKeyHighlight(el);
            // Dispatch synthetic keydown without recording it as a physical hold.
            syntheticKeydowns.add(code);
            try {
                document.dispatchEvent(new KeyboardEvent('keydown', {
                    code,
                    key: code.replace('Key', '').toLowerCase(),
                    bubbles: true,
                    cancelable: true
                }));
            } finally {
                syntheticKeydowns.delete(code);
            }
        };

        const stopSimulation = () => {
            if (!activePointers.has(code)) return;
            activePointers.delete(code);
            simulatedKeys.delete(code);
            // Dispatch synthetic keyup without treating it as a physical release
            // of the same code, which may still be held on the keyboard.
            syntheticKeyups.add(code);
            try {
                document.dispatchEvent(new KeyboardEvent('keyup', {
                    code,
                    key: code.replace('Key', '').toLowerCase(),
                    bubbles: true,
                    cancelable: true
                }));
            } finally {
                syntheticKeyups.delete(code);
            }
            updateKeyHighlight(el);
        };

        el.addEventListener('pointerdown', (e: PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            el.setPointerCapture(e.pointerId);
            startSimulation(e.pointerId);
        });

        el.addEventListener('pointerup', (e: PointerEvent) => {
            e.stopPropagation();
            stopSimulation();
        });

        el.addEventListener('pointercancel', () => {
            stopSimulation();
        });

        el.addEventListener('pointerleave', () => {
            stopSimulation();
        });

        // Prevent context menu on long press
        el.addEventListener('contextmenu', (e) => e.preventDefault());
    });

    // Cleanup
    return () => {
        document.removeEventListener('keydown', onKeyDown);
        document.removeEventListener('keyup', onKeyUp);
        // Stop any active simulations
        activePointers.forEach((_, code) => {
            const el = keyMap.get(code);
            if (el) el.classList.remove('active');
            document.dispatchEvent(new KeyboardEvent('keyup', {
                code,
                key: code.replace('Key', '').toLowerCase(),
                bubbles: true,
                cancelable: true
            }));
        });
        activePointers.clear();
        simulatedKeys.clear();
        heldKeys.clear();
        syntheticKeydowns.clear();
        syntheticKeyups.clear();
    };
}
/**
 * Show/hide double-tap hint overlay (explore mode)
 */
export function setDoubleTapHintVisible(elements: UIElements, visible: boolean): void {
    if (elements.doubleTapHint) {
        if (visible) {
            elements.doubleTapHint.classList.remove('fading');
            elements.doubleTapHint.classList.add('visible');
        }
        else {
            // Fade out then hide
            elements.doubleTapHint.classList.add('fading');
            setTimeout(() => {
                elements.doubleTapHint?.classList.remove('visible', 'fading');
            }, 400);
        }
    }
}
/**
 * Show/hide tour-mode look-around hint. Visible while tour mode is active and
 * the user hasn't interacted yet — dismissed on first drag in createViewer.
 */
export function setTourLookHintVisible(elements: UIElements, visible: boolean): void {
    if (!elements.tourLookHint) return;
    if (visible) {
        elements.tourLookHint.classList.remove('fading');
        elements.tourLookHint.classList.add('visible');
    } else {
        elements.tourLookHint.classList.add('fading');
        setTimeout(() => {
            elements.tourLookHint?.classList.remove('visible', 'fading');
        }, 400);
    }
}
/**
 * Update active waypoint in the waypoint list dropdown
 */
export function updateWaypointListActive(elements: UIElements, index: number): void {
    if (!elements.waypointListContainer)
        return;
    // Match on the tour index, not the item's position: hidden waypoints have no item. While the
    // tour is at one of them, no item is active.
    const items = elements.waypointListContainer.querySelectorAll('.storysplat-waypoint-item');
    items.forEach((item) => {
        if (item.getAttribute('data-waypoint-index') === String(index)) {
            item.classList.add('active');
        }
        else {
            item.classList.remove('active');
        }
    });
}
/**
 * Setup waypoint list click handlers. A tour waypoint calls `onWaypointClick` with its tour index;
 * an extra waypoint calls `onExtraWaypointClick` with its id and stays marked active until the
 * tour moves (updateWaypointListActive).
 */
export function setupWaypointListClickHandlers(elements: UIElements, onWaypointClick: (index: number) => void, onExtraWaypointClick?: (id: string) => void): void {
    if (!elements.waypointListContainer)
        return;
    const items = elements.waypointListContainer.querySelectorAll<HTMLElement>('.storysplat-waypoint-item');
    const toggleBtn = elements.waypointListContainer.querySelector('.storysplat-waypoint-list-toggle');
    const dropdown = elements.waypointListContainer.querySelector('.storysplat-waypoint-list-dropdown');
    items.forEach((item) => {
        item.addEventListener('click', () => {
            const extraId = item.dataset.extraWaypointId;
            if (extraId) {
                items.forEach((other) => other.classList.toggle('active', other === item));
                onExtraWaypointClick?.(extraId);
            } else {
                const index = parseInt(item.getAttribute('data-waypoint-index') || '0', 10);
                onWaypointClick(index);
            }
            // Close dropdown after selection
            toggleBtn?.classList.remove('open');
            dropdown?.classList.remove('open');
        });
    });
}
/**
 * Lazy load UI options
 */
export interface LazyLoadUIOptions {
    thumbnailUrl?: string;
    /**
     * Type of media for the thumbnail.
     * - 'image': Static image (jpg, png, webp)
     * - 'video': Video file (mp4, webm) - will autoplay muted and loop
     * - 'gif': Animated GIF
     * If not specified, auto-detects from URL extension
     */
    thumbnailType?: 'image' | 'video' | 'gif';
    buttonText?: string;
    uiColor?: string;
    onStart: () => void;
}
/**
 * Detect media type from URL extension
 */
function detectMediaType(url: string): 'image' | 'video' | 'gif' {
    const lowerUrl = url.toLowerCase();
    // Check for video extensions
    if (lowerUrl.includes('.mp4') || lowerUrl.includes('.webm') || lowerUrl.includes('.mov') || lowerUrl.includes('.ogg')) {
        return 'video';
    }
    // Check for GIF
    if (lowerUrl.includes('.gif')) {
        return 'gif';
    }
    // Default to image
    return 'image';
}
/**
 * Create lazy load UI with thumbnail and start button
 * Supports image, video, and GIF thumbnails
 * Returns the container element for later removal
 */
export function createLazyLoadUI(container: HTMLElement, options: LazyLoadUIOptions): HTMLElement {
    const { thumbnailUrl, thumbnailType, buttonText = 'Start Experience', uiColor = '#4CAF50', onStart } = options;
    // Inject styles if not already present
    injectStyles(uiColor, 'minimal', undefined, container.id);
    // Add container class
    container.classList.add('storysplat-viewer-container');
    // Create lazy load container
    const lazyLoadContainer = document.createElement('div');
    lazyLoadContainer.className = 'storysplat-lazy-load-container';
    // Determine media type (auto-detect from URL takes priority over stored type,
    // so manually swapping e.g. a .jpg URL for a .mp4 URL works correctly)
    const mediaType = (thumbnailUrl ? detectMediaType(thumbnailUrl) : null) || thumbnailType || 'image';
    // Build HTML content
    let html = '';
    // Thumbnail if provided - supports image, video, and GIF
    if (thumbnailUrl) {
        if (mediaType === 'video') {
            // Video thumbnail - autoplay, muted, loop for background video effect
            html += `<video class="storysplat-lazy-load-thumbnail-video" src="${thumbnailUrl}" autoplay muted loop playsinline webkit-playsinline></video>`;
        }
        else {
            // Image or GIF thumbnail (GIFs work naturally with img tags)
            html += `<img class="storysplat-lazy-load-thumbnail" src="${thumbnailUrl}" alt="Scene preview" />`;
        }
    }
    // Overlay gradient
    html += `<div class="storysplat-lazy-load-overlay"></div>`;
    // Content with start button
    html += `
    <div class="storysplat-lazy-load-content">
      <button class="storysplat-lazy-load-start-btn" style="background: ${uiColor}">
        <svg viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z"/>
        </svg>
        ${buttonText}
      </button>
    </div>
  `;
    lazyLoadContainer.innerHTML = html;
    container.appendChild(lazyLoadContainer);
    // Add click handler to start button
    const startBtn = lazyLoadContainer.querySelector('.storysplat-lazy-load-start-btn');
    startBtn?.addEventListener('click', () => {
        // Stop video playback if present before removing
        const video = lazyLoadContainer.querySelector('video');
        if (video) {
            video.pause();
            video.src = ''; // Clear source to stop loading
        }
        // Fade out and remove
        lazyLoadContainer.style.transition = 'opacity 0.3s ease-out';
        lazyLoadContainer.style.opacity = '0';
        setTimeout(() => {
            lazyLoadContainer.remove();
            onStart();
        }, 300);
    });
    return lazyLoadContainer;
}

// ---- Overlays (trigger-driven information panels) ----

const layerObservers = new WeakMap<HTMLElement, ResizeObserver>();

/** Remove an overlay layer and stop its resize tracking (call from the viewer's destroy()). */
export function destroyOverlayLayer(layer: HTMLElement): void {
    layerObservers.get(layer)?.disconnect();
    layerObservers.delete(layer);
    const host = layer.parentElement;
    host?.classList.remove('storysplat-overlay-locked', 'storysplat-drawer-open-left', 'storysplat-drawer-open-right');
    host?.style.removeProperty('--storysplat-drawer-left');
    host?.style.removeProperty('--storysplat-drawer-right');
    layer.remove();
}

/** The layer that holds overlay panels; created once per viewer, above the canvas and below popups. */
export function createOverlayLayer(container: HTMLElement): HTMLElement {
    const previous = container.querySelector<HTMLElement>('.storysplat-overlay-layer');
    if (previous) destroyOverlayLayer(previous);
    container.classList.remove('storysplat-overlay-locked');
    const layer = document.createElement('div');
    layer.className = 'storysplat-overlay-layer';
    // Scrim behind drawer sheets in narrow embeds (drawers are modal, like on desktop):
    // tapping it dismisses every open drawer sheet. Floating sheets have no scrim.
    const scrim = document.createElement('div');
    scrim.className = 'storysplat-overlay-scrim';
    scrim.addEventListener('click', () => {
        layer.querySelectorAll('.storysplat-overlay--drawer.visible').forEach((el) => {
            (el as OverlaySheetElement).__dismiss?.();
        });
    });
    layer.appendChild(scrim);
    // Mirror the container query into a data attribute so the drag / dismiss logic knows
    // when overlays are rendered as bottom sheets. Browsers without container queries get
    // the sheet CSS from the viewport-width media query, so measure the same thing they do.
    const containerQueries = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('container-type: inline-size');
    const update = () => {
        const width = containerQueries ? layer.clientWidth : window.innerWidth;
        layer.dataset.compact = width <= COMPACT_EMBED_WIDTH ? '1' : '';
        syncDrawerInsets(layer);
    };
    if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(update);
        observer.observe(containerQueries ? layer : document.documentElement);
        layerObservers.set(layer, observer);
    }
    container.appendChild(layer);
    update();
    return layer;
}

/** Embeds at or below this width render overlays as sheets. Keep in sync with the CSS. */
export const COMPACT_EMBED_WIDTH = 600;
type OverlaySheetElement = HTMLElement & { __dismiss?: () => void };
export const isCompactLayer = (layer: HTMLElement | null): boolean => layer?.dataset.compact === '1';

/**
 * Standard sheet gesture for a panel that is a sheet in narrow embeds: drag the handle
 * toward the edge the sheet is attached to (down for a bottom sheet, up for a top sheet) to
 * dismiss, spring back on a short drag. `isSheet` is consulted at gesture start so the same
 * panel works as a side drawer / floating panel on desktop.
 */
export function attachSheetDrag(panel: HTMLElement, grip: HTMLElement, isSheet: () => boolean, dismiss: () => void, direction: 'down' | 'up' = 'down'): void {
    const sign = direction === 'down' ? 1 : -1;
    let startY = 0;
    let dragging = false;
    let pointerId = -1;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    const onMove = (event: PointerEvent) => {
        if (!dragging || event.pointerId !== pointerId) return;
        const dy = Math.max(0, sign * (event.clientY - startY));
        const now = performance.now();
        if (now > lastT) velocity = sign * (event.clientY - lastY) / (now - lastT);
        lastY = event.clientY;
        lastT = now;
        panel.style.transition = 'none';
        // The sheet rules pin transform with !important; only an important inline value moves it.
        panel.style.setProperty('transform', `translateY(${sign * dy}px)`, 'important');
    };
    const finish = (event: PointerEvent) => {
        if (!dragging || event.pointerId !== pointerId) return;
        dragging = false;
        grip.releasePointerCapture?.(event.pointerId);
        const dy = Math.max(0, sign * (event.clientY - startY));
        panel.style.transition = '';
        panel.style.removeProperty('transform');
        const threshold = Math.min(120, panel.offsetHeight * 0.3);
        if (dy > threshold || (dy > 24 && velocity > 0.6)) dismiss();
    };
    grip.addEventListener('pointerdown', (event) => {
        // A second finger on the handle must not restart or hijack the gesture.
        if (dragging || !isSheet() || event.button !== 0) return;
        dragging = true;
        pointerId = event.pointerId;
        startY = lastY = event.clientY;
        lastT = performance.now();
        velocity = 0;
        grip.setPointerCapture?.(event.pointerId);
        event.stopPropagation();
    });
    grip.addEventListener('pointermove', onMove);
    grip.addEventListener('pointerup', finish);
    grip.addEventListener('pointercancel', finish);
}

/** Drawer side for an anchor: *-left docks left, anything else (or unset) docks right. */
export function popupDrawerSide(anchor: string | undefined): 'left' | 'right' {
    return anchor && anchor.endsWith('left') ? 'left' : 'right';
}

const OVERLAY_ANCHORS = new Set([
    'top-left', 'top-center', 'top-right', 'center-left', 'center', 'center-right',
    'bottom-left', 'bottom-center', 'bottom-right',
]);

/** Overlay legacy media fields: the same policy as content-block media (local asset URLs included). */
const safeMediaUrl = (url: unknown): string | null => safeUrl(url, 'media');

/** Layout / anchor classes of an overlay panel (without the transient 'visible' state). */
function overlayPanelClass(overlay: OverlayData): string {
    if (overlay.layout !== 'floating') {
        // Edge-to-edge drawer is the default; an unset anchor docks right.
        return `storysplat-overlay storysplat-overlay--drawer storysplat-overlay--drawer-${popupDrawerSide(overlay.anchor)}`;
    }
    const anchor = OVERLAY_ANCHORS.has(overlay.anchor || '') ? overlay.anchor! : 'bottom-left';
    return `storysplat-overlay storysplat-overlay--${anchor}`;
}

function overlayPanelWidth(overlay: OverlayData): string {
    const width = Number(overlay.width);
    return Number.isFinite(width) && width > 0 ? `${Math.round(width)}px` : '360px';
}

/**
 * Apply an edit to a panel that is on screen without rebuilding it, so typing in the editor
 * does not replay the entrance animation or reload its media. Handles text, title, link
 * labels, width and anchor. Returns false (the caller rebuilds) for anything that
 * changes the panel's structure or media: dismissibility, layout, media URLs / options, adding or
 * removing a title, text or block, reordering blocks.
 */
export function updateOverlayElement(panel: HTMLElement, prev: OverlayData, next: OverlayData, labels?: ButtonLabels): boolean {
    const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    // The top banner lives outside the overlay layer: switching to or from it rebuilds.
    if ((prev.layout === 'top') !== (next.layout === 'top')) return false;
    if (next.layout === 'top') {
        const h2 = panel.querySelector('h2');
        const p = panel.querySelector<HTMLElement>(':scope > .storysplat-rich-text');
        if (!h2 || !p) return false;
        h2.textContent = next.title || '';
        p.innerHTML = richTextHtml(next.subtitle, next.subtitleHtml);
        return true;
    }
    if ((prev.dismissible !== false) !== (next.dismissible !== false)) return false;
    // Drawer <-> floating changes the sheet's swipe direction, which is bound at build time.
    if ((prev.layout !== 'floating') !== (next.layout !== 'floating')) return false;
    if (!!prev.title !== !!next.title) return false;
    const prevRich = contentBlocksHaveContent(prev.contentBlocks);
    const nextRich = contentBlocksHaveContent(next.contentBlocks);
    if (prevRich !== nextRich) return false;
    const title = panel.querySelector<HTMLElement>(':scope > .storysplat-overlay-title');
    if (!!next.title !== !!title) return false;
    const textPatches: Array<[HTMLElement, string]> = [];
    const richPatches: Array<[HTMLElement, string]> = [];
    if (nextRich) {
        const a = prev.contentBlocks!;
        const b = next.contentBlocks!;
        if (a.length !== b.length) return false;
        const content = panel.querySelector<HTMLElement>(':scope > .storysplat-overlay-content');
        if (!content) return false;
        // Gallery alts and iframe titles are derived from the title; rebuild media then.
        if (prev.title !== next.title && content.querySelector('iframe, img')) return false;
        const paragraphs = Array.from(content.querySelectorAll<HTMLElement>(':scope > .storysplat-rich-text'));
        const links = Array.from(content.querySelectorAll<HTMLElement>(':scope > .storysplat-hotspot-popup-link'));
        let p = 0;
        let l = 0;
        for (let i = 0; i < b.length; i++) {
            const x = a[i];
            const y = b[i];
            if (x.kind !== y.kind) return false;
            if (y.kind === 'text') {
                const el = paragraphs[p++];
                if (!el) return false;
                richPatches.push([el, richTextHtml(y.text, y.textHtml)]);
            } else if (y.kind === 'link' && x.kind === 'link') {
                if (!same(x.url, y.url) || !same(x.buttonColor, y.buttonColor)) return false;
                if (!attrUrl(y.url, 'link')) continue; // not rendered either way
                const el = links[l++];
                if (!el) return false;
                textPatches.push([el, y.text || getButtonLabel(labels, 'openExternalLink')]);
            } else if (!same({ ...x, id: undefined }, { ...y, id: undefined })) {
                return false;
            }
        }
        if (p !== paragraphs.length || l !== links.length) return false;
    } else {
        const media = (o: OverlayData) => [o.imageUrl, o.imageAlt, o.videoUrl, o.videoAutoplay, o.videoLoop, o.videoMuted, o.linkUrl];
        if (!same(media(prev), media(next))) return false;
        if (!!prev.text !== !!next.text) return false;
        if (!next.imageAlt && prev.title !== next.title && panel.querySelector('.storysplat-overlay-image')) return false;
        const text = panel.querySelector<HTMLElement>('.storysplat-overlay-text');
        if (next.text) {
            if (!text) return false;
            richPatches.push([text, richTextHtml(next.text, next.textHtml)]);
        }
        const link = panel.querySelector<HTMLElement>('.storysplat-overlay-link');
        if (link) textPatches.push([link, next.linkText || link.getAttribute('href') || '']);
    }
    // Everything checked: apply.
    if (title && next.title) title.textContent = next.title;
    for (const [el, value] of textPatches) if (el.textContent !== value) el.textContent = value;
    for (const [el, value] of richPatches) if (el.innerHTML !== value) el.innerHTML = value;
    if (next.title) panel.setAttribute('aria-label', next.title);
    else panel.removeAttribute('aria-label');
    panel.style.width = overlayPanelWidth(next);
    const visible = panel.classList.contains('visible');
    panel.className = overlayPanelClass(next);
    if (visible) panel.classList.add('visible');
    if (panel.parentElement) syncOverlayScrim(panel.parentElement);
    return true;
}

/**
 * Build a panel for one overlay. Content is authored data, so everything is set
 * through textContent/attributes, never innerHTML. Returns null for an overlay
 * with nothing to show.
 */
export function buildOverlayElement(
    overlay: OverlayData,
    labels: ButtonLabels | undefined,
    onDismiss: () => void,
    /** The scene protects its media (uiOptions.protectMedia): PDFs use the in-viewer renderer. */
    opts: { protectMedia?: boolean } = {},
): HTMLElement | null {
    if (overlay.layout === 'top') return buildTopOverlayElement(overlay);
    const panel = document.createElement('div');
    const drawer = overlay.layout !== 'floating'; // edge-to-edge is the default
    panel.className = overlayPanelClass(overlay);
    panel.dataset.overlayId = overlay.id;
    panel.setAttribute('role', 'dialog');
    if (overlay.title) panel.setAttribute('aria-label', overlay.title);
    panel.style.width = overlayPanelWidth(overlay);

    let hasContent = false;
    (panel as OverlaySheetElement).__dismiss = overlay.dismissible !== false ? onDismiss : undefined;
    // Drag handle: visible only in narrow embeds, where a drawer is a bottom sheet (swipe down)
    // and a floating panel a sheet under the toolbar (handle on its lower edge, swipe up).
    // Only dismissible overlays get one — a grab handle that cannot move anything misleads.
    if (overlay.dismissible !== false) {
        const handle = document.createElement('div');
        handle.className = 'storysplat-overlay-handle';
        handle.setAttribute('aria-hidden', 'true');
        panel.appendChild(handle);
        attachSheetDrag(panel, handle, () => isCompactLayer(panel.parentElement), onDismiss, drawer ? 'down' : 'up');
    }
    if (overlay.dismissible !== false) {
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'storysplat-overlay-close';
        close.setAttribute('aria-label', getButtonLabel(labels, 'close'));
        close.textContent = '×';
        close.addEventListener('click', (event) => {
            event.stopPropagation();
            onDismiss();
        });
        panel.appendChild(close);
    }
    if (overlay.title) {
        const title = document.createElement('h3');
        title.className = 'storysplat-overlay-title';
        title.textContent = overlay.title;
        panel.appendChild(title);
        hasContent = true;
    }
    // Rich content blocks (same pipeline as hotspot popups) replace the single fields.
    if (contentBlocksHaveContent(overlay.contentBlocks)) {
        const content = document.createElement('div');
        content.className = 'storysplat-overlay-content';
        // Text fields are escaped in buildContentBlocksHtml(); media urls are authored data.
        content.innerHTML = buildContentBlocksHtml(overlay.contentBlocks!, overlay.title, labels, { pdfInViewer: opts.protectMedia }); // eslint-disable-line -- structured HTML with escaped user text
        panel.appendChild(content);
        isolatePanelInput(panel);
        return panel;
    }
    // Legacy single fields: a scrolling body under the pinned title / close button.
    const body = document.createElement('div');
    body.className = 'storysplat-overlay-body';
    const imageUrl = safeMediaUrl(overlay.imageUrl);
    if (imageUrl) {
        const img = document.createElement('img');
        img.className = 'storysplat-overlay-image';
        img.src = imageUrl;
        img.alt = overlay.imageAlt || overlay.title || '';
        img.loading = 'lazy';
        img.draggable = false;
        body.appendChild(img);
        hasContent = true;
    }
    const videoUrl = safeMediaUrl(overlay.videoUrl);
    if (videoUrl) {
        const video = document.createElement('video');
        video.className = 'storysplat-overlay-video';
        video.src = videoUrl;
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.loop = overlay.videoLoop === true;
        video.muted = overlay.videoMuted === true || overlay.videoAutoplay === true;
        if (overlay.videoAutoplay) video.dataset.autoplay = '1';
        body.appendChild(video);
        hasContent = true;
    }
    if (overlay.text) {
        const text = document.createElement('div');
        text.className = 'storysplat-overlay-text storysplat-rich-text';
        text.innerHTML = richTextHtml(overlay.text, overlay.textHtml);
        body.appendChild(text);
        hasContent = true;
    }
    const linkUrl = safeUrl(overlay.linkUrl, 'link');
    if (linkUrl && !linkUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.className = 'storysplat-overlay-link';
        link.href = linkUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = overlay.linkText || linkUrl;
        body.appendChild(link);
        hasContent = true;
    }
    if (body.childElementCount > 0) panel.appendChild(body);
    isolatePanelInput(panel);
    return hasContent ? panel : null;
}

/**
 * A 'top' overlay: the waypoint banner's markup (title and subtitle) under its own class, so it
 * looks exactly like a waypoint's name and info. Nothing to show without either.
 */
function buildTopOverlayElement(overlay: OverlayData): HTMLElement | null {
    if (!overlay.title && !overlay.subtitle) return null;
    const panel = document.createElement('div');
    panel.className = 'storysplat-overlay-top';
    panel.dataset.overlayId = overlay.id;
    panel.setAttribute('role', 'status');
    const title = document.createElement('h2');
    title.textContent = overlay.title || '';
    const subtitle = document.createElement('div');
    subtitle.className = 'storysplat-rich-text';
    subtitle.innerHTML = richTextHtml(overlay.subtitle, overlay.subtitleHtml);
    panel.append(title, subtitle);
    return panel;
}

/** The waypoint banner yields while a 'top' overlay is up. */
const syncTopOverlayState = (container: HTMLElement) => {
    container.classList.toggle('storysplat-top-overlay-active', !!container.querySelector(':scope > .storysplat-overlay-top.visible'));
};

/** Show a 'top' overlay in the waypoint banner's place: in the container, not the overlay layer. */
export function showTopOverlayElement(container: HTMLElement, panel: HTMLElement): void {
    if (panel.parentElement !== container) container.appendChild(panel);
    panel.classList.add('visible');
    syncTopOverlayState(container);
}

export function hideTopOverlayElement(panel: HTMLElement): void {
    const container = panel.parentElement;
    panel.classList.remove('visible');
    panel.remove();
    if (container) syncTopOverlayState(container);
}

/**
 * Keep panel interaction (scrolling, clicks, typing in embedded controls) from steering the
 * camera behind it. Escape still bubbles so a focused panel can be dismissed.
 */
function isolatePanelInput(panel: HTMLElement): void {
    for (const type of ['pointerdown', 'touchstart', 'wheel'] as const) {
        panel.addEventListener(type, (event) => event.stopPropagation(), { passive: true });
    }
    panel.addEventListener('keydown', (event) => { if (event.key !== 'Escape') event.stopPropagation(); });
}

/**
 * While a side drawer is open (desktop: in narrow embeds drawers are bottom sheets), publish its
 * width on the viewer container so the top bar's buttons on that side slide clear of it instead of
 * sitting hidden under the drawer.
 */
export function syncDrawerInsets(layer: HTMLElement): void {
    const host = layer.parentElement;
    if (!host) return;
    const compact = isCompactLayer(layer);
    // A hotspot popup shown as a drawer (per hotspot, or the theme's default layout) is a
    // full-height panel docked to one edge; computed left / right ignore its slide-in transform.
    const popup = host.querySelector<HTMLElement>(':scope > .storysplat-hotspot-popup.visible');
    let popupSide: 'left' | 'right' | null = null;
    if (popup && !compact && popup.offsetHeight >= host.clientHeight - 2 && popup.offsetWidth < host.clientWidth * 0.9) {
        const cs = getComputedStyle(popup);
        popupSide = parseFloat(cs.right) === 0 ? 'right' : parseFloat(cs.left) === 0 ? 'left' : null;
    }
    for (const side of ['left', 'right'] as const) {
        let width = popupSide === side ? popup!.offsetWidth : 0;
        if (!compact) {
            layer.querySelectorAll<HTMLElement>(`.storysplat-overlay--drawer-${side}.visible`).forEach((el) => { width = Math.max(width, el.offsetWidth); });
        }
        host.style.setProperty(`--storysplat-drawer-${side}`, `${width}px`);
        host.classList.toggle(`storysplat-drawer-open-${side}`, width > 0);
    }
}

/** Re-measure side drawers after the hotspot popup opens or closes. */
function syncPopupDrawerInsets(container: HTMLElement): void {
    const layer = container.querySelector<HTMLElement>(':scope > .storysplat-overlay-layer');
    if (layer) syncDrawerInsets(layer);
}

const syncOverlayScrim = (layer: HTMLElement) => {
    syncDrawerInsets(layer);
    // Only a dismissible drawer sheet raises the scrim: a non-dismissible one must not trap
    // the visitor behind a scrim that nothing can tap away.
    const drawers = Array.from(layer.querySelectorAll('.storysplat-overlay--drawer.visible')) as OverlaySheetElement[];
    layer.classList.toggle('has-sheet', drawers.some((el) => typeof el.__dismiss === 'function'));
    // A visible overlay visitors cannot close lifts the movement / tour controls above the
    // layer (see .storysplat-overlay-locked), so it can never trap them.
    const locked = (Array.from(layer.querySelectorAll('.storysplat-overlay.visible')) as OverlaySheetElement[]).some((el) => typeof el.__dismiss !== 'function');
    layer.parentElement?.classList.toggle('storysplat-overlay-locked', locked);
};

const pendingShowFrame = new WeakMap<HTMLElement, number>();
const pendingRemoval = new WeakMap<HTMLElement, number>();

export function showOverlayElement(layer: HTMLElement, panel: HTMLElement): void {
    const removal = pendingRemoval.get(panel);
    if (removal !== undefined) { clearTimeout(removal); pendingRemoval.delete(panel); }
    if (!panel.isConnected) layer.appendChild(panel);
    const content = panel.querySelector('.storysplat-overlay-content') as HTMLElement | null;
    if (content && !panel.dataset.hydrated) {
        panel.dataset.hydrated = '1';
        hydratePopupMedia(panel, content);
    } else if (content) {
        // Re-shown after a hide: autoplay videos again (galleries/models stay wired).
        content.querySelectorAll('video[data-autoplay="true"]').forEach((v) => { (v as HTMLVideoElement).play().catch(() => { /* needs a gesture */ }); });
    }
    // Two frames so the entrance transition runs from the initial state. Tracked so a hide
    // within that window cancels it, instead of a stray frame marking a hidden panel visible.
    pendingShowFrame.set(panel, requestAnimationFrame(() => {
        pendingShowFrame.set(panel, requestAnimationFrame(() => {
            pendingShowFrame.delete(panel);
            panel.classList.add('visible');
            syncOverlayScrim(layer);
        }));
    }));
    const video = panel.querySelector('video');
    if (video && video.dataset.autoplay === '1') {
        video.currentTime = 0;
        video.play().catch(() => { /* autoplay may be blocked without a gesture */ });
    }
}

export function hideOverlayElement(panel: HTMLElement): void {
    const frame = pendingShowFrame.get(panel);
    if (frame !== undefined) { cancelAnimationFrame(frame); pendingShowFrame.delete(panel); }
    panel.classList.remove('visible');
    if (panel.parentElement) syncOverlayScrim(panel.parentElement);
    if (panel.dataset.hydrated) {
        // Full teardown (lightbox, videos, model viewers, pdf renders); the panel is rebuilt on next show.
        teardownPopupMedia(panel);
        delete panel.dataset.hydrated;
    } else {
        const video = panel.querySelector('video');
        if (video) video.pause();
    }
    const done = () => {
        pendingRemoval.delete(panel);
        panel.removeEventListener('transitionend', onEnd);
        if (!panel.classList.contains('visible')) panel.remove();
    };
    // Only the panel's own transition counts; a child's (the close button's hover) must not
    // cut the fade short.
    const onEnd = (event: TransitionEvent) => { if (event.target === panel) done(); };
    panel.addEventListener('transitionend', onEnd);
    pendingRemoval.set(panel, window.setTimeout(done, 400));
}
